import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AcessosAosPortais from './licitacoes/AcessosAosPortais';
import { CartaoDoTopo } from './shared/lista/PecasDaLista';
import { COMPANY, SIGNERS } from '../config/company';
import { procurementService } from '../services/api';
import { disputeService } from '../services/disputeService';
import { fmtDate, fmtDateTime, fmtDateTimeDeParede, fmtTimeWithSeconds, getOrgaoDisplay, getModoDisputaBadge, getStructureBadge, formatNumeroCompraComAno, getProrrogacao, isLicitacaoOver } from '../utils/formatters';
import { pncpPublicUrl, comprasnetPublicUrl } from '../utils/procurementEnums';
import { computeProposalTotal, ChargeType } from '../utils/proposalMath';
import ProposalWorkspace from './licitacoes/ProposalWorkspace';
import SignatureBatchModal from './licitacoes/SignatureBatchModal';
import { useProposalWorkspace } from '../hooks/useProposalWorkspace';
import { useTravaDeEnvio } from '../hooks/useTravaDeEnvio';
import {
  CONSEQUENCIA_DA_DESISTENCIA_LICITANET, DESFECHO_DESCONHECIDO, fraseDaRecusaDaDesistenciaLicitanet, pedirDesistenciaGkz, resultadoDaDesistenciaLicitanet, rotaDaDesistencia,
} from './licitacoes/rotaDaDesistencia';
import { decidirAdequacao } from './licitacoes/adequacao';
import { conferirAntesDeEnviar } from './licitacoes/conferenciaDoEnvio';
import { desfechoJaCapturado, fraseDoDesfechoQueNaoGravou } from './licitacoes/desfechoAoEncerrar';
import { confirmacaoDeMandarParaDisputa } from '../utils/disputaEscrita';
import { PROPOSAL_STATUS_LABELS, proposalStatusBadge, TONE_BADGE, TONE_BORDER } from '../utils/statusColors';
import { deriveProposalStage, STAGE_LABELS, STAGE_TONE, classifyProposals, proposalNextEventTs, isProrrogadoSemData, pricedItems } from '../utils/proposalStage';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import { useConfirm, usePedirTexto } from './shared/ConfirmDialog';
import { CANCELADO, comLinkDoPortal, PedidoDeLink } from '../utils/linkDoPortal';
import { sincronizarPeloJob, FRASE_REAPROVEITADO } from '../utils/scrapeAssincrono';
import { avisosNaoApurados, EXPLICACAO_NAO_APURADO, TEXTO_NAO_APURADO } from '../utils/avisosDoPortal';
import { suspensaoDaLicitacao } from '../utils/suspensaoDaLicitacao';
import { useEstadoDaCarga, mensagemDoErroDeCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, campo, campoComEstado } from '../design';
import PanelHeader from './shared/PanelHeader';
import CascaDeModal from './shared/CascaDeModal';
import { seloDoEnvio, passosDaProva, SeloDoEnvio } from '../utils/seloDeEnvio';
import CascaDeGaveta from './shared/CascaDeGaveta';
import MaisAcoes, { ItemDeMenu } from './shared/MaisAcoes';
import NovaVersaoModal from './licitacoes/NovaVersaoModal';
import HistoricoDeVersoesModal from './licitacoes/HistoricoDeVersoesModal';
import { acaoPrincipalDoDocumento, mensagemDoErroDaVersao } from '../utils/novaVersaoDoDocumento';
import { DocumentViewerModal } from './shared/DocumentViewerModal';
import { lerPar, ladoEscolhido, pedidoDeJuntar } from './licitacoes/revisaoDuplicatas';
import EncontrarTable from './licitacoes/EncontrarTable';
import { ITEM_STATUSES, PORTAL_MAP, PORTAIS_COM_AUTOMACAO, MURAL_PORTAL_KEYS, fmt, getStatusInfo, isMuralSource, getDispensaBadge, operationalPortalUrl, portalLinkUrl, portalKeyForAccount, accountIdForSource } from './licitacoes/procurementUi';
import PortalSelect from './licitacoes/PortalSelect';
import PortalAccountSelect from './licitacoes/PortalAccountSelect';
import { buildProposalFormData as buildProposalFormDataPure } from '../utils/proposalForm';
import MonitoringView from './MonitoringView';
import DisputeView from './DisputeView';
import PipelineView from './PipelineView';
import ComprasNetHealthDot from './ComprasNetHealthDot';
import { useAuth } from '../contexts/AuthContext';
import { estadoDaAgenda, SELO_DA_AGENDA, mensagemDoMarcar, mensagemDoRemover, EstadoDaAgenda } from './licitacoes/agendaGoogle';
import AgendaGooglePanel from './licitacoes/AgendaGooglePanel';
import type { JobDaLista } from '../utils/jobDeAutomacao';

// Vocabulário alinhado com situacaoCompraFaseExterna do portal ComprasNet
// (confirmado via HAR real 24/05/2026). 11 estados ATIVOS (era 16).
// Deprecated (não mostrados no dropdown): INTERESSADO→ANALISANDO,
// ADJUDICADO→HOMOLOGADO, ANULADA→REVOGADA, GANHO/PERDIDO→HOMOLOGADO.
// SQL migration anterior: 5 items 'perdido' → 'homologado'.

// Cores de status vêm de utils/statusColors (fonte única — guia §4).
const PROPOSAL_STATUSES_MAP: Record<string, { label: string; color: string }> =
  Object.fromEntries(
    Object.keys(PROPOSAL_STATUS_LABELS).map((k) => [k, proposalStatusBadge(k)]),
  );



/** Editor de chips pros campos de keywords do Perfil de Busca (Enter/vírgula
 *  adiciona, × ou Backspace remove, colar lista com vírgulas funciona).
 *  Mantém o valor como string separada por vírgula — o save já fazia split. */
function KeywordChipsInput({ value, onChange, placeholder, chipClass }: {
  value: string; onChange: (v: string) => void; placeholder?: string; chipClass: string;
}) {
  const [draft, setDraft] = useState('');
  const tokens = value.split(',').map((t) => t.trim()).filter(Boolean);
  const commit = (raw: string) => {
    const parts = raw.split(',').map((t) => t.trim()).filter(Boolean);
    if (parts.length === 0) { setDraft(''); return; }
    const merged = [...tokens];
    for (const p of parts) {
      if (!merged.some((t) => t.toLowerCase() === p.toLowerCase())) merged.push(p);
    }
    onChange(merged.join(', '));
    setDraft('');
  };
  const removeAt = (i: number) => onChange(tokens.filter((_, idx) => idx !== i).join(', '));
  return (
    <div className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-2 py-1.5 flex flex-wrap gap-1.5 items-center focus-within:ring-2 focus-within:ring-orange-500">
      {tokens.map((t, i) => (
        <span key={`${t}-${i}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${chipClass}`}>
          {t}
          <button type="button" onClick={() => removeAt(i)} className="hover:text-white leading-none" title="Remover">×</button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => { const v = e.target.value; if (v.includes(',')) commit(v); else setDraft(v); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
          else if (e.key === 'Backspace' && !draft && tokens.length > 0) removeAt(tokens.length - 1);
        }}
        onBlur={() => commit(draft)}
        placeholder={tokens.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[140px] bg-transparent text-sm text-white placeholder-slate-400 outline-none py-0.5"
      />
    </div>
  );
}

/** Fonte "portal" que na verdade é mural de transparência/diário oficial —
 *  o AlertaLicitação achou o AVISO na publicação, mas o portal de disputa real
 *  é desconhecido (ex.: mural TCE-PI, diário municipal SC, DIO-ES, CEPE-PE).
 *  Detecta pelo link (padrões de diário/mural) ou pela sigla conhecida. */

/** Badge informativo de dispensa (não auto-descarta — só sinaliza pro user).
 *  - "Aviso de Contratação Direta" + sem portal      → 📧 Dispensa por email (envio direto).
 *  - "Ato que autoriza a Contratação Direta"         → 🚫 Já contratada (geralmente auto-lixeira).
 *  - Retorna null nos demais casos — inclusive aviso de CD COM portal (participável
 *    normal): esse selo ("🏛️ Aviso de CD") era redundante com os badges DE/C-disputa
 *    da coluna NO/MOD e foi removido 05/07/26 a pedido do user.
 */

const BR_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const MODALIDADES: Record<number, string> = {
  1: 'Leilão - Eletrônico',
  2: 'Diálogo Competitivo',
  3: 'Concurso',
  4: 'Concorrência - Eletrônica',
  5: 'Concorrência - Presencial',
  6: 'Pregão - Eletrônico',
  7: 'Pregão - Presencial',
  8: 'Dispensa de Licitação',
  9: 'Inexigibilidade',
  10: 'Manifestação de Interesse',
  11: 'Pré-qualificação',
  12: 'Credenciamento',
  13: 'Leilão - Presencial',
};



// Sugestões de CATSER (Catálogo de Serviços gov.br) pra produtos de telecom —
// STFC (1411) + SMP (1412). Autocomplete no editor de itens da proposta.
// (O "Modelo STFC", que montava 6 linhas de telefonia por minuto, saiu em 21/10/2026: nenhuma das 248 propostas de
// produção tinha rastro dele. Está em baadab7 se um edital pedir de novo.)
const CATSER_TELECOM: { code: string; label: string }[] = [
  { code: '26085', label: 'Taxa de instalação/habilitação STFC' },
  { code: '27731', label: 'Assinatura de entroncamento digital E1' },
  { code: '26093', label: 'Assinatura de DDR' },
  { code: '26107', label: 'Assinatura de ramal telefônico' },
  { code: '26182', label: 'Assinatura de linha analógica' },
  { code: '30177', label: 'Assinatura de serviço de PABX IP' },
  { code: '26115', label: 'Chamadas locais FIXO-FIXO (STFC-LOCAL-FF)' },
  { code: '26123', label: 'Chamadas locais FIXO-MÓVEL (STFC-LOCAL-FM VC1)' },
  { code: '26131', label: 'Chamadas nacionais FIXO-FIXO (STFC-LDN-FF)' },
  { code: '26140', label: 'Chamadas nacionais FIXO-MÓVEL (STFC-LDN-FM VC2/VC3)' },
  { code: '26166', label: 'Taxa instalação link de internet STFC' },
  { code: '26174', label: 'Acesso à internet STFC (banda larga)' },
  { code: '26190', label: 'Taxa instalação DDG (0800)' },
  { code: '26204', label: 'Assinatura DDG (0800)' },
  { code: '26271', label: 'Chamadas locais MÓVEL-MÓVEL intra (SMP VC1)' },
  { code: '26280', label: 'Chamadas locais MÓVEL-MÓVEL extra (SMP VC1)' },
  { code: '26298', label: 'Chamadas locais MÓVEL-FIXO (SMP VC1)' },
  { code: '26301', label: 'Chamadas nacionais MÓVEL-MÓVEL intra (SMP VC2/VC3)' },
  { code: '26310', label: 'Chamadas nacionais MÓVEL-MÓVEL extra (SMP VC2/VC3)' },
  { code: '26328', label: 'Chamadas nacionais MÓVEL-FIXO (SMP VC2/VC3)' },
  { code: '26344', label: 'Acesso à internet móvel (banda larga)' },
  { code: '26352', label: 'Mensagens de texto (SMS)' },
  { code: '26387', label: 'Pacote de serviços SMP (voz, dados, SMS)' },
];

// Critérios de desempate PADRÃO do PCP (Art. 60 Lei 14.133). Só aparecem no portal
// após salvar as declarações, então o scrape não os pega na 1ª sync — mostramos esse
// default no editor pro user ajustar (o backend também semeia no enrichProposals).
const DEFAULT_PCP_DESEMPATE_FE: { field: string; label: string; resposta: string }[] = [
  { field: 'desempate-default-1', label: 'Declaro para os devidos fins legais, realizar ações de equidade entre homens e mulheres.', resposta: 'Sim' },
  { field: 'desempate-default-2', label: 'Declaro para os devidos fins legais, realizar ações de integridade, conforme orientações dos órgãos de controle.', resposta: 'Sim' },
  { field: 'desempate-default-3', label: 'Empresa estabelecida no território do Estado ou do Distrito Federal do órgão licitante.', resposta: 'Não' },
  { field: 'desempate-default-4', label: 'Empresa brasileira.', resposta: 'Sim' },
  { field: 'desempate-default-5', label: 'Empresa que investe em pesquisa e no desenvolvimento de tecnologia no País.', resposta: 'Sim' },
  { field: 'desempate-default-6', label: 'Empresa capaz de comprovar a prática de mitigação, nos termos da Lei nº 12.187, de 29 de dezembro de 2009.', resposta: 'Sim' },
];


const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    search: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    plus: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
    x: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
    refresh: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
    upload: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>,
    edit: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
    trash: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    star: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>,
    starFilled: <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>,
    externalLink: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
    play: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    check: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
    chevronLeft: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>,
    chevronRight: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
    robot: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    folder: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>,
    folderOpen: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" /></svg>,
    chevronDown: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>,
    list: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>,
    grid: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>,
    alertTriangle: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
    document: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    clock: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    download: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
    eye: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    'x-circle': <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    bell: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
    target: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /><circle cx="12" cy="12" r="6" strokeWidth={2} /><circle cx="12" cy="12" r="2" strokeWidth={2} /></svg>,
    calendar: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  };
  return icons[name] || null;
};

export default function LicitacoesTab({ subView = 'items', onNavigate, embedded = false, externalSearch, openItemId, openAdequar = false }: { subView?: string; onNavigate?: (section: string) => void; embedded?: boolean; externalSearch?: string; openItemId?: number | null; openAdequar?: boolean }) {
  // View state (controlled by parent via sidebar navigation)
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'pipeline'>(() => {
    try { return (localStorage.getItem('lic_view_mode') as 'table' | 'pipeline') || 'table'; } catch { return 'table'; }
  });
  const [pipelineRefreshKey, setPipelineRefreshKey] = useState(0);

  // Dashboard
  const [dashboard, setDashboard] = useState<Record<string, number>>({});

  // Items
  const [readItemIds, setReadItemIds] = useState<Set<number>>(() => {
    try { const stored = localStorage.getItem('lic_read_items'); return stored ? new Set(JSON.parse(stored)) : new Set(); } catch { return new Set(); }
  });
  const markItemRead = (id: number) => {
    setReadItemIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem('lic_read_items', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };
  const [items, setItems] = useState<any[]>([]);
  const [itemsMeta, setItemsMeta] = useState<any>(null);
  // Sugestões de duplicata fuzzy cross-portal (Classe B.2)
  const [dupSuggestions, setDupSuggestions] = useState<any[]>([]);
  const [mergingDup, setMergingDup] = useState(false);
  const [itemsPage, setItemsPage] = useState(1);
  // Default = '' (Total): mostra abertas + suspensa + revogada (todos do funil Encontrar).
  // Card "Abertas" filtra só os em triagem (novo+analisando+interessado).
  const [statusFilter, setStatusFilter] = useState('abertas');
  const [searchFilter, setSearchFilter] = useState('');
  // Input imediato da busca — o searchFilter (que dispara o fetch) só atualiza
  // após 350ms sem digitar (P1 auditoria 04/07: era 1 request por TECLA).
  const [searchInput, setSearchInput] = useState('');
  React.useEffect(() => {
    const t = setTimeout(() => {
      setSearchFilter((prev) => {
        if (prev !== searchInput) setItemsPage(1);
        return searchInput;
      });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  const [searchIdFilter, setSearchIdFilter] = useState('');
  const [showDupReview, setShowDupReview] = useState(false);
  // Override manual do sobrevivente por par (key=`${aId}-${bId}`, value='a'|'b').
  // Quando ausente, usa a previsão automática (CNPJ tie-break) — espelha o
  // ranking do mergeGroup no backend.
  const [survivorOverrides, setSurvivorOverrides] = useState<Record<string, 'a' | 'b'>>({});
  const [trashMode, setTrashMode] = useState(false);
  const [sortBy, setSortBy] = useState('proximoEvento');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  // Searches
  const [searches, setSearches] = useState<any[]>([]);
  // Modalidades reais do AlertaLicitação (distinct dos items ingeridos) — espelho
  // fiel do vocabulário do AL, usado no editor de perfil em vez da numeração PNCP.
  const [alModalidades, setAlModalidades] = useState<Array<{ id: number | null; nome: string; count: number }>>([]);
  const [searchModal, setSearchModal] = useState<'create' | 'edit' | null>(null);
  // Popup de revisão de exclusão (preview → escolher descartar/whitelist).
  const [exclModal, setExclModal] = useState<{ termos: string[]; items: any[] } | null>(null);
  // Contratação direta (fornecedor já nomeado) — preview + descarte
  const [cdModal, setCdModal] = useState<Array<{ id: number; orgao: string; municipio: string; objeto: string; modo: string }> | null>(null);
  const [cdBusy, setCdBusy] = useState(false);
  const [exclDescartar, setExclDescartar] = useState<Set<number>>(new Set());
  const [exclWhitelist, setExclWhitelist] = useState<Set<number>>(new Set());
  const [exclBusy, setExclBusy] = useState(false);
  // Re-enrich por ITEM (🔄 no card) — o modal/botão de re-enrich em LOTE foi
  // removido 05/07/26 (cron 02h/14h + auto-recovery cobrem; auditoria).
  const [reenrichingItem, setReenrichingItem] = useState(false);
  // "Verificar vencidos" — async com progress, padrão idêntico ao Re-enriquecer.
  // Pra items COM proposta enviada com sessão vencida >24h (complementa Re-enriquecer).
  const [checkExpiredModal, setCheckExpiredModal] = useState(false);
  const [checkExpiredData, setCheckExpiredData] = useState<any>(null);
  const checkExpiredPollRef = React.useRef<any>(null);
  const [checkExpiredPending, setCheckExpiredPending] = useState<number | null>(null);

  // 24/05/2026 — A4: tracking de items aguardando callback do scraper pra
  // renderizar badge "🔄 verificando" na lista. Polling a cada 15s quando há pendência.
  const [scraperPendingIds, setScraperPendingIds] = useState<Set<number>>(new Set());
  const scraperPendingPollRef = React.useRef<any>(null);
  const pollScraperPending = React.useCallback(async () => {
    try {
      const d = await procurementService.scraperPending();
      setScraperPendingIds(new Set(d.itemIds || []));
      // Continua polling enquanto houver pendência
      if (d.total > 0) {
        scraperPendingPollRef.current = setTimeout(pollScraperPending, 15000);
      }
    } catch {
      // best-effort, sem retry agressivo
    }
  }, []);
  React.useEffect(() => {
    // 1º load + retomar se houver pendência salva
    pollScraperPending();
    return () => {
      if (scraperPendingPollRef.current) clearTimeout(scraperPendingPollRef.current);
    };
  }, [pollScraperPending]);
  // Inicia polling sempre que checkExpired enfileira algo
  React.useEffect(() => {
    if ((checkExpiredData?.enqueued || 0) > 0) {
      if (scraperPendingPollRef.current) clearTimeout(scraperPendingPollRef.current);
      pollScraperPending();
    }
  }, [checkExpiredData?.enqueued, pollScraperPending]);
  const [reenrichItemResult, setReenrichItemResult] = useState<any>(null);
  // Popup "Resumo da coleta" (botão Sincronizar → AlertaLicitação)
  const [syncModal, setSyncModal] = useState(false);
  const [syncData, setSyncData] = useState<any>(null);
  const syncPollRef = React.useRef<any>(null);
  const [editingSearch, setEditingSearch] = useState<any>(null);
  const [searchForm, setSearchForm] = useState({ name: '', keywords: '', excludeKeywords: '', uf: [] as string[], modalidades: [] as number[], notifyEmail: '', isActive: true });
  const [executingSearchId, setExecutingSearchId] = useState<number | null>(null);

  // Inline date editing
  const [editingDateId, setEditingDateId] = useState<number | null>(null);
  const [editingDateValue, setEditingDateValue] = useState('');

  // Detail modal
  const [detailItem, setDetailItem] = useState<any>(null);
  // Tracks the id of the most recently requested detail item so that in-flight
  // getItemDetails() responses for an outdated item don't overwrite the current
  // one. Prevents the "card changes to another licitacao after a few seconds"
  // race when the user clicks quickly through items.
  const detailRequestIdRef = React.useRef<number | null>(null);
  // Lazy-load dos detalhes do PNCP (itens/arquivos/detalhe). O modal abre
  // instantâneo só com o item armazenado (aba Dados); itens/anexos só são
  // buscados ao abrir essas abas. Evita 3 chamadas live ao PNCP por abertura
  // (eram ~15s com o gap do proxy + alimentavam o rate-limit do WAF).
  const detailExtraLoadedRef = React.useRef(false);
  // Última tentativa de loadDetailExtra FALHOU (504/timeout/rede). Distingue
  // "essa licitação não tem itens publicados" de "não consegui buscar os itens
  // agora" — os dois davam lista vazia e o user não tinha como saber qual era.
  const detailExtraErrorRef = React.useRef(false);
  // Espelho em STATE do ref acima: o ref não re-renderiza, então a tela não
  // conseguia distinguir "o PNCP não respondeu" de "esta licitação não tem
  // itens" — e mostrava "Nenhum item encontrado" nos dois casos, que é uma
  // mentira quando a busca só falhou (caso EBSERH 16/2026: a tela disse que
  // não havia item e 17s depois o cache foi gravado com 4 itens e 2 anexos).
  // Falha POR ABA. O estado único de antes (`detailExtraError`) só cobria a
  // request inteira estourando; itens e anexos vêm de um serviço do PNCP
  // (`api/pncp/v1`) diferente do detalhe (`api/consulta/v1`), e um pode cair
  // sozinho — foi o que aconteceu em 06/09/2026: detalhe 200, itens 503, e a
  // tela dizia "Nenhum item encontrado para esta licitacao".
  const [detailItensErro, setDetailItensErro] = useState<string | null>(null);
  const [detailArquivosErro, setDetailArquivosErro] = useState<string | null>(null);
  // Trava o botão "Cadastrar Proposta" enquanto os itens estão sendo buscados.
  // Sem isso o clique ficava até 1min sem resposta visível e aceitava reclique.
  const [preparingProposal, setPreparingProposal] = useState(false);
  React.useEffect(() => {
    // Clear the ref when the modal closes so any in-flight responses are
    // treated as stale and discarded.
    if (!detailItem) detailRequestIdRef.current = null;
  }, [detailItem]);
  // Popup do Encontrar (D-3): sem abas. O caminho da decisão (objeto, prazo,
  // itens, ações) fica sempre visível e o resto colapsa em "Dados da
  // licitação" — mesmo rótulo do bloco da página de trabalho (§8 do guia).
  const [detailMore, setDetailMore] = useState(false);
  // Confirmação padrão do HUB (§8): substitui o window.confirm nativo, que
  // não distingue ação destrutiva nem segue o tema.
  const confirm = useConfirm();
  // Pedido do link do processo na BLL/BNC (utils/linkDoPortal): a instrução diz QUAL página antes de
  // colar, e a recusa do backend aparece como veio, sem reescrita.
  const pedirTexto = usePedirTexto();
  const pedirLinkDoPortal = (p: PedidoDeLink) =>
    pedirTexto({
      title: p.title,
      message: (
        <div className="space-y-2">
          <p>{p.instrucao}</p>
          {p.recusa && <p className="text-amber-300">{p.recusa}</p>}
        </div>
      ),
      label: p.label,
      placeholder: p.placeholder,
      confirmLabel: p.confirmLabel,
      obrigatorio: true,
      linhaUnica: true,
    });
  const [detailProposal, setDetailProposal] = useState<any>(null);
  const [detailProposalLoading, setDetailProposalLoading] = useState(false);
  const [detailItens, setDetailItens] = useState<any[]>([]);
  const [detailArquivos, setDetailArquivos] = useState<any[]>([]);
  const [detailExtra, setDetailExtra] = useState<any>(null);
  const [detailPncpStatus, setDetailPncpStatus] = useState<'ok' | 'gone' | 'not_found' | 'error' | 'unknown' | null>(null);
  const [detailRepublication, setDetailRepublication] = useState<any | null>(null);
  const [detailDataFromRepub, setDetailDataFromRepub] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Messages
  const [detailMessages, setDetailMessages] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<number, number>>({});

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  // Documents (Habilitação)
  const [documents, setDocuments] = useState<any[]>([]);
  const [docModal, setDocModal] = useState<'create' | 'edit' | null>(null);
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [docForm, setDocForm] = useState({ name: '', type: 'outro', category: '', subcategory: '', description: '', issueDate: '', expiryDate: '', isActive: true, noExpiry: false });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docExtracting, setDocExtracting] = useState(false);
  const [docViewMode, setDocViewMode] = useState<'folders' | 'list'>('folders');
  const [docCategoryFilter, setDocCategoryFilter] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [docStatusFilter, setDocStatusFilter] = useState('');
  const [showArchivedDocs, setShowArchivedDocs] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [downloadingZip, setDownloadingZip] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Declaration Templates
  const [declarationTemplates, setDeclarationTemplates] = useState<any[]>([]);
  // Pending signatures global view
  const [pendingSignatures, setPendingSignatures] = useState<any[]>([]);
  const [pendingSignaturesLoading, setPendingSignaturesLoading] = useState(false);
  const [signatureModal, setSignatureModal] = useState(false);
  // Modal de download flexível
  // Seleção do envio em lote do drawer legado
  const [signatureSingleDoc, setSignatureSingleDoc] = useState<any>(null); // null = all, doc object = single
  // Trocar signatário: cancela na ClickSign e recria com o mesmo PDF.
  const [signatureReplaceMode, setSignatureReplaceMode] = useState(false);
  const [declTemplateModal, setDeclTemplateModal] = useState<'create' | 'edit' | null>(null);
  const [editingDeclTemplate, setEditingDeclTemplate] = useState<any>(null);
  const [declTemplateForm, setDeclTemplateForm] = useState({ name: '', title: '', body: '', isActive: true, sortOrder: 0, category: 'declaracao', isDefault: false });

  // Products (Catálogo)
  const [products, setProducts] = useState<any[]>([]);
  const [productModal, setProductModal] = useState<'create' | 'edit' | null>(null);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', unit: '', referencePrice: '', category: '', ncmCode: '' });

  // Portals (Acessos)
  const [portals, setPortals] = useState<any[]>([]);
  const [portalModal, setPortalModal] = useState<'create' | 'edit' | null>(null);
  const [editingPortal, setEditingPortal] = useState<any>(null);
  const [portalForm, setPortalForm] = useState({ portalName: '', titular: '', system: 'todos', portalUrl: '', companyCnpj: '', companyName: '', login: '', password: '', notes: '' });

  // Portal Health & Capabilities
  const [portalHealthData, setPortalHealthData] = useState<any[]>([]);
  const [portalHealthLoading, setPortalHealthLoading] = useState(false);

  // Metrics
  const [metrics, setMetrics] = useState<any>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Proposals
  const [proposals, setProposals] = useState<any[]>([]);
  const [proposalModal, setProposalModal] = useState<'create' | null>(null);
  // W-F2: página de trabalho da proposta (substituirá os 2 drawers no cutover).
  // Nasce atrás de um botão próprio; a lista e os drawers seguem intactos.
  const [workspaceProposalId, setWorkspaceProposalId] = useState<number | null>(null);
  const [editingProposal, setEditingProposal] = useState<any>(null);
  const [proposalForm, setProposalForm] = useState({ itemId: '', title: '', description: '', totalValue: '', status: 'rascunho', checklistNotes: '', validityDays: '60', observations: '', conditions: '', representative: '', representativeRole: '', bankInfo: COMPANY.bankInfoLine, brand: 'IPCOM', model: 'IPCOM CLOUD', valorRecorrente: false, contractMonths: '12', pdfLayout: '', pdfIncluirTributacao: false });
  const [proposalStatusFilter, setProposalStatusFilter] = useState('');
  const [proposalSearch, setProposalSearch] = useState('');
  // R1.14: sincroniza com busca global do DisputeView quando embedded
  useEffect(() => {
    if (externalSearch !== undefined) setProposalSearch(externalSearch);
  }, [externalSearch]);
  // Tabs de status: 'ativas' = sem cotações/encerradas/arquivadas, 'cotacoes' =
  // pré-edital (orçamento por TR: sem número de compra e sem data nenhuma),
  // 'encerradas' = só encerradas, 'arquivadas' = só arquivadas, 'todas' = tudo
  const [proposalViewMode, setProposalViewMode] = useState<'ativas' | 'cotacoes' | 'encerradas' | 'arquivadas' | 'todas'>('ativas');
  // R1.13: histórico (encerradas + arquivadas) oculto por default — reduz ruído visual
  const [showHistorico, setShowHistorico] = useState(false);
  // Bulk select no Cadastrar — Set de proposalIds selecionados via checkbox.
  const [selectedProposalIds, setSelectedProposalIds] = useState<Set<number>>(new Set());
  // Modal de confirmação ao deletar — quando preenchido, abre o dialog.
  // type=single: confirma delete de 1 proposta. type=bulk: confirma N.
  const [deleteProposalModal, setDeleteProposalModal] = useState<{
    type: 'single' | 'bulk';
    ids: number[];
    titles: string[];
  } | null>(null);
  const [deleteDestination, setDeleteDestination] = useState<'lixeira' | 'encontrar'>('lixeira');
  const [deleteKeepMonitored, setDeleteKeepMonitored] = useState(false);
  const [deleteKeepDispute, setDeleteKeepDispute] = useState(false);
  const [deletingProposals, setDeletingProposals] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  // Chips de prazo: '' = sem filtro, 'hoje' = encerra hoje, '3d' = encerra em 3d, 'semana' = encerra na semana, 'custom' = range custom
  const [prazoFilter, setPrazoFilter] = useState<'' | 'hoje' | '3d' | 'semana' | 'custom'>('');
  const [prazoCustomStart, setPrazoCustomStart] = useState('');
  const [prazoCustomEnd, setPrazoCustomEnd] = useState('');
  // R1.18: filtro por badge de contagem (needsAction/completed/waiting) clicável
  const [metaFilter, setMetaFilter] = useState<'' | 'needsAction' | 'completed' | 'waiting'>('');
  // Agenda do Google. Os botões de marcar/tirar e o diagnóstico são só ADMIN
  // (as rotas são @Roles(ADMIN)); o selo e o filtro, todo mundo vê.
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const [agendaFilter, setAgendaFilter] = useState<Exclude<EstadoDaAgenda, null> | ''>('');
  const [agendaPanelOpen, setAgendaPanelOpen] = useState(false);
  const [agendaBusyItemId, setAgendaBusyItemId] = useState<number | null>(null);
  // O `motivo` de um "Pôr na agenda" que não publicou não fica gravado no item;
  // guardado aqui para o selo âmbar explicar por que não há evento.
  const [agendaMotivos, setAgendaMotivos] = useState<Record<number, string>>({});
  // R1.18: progresso ao vivo das operações por itemId (scrape/submit/withdraw)
  const [activeOps, setActiveOps] = useState<Record<number, { type: string; label: string; currentStep: string; steps: string[]; elapsedMs: number }>>({});
  // R1.18: tick de 1s pra countdown vivo (só ativo quando há item encerrando hoje)
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [proposalSortBy, setProposalSortBy] = useState('dataEncerramento');
  const [proposalSortOrder, setProposalSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [proposalItems, setProposalItems] = useState<any[]>([]);
  const [proposalItemsForm, setProposalItemsForm] = useState<Array<{
    itemNumber: number; description: string; descricaoDetalhada?: string; quantity: number; unit: string;
    estimatedPrice: number; valorEstimadoSigiloso?: boolean; unitPrice: string; brand: string; model: string;
    groupNumber?: number; groupDescription?: string; requiresBrandModel?: boolean; requiresModel?: boolean; lote?: number; catser?: string;
    // Proposta composta: tipo de cobrança por item + teto do edital (4 casas)
    chargeType?: ChargeType; maxUnitPrice?: string;
    // Item julgado por MAIOR DESCONTO: o portal pede percentual e deriva o
    // unitário do valor estimado. Vem do scrape. Ver utils/proposalMath.ts.
    priceMode?: 'unitario' | 'desconto'; discountPercent?: string;
  }>>([]);
  // Modal "Adequar ao lance final" (pós-disputa, prazo curto p/ readequar)
  const [adequarModal, setAdequarModal] = useState(false);
  const [adequarAlvo, setAdequarAlvo] = useState('');
  const [adequarMeses, setAdequarMeses] = useState('12');
  const [adequarResult, setAdequarResult] = useState<any>(null);
  const [adequarLoading, setAdequarLoading] = useState(false);
  // A proposta para a qual o modal foi aberto. Pelo deep-link de convocação o
  // editingProposal não é definido, e o "Calcular" seguinte rodava sem id e não
  // fazia nada.
  const [adequarProposalId, setAdequarProposalId] = useState<number | null>(null);
  const [proposalPortalId, setProposalPortalId] = useState('');
  const [proposalDeclarations, setProposalDeclarations] = useState<Array<{ id: string; label: string; currentValue: string }>>([]);
  // "Importar licitação" — cadastra uma licitação específica por link/UASG+número
  // (padrão do Encontrar), via registerManual (PNCP egress Mac mini) + scrape de itens.
  const [importModal, setImportModal] = useState(false);
  const [importInput, setImportInput] = useState('');
  const [importPortal, setImportPortal] = useState('comprasnet');
  const [importUasg, setImportUasg] = useState('');
  const [importNumero, setImportNumero] = useState('');
  const [importModalidade, setImportModalidade] = useState<'pe' | 'de'>('pe');
  const [importPreview, setImportPreview] = useState<any | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  // Modo "sem edital publicado" — oportunidade pré-edital (cadastro manual).
  const [importSemEdital, setImportSemEdital] = useState(false);
  const [importOrgao, setImportOrgao] = useState('');
  const [importObjeto, setImportObjeto] = useState('');
  const [importValor, setImportValor] = useState('');
  // PCP — informações complementares (desempate) editáveis no editor.
  const [pcpInfoComp, setPcpInfoComp] = useState<Array<{ field: string; label: string; resposta: string }>>([]);
  // Termos+declarações específicos do portal (capturados via scrape extension)
  const [meeppValue, setMeeppValue] = useState<'Sim' | 'Não' | null>(null);
  // Margem de preferência (CICS, campo novo ComprasNet 07/2026) — default da
  // proposta; o robô aplica SÓ nos itens com utilizaMargemPreferencia=true.
  // "0" = Não se aplica (padrão IPCOM — produto importado sem PPB).
  const [margemPaisOrigem, setMargemPaisOrigem] = useState<string>('CHN');
  const [margemEnquadramento, setMargemEnquadramento] = useState<string>('0');
  const [selectedProposalItemInfo, setSelectedProposalItemInfo] = useState<any>(null);

  // Automation
  const [automationModal, setAutomationModal] = useState<any>(null); // proposal being automated
  const [automationPortalId, setAutomationPortalId] = useState('');
  const [automationDryRun, setAutomationDryRun] = useState(false);
  const [automationStarting, setAutomationStarting] = useState(false);
  // A LISTA de jobs vem no formato enxuto (backend #491): o que só existe no detalhe se lê do getAutomationJob.
  const [automationJobs, setAutomationJobs] = useState<JobDaLista[]>([]);
  const [automationJobModal, setAutomationJobModal] = useState<any>(null); // job detail
  // Quando o modal do job foi lido do servidor (16/10/2026, TCE-PR): o modal parado em "Executando" não podia parecer vivo.
  const [jobLidoEm, setJobLidoEm] = useState<number | null>(null);
  // ⛔ Aqui dentro é setAutomationJobModal, nunca mostrarJob: de 16/10/2026 até o conserto, um replace-all deixou esta
  // função chamando a si mesma, e todo modal de job estourava a pilha em vez de abrir (LicitacoesTab.modalDoJob.test).
  const mostrarJob = (job: any) => { setAutomationJobModal(job); setJobLidoEm(Date.now()); };
  const [automationJobPolling, setAutomationJobPolling] = useState<ReturnType<typeof setInterval> | null>(null);
  const automationJobPollingRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const automationModalOpenRef = React.useRef(false);

  // Proposal actions dropdown
  const [openProposalMenu, setOpenProposalMenu] = useState<number | null>(null);
  // O selo de envio (26/10/2026, P1): o clique abre a prova — a trilha que o robô gravou, ou o que se sabe da marcada à mão.
  const [provaDoEnvio, setProvaDoEnvio] = useState<{ proposal: any; selo: SeloDoEnvio } | null>(null);
  // Modal de Análise IA acionado pelo menu da linha em lic-cadastrar
  const [aiModalItem, setAiModalItem] = useState<any | null>(null);
  const [aiModalLoading, setAiModalLoading] = useState(false);
  const [aiModalRunning, setAiModalRunning] = useState(false);
  const [aiModalData, setAiModalData] = useState<any | null>(null);
  const [aiModalAnalyzedAt, setAiModalAnalyzedAt] = useState<string | null>(null);
  const [aiModalError, setAiModalError] = useState<string | null>(null);
  // A lista de arquivos do PNCP NÃO CARREGOU (08/10/2026). Antes, `.catch(() => [])` e a tela dizia "Nenhum arquivo
  // encontrado no PNCP. Use upload manual abaixo.": afirmava ausência sem ter consultado, e mandava para o upload manual.
  const [aiModalFilesErro, setAiModalFilesErro] = useState<string | null>(null);
  const [aiModalFiles, setAiModalFiles] = useState<Array<{ sequencialDocumento: number; titulo: string; tipoDocumentoNome: string; tipoDocumentoId: number; url: string; dataPublicacaoPncp: string }> | null>(null);
  const [aiModalSelectedFile, setAiModalSelectedFile] = useState<number | null>(null);
  const [expandedProposalId, setExpandedProposalId] = useState<number | null>(null);
  const [submitModal, setSubmitModal] = useState<{ proposal: any; dryRun: boolean } | null>(null);
  // Cobre do clique até o modal fechar: no BLL/BNC há um await (documentos)
  // antes disso, e um segundo clique nessa janela enfileirava outro envio.
  const travaDoEnvio = useTravaDeEnvio();
  // Lido depois do await da conferência: fechar o modal nessa janela cancela o envio.
  const submitModalRef = React.useRef(submitModal);
  submitModalRef.current = submitModal;

  // Track the BLL scrape job created during Nova Proposta so we can backfill
  // its proposalId after the proposal is saved (enables "último job" button
  // + "Carregando..." badge on the new row retroactively).
  const pendingBllScrapeJobIdRef = React.useRef<number | null>(null);
  // Uploaded BLL documents for the proposal being created/edited — keyed by
  // tipo. For existing proposals this is seeded from editingProposal.bllDocuments.


  // Status logs (detail modal)
  const [detailLogs, setDetailLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [newLogMessage, setNewLogMessage] = useState('');

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const inferPortalOrigemFromPortal = useCallback((portal: any): string | null => {
    if (!portal) return null;
    if (portal.system && portal.system !== 'todos') return portal.system;
    const name = (portal.portalName || '').toLowerCase();
    const nameNorm = name.replace(/[^a-z0-9]/g, '');
    for (const [key, map] of Object.entries(PORTAL_MAP)) {
      const label = map.label.toLowerCase();
      if (name === label || name.includes(label) || label.includes(name) || nameNorm.includes(key) || key.includes(nameNorm)) {
        return key;
      }
    }
    return null;
  }, []);

  const findPortalIdBySource = useCallback((portalOrigem: string | null, linkOrigem: string | null): string => {
    if (!portalOrigem && !linkOrigem) return '';
    const source = portalOrigem || (() => {
      try {
        const host = new URL(linkOrigem!).hostname.toLowerCase();
        if (host.includes('comprasnet') || host.includes('cnetmobile')) return 'comprasnet';
        if (host.includes('bnc')) return 'bnc';
        if (host.includes('bll')) return 'bll';
        if (host.includes('portaldecompraspublicas')) return 'pcp';
        if (host.includes('licitacoes.caixa')) return 'caixa';
        return null;
      } catch { return null; }
    })();
    return accountIdForSource(portals, source);
  }, [portals]);

  const { portalsWithAccess, extraPortals } = useMemo(() => {
    const keys = new Set<string>();
    const extras: { key: string; label: string }[] = [];
    const seenExtras = new Set<string>();
    if (!portals.length) return { portalsWithAccess: keys, extraPortals: extras };
    for (const portal of portals) {
      const key = portalKeyForAccount(portal.portalName);
      if (key) {
        keys.add(key);
      } else {
        const baseName = (portal.portalName || '').replace(/\s*\(.*?\)\s*/g, '').trim();
        const slug = baseName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        if (slug && !seenExtras.has(slug)) {
          seenExtras.add(slug);
          extras.push({ key: slug, label: baseName });
        }
      }
    }
    extras.sort((a, b) => a.label.localeCompare(b.label));
    return { portalsWithAccess: keys, extraPortals: extras };
  }, [portals]);

  // ==================== Data Loading ====================

  // ERRO NÃO É VAZIO (07/10/2026): as cargas abaixo só logavam. Com a API fora, os cartões do Encontrar diziam 0,
  // os perfis "Nenhum perfil de busca configurado", os documentos "Nenhum documento cadastrado" (e os cartões
  // Vencidos 0), o histórico do item "Nenhum registro" e as métricas ficavam em "Carregando" para sempre. Agora a
  // falha vira estado (design/useEstadoDaCarga) e o dado só é gravado quando a chamada dá certo.
  const { rodar: rodarCargaDoPainel, ...cargaDoPainel } = useEstadoDaCarga();
  const loadDashboard = useCallback(async () => {
    await rodarCargaDoPainel(async () => {
      const data = await procurementService.getDashboard();
      setDashboard(data);
    });
  }, [rodarCargaDoPainel]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: itemsPage, limit: 50, sortBy, sortOrder };
      // Encontrar = funil de entrada. Status que aparecem aqui:
      //   - novo/analisando/interessado: triagem em curso (sem proposta).
      //   - suspensa/revogada: terminais PNCP (visíveis mas separados).
      // NÃO aparecem: participando (vai pra Cadastrar), descartado (Lixeira),
      // aguardando_julgamento/ganho/perdido/encerrada/homologado (vão pra Disputar/Histórico).
      const ENCONTRAR_STATUSES = 'novo,analisando,interessado,suspensa,revogada';
      if (trashMode) {
        params.status = 'descartado';
      } else if (statusFilter === 'abertas') {
        params.statusIn = 'novo,analisando,interessado';
        // Triagem é sobre o que ainda dá pra disputar. Edital com prazo vencido
        // ocupava lugar na fila e competia pela atenção com os vivos — 33 deles
        // em 18/08/2026, o mais antigo de março. Vão pro card "Vencidas".
        params.prazo = 'vigente';
      } else if (statusFilter === 'vencidas') {
        // SUSPENSA entra aqui desde 04/09/2026. A aba "Suspensa" mostrava 22
        // itens dos quais 17 já tinham prazo vencido e 10 estavam parados havia
        // mais de 30 dias: nada as tira de lá, porque nenhuma varredura toca
        // suspensa sem proposta. Elas saem da fila de decisão sem sumir do
        // sistema — quem quiser conferir o que escapou acha aqui.
        params.statusIn = 'novo,analisando,interessado,suspensa';
        params.prazo = 'vencido';
      } else if (statusFilter === 'suspensa') {
        params.status = 'suspensa';
        params.prazo = 'vigente';
      } else if (statusFilter === 'revogada') {
        // Revogada é desfecho, não prazo: não faz sentido filtrar por data.
        params.status = statusFilter;
      } else if (statusFilter) {
        // Status INDIVIDUAL do dropdown (novo/analisando/interessado/participando/
        // homologado...). Antes qualquer valor fora dos agregados era IGNORADO
        // silenciosamente e a lista continuava igual (auditoria 04/07).
        params.status = statusFilter;
      } else {
        // Default (Total): só os status do funil do Encontrar.
        params.statusIn = ENCONTRAR_STATUSES;
      }
      // 24/05/2026: excluir items que JÁ TÊM proposta ativa do Encontrar.
      // Mesmo se status ficou stale (NOVO/SUSPENSA com proposta enviada — caso real
      // 24/05 com 3 items órfãos), o filtro robusto por proposta resolve. Cobre gaps
      // do createProposal/updateProposal não promoverem pra PARTICIPANDO.
      // EXCEÇÃO (04/07): status individual FORA do funil (participando/homologado…)
      // implica itens COM proposta — aplicar hasProposal=false mostraria lista vazia.
      const FUNIL = ['', 'abertas', 'novo', 'analisando', 'interessado', 'suspensa', 'revogada'];
      if (!trashMode && FUNIL.includes(statusFilter)) params.hasProposal = 'false';
      if (searchIdFilter) params.searchId = searchIdFilter;
      if (searchFilter) params.q = searchFilter;

      const result = await procurementService.getItems(params);
      if (result?.data) {
        setItems(result.data);
        setItemsMeta(result.meta);
      } else {
        setItems(Array.isArray(result) ? result : []);
        setItemsMeta(null);
      }
    } catch (e: any) {
      showToast('Erro ao carregar licitacoes', 'error');
    }
    setLoading(false);
  }, [itemsPage, statusFilter, searchIdFilter, trashMode, searchFilter, sortBy, sortOrder, showToast]);

  const loadDupSuggestions = useCallback(async () => {
    try {
      const data = await procurementService.getDuplicateSuggestions();
      setDupSuggestions(Array.isArray(data) ? data : []);
    } catch {
      // silent — feature secundária, não bloqueia a lista
    }
  }, []);

  // Mapa itemId → { twin, similarity } pra badge na lista e painel no modal.
  // Cada par {a,b} indexa os dois lados apontando um pro outro.
  const dupMap = useMemo(() => {
    const m = new Map<number, { twin: any; similarity: number }>();
    for (const s of dupSuggestions) {
      if (s?.a?.id && s?.b?.id) {
        m.set(s.a.id, { twin: s.b, similarity: s.similarity });
        m.set(s.b.id, { twin: s.a, similarity: s.similarity });
      }
    }
    return m;
  }, [dupSuggestions]);

  const handleMergeDuplicate = async (itemId: number, otherId: number, survivorId?: number) => {
    if (mergingDup) return;
    setMergingDup(true);
    try {
      const res = await procurementService.mergeDuplicate(itemId, otherId, survivorId);
      showToast('Licitações unificadas');
      setDetailItem(null);
      await Promise.all([loadItems(), loadDupSuggestions()]);
      void res;
    } catch (e: any) {
      showToast(`Erro ao unificar: ${e?.response?.data?.message || e?.message || 'falha desconhecida'}`, 'error');
    }
    setMergingDup(false);
  };

  const handleDismissDuplicate = async (itemId: number, otherId: number) => {
    try {
      await procurementService.dismissDuplicate(itemId, otherId);
      showToast('Marcado como não-duplicata');
      loadDupSuggestions();
    } catch (e: any) {
      showToast(`Erro ao dispensar: ${e?.response?.data?.message || e?.message || 'falha desconhecida'}`, 'error');
    }
  };

  const loadUnreadCounts = useCallback(async () => {
    try {
      const data = await procurementService.getUnreadMessageCounts();
      const map: Record<number, number> = {};
      if (Array.isArray(data)) {
        for (const r of data) map[r.itemId] = r.unread ?? r.count ?? 0;
      }
      setUnreadCounts(map);
    } catch {
      // silent
    }
  }, []);

  const { rodar: rodarCargaDosPerfis, ...cargaDosPerfis } = useEstadoDaCarga();
  const loadSearches = useCallback(async () => {
    await rodarCargaDosPerfis(async () => {
      const data = await procurementService.getSearches();
      setSearches(Array.isArray(data) ? data : []);
    });
  }, [rodarCargaDosPerfis]);

  // P1 (12/10/2026): as modalidades que não carregavam diziam "Nenhuma modalidade capturada ainda".
  const [erroDasModalidades, setErroDasModalidades] = useState<string | null>(null);
  const loadAlModalidades = useCallback(async () => {
    try {
      const data = await procurementService.alertaLicitacaoModalidades();
      // Canonicaliza pra eliminar duplicatas de grafia ("Pregão eletrônico" +
      // "Pregão - Eletrônico" + "PREGÃO ELETRÔNICO" = 1 só), somando os counts.
      const canon = (nome: string) => {
        const n = (nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "").trim();
        if (n.includes('preg') && n.includes('eletr')) return 'Pregão Eletrônico';
        if (n.includes('preg') && n.includes('presenc')) return 'Pregão Presencial';
        if (n.includes('preg')) return 'Pregão';
        if (n.includes('concorr') && n.includes('eletr')) return 'Concorrência Eletrônica';
        if (n.includes('concorr')) return 'Concorrência';
        if (n.includes('dispensa')) return 'Dispensa';
        if (n.includes('inexig')) return 'Inexigibilidade';
        if (n.includes('credenc')) return 'Credenciamento';
        if (n.includes('chamamento')) return 'Chamamento Público';
        if (n.includes('manifesta')) return 'Manifestação de Interesse';
        if (n.includes('leil')) return 'Leilão';
        return nome || 'Outras';
      };
      const raw = Array.isArray(data) ? data.filter((m) => m.id != null) : [];
      const grouped = new Map<string, { id: number | null; nome: string; count: number; _max: number }>();
      for (const m of raw) {
        const key = canon(m.nome);
        const ex = grouped.get(key);
        if (ex) {
          ex.count += m.count;
          if (m.count > ex._max) { ex.id = m.id; ex._max = m.count; } // id do maior variante
        } else {
          grouped.set(key, { id: m.id, nome: key, count: m.count, _max: m.count });
        }
      }
      setAlModalidades(Array.from(grouped.values()).map(({ _max, ...rest }) => rest).sort((a, b) => b.count - a.count));
      setErroDasModalidades(null);
    } catch (e: any) {
      setErroDasModalidades(mensagemDoErroDeCarga(e));
    }
  }, []);

  const { rodar: rodarCargaDasMetricas, ...cargaDasMetricas } = useEstadoDaCarga();
  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    await rodarCargaDasMetricas(async () => {
      const data = await procurementService.getMetrics();
      setMetrics(data);
    });
    setMetricsLoading(false);
  }, [rodarCargaDasMetricas]);

  // ERRO NÃO É VAZIO (07/10/2026): com a API fora (todo deploy de backend), esta carga só logava, a lista ficava
  // vazia e a tela dizia "Nenhuma proposta cadastrada". Agora a falha vira estado e a tela diz que não carregou.
  // A lista só é gravada quando a chamada dá certo: recarga que falha NÃO apaga o que já estava na tela.
  const { rodar: rodarCargaDasPropostas, ...cargaDasPropostas } = useEstadoDaCarga();
  const loadProposals = useCallback(async () => {
    await rodarCargaDasPropostas(async () => {
      const data = await procurementService.getProposals({});
      setProposals(Array.isArray(data) ? data : []);
    });
  }, [rodarCargaDasPropostas]);

  // R1.18: roda uma operação de automação (scrape/submit/withdraw) mostrando
  // barra de progresso ao vivo. Faz poll de getAutomationProgress(itemId) a cada
  // 1.5s enquanto fn() roda; limpa ao terminar. itemId pode ser null (sem progresso).
  const runWithProgress = useCallback(async <T,>(
    itemId: number | null | undefined,
    type: string,
    label: string,
    fn: () => Promise<T>,
    // O scrape pelo job traz o progresso DENTRO do job (uma consulta só): ele mesmo atualiza o quadro, e aqui só corre o cronômetro.
    opcoes?: { progressoPeloJob?: boolean },
  ): Promise<T> => {
    if (!itemId) return fn();
    const startedAt = Date.now();
    setActiveOps((prev) => ({ ...prev, [itemId]: { type, label, currentStep: 'iniciando…', steps: [], elapsedMs: 0 } }));
    let stopped = false;
    (async () => {
      while (!stopped) {
        await new Promise((r) => setTimeout(r, 1500));
        if (stopped) break;
        // Cronômetro LOCAL primeiro: operação sem job no backend (o carregamento
        // de itens pelo PNCP) não tem progresso pra pollar, e um "0s" parado ao
        // lado do spinner lê como travado. O elapsed do backend, quando existe,
        // continua vencendo logo abaixo.
        setActiveOps((prev) => prev[itemId]
          ? { ...prev, [itemId]: { ...prev[itemId], elapsedMs: Date.now() - startedAt } }
          : prev);
        if (opcoes?.progressoPeloJob) continue;
        try {
          const pg = await procurementService.getAutomationProgress(itemId);
          if (pg && Array.isArray(pg.steps)) {
            setActiveOps((prev) => prev[itemId]
              ? { ...prev, [itemId]: { ...prev[itemId], currentStep: pg.currentStep || prev[itemId].currentStep, steps: pg.steps, elapsedMs: pg.elapsedMs || prev[itemId].elapsedMs } }
              : prev);
          }
        } catch { /* poll best-effort */ }
      }
    })();
    try {
      return await fn();
    } finally {
      stopped = true;
      setActiveOps((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
    }
  }, []);

  const loadProposalItems = useCallback(async (): Promise<any[]> => {
    try {
      const result = await procurementService.getItems({ limit: 200, sortBy: 'dataAbertura', sortOrder: 'DESC' });
      const allItems = result?.data || (Array.isArray(result) ? result : []);
      const filtered = allItems.filter((i: any) => ['analisando', 'interessado', 'participando'].includes(i.status));
      setProposalItems(filtered);
      return filtered;
    } catch (e: any) {
      console.error('Erro ao carregar itens para propostas:', e);
      return [];
    }
  }, []);

  // ===== Importar licitação (por link ComprasNet ou UASG+número) =====
  // Detecta o compraId do ComprasNet {UASG(6)}{MOD(2)}{NUMERO}{ANO(4)} e preenche.
  const parseComprasnetCompra = (raw: string): { uasg: string; numero: string; ano: string; modalidade: 'pe' | 'de' } | null => {
    const m = raw.match(/compra=(\d{15,20})/) || raw.match(/(^|\D)(\d{16,20})(\D|$)/);
    const id = m ? (m[1] && /^\d/.test(m[1]) ? m[1] : (m[2] || m[1])) : null;
    if (!id || id.length < 16) return null;
    const uasg = id.slice(0, 6);
    const mod = id.slice(6, 8);
    const ano = id.slice(-4);
    const numero = id.slice(8, -4).replace(/^0+/, '') || '0';
    if (Number(ano) < 2015 || Number(ano) > 2099) return null;
    return { uasg, numero, ano, modalidade: mod === '06' ? 'de' : 'pe' };
  };

  const handleImportDetect = () => {
    const p = parseComprasnetCompra(importInput.trim());
    if (p) {
      setImportPortal('comprasnet');
      setImportUasg(p.uasg);
      setImportNumero(`${p.numero}/${p.ano}`);
      setImportModalidade(p.modalidade);
      setImportPreview(null);
      showToast(`Detectado: UASG ${p.uasg} · ${p.modalidade === 'de' ? 'Dispensa' : 'Pregão'} ${p.numero}/${p.ano}`, 'success');
    } else {
      showToast('Não reconheci o link/ID do ComprasNet — preencha UASG + número manualmente abaixo', 'success');
    }
  };

  const handleImportPreview = async () => {
    if (!importUasg || !importNumero) { showToast('Informe UASG e número', 'error'); return; }
    setImportLoading(true);
    try {
      const data = await procurementService.lookupUasg(importUasg, importNumero);
      setImportPreview(data || { _empty: true });
      if (!data) showToast('Não encontrei no PNCP — pode importar mesmo assim (dados completos vêm via "Sincronizar do Portal")', 'success');
    } catch (e: any) {
      setImportPreview({ _empty: true });
      showToast('Pré-visualização falhou (PNCP) — pode importar mesmo assim', 'success');
    } finally { setImportLoading(false); }
  };

  const handleImportSubmit = async () => {
    if (importSemEdital) {
      if (!importOrgao.trim() && !importUasg.trim()) { showToast('Informe o órgão ou a UASG', 'error'); return; }
    } else if (!importUasg.trim() || !importNumero.trim()) {
      showToast('Informe UASG e número da compra', 'error'); return;
    }
    setImportBusy(true);
    try {
      // O backend SEMPRE busca o detalhe completo do PNCP (objeto/valor/datas/modo/
      // processo/critério). Só passamos o preview se o usuário já visualizou (override).
      const pv: any = (importPreview && !importPreview._empty) ? importPreview : {};
      await procurementService.registerManual({
        portal: importPortal,
        semEdital: importSemEdital || undefined,
        codigoUnidade: importUasg.trim() || undefined,
        numeroCompra: importNumero.trim() || undefined,
        modalidade: importModalidade,
        modoDisputa: pv.modoDisputa || undefined,
        // No modo sem-edital, órgão/objeto/valor vêm dos campos manuais.
        orgaoNome: (importSemEdital ? importOrgao.trim() : '') || pv.orgaoNome || undefined,
        objetoCompra: (importSemEdital ? importObjeto.trim() : '') || pv.objetoCompra || undefined,
        valorEstimado: (importSemEdital && importValor ? Number(importValor) : undefined) ?? pv.valorEstimado ?? undefined,
        dataAbertura: pv.dataAbertura || undefined,
        dataEncerramento: pv.dataEncerramento || undefined,
        srp: pv.srp,
        criterioJulgamento: pv.criterioJulgamento || undefined,
        processoSei: pv.processo || undefined,
      });
      showToast(
        importSemEdital
          ? '✅ Oportunidade cadastrada (rascunho). Preencha os itens/preços e anexe os documentos na proposta.'
          : '✅ Licitação importada e proposta criada. Abra a proposta e clique "Sincronizar do Portal" pra puxar os itens.',
        'success',
      );
      setImportModal(false);
      setImportInput(''); setImportPreview(null); setImportUasg(''); setImportNumero('');
      setImportSemEdital(false); setImportOrgao(''); setImportObjeto(''); setImportValor('');
      loadProposals(); loadProposalItems();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || '';
      showToast(`Falha ao importar: ${Array.isArray(msg) ? msg.join(', ') : msg}`, 'error');
    } finally { setImportBusy(false); }
  };

  useEffect(() => {
    loadDashboard();
    loadSearches();
    loadAlModalidades();
  }, [loadDashboard, loadSearches, loadAlModalidades]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Contagem de nao lidas 1x no mount, nao a cada troca de filtro/pagina: ela
  // so muda quando chega mensagem nova, e `loadItems` troca de identidade a
  // cada filtro, o que disparava /messages/unread-counts junto. Mensagem so
  // existe em item monitorado, que ja saiu do funil do Encontrar (eram 3 nao
  // lidas em `novo` contra 2.846 em homologado) — quem consome de fato e a
  // lista do Cadastrar.
  useEffect(() => {
    loadUnreadCounts();
  }, [loadUnreadCounts]);

  useEffect(() => {
    if (subView === 'items' && !trashMode) loadDupSuggestions();
  }, [subView, trashMode, loadDupSuggestions]);

  useEffect(() => {
    if (subView === 'metrics') loadMetrics();
    if (subView === 'proposals') { loadProposals(); loadProposalItems(); loadAutomationJobs(); }
  }, [subView, loadMetrics, loadProposals, loadProposalItems]);

  const loadPendingSignatures = async () => {
    setPendingSignaturesLoading(true);
    try {
      const data = await procurementService.getProposalsWithPendingSignatures();
      setPendingSignatures(Array.isArray(data) ? data : []);
    } catch (e: any) {
      showToast('Erro ao carregar pendências de assinatura', 'error');
    } finally {
      setPendingSignaturesLoading(false);
    }
  };

  // Auto-refresh da lista global a cada 60s enquanto a tela estiver aberta
  useEffect(() => {
    if (subView !== 'signatures') return;
    const interval = setInterval(() => loadPendingSignatures(), 60000);
    return () => clearInterval(interval);
  }, [subView]);

  // Close proposal dropdown on outside click
  useEffect(() => {
    if (openProposalMenu === null) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.proposal-menu-container')) setOpenProposalMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openProposalMenu]);

  // R1.18: tick de 1s pro countdown vivo — só quando estamos no Cadastrar e há
  // alguma proposta encerrando hoje (evita re-render de 1s desnecessário).
  useEffect(() => {
    if (subView !== 'proposals') return;
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(); endOfDay.setHours(23, 59, 59, 999);
    const hasToday = proposals.some((p: any) => {
      const enc = p.item?.dataEncerramento;
      if (!enc) return false;
      const t = new Date(enc).getTime();
      return t >= Date.now() && t <= endOfDay.getTime();
    });
    if (!hasToday) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [subView, proposals]);

  // ==================== Actions ====================

  const handleViewModeChange = (mode: 'table' | 'pipeline') => {
    setViewMode(mode);
    localStorage.setItem('lic_view_mode', mode);
  };

  const handlePipelineStatusChange = async (itemId: number, newStatus: string) => {
    try {
      await procurementService.updateItemStatus(itemId, { status: newStatus });
      loadDashboard();
    } catch (e: any) {
      // Sem isso o drag falhava MUDO e o card voltava sem explicação (P1 auditoria).
      showToast('Erro ao mover no pipeline: ' + (e?.response?.data?.message || e?.message), 'error');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncModal(true);
    setSyncData({ running: true });
    try {
      const { startedAt } = await procurementService.syncAll();
      // Poll o resumo da coleta até concluir (~120s máx). Popup mostra ao vivo.
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const r = await procurementService.syncLastResult();
          if (r && !r.running && r.at && (!startedAt || r.at >= startedAt)) {
            setSyncData(r);
            setSyncing(false);
            loadItems(); loadDashboard();
            return;
          }
        } catch { /* ignora erro de poll */ }
        if (tries < 24) syncPollRef.current = setTimeout(poll, 5000);
        else { setSyncData({ running: false, error: 'Sincronização demorou mais que o esperado — atualize em instantes.' }); setSyncing(false); loadItems(); loadDashboard(); }
      };
      syncPollRef.current = setTimeout(poll, 5000);
    } catch (e: any) {
      setSyncData({ running: false, error: e.response?.data?.message || e.message });
      setSyncing(false);
    }
  };
  const closeSync = () => {
    setSyncModal(false);
    if (syncPollRef.current) clearTimeout(syncPollRef.current);
  };

  const handlePortalChange = async (itemId: number, portalOrigem: string) => {
    try {
      await procurementService.updateItemPortal(itemId, portalOrigem);
      showToast('Portal atualizado');
      loadItems();
      loadProposalItems();
    } catch (e: any) {
      showToast('Erro ao atualizar portal', 'error');
    }
  };

  const [refreshingSituacaoId, setRefreshingSituacaoId] = useState<number | null>(null);
  // Parse "DD/MM/YYYY" (previsao de reabertura) e retorna true se ja passou
  // o fim daquele dia em BRT. Decisão de ser "vencida" precisa ser local pra
  // não depender de re-check periódico — o badge muda de cor em tempo real.
  // ComprasNet retorna permitirInclusao=false para qualquer evento pendente
  // (Alteração, Esclarecimento, Suspensão...). Só os eventos de Suspensão/
  // Revogação/Anulação bloqueiam de fato o cadastro — alinhado com o guard
  // do bot em comprasnet-dispensa.ts. Sem esse filtro, o badge "Suspensa"
  // aparecia indevidamente em eventos de Alteração (ex.: MGI-PI 46/2026).
  const isSituacaoBlockingEvent = useCallback((situacaoCompra: any): boolean => {
    if (!situacaoCompra) return false;
    // Evento PENDENTE DE PUBLICAÇÃO (possuiAvisoDeEvento) — pode vir com
    // permitirInclusao=true, mas normalmente será adiada/suspensa → bloqueia.
    if (situacaoCompra.eventoPendente === true) return true;
    if (situacaoCompra.permitirInclusao !== false) return false;
    const msg = situacaoCompra.ultimoAviso?.mensagem || '';
    return /suspen|revogad|anulad/i.test(msg);
  }, []);

  const isSituacaoOverdue = useCallback((situacaoCompra: any): boolean => {
    if (!situacaoCompra || situacaoCompra.permitirInclusao !== false) return false;
    const reabertura = situacaoCompra.ultimoAviso?.previsaoReabertura;
    if (!reabertura) return false;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(reabertura);
    if (!m) return false;
    const [, dd, mo, yy] = m;
    // 00:00 BRT do dia seguinte = 03:00 UTC
    const endOfDayUtc = Date.UTC(Number(yy), Number(mo) - 1, Number(dd) + 1, 3, 0, 0);
    return Date.now() > endOfDayUtc;
  }, []);

  const handleRefreshSituacao = async (itemId: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setRefreshingSituacaoId(itemId);
    try {
      const result = await procurementService.refreshItemSituacao(itemId);
      if (result?.reopened) {
        showToast('Licitação REABERTA no portal — pronto para cadastrar');
      } else if (result?.situacaoCompra?.permitirInclusao === false) {
        const prev = result.situacaoCompra.ultimoAviso?.previsaoReabertura;
        showToast(`Ainda suspensa${prev ? ` — nova previsão: ${prev}` : ''}`);
      } else {
        showToast('Status atualizado — nenhum bloqueio detectado');
      }
      await loadItems();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Falha ao verificar status', 'error');
    } finally {
      setRefreshingSituacaoId(null);
    }
  };

  const handleStatusChange = async (itemId: number, status: string, notes?: string) => {
    try {
      await procurementService.updateItemStatus(itemId, { status, notes });
      showToast('Status atualizado');
      loadItems();
      loadDashboard();
      setPipelineRefreshKey(k => k + 1);
      if (detailItem?.id === itemId) {
        const updated = await procurementService.getItem(itemId);
        setDetailItem(updated);
      }
    } catch (e: any) {
      showToast('Erro ao atualizar status', 'error');
    }
  };

  const lastCheckedRef = React.useRef<number | null>(null);

  const toggleSelectItem = (id: number, e?: React.MouseEvent | React.ChangeEvent) => {
    const shiftKey = e && 'shiftKey' in e && (e as React.MouseEvent).shiftKey;
    if (shiftKey && lastCheckedRef.current !== null) {
      // Shift+click: select range
      const ids = items.map(i => i.id);
      const start = ids.indexOf(lastCheckedRef.current);
      const end = ids.indexOf(id);
      if (start !== -1 && end !== -1) {
        const range = ids.slice(Math.min(start, end), Math.max(start, end) + 1);
        setSelectedIds(prev => {
          const next = new Set(prev);
          range.forEach(rid => next.add(rid));
          return next;
        });
        lastCheckedRef.current = id;
        return;
      }
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastCheckedRef.current = id;
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };


  // Descarte de um item único (lixeira inline da tabela). Confirma com o mesmo
  // texto do modal e usa deleteItem (cascade real) — não updateItemStatus.
  const handleDiscardItem = async (itemId: number, hasProposta: boolean) => {
    const okConfirm = await confirm({
      title: 'Descartar esta licitação?',
      danger: true,
      confirmLabel: 'Descartar',
      consequence: (
        <>
          Vai para a <strong>Lixeira</strong> e é recuperável por 90 dias.
          {hasProposta && <> A proposta é <strong>arquivada</strong> e jobs/sessões de disputa são cancelados.</>}
          {' '}Não volta sozinha para o Encontrar.
        </>
      ),
    });
    if (!okConfirm) return;
    try {
      await procurementService.deleteItem(itemId);
      showToast(hasProposta ? '✓ Licitação descartada + proposta arquivada' : '✓ Licitação na Lixeira');
      if (detailItem?.id === itemId) setDetailItem(null);
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      loadItems();
      loadDashboard();
      setPipelineRefreshKey(k => k + 1);
    } catch (e: any) {
      showToast('Erro ao descartar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleBulkDiscard = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const withProposta = items.filter((i) => ids.includes(i.id) && i.temProposta).length;
    const okConfirm = await confirm({
      title: `Descartar ${ids.length} licitação(ões) selecionada(s)?`,
      danger: true,
      confirmLabel: `Descartar ${ids.length}`,
      consequence: (
        <>
          Vão para a <strong>Lixeira</strong> (recuperáveis por 90 dias).
          {withProposta > 0 && <> {withProposta} com proposta cadastrada: será <strong>arquivada</strong> e jobs/sessões cancelados.</>}
          {' '}Não voltam sozinhas para o Encontrar.
        </>
      ),
    });
    if (!okConfirm) return;
    try {
      for (const id of ids) {
        await procurementService.deleteItem(id);
      }
      showToast(`${ids.length} licitação(ões) na Lixeira`);
      setSelectedIds(new Set());
      loadItems();
      loadDashboard();
      setPipelineRefreshKey(k => k + 1);
    } catch (e: any) {
      showToast('Erro ao descartar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  // Busca itens/arquivos/detalhe do PNCP sob demanda (lazy). Retorna os dados
  // pra quem precisar usar na hora (ex: criar proposta). Idempotente via ref.
  const loadDetailExtra = async (itemId: number, force = false) => {
    if (detailExtraLoadedRef.current && !force) return undefined;
    setDetailLoading(true);
    detailExtraErrorRef.current = false;
    setDetailItensErro(null);
    setDetailArquivosErro(null);
    try {
      const data = await procurementService.getItemDetails(itemId, force);
      // Guard: usuário pode ter trocado de item enquanto a request voava.
      if (detailRequestIdRef.current !== itemId) return undefined;
      setDetailItensErro(data.itensStatus === 'erro' ? (data.itensMotivo || 'O PNCP não respondeu') : null);
      setDetailArquivosErro(data.arquivosStatus === 'erro' ? (data.arquivosMotivo || 'O PNCP não respondeu') : null);
      setDetailItens(data.itens || []);
      setDetailArquivos(data.arquivos || []);
      setDetailExtra(data.detalhes || null);
      setDetailPncpStatus(data.pncpStatus || null);
      setDetailRepublication(data.republicationCandidate || null);
      setDetailDataFromRepub(!!data.dataFromRepublication);
      if (data.item) setDetailItem(data.item);
      detailExtraLoadedRef.current = true;
      return data;
    } catch (e: any) {
      console.error('Erro ao carregar detalhes do PNCP:', e);
      detailExtraErrorRef.current = true;
      setDetailItensErro('O PNCP não respondeu');
      setDetailArquivosErro('O PNCP não respondeu');
      return undefined;
    } finally {
      if (detailRequestIdRef.current === itemId) setDetailLoading(false);
    }
  };

  /**
   * Recarrega o detalhe do servidor DEPOIS de sincronizar com um portal.
   *
   * ⛔ NÃO usar `openDetail({ ...detailItem })` pra isso, que era o que os três
   * handlers de portal (BLL/BNC/PCP) faziam. Dois problemas somados:
   *
   *   1. `openDetail` NÃO busca nada — ele renderiza o objeto que recebe. E o
   *      objeto passado era o `detailItem` de ANTES do scrape, ou seja, a tela
   *      voltava a mostrar exatamente os dados velhos que o sync acabou de
   *      substituir no banco.
   *   2. `openDetail` ainda dispara `loadDetailExtra(id)` SEM force — que lê o
   *      cache de 24h — enquanto o handler disparava `loadDetailExtra(id, true)`
   *      logo em seguida. Duas requisições concorrentes escrevendo o mesmo
   *      estado: quem responde por último ganha, e a cacheada costuma ser a mais
   *      rápida. Daí a intermitência do "às vezes não atualiza, aí eu saio e
   *      entro de novo" — sair e entrar refazia a carga sem corrida.
   *
   * Aqui é uma coisa só: refetch do item (modal + linha da lista) e UMA carga
   * forçada dos detalhes. Mesmo padrão que o handleReenrichItem já usava desde
   * 28/05/2026 e que nunca chegou aos handlers de portal.
   */
  const resyncDetail = async (itemId: number) => {
    detailRequestIdRef.current = itemId;
    try {
      const fresh = await procurementService.getItem(itemId);
      if (fresh) {
        setDetailItem((prev: any) => (prev && prev.id === fresh.id ? { ...prev, ...fresh } : prev));
        setItems((prev) => prev.map((it) => (it.id === fresh.id ? { ...it, ...fresh } : it)));
      }
    } catch {
      // Segue: o loadDetailExtra abaixo ainda pode trazer o item atualizado
      // (ele faz `if (data.item) setDetailItem(data.item)`).
    }
    await loadDetailExtra(itemId, true);
  };

  const openDetail = async (item: any) => {
    // Abre INSTANTÂNEO com o item já armazenado (aba Dados). itens/arquivos
    // são lazy (carregados ao abrir Itens/Anexos) — ver loadDetailExtra.
    detailRequestIdRef.current = item.id;
    detailExtraLoadedRef.current = false;
    detailExtraErrorRef.current = false;
    markItemRead(item.id);
    setDetailItem(item);
    setDetailItens([]);
    setDetailArquivos([]);
    setDetailExtra(null);
    // Banner de republicação: usa o estado JÁ persistido no item (sem chamar o
    // PNCP). Só itens excluídos têm isso, e o cron mantém atualizado.
    setDetailPncpStatus(item.excludedFromPncpAt ? 'gone' : null);
    setDetailRepublication(item.republicationCandidate || null);
    setDetailDataFromRepub(false);
    setDetailLogs([]);
    setNewLogMessage('');
    setReenrichItemResult(null);
    setDetailLoading(false);
    loadItemLogs(item.id);
    // Itens e anexos agora aparecem na própria tela (D-3), então o lazy deixa
    // de ser "ao clicar na aba" e passa a ser "ao abrir". Segue assíncrono: o
    // modal abre instantâneo com o que já está em memória e os itens entram
    // quando chegam. O cache de 24h do getItemDetails evita re-consultar o PNCP
    // a cada reabertura.
    loadDetailExtra(item.id);
  };

  // Deep-link: abrir o detalhe de um item específico via prop (ex. link do alerta
  // de convocação no Telegram → hub.ipcom.app.br/?licItem=<id>). Busca o item e
  // abre o modal. Roda 1x por id.
  const deepOpenedRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!openItemId || deepOpenedRef.current === openItemId) return;
    deepOpenedRef.current = openItemId;
    (async () => {
      try {
        if (openAdequar) {
          // ?licItem=<id>&adequar=1 — convocação de READEQUAÇÃO: abre o editor
          // da proposta do item já com o modal "Adequar ao lance final" e o
          // valor sugerido calculado (corrida contra o prazo, ex. 2h CRMV).
          const list = await procurementService.getProposals({ itemId: openItemId });
          const arr = Array.isArray(list) ? list : list?.data || [];
          const p = arr.find((x: any) => String(x.itemId) === String(openItemId) && !x.archived) || arr[0];
          if (p) {
            openWorkspace(p);
            openAdequarModal(p);
            return;
          }
          showToast('Nenhuma proposta encontrada pra esta licitação — abra o detalhe e crie/edite manualmente.', 'error');
        }
        const it = await procurementService.getItem(openItemId);
        if (it) openDetail(it);
      } catch {
        /* item não encontrado / sem permissão — ignora */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, openAdequar]);

  const handleSaveDate = async (itemId: number) => {
    if (!editingDateValue) { setEditingDateId(null); return; }
    try {
      await procurementService.updateItemDetails(itemId, { dataEncerramento: editingDateValue });
      setEditingDateId(null);
      // Recarrega do servidor em vez de optimistic com hora INVENTADA (T09:00
      // aparecia como horário real na coluna — P1 auditoria 04/07).
      loadItems();
    } catch (e: any) {
      // Era `alert()` — caixa branca do SO que trava a thread, no meio de um app
      // escuro que já tem toast para exatamente isto (§8 do guia).
      showToast('Erro ao salvar data: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  // "Testar perfil" (P2 auditoria): dry-run das keywords — mostra contagem e
  // amostra do que casaria HOJE na base crua, sem criar itens.
  const [previewingSearchId, setPreviewingSearchId] = useState<number | null>(null);
  const [searchPreview, setSearchPreview] = useState<any>(null);
  const handlePreviewSearch = async (s: any) => {
    setPreviewingSearchId(s.id);
    setSearchPreview(null);
    try {
      const data = await procurementService.previewSearch(s.id);
      setSearchPreview({ name: s.name, ...data });
    } catch (e: any) {
      showToast('Erro ao testar perfil: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setPreviewingSearchId(null);
    }
  };

  const handleExecuteSearch = async (searchId: number) => {
    setExecutingSearchId(searchId);
    try {
      await procurementService.executeSearch(searchId);
      showToast('Busca iniciada em background. Aguarde alguns minutos e atualize a pagina.');
      // Poll after delay to refresh data
      setTimeout(() => {
        loadItems();
        loadDashboard();
        loadSearches();
        setExecutingSearchId(null);
      }, 15000);
    } catch (e: any) {
      showToast('Erro ao executar busca: ' + (e.response?.data?.message || e.message), 'error');
      setExecutingSearchId(null);
    }
  };

  // ==================== Search CRUD ====================

  const openCreateSearch = () => {
    setSearchForm({ name: '', keywords: '', excludeKeywords: '', uf: [], modalidades: [], notifyEmail: '', isActive: true });
    setEditingSearch(null);
    setSearchModal('create');
  };

  const openEditSearch = (s: any) => {
    setSearchForm({
      name: s.name,
      keywords: (s.keywords || []).join(', '),
      excludeKeywords: (s.excludeKeywords || []).join(', '),
      uf: s.uf || [],
      modalidades: s.modalidades || [],
      notifyEmail: s.notifyEmail || '',
      isActive: s.isActive,
    });
    setEditingSearch(s);
    setSearchModal('edit');
  };

  const handleSaveSearch = async () => {
    try {
      const data = {
        name: searchForm.name,
        keywords: searchForm.keywords.split(',').map((k: string) => k.trim()).filter(Boolean),
        excludeKeywords: searchForm.excludeKeywords.split(',').map((k: string) => k.trim()).filter(Boolean),
        uf: searchForm.uf,
        modalidades: searchForm.modalidades,
        notifyEmail: searchForm.notifyEmail || undefined,
        isActive: searchForm.isActive,
      };

      if (searchModal === 'create') {
        await procurementService.createSearch(data);
        showToast('Perfil de busca criado');
      } else if (editingSearch) {
        await procurementService.updateSearch(editingSearch.id, data);
        showToast('Perfil de busca atualizado');
      }

      setSearchModal(null);
      loadSearches();
    } catch (e: any) {
      showToast('Erro ao salvar perfil: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleDeleteSearch = async (id: number) => {
    try {
      await procurementService.deleteSearch(id);
      showToast('Perfil de busca removido');
      loadSearches();
      setDeleteConfirm(null);
    } catch (e: any) {
      showToast('Erro ao remover perfil', 'error');
    }
  };

  // ==================== Documents CRUD ====================

  const { rodar: rodarCargaDosDocumentos, ...cargaDosDocumentos } = useEstadoDaCarga();
  const loadDocuments = useCallback(async () => {
    await rodarCargaDosDocumentos(async () => {
      const data = await procurementService.getDocumentsWithRenewal();
      setDocuments(Array.isArray(data) ? data : []);
    });
  }, [rodarCargaDosDocumentos]);

  const handleDocFileChange = async (file: File | null) => {
    setDocFile(file);
    if (!file || file.type !== 'application/pdf') return;
    setDocExtracting(true);
    try {
      const result = await procurementService.extractDates(file);
      setDocForm(prev => ({
        ...prev,
        ...(result.issueDate ? { issueDate: result.issueDate } : {}),
        ...(result.expiryDate && !prev.noExpiry ? { expiryDate: result.expiryDate } : {}),
        ...(result.detectedType ? { type: result.detectedType } : {}),
        ...(result.detectedName ? { name: result.detectedName } : {}),
        ...(result.detectedCategory ? { category: result.detectedCategory } : {}),
        ...(result.detectedDescription ? { description: result.detectedDescription } : {}),
      }));
      if (result.issueDate || result.expiryDate) showToast('Datas extraidas do PDF');
    } catch { /* silent */ }
    setDocExtracting(false);
  };

  const handleSaveDocument = async () => {
    try {
      const fd = new FormData();
      fd.append('name', docForm.name);
      fd.append('type', docForm.type);
      // Enviar SEMPRE, inclusive vazio: o backend converte '' em null. Enviar só
      // quando preenchido tornava impossível LIMPAR um campo pela tela — o update
      // fazia Object.assign e o valor antigo sobrevivia.
      fd.append('category', docForm.category);
      fd.append('subcategory', docForm.subcategory);
      fd.append('description', docForm.description);
      fd.append('issueDate', docForm.issueDate);
      fd.append('expiryDate', docForm.noExpiry ? '' : docForm.expiryDate);
      fd.append('isActive', String(docForm.isActive));
      fd.append('noExpiry', String(docForm.noExpiry));
      if (docFile) fd.append('file', docFile);

      if (docModal === 'create') {
        await procurementService.createDocument(fd);
        showToast('Documento criado');
      } else if (editingDoc) {
        await procurementService.updateDocument(editingDoc.id, fd);
        // Com arquivo, o PUT vira versão nova desde a #540 (outro id; a anterior fica guardada).
        showToast(docFile ? 'Nova versão salva. A anterior fica guardada no Histórico de versões.' : 'Documento atualizado');
      }
      setDocModal(null);
      setDocFile(null);
      loadDocuments();
    } catch (e: any) { showToast(docModal === 'edit' && docFile ? mensagemDoErroDaVersao(e) : 'Erro ao salvar documento', 'error'); }
  };

  const handleDeleteDocument = async (id: number) => {
    try {
      await procurementService.deleteDocument(id);
      showToast('Documento removido');
      loadDocuments();
      setDeleteConfirm(null);
    } catch (e: any) { showToast('Erro ao remover', 'error'); }
  };

  const handleArchiveDocs = async (ids: number[], archived: boolean) => {
    if (!ids.length) return;
    try {
      await procurementService.archiveDocuments(ids, archived);
      showToast(archived ? `${ids.length} documento(s) arquivado(s)` : `${ids.length} documento(s) desarquivado(s)`);
      loadDocuments();
    } catch (e: any) { showToast('Erro ao arquivar', 'error'); }
  };

  const handleViewDocument = async (doc: any) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPublicLink(null);
    setPreviewLoading(true);
    try {
      const { url } = await procurementService.getDocumentUrl(doc.id);
      setPreviewUrl(url);
    } catch (e: any) {
      showToast('Erro ao obter URL do documento', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadDocumentFile = () => {
    if (!previewUrl || !previewDoc) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = previewDoc.fileName || 'documento.pdf';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const [publicLink, setPublicLink] = useState<{ url: string; expiresAt: string | null } | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkDays, setLinkDays] = useState(365); // 0 = sem expiração

  const handleGeneratePublicLink = async () => {
    if (!previewDoc) return;
    setGeneratingLink(true);
    try {
      const res = await procurementService.createDocumentPublicLink(previewDoc.id, linkDays);
      setPublicLink({ url: res.url, expiresAt: res.expiresAt });
      const validadeMsg = linkDays === 0 ? 'sem expiração' : `válido ${linkDays} dias`;
      try {
        await navigator.clipboard.writeText(res.url);
        showToast(`Link público gerado e copiado (${validadeMsg})`);
      } catch {
        showToast(`Link público gerado (${validadeMsg})`);
      }
    } catch (e: any) {
      showToast('Erro ao gerar link público', 'error');
    } finally {
      setGeneratingLink(false);
    }
  };

  /** Copia um dado da linha (nº da compra, UASG) pra colar no portal.
   *  A Clipboard API pode REJEITAR (contexto sem permissão) ou simplesmente
   *  nunca resolver (aba sem foco/gesto não reconhecido) — por isso o corrida
   *  com timeout e o fallback do execCommand, senão o toast nunca aparecia e
   *  o clique parecia não ter feito nada. */
  const copiarDaLinha = async (valor: string, rotulo: string) => {
    const viaTextarea = () => {
      const ta = document.createElement('textarea');
      ta.value = valor;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        return document.execCommand('copy');
      } catch {
        return false;
      } finally {
        document.body.removeChild(ta);
      }
    };
    let ok = false;
    try {
      ok = await Promise.race([
        navigator.clipboard.writeText(valor).then(() => true),
        new Promise<boolean>((r) => setTimeout(() => r(false), 800)),
      ]);
    } catch {
      ok = false;
    }
    if (!ok) ok = viaTextarea();
    showToast(ok ? `${rotulo} copiado: ${valor}` : 'Não foi possível copiar', ok ? 'success' : 'error');
  };

  const handleCopyPublicLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink.url);
      showToast('Link copiado');
    } catch {
      showToast('Não foi possível copiar', 'error');
    }
  };

  const [renewingCndt, setRenewingCndt] = useState(false);
  const [renewingAuto, setRenewingAuto] = useState<string | null>(null);
  const [renewingUpload, setRenewingUpload] = useState<string | null>(null);
  const [showRenewalMenu, setShowRenewalMenu] = useState(false);
  const [showSearchProfilesModal, setShowSearchProfilesModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const bulkInputRef = React.useRef<HTMLInputElement>(null);
  const renewFileRef = React.useRef<HTMLInputElement>(null);
  const renewTypeRef = React.useRef<string>('');
  // A nova versão de um documento (27/10/2026, A1/D1): o modal, e a linha que está com um arquivo arrastado em cima.
  const [novaVersao, setNovaVersao] = useState<{ doc: any; arquivo?: File | null } | null>(null);
  const [historicoDoDoc, setHistoricoDoDoc] = useState<any | null>(null);
  const [linhaComArquivo, setLinhaComArquivo] = useState<number | null>(null);

  const RENEWAL_URLS: Record<string, { url: string; label: string }> = {
    certidao_negativa_federal: { url: 'https://servicos.receitafederal.gov.br/servico/certidoes/#/home/cnpj', label: 'Receita Federal' },
    certidao_negativa_estadual: { url: 'https://cdwfazenda.paas.pr.gov.br/cdwportal/certidao/automatica', label: 'Portal Certidao PR' },
    certidao_negativa_municipal: { url: 'https://cascavel.atende.net/', label: 'Prefeitura de Cascavel' },
    cndt: { url: 'https://cndt-certidao.tst.jus.br/gerarCertidao.faces', label: 'TST' },
    fgts: { url: 'https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf', label: 'Caixa' },
    certidao_falencia: { url: 'https://www.tjpr.jus.br/custas-judiciais-e-taxa-judiciaria', label: 'TJ-PR (Emissao de Guias)' },
    certidao_junta_comercial: { url: 'https://www.empresafacil.pr.gov.br/acoes/certidao', label: 'Empresa Facil PR - Junta Comercial' },
    cicad_municipal: { url: 'https://cascavel.atende.net/', label: 'Prefeitura de Cascavel' },
    cicad_estadual: { url: 'https://receita.pr.gov.br/certificado', label: 'Receita PR (certificado)' },
    cnd_tcu: { url: 'https://certidoes.apps.tcu.gov.br/emitir-certidao-contas-julgadas-irregulares', label: 'TCU - Plataforma de Certidoes' },
    cnd_cgu: { url: 'https://certidoes.cgu.gov.br/', label: 'CGU - Certidoes Correcionais' },
  };

  const handleRenewCndt = async () => {
    setRenewingCndt(true);
    try {
      const result = await procurementService.renewCndt();
      if (result.success) {
        showToast(result.message);
        loadDocuments();
      } else {
        showToast(result.message, 'error');
        if (result.renewalUrl) window.open(result.renewalUrl, '_blank');
      }
    } catch (e: any) {
      showToast('Erro ao renovar CNDT', 'error');
      window.open('https://cndt-certidao.tst.jus.br/gerarCertidao.faces', '_blank');
    }
    setRenewingCndt(false);
  };

  const handleAutoRenew = async (type: string, renewFn: () => Promise<any>, fallbackUrl: string) => {
    setShowRenewalMenu(false);
    setRenewingAuto(type);
    try {
      const result = await renewFn();
      if (result.success) {
        showToast(result.message);
        loadDocuments();
      } else {
        showToast(result.message, 'error');
        if (result.renewalUrl) window.open(result.renewalUrl, '_blank');
      }
    } catch (e: any) {
      showToast(`Erro ao renovar. Acesse manualmente.`, 'error');
      window.open(fallbackUrl, '_blank');
    }
    setRenewingAuto(null);
  };

  const handleGenerateGuia = async (type: string, generateFn: () => Promise<any>, fallbackUrl: string) => {
    setShowRenewalMenu(false);
    setRenewingAuto(type);
    try {
      const result = await generateFn();
      if (result.success && result.pdfBase64) {
        // Download the boleto PDF
        const byteChars = atob(result.pdfBase64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.pdfFileName || 'guia_recolhimento.pdf';
        a.click();
        URL.revokeObjectURL(url);
        showToast(result.message);
      } else {
        showToast(result.message || 'Erro ao gerar guia', 'error');
        if (result.renewalUrl) window.open(result.renewalUrl, '_blank');
      }
    } catch (e: any) {
      showToast('Erro ao gerar guia. Acesse manualmente.', 'error');
      window.open(fallbackUrl, '_blank');
    }
    setRenewingAuto(null);
  };

  const handleOpenAndUpload = (type: string, url: string) => {
    setShowRenewalMenu(false);
    window.open(url, '_blank');
    renewTypeRef.current = type;
    setTimeout(() => {
      if (renewFileRef.current) {
        renewFileRef.current.value = '';
        renewFileRef.current.click();
      }
    }, 500);
  };

  const handleRenewFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = renewTypeRef.current;
    if (!type) return;
    setRenewingUpload(type);
    try {
      const result = await procurementService.renewUpload(file, type);
      if (result.success) {
        showToast(result.message);
        loadDocuments();
      } else {
        showToast(result.message, 'error');
      }
    } catch (err: any) {
      showToast('Erro ao enviar certidao', 'error');
    }
    setRenewingUpload(null);
    if (renewFileRef.current) renewFileRef.current.value = '';
  };

  const detectDocType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (/crf|fgts|regularidade.*empregador/.test(lower)) return 'fgts';
    if (/cndt|debito.*trabalhist/.test(lower)) return 'cndt';
    if (/cnd.*federal|certidao.*negativa.*federal|pgfn|tribut.*federal/.test(lower)) return 'certidao_negativa_federal';
    if (/cnd.*estadual|certidao.*negativa.*estadual/.test(lower)) return 'certidao_negativa_estadual';
    if (/cnd.*municipal|certidao.*negativa.*municipal/.test(lower)) return 'certidao_negativa_municipal';
    if (/cicad.*municipal/.test(lower)) return 'cicad_municipal';
    if (/cicad.*estadual/.test(lower)) return 'cicad_estadual';
    if (/contrato.*social/.test(lower)) return 'contrato_social';
    if (/balanco|balan[çc]o/.test(lower)) return 'balanco_patrimonial';
    if (/atestado.*capacidade/.test(lower)) return 'atestado_capacidade';
    if (/alvara|alvar[áa]/.test(lower)) return 'alvara';
    if (/procura[çc][aã]o/.test(lower)) return 'procuracao';
    if (/fal[eê]ncia|recupera[çc][aã]o.*judicial/.test(lower)) return 'certidao_falencia';
    if (/anatel.*licen[çc]a|licen[çc]a.*anatel/.test(lower)) return 'licenca_anatel';
    if (/certificado.*anatel/.test(lower)) return 'certificado_anatel';
    if (/crea|registro.*crea/.test(lower)) return 'registro_crea';
    if (/stfc|contrato.*stfc/.test(lower)) return 'contrato_stfc';
    if (/iso.*900|certificac.*iso/.test(lower)) return 'certificacao_iso';
    if (/sicaf/.test(lower)) return 'sicaf';
    if (/junta.*comercial/.test(lower)) return 'certidao_junta_comercial';
    if (/cartao.*cnpj|cnpj/.test(lower)) return 'cartao_cnpj';
    if (/declara[çc][aã]o/.test(lower)) return 'declaracao';
    return 'outro';
  };

  const importBulkFiles = async (files: FileList) => {
    if (!files || files.length === 0) return;

    setBulkImporting(true);
    try {
      const fd = new FormData();
      const metadata: any[] = [];

      for (let i = 0; i < files.length; i++) {
        fd.append('files', files[i]);
        const cleanName = files[i].name
          .replace(/\.[^.]+$/, '')       // remove extension
          .replace(/_/g, ' ')            // underscores to spaces
          .replace(/\d{6,}/g, '')        // remove long numbers (dates, IDs)
          .replace(/\d{2}[./]\d{2}[./]\d{2,4}/g, '') // remove date patterns
          .replace(/\s+/g, ' ')          // collapse spaces
          .trim();
        metadata.push({
          name: cleanName || files[i].name.replace(/\.[^.]+$/, ''),
          type: detectDocType(files[i].name),
        });
      }
      fd.append('metadata', JSON.stringify(metadata));

      const result = await procurementService.bulkImportDocuments(fd);
      if (result.errors?.length > 0) {
        showToast(`${result.imported} importados, ${result.errors.length} erros: ${result.errors.join('; ')}`, result.imported > 0 ? 'success' : 'error');
      } else {
        showToast(`${result.imported} documentos importados`);
      }
      loadDocuments();
    } catch (e: any) {
      showToast('Erro ao importar documentos', 'error');
    }
    setBulkImporting(false);
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };

  const handleBulkImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await importBulkFiles(files);
  };

  const analyzeAiModalEditalFile = async (file: File) => {
    if (!aiModalItem) return;
    setAiModalError(null);
    setAiModalRunning(true);
    try {
      const result = await procurementService.analyzeEditalUpload(aiModalItem.id, file);
      setAiModalData(result);
      setAiModalAnalyzedAt(new Date().toISOString());
    } catch (err: any) {
      setAiModalError(err?.response?.data?.message || err.message);
    } finally {
      setAiModalRunning(false);
    }
  };

  // ==================== Products CRUD ====================

  // P1 (12/10/2026): a lista que não carregava dizia "Nenhum template cadastrado" com "Novo Template" ao lado.
  const { rodar: rodarCargaDosTemplates, ...cargaDosTemplates } = useEstadoDaCarga();
  const loadDeclarationTemplates = useCallback(async () => {
    await rodarCargaDosTemplates(async () => {
      const data = await procurementService.getDeclarationTemplates();
      setDeclarationTemplates(Array.isArray(data) ? data : []);
    });
  }, [rodarCargaDosTemplates]);

  const loadProducts = useCallback(async () => {
    try {
      const data = await procurementService.getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e: any) { console.error('Erro products:', e); }
  }, []);

  const handleSaveProduct = async () => {
    try {
      const data = {
        name: productForm.name,
        description: productForm.description || undefined,
        unit: productForm.unit || undefined,
        referencePrice: productForm.referencePrice ? parseFloat(productForm.referencePrice) : undefined,
        category: productForm.category || undefined,
        ncmCode: productForm.ncmCode || undefined,
      };
      if (productModal === 'create') {
        await procurementService.createProduct(data);
        showToast('Produto criado');
      } else if (editingProduct) {
        await procurementService.updateProduct(editingProduct.id, data);
        showToast('Produto atualizado');
      }
      setProductModal(null);
      loadProducts();
    } catch (e: any) { showToast('Erro ao salvar produto', 'error'); }
  };

  const handleDeleteProduct = async (id: number) => {
    try {
      await procurementService.deleteProduct(id);
      showToast('Produto removido');
      loadProducts();
      setDeleteConfirm(null);
    } catch (e: any) { showToast('Erro ao remover', 'error'); }
  };

  // ==================== Portals CRUD ====================

  // P1 (12/10/2026): a lista que não carregava dizia "Nenhum acesso cadastrado" com "Novo Acesso" ao lado, e o
  // operador cadastrava de novo login e senha de um portal que já existe. Grau 2.
  const { rodar: rodarCargaDosPortais, ...cargaDosPortais } = useEstadoDaCarga();
  const loadPortals = useCallback(async (): Promise<any[]> => {
    let list: any[] = [];
    await rodarCargaDosPortais(async () => {
      const data = await procurementService.getPortals();
      list = Array.isArray(data) ? data : [];
      setPortals(list);
    });
    return list;
  }, [rodarCargaDosPortais]);

  const handleSavePortal = async () => {
    try {
      const data: any = {
        portalName: portalForm.portalName,
        // O titular (backend #532). Vazio não apaga o que já está gravado: o backend ignora o campo ausente.
        titular: portalForm.titular?.trim() || undefined,
        system: portalForm.system || 'todos',
        portalUrl: portalForm.portalUrl || undefined,
        companyCnpj: portalForm.companyCnpj || undefined,
        companyName: portalForm.companyName || undefined,
        login: portalForm.login || undefined,
        password: portalForm.password || undefined,
        notes: portalForm.notes || undefined,
      };
      if (portalModal === 'create') {
        await procurementService.createPortal(data);
        showToast('Acesso criado');
      } else if (editingPortal) {
        await procurementService.updatePortal(editingPortal.id, data);
        showToast('Acesso atualizado');
      }
      setPortalModal(null);
      loadPortals();
    } catch (e: any) {
      const msg = e.response?.data?.message;
      const detail = Array.isArray(msg) ? msg.join(', ') : (msg || e.message);
      showToast('Erro ao salvar acesso: ' + detail, 'error');
      console.error('Portal save error:', e.response?.data || e);
    }
  };

  const handleDeletePortal = async (id: number) => {
    try {
      await procurementService.deletePortal(id);
      showToast('Acesso removido');
      loadPortals();
      setDeleteConfirm(null);
    } catch (e: any) { showToast('Erro ao remover', 'error'); }
  };

  // Portal Health
  const loadPortalHealth = useCallback(async () => {
    setPortalHealthLoading(true);
    try {
      const data = await procurementService.getPortalHealth();
      setPortalHealthData(Array.isArray(data) ? data : []);
    } catch (e: any) { console.error('Erro ao carregar status portais:', e); }
    setPortalHealthLoading(false);
  }, []);


  // Status logs
  // O histórico é POR ITEM: o openDetail zera a lista antes de carregar, então um erro nunca mostra o de outro item.
  const { rodar: rodarCargaDoHistorico, ...cargaDoHistorico } = useEstadoDaCarga();
  const loadItemLogs = async (itemId: number) => {
    setLogsLoading(true);
    await rodarCargaDoHistorico(async () => {
      const data = await procurementService.getItemLogs(itemId);
      setDetailLogs(Array.isArray(data) ? data : []);
    });
    setLogsLoading(false);
  };

  const handleAddLog = async () => {
    if (!detailItem || !newLogMessage.trim()) return;
    try {
      await procurementService.addItemLog(detailItem.id, newLogMessage.trim());
      setNewLogMessage('');
      loadItemLogs(detailItem.id);
      showToast('Observacao adicionada');
    } catch (e: any) { showToast('Erro ao adicionar observacao', 'error'); }
  };

  // ==================== Proposal Actions ====================

  // Total via proposalMath (espelho do backend): itens com chargeType calculam
  // mensal×meses / taxa única; itens legados (sem tipo) seguem unit×qtd.
  const contractMonthsNum = parseInt(proposalForm.contractMonths, 10) || 12;
  const computedTotalValue = useMemo(() => {
    if (proposalItemsForm.length === 0) return 0;
    return computeProposalTotal(proposalItemsForm, contractMonthsNum);
  }, [proposalItemsForm, contractMonthsNum]);

  // Retorna os itens montados (PNCP + composição da Análise IA) e também
  // popula o estado legado do modal de criação. A página de trabalho usa o
  // retorno — assim a regra de importação vive num lugar só.
  const handleLoadProposalItems = async (itemId?: string): Promise<{ items: any[]; meses?: number } | null> => {
    const targetId = itemId || proposalForm.itemId;
    if (!targetId) return null;
    let resultado: { items: any[]; meses?: number } | null = null;
    try {
      const details = await procurementService.getItemDetails(parseInt(targetId, 10));
      if (details?.itens?.length > 0) {
        // F2: itensEdital extraídos pela IA do edital (Análise IA) — trazem o
        // que o PNCP NÃO tem: grupo, CATSER, tipo de cobrança, teto com 4
        // casas e meses do contrato. Sugestão: preenche buracos, nunca
        // sobrescreve o que veio "melhor" do PNCP.
        const aiItens: any[] = Array.isArray(details.item?.aiAnalysis?.itensEdital)
          ? details.item.aiAnalysis.itensEdital : [];
        const aiByNumber = new Map<number, any>(
          aiItens.filter((a: any) => typeof a?.numero === 'number').map((a: any) => [a.numero, a]),
        );
        const aiMeses = Number(details.item?.aiAnalysis?.mesesContrato) || 0;
        if (aiMeses > 0) setProposalForm(prev => ({ ...prev, contractMonths: String(aiMeses) }));
        // Caso CRMV: PNCP publica o GRUPO como 1 item colapsado, mas a tabela
        // do edital tem N subitens. Quando a IA achou MAIS itens que o PNCP,
        // a composição do edital é a fonte — senão a proposta sai flat de novo.
        if (aiItens.length > details.itens.length) {
          setProposalItemsForm(aiItens.map((ai: any, i: number) => {
            const aiMax = Number(ai?.valorUnitarioMaximo) > 0 ? Number(ai.valorUnitarioMaximo) : 0;
            return {
              itemNumber: typeof ai.numero === 'number' ? ai.numero : i + 1,
              description: ai.descricao || '',
              quantity: ai.quantidade || 0,
              unit: ai.unidade || '',
              estimatedPrice: aiMax,
              valorEstimadoSigiloso: !aiMax,
              unitPrice: '',
              brand: 'IPCOM',
              model: 'IPCOM CLOUD',
              catser: ai.catser ? String(ai.catser) : undefined,
              chargeType: ai.tipoCobrancaSugerido || undefined,
              groupNumber: ai.grupo ?? undefined,
              groupDescription: ai.grupoDescricao ?? undefined,
              maxUnitPrice: aiMax > 0 ? String(aiMax) : undefined,
            };
          }));
          showToast(`✓ Composição do EDITAL aplicada via Análise IA: ${aiItens.length} subitens (PNCP tinha ${details.itens.length}) — confira quantidades e máximos`, 'success');
          resultado = { items: aiItens.map((ai: any, i: number) => {
            const aiMax = Number(ai?.valorUnitarioMaximo) > 0 ? Number(ai.valorUnitarioMaximo) : 0;
            return {
              itemNumber: typeof ai.numero === 'number' ? ai.numero : i + 1,
              description: ai.descricao || '', quantity: ai.quantidade || 0, unit: ai.unidade || '',
              estimatedPrice: aiMax, valorEstimadoSigiloso: !aiMax, unitPrice: '',
              brand: 'IPCOM', model: 'IPCOM CLOUD',
              catser: ai.catser ? String(ai.catser) : undefined,
              chargeType: ai.tipoCobrancaSugerido || undefined,
              groupNumber: ai.grupo ?? undefined, groupDescription: ai.grupoDescricao ?? undefined,
              maxUnitPrice: aiMax > 0 ? String(aiMax) : undefined,
            };
          }), meses: aiMeses || undefined };
        } else {
        setProposalItemsForm(details.itens.map((it: any) => {
          const ai = aiByNumber.get(it.numeroItem);
          const aiMax = Number(ai?.valorUnitarioMaximo) > 0 ? Number(ai.valorUnitarioMaximo) : 0;
          const estimado = it.valorUnitarioEstimado || aiMax || 0;
          return {
            itemNumber: it.numeroItem,
            description: it.descricao || ai?.descricao || '',
            descricaoDetalhada: it.descricao || undefined,
            quantity: it.quantidade || ai?.quantidade || 0,
            unit: it.unidadeMedida || ai?.unidade || '',
            estimatedPrice: estimado,
            valorEstimadoSigiloso: !estimado || estimado <= 0,
            unitPrice: '',
            brand: 'IPCOM',
            model: '',
            // CATSER: IA do edital > catálogo do PNCP (quando o órgão classificou)
            catser: ai?.catser ? String(ai.catser) : (it.catalogoCodigoItem != null ? String(it.catalogoCodigoItem) : undefined),
            chargeType: ai?.tipoCobrancaSugerido || undefined,
            groupNumber: ai?.grupo ?? undefined,
            groupDescription: ai?.grupoDescricao ?? undefined,
            // Teto do edital: IA (4 casas do PDF) > estimado do PNCP.
            maxUnitPrice: aiMax > 0 ? String(aiMax) : (it.valorUnitarioEstimado > 0 ? String(it.valorUnitarioEstimado) : undefined),
            // Tratamento diferenciado (Art. 4º) — o portal exibe por item
            tipoBeneficio: it.tipoBeneficio ?? undefined,
            tipoBeneficioNome: it.tipoBeneficioNome || undefined,
          };
        }));
        if (aiItens.length > 0) {
          showToast(`✓ ${details.itens.length} item(ns) + composição da Análise IA (grupos/tipos/máximos) aplicados`, 'success');
        }
        resultado = { items: details.itens.map((it: any) => {
          const ai = aiByNumber.get(it.numeroItem);
          const aiMax = Number(ai?.valorUnitarioMaximo) > 0 ? Number(ai.valorUnitarioMaximo) : 0;
          const estimado = it.valorUnitarioEstimado || aiMax || 0;
          return {
            itemNumber: it.numeroItem, description: it.descricao || ai?.descricao || '',
            descricaoDetalhada: it.descricao || undefined,
            quantity: it.quantidade || ai?.quantidade || 0,
            unit: it.unidadeMedida || ai?.unidade || '',
            estimatedPrice: estimado, valorEstimadoSigiloso: !estimado, unitPrice: '',
            brand: 'IPCOM', model: '',
            // CATSER: IA do edital > catálogo do PNCP (quando o órgão classificou)
            catser: ai?.catser ? String(ai.catser) : (it.catalogoCodigoItem != null ? String(it.catalogoCodigoItem) : undefined),
            chargeType: ai?.tipoCobrancaSugerido || undefined,
            groupNumber: ai?.grupo ?? undefined, groupDescription: ai?.grupoDescricao ?? undefined,
            maxUnitPrice: aiMax > 0 ? String(aiMax) : (it.valorUnitarioEstimado > 0 ? String(it.valorUnitarioEstimado) : undefined),
            tipoBeneficio: it.tipoBeneficio ?? undefined,
            tipoBeneficioNome: it.tipoBeneficioNome || undefined,
          };
        }), meses: aiMeses || undefined };
        }
        setSelectedProposalItemInfo({
          id: details.item?.id,
          orgaoNome: details.item?.orgaoNome,
          objetoCompra: details.item?.objetoCompra,
          modalidade: details.item?.modalidadeNome || (details.item?.modalidadeId ? MODALIDADES[details.item.modalidadeId] : ''),
          processoSei: details.item?.processoSei || '',
          criterioJulgamento: details.item?.criterioJulgamento || '',
          modoDisputa: details.item?.modoDisputa || '',
          orgaoCnpj: details.item?.orgaoCnpj || '',
          codigoUnidade: details.item?.codigoUnidade || '',
            numeroCompra: details.item?.numeroCompra || '',
            anoCompra: details.item?.anoCompra || '',
          dataAbertura: details.item?.dataAbertura || null,
          dataEncerramento: details.item?.dataEncerramento || null,
          dataSessaoInicio: details.item?.dataSessaoInicio || null,
        });
      } else if (details?.itensStatus === 'erro') {
        // Não afirmar que a licitação não tem itens quando quem falhou foi o
        // PNCP. Em 06/09/2026 o `api/pncp/v1` (único que serve /itens) deu 503 o
        // dia inteiro e as 5 licitações detalhadas foram dadas como "sem itens".
        showToast(
          `${details.itensMotivo || 'O PNCP não respondeu'} — os itens não puderam ser carregados. Tente de novo em alguns minutos ou sincronize pelo portal.`,
          'error',
        );
      } else {
        showToast('Nenhum item encontrado para esta licitacao', 'error');
      }
    } catch (e: any) {
      showToast('Erro ao carregar itens: ' + (e.response?.data?.message || e.message), 'error');
    }
    return resultado;
  };





  // Montagem ÚNICA do FormData da proposta — usada pelos 3 saves (create,
  // update e draft silencioso). Antes eram 3 blocos quase idênticos de ~40
  // linhas; o draft NÃO enviava acceptedTerms, então gerar PDF salvava o
  // rascunho PERDENDO termos não salvos (bug corrigido nesta unificação).
  // Wrapper do serializador PURO (utils/proposalForm) — via ÚNICA de escrita,
  // compartilhada com o ProposalWorkspace. Aqui só se coleta o estado do React.
  const buildProposalFormData = (opts: { includeItemId?: boolean } = {}) =>
    buildProposalFormDataPure({
      form: proposalForm,
      items: proposalItemsForm,
      computedTotal: computedTotalValue,
      declarations: proposalDeclarations,
      // A gaveta só cria, e a única porta dela (prepareProposalFromItem) zerava os
      // termos antes de abrir: o aceite dos termos do portal é feito na página de
      // trabalho.
      termsAccepted: false,
      meeppValue,
      margemPaisOrigem,
      margemEnquadramento,
      pcpInfoComp,
      portalOrigem: selectedProposalItemInfo?.portalOrigem || editingProposal?.item?.portalOrigem || null,
      hasPortalTerms: !!editingProposal?.item?.termsDeclarations,
      file: null,
      includeItemId: opts.includeItemId,
    });

  const handleCreateProposal = async () => {
    try {
      const formData = buildProposalFormData({ includeItemId: true });
      const savedPortalId = proposalPortalId;
      const savedItemId = proposalForm.itemId;
      const created = await procurementService.createProposal(formData);
      // Backfill the orphan BLL scrape job (created before the proposal existed)
      // so row-level badges + "último job" button find it via getLatestJob(proposalId).
      if (pendingBllScrapeJobIdRef.current && created?.id) {
        try {
          await procurementService.linkAutomationJobToProposal(pendingBllScrapeJobIdRef.current, created.id);
        } catch { /* ignore */ }
        pendingBllScrapeJobIdRef.current = null;
        loadAutomationJobs();
      }
      // Update item portalOrigem if user selected a specific portal
      if (savedPortalId && savedItemId) {
        const selectedPortal = portals.find((p: any) => String(p.id) === savedPortalId);
        const inferred = inferPortalOrigemFromPortal(selectedPortal);
        if (inferred) {
          procurementService.updateItemPortal(parseInt(savedItemId, 10), inferred).catch((e) => {
            showToast('Erro ao atualizar portal do item: ' + (e.response?.data?.message || e.message), 'error');
          });
        }
      }
      // Criar proposta SEMPRE termina na página de trabalho. O drawer é a
      // porta de entrada (portal + licitação) e o trabalho de verdade acontece
      // nos estágios — antes só o BLL com documentos pendentes ia pra lá, e o
      // resto caía numa lista, deixando o operador sem saber qual era o próximo
      // passo. Também acaba com os dois formulários concorrentes pra mesma
      // coisa (o drawer pedia itens/preços/termos/PDF, que são os estágios).
      if (created?.id) {
        setProposalModal(null);
        setProposalItemsForm([]);
        setSelectedProposalItemInfo(null);
        setProposalPortalId('');
        setProposalDeclarations([]);
        await loadProposals();
        setWorkspaceProposalId(created.id);
        // A página de trabalho só existe no subView 'proposals'. Criando a
        // partir do Encontrar, setar o id sem navegar deixava o operador na
        // lista, sem sinal de que a proposta tinha sido criada.
        if (subView !== 'proposals') onNavigate?.('lic-cadastrar');
      } else {
        setProposalItemsForm([]);
        setProposalDeclarations([]);
      }
      loadProposals();
      // Re-busca a lista do Encontrar: ao cadastrar, o item sai do filtro
      // "abertas" (ganha proposta/muda status). Sem isso a linha só some após
      // refresh manual da página.
      loadItems();
      // R1.17: scrape automático REMOVIDO — disparava 30-60s de carregamento sem o user pedir.
      // User decide quando precisa: botão "🔄 Sincronizar do Portal" no card da proposta.
      showToast(created?.id ? 'Proposta criada. Use "Sincronizar do Portal" se precisar puxar items.' : 'Proposta criada com sucesso');
    } catch (e: any) { showToast('Erro ao criar proposta: ' + (e.response?.data?.message || e.message), 'error'); }
  };


  // Fecha a gaveta de criação LIMPANDO o estado dela. Fechar pelo × ou Cancelar
  // sem limpar fazia o próximo "Criar Proposta" herdar dados de OUTRA proposta
  // (bug 02/07).
  const closeProposalModal = () => {
    setProposalModal(null);
    setEditingProposal(null);
    setProposalItemsForm([]);
    setSelectedProposalItemInfo(null);
    setProposalPortalId('');
    setProposalDeclarations([]);
    setMargemPaisOrigem('CHN');
    setMargemEnquadramento('0');
  };






  // "Adequar ao lance final" — dry-run no backend; o Aplicar grava os novos
  // unitários no formulário e o user salva pelo fluxo normal.
  // proposalIdOverride/mesesOverride: usados quando chamado logo após
  // openProposalEditor (deep-link) — o state ainda não assentou.
  const runAdequar = async (alvoOverride?: string, proposalIdOverride?: number, mesesOverride?: string) => {
    const pid = proposalIdOverride ?? adequarProposalId ?? editingProposal?.id;
    if (!pid) return;
    setAdequarLoading(true);
    try {
      const alvoStr = alvoOverride !== undefined ? alvoOverride : adequarAlvo;
      const body: any = { meses: parseInt(mesesOverride ?? adequarMeses, 10) || 12 };
      const alvoNum = parseFloat(String(alvoStr).replace(',', '.'));
      if (alvoNum > 0) body.valorAlvo = alvoNum;
      const res = await procurementService.adequarProposta(pid, body);
      setAdequarResult(res);
      if (!alvoStr && res?.valorAlvoSugerido?.valor) {
        setAdequarAlvo(String(res.valorAlvoSugerido.valor));
      }
    } catch (e: any) {
      showToast('Erro ao calcular adequação: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setAdequarLoading(false);
    }
  };


  const openAdequarModal = (p?: any) => {
    const meses = p?.contractMonths ? String(p.contractMonths) : (proposalForm.contractMonths || '12');
    setAdequarAlvo('');
    setAdequarMeses(meses);
    setAdequarResult(null);
    setAdequarProposalId(p?.id ?? editingProposal?.id ?? null);
    setAdequarModal(true);
    // 1ª chamada sem alvo → backend responde com valorAlvoSugerido (e já
    // calcula com ele quando existir)
    runAdequar('', p?.id, meses);
  };

  // ⛔ Escreve nos itens da PÁGINA DE TRABALHO (ws). De 11/08 (d0853d7) a 05/10/2026
  // escrevia no proposalItemsForm da gaveta antiga, que ninguém mais exibe: o toast
  // dizia "aplicada" e a tabela seguia com os preços antigos. O toast de sucesso só
  // sai quando a escrita aconteceu; sem página de trabalho da MESMA proposta, avisa
  // que não aplicou.
  const applyAdequacao = () => {
    const d = decidirAdequacao({
      resultado: adequarResult,
      propostaNaPagina: workspaceProposalId,
      propostaCalculada: adequarProposalId,
      itensDaPagina: ws.items,
    });
    if (d.tipo === 'nao-aplicada') {
      showToast(`Adequação NÃO aplicada: ${d.motivo}`, 'error');
      return;
    }
    ws.setItems(d.itens);
    if (d.meses) ws.setForm({ contractMonths: String(d.meses) });
    setAdequarModal(false);
    showToast(`Adequação aplicada em ${d.aplicados} item(ns) — total R$ ${fmt(adequarResult.somaFinal)}. Confira e SALVE a proposta.`, 'success');
  };

  const prepareProposalFromItem = async (item: any, itensData?: any[]) => {
    // NOTA (23/05/2026): removido `updateItemStatus(item.id, 'participando')`
    // que rodava ao ABRIR o modal — fazia status mudar pra Analisando/Participando
    // sem o user salvar nada (e se cancelasse, o status ficava errado).
    // A promoção de status correta acontece NO BACKEND quando a proposta é
    // efetivamente salva (procurement.service.ts:createProposal → promovaveis):
    //   - rascunho → ANALISANDO
    //   - enviada  → PARTICIPANDO
    // Pre-fill from item data
    setProposalForm({
      itemId: String(item.id),
      title: `Proposta — ${(getOrgaoDisplay(item) || '').substring(0, 80)}`,
      description: '',
      totalValue: '',
      status: 'rascunho',
      checklistNotes: '',
      valorRecorrente: false,
      contractMonths: '12',
      pdfLayout: '',
      pdfIncluirTributacao: false,
      validityDays: '60',
      observations: '',
      conditions: '',
      representative: '',
      representativeRole: '',
      bankInfo: '',
      brand: '',
      model: '',
    });
    setEditingProposal(null);
    setProposalPortalId(findPortalIdBySource(item.portalOrigem, item.linkOrigem));
    // Reset declarações entre propostas + default ME/EPP pra BLL:
    // IPCOM Tecnologia LTDA é EPP enquadrada no Simples Nacional — pra QUALQUER
    // licitação BLL marcamos "Sim" automaticamente (LC 123/2006). User pode
    // desmarcar manualmente no bloco "Declaração ME/EPP (BLL)" do modal.
    // Pra ComprasNet o radio só aparece quando o scrape detectou termsDeclarations.meepp.
    setMeeppValue((item.portalOrigem === 'bll' || item.portalOrigem === 'pcp') ? 'Sim' : null);
    setProposalDeclarations([]);
    // Map itens to proposalItemsForm if available
    const itens = itensData || [];
    if (itens.length > 0) {
      setProposalItemsForm(itens.map((it: any) => ({
        itemNumber: it.numeroItem,
        description: it.descricao || '',
        quantity: it.quantidade || 0,
        unit: it.unidadeMedida || '',
        estimatedPrice: it.valorUnitarioEstimado || 0,
        unitPrice: '',
        brand: 'IPCOM',
        model: '',
        maxUnitPrice: it.valorUnitarioEstimado > 0 ? String(it.valorUnitarioEstimado) : undefined,
      })));
      setSelectedProposalItemInfo({
        id: item.id,
        orgaoNome: item.orgaoNome,
        unidadeNome: item.unidadeNome,
        objetoCompra: item.objetoCompra,
        modalidade: item.modalidadeNome || (item.modalidadeId ? MODALIDADES[item.modalidadeId] : ''),
        valorEstimado: item.valorEstimado,
        processoSei: item.processoSei || '',
        criterioJulgamento: item.criterioJulgamento || '',
        modoDisputa: item.modoDisputa || '',
        orgaoCnpj: item.orgaoCnpj || '',
        codigoUnidade: item.codigoUnidade || '',
        numeroCompra: item.numeroCompra || '',
        anoCompra: item.anoCompra || '',
        dataAbertura: item.dataAbertura || null,
        dataEncerramento: item.dataEncerramento || null,
        dataSessaoInicio: item.dataSessaoInicio || null,
        portalOrigem: item.portalOrigem || null,
      });
    } else {
      setProposalItemsForm([]);
      setSelectedProposalItemInfo({
        id: item.id,
        orgaoNome: item.orgaoNome,
        unidadeNome: item.unidadeNome,
        objetoCompra: item.objetoCompra,
        modalidade: item.modalidadeNome || (item.modalidadeId ? MODALIDADES[item.modalidadeId] : ''),
        valorEstimado: item.valorEstimado,
        processoSei: item.processoSei || '',
        criterioJulgamento: item.criterioJulgamento || '',
        modoDisputa: item.modoDisputa || '',
        orgaoCnpj: item.orgaoCnpj || '',
        codigoUnidade: item.codigoUnidade || '',
        numeroCompra: item.numeroCompra || '',
        anoCompra: item.anoCompra || '',
        dataAbertura: item.dataAbertura || null,
        dataEncerramento: item.dataEncerramento || null,
        dataSessaoInicio: item.dataSessaoInicio || null,
        portalOrigem: item.portalOrigem || null,
      });
    }
    // Ensure portals and proposalItems are loaded (they may not be if we're on items subView)
    const loadedPortals = portals.length > 0 ? portals : await loadPortals();
    const loadedItems = await loadProposalItems();

    // Force-include this item in proposalItems if not already there (React state is async)
    // NOTA: NÃO alteramos item.status aqui — a promoção real (rascunho→Analisando
    // ou enviada→Participando) acontece no backend ao salvar a proposta.
    const itemInList = loadedItems.find((i: any) => String(i.id) === String(item.id));
    if (!itemInList) {
      setProposalItems((prev: any[]) => [item, ...prev]);
    }

    // Derive portal ID using loaded portals directly (React state may not be updated yet)
    const source = item.portalOrigem || (() => {
      try {
        const host = new URL(item.linkOrigem!).hostname.toLowerCase();
        if (host.includes('comprasnet') || host.includes('cnetmobile')) return 'comprasnet';
        if (host.includes('bnc')) return 'bnc';
        if (host.includes('bll')) return 'bll';
        if (host.includes('portaldecompraspublicas')) return 'pcp';
        return null;
      } catch { return null; }
    })();
    const autoPortalId = accountIdForSource(loadedPortals, source);
    if (autoPortalId) setProposalPortalId(autoPortalId);

    // Close detail modal if open, open proposal modal.
    // Limpa estado BLL/margem de sessões anteriores (cinto e suspensório do
    // closeProposalModal — o create NUNCA deve herdar docs de outra proposta).
    setMargemPaisOrigem('CHN');
    setMargemEnquadramento('0');
    setDetailItem(null);
    setProposalModal('create');
  };

  const handleCreateProposalFromDetail = async () => {
    if (!detailItem || preparingProposal) return;
    setPreparingProposal(true);
    try {
      // Garante que os itens do PNCP estejam carregados (lazy) ANTES de criar a
      // proposta — senão criaria sem itens (o modal pode ter aberto só na aba Dados).
      const data = await loadDetailExtra(detailItem.id);
      const itens = data?.itens ?? detailItens;
      // Falha de rede + lista vazia = a proposta ia abrir em branco sem explicar
      // nada (era o sintoma reportado em 11/08/2026, quando o PNCP caiu e o nginx
      // devolveu 504 aos 60s). Abre assim mesmo, mas dizendo o que aconteceu.
      if ((detailExtraErrorRef.current || data?.itensStatus === 'erro') && itens.length === 0) {
        showToast(
          'Não consegui buscar os itens no PNCP agora (portal lento ou fora). A proposta abre sem itens — use "Sincronizar com o portal" ou adicione manualmente.',
          'error',
        );
      }
      // Vindo do Encontrar, a proposta é criada DIRETO e abre na página de
      // trabalho. Antes o clique fechava este painel e abria um SEGUNDO drawer
      // ("Nova Proposta") só pra reconfirmar portal e licitação — dois popups
      // empilhados pra uma ação só, e nenhum dos dois perguntava nada que já
      // não se soubesse: a licitação é a que está aberta, o portal é inferido
      // dela e os itens acabaram de ser carregados aqui em cima.
      //
      // O drawer continua existindo pro caminho em que a informação de fato
      // falta: sem portal identificado, ele abre pra você escolher.
      const portalId = findPortalIdBySource(detailItem.portalOrigem || null, detailItem.linkOrigem || null);
      if (!portalId) {
        await prepareProposalFromItem(detailItem, itens);
        return;
      }
      await createProposalDirectFromItem(detailItem, itens, portalId);
    } finally {
      setPreparingProposal(false);
    }
  };

  /**
   * Cria a proposta a partir do item do Encontrar, sem passar pelo drawer.
   *
   * Monta o payload a partir dos ARGUMENTOS, e não do estado: `setState` do
   * React não é síncrono, então preparar o formulário e criar na sequência
   * enviaria o form ainda vazio. É por isso que esta função não reaproveita o
   * `buildProposalFormData` do componente, que lê o estado por closure.
   */
  const createProposalDirectFromItem = async (item: any, itens: any[], portalId: string) => {
    try {
      const formData = buildProposalFormDataPure({
        form: {
          itemId: String(item.id),
          title: `Proposta — ${(getOrgaoDisplay(item) || '').substring(0, 80)}`,
          description: '',
          totalValue: '',
          status: 'rascunho',
          checklistNotes: '',
          valorRecorrente: false,
          contractMonths: '12',
          validityDays: '60',
          observations: '',
          conditions: '',
          representative: '',
          representativeRole: '',
          bankInfo: '',
          brand: '',
          model: '',
        } as any,
        items: (itens || []).map((it: any) => ({
          itemNumber: it.numeroItem,
          description: it.descricao || '',
          quantity: it.quantidade || 0,
          unit: it.unidadeMedida || '',
          estimatedPrice: it.valorUnitarioEstimado || 0,
          unitPrice: '',
          brand: 'IPCOM',
          model: '',
          maxUnitPrice: it.valorUnitarioEstimado > 0 ? String(it.valorUnitarioEstimado) : undefined,
        })),
        computedTotal: 0,
        declarations: [],
        termsAccepted: false,
        // Mesma regra do fluxo antigo: BLL e PCP entram com ME/EPP "Sim" (a
        // IPCOM é EPP no Simples), o resto fica em branco até o scrape dizer.
        meeppValue: (item.portalOrigem === 'bll' || item.portalOrigem === 'pcp') ? 'Sim' : null,
        margemPaisOrigem: 'CHN',
        margemEnquadramento: '0',
        pcpInfoComp: '',
        portalOrigem: item.portalOrigem || null,
        hasPortalTerms: false,
        file: null,
        includeItemId: true,
        includeFile: false,
      } as any);

      const created = await procurementService.createProposal(formData);
      if (!created?.id) {
        showToast('Proposta criada, mas o Hub não devolveu o id — abra pela lista do Cadastrar', 'error');
        return;
      }
      const selectedPortal = portals.find((pp: any) => String(pp.id) === portalId);
      const inferred = inferPortalOrigemFromPortal(selectedPortal);
      if (inferred) {
        // Não bloqueia a criação, mas também não some calado: sem portal a linha do Cadastrar fica "não registrado" (26/10/2026,
        // Alto Rio Doce, cadastrada no LicitApp, ficou sem portal e nada avisou).
        procurementService.updateItemPortal(item.id, inferred).catch((e: any) => {
          showToast('Proposta criada, mas o portal da licitação não foi gravado: ' + (e?.response?.data?.message || e?.message || 'erro desconhecido') + '. Escolha o portal no detalhe da licitação.', 'error');
        });
      }
      setDetailItem(null);
      await loadProposals();
      setWorkspaceProposalId(created.id);
      loadItems();
      loadDashboard();
      onNavigate?.('lic-cadastrar');
    } catch (e: any) {
      showToast('Erro ao criar proposta: ' + (e?.response?.data?.message || e?.message), 'error');
    }
  };

  // Fase 1: scrape público do BLL (HTTP, sem login). Puxa Dados + Itens reais
  // do bllcompras.com e recarrega o modal.
  const [bllScraping, setBllScraping] = useState(false);
  const handleBllScrapePublic = async () => {
    if (!detailItem?.id) return;
    setBllScraping(true);
    try {
      const id = detailItem.id;
      const r = await comLinkDoPortal({
        portal: 'bll',
        executar: () => procurementService.bllScrapePublic(id),
        comLink: (link) => procurementService.bllScrapePublic(id, link),
        pedir: pedirLinkDoPortal,
      });
      if (r === CANCELADO) return;
      showToast(
        `✓ BLL sincronizado: edital ${r.info?.numeroEdital || '—'}, ${r.lotes} lote(s), ${r.itens} item(ns)`,
        'success',
      );
      // Acabou de sincronizar pra VER os itens — refetch do item + carga
      // forçada, numa chamada só (ver resyncDetail).
      await resyncDetail(detailItem.id);
    } catch (e: any) {
      showToast(`Erro no scrape BLL: ${e?.response?.data?.message || e?.message || e}`, 'error');
    } finally {
      setBllScraping(false);
    }
  };

  // PCP: scrape autenticado (login Keycloak + GET RegistroProposta). Puxa itens
  // reais (slCD_ITEM_LICITACAO), declarações e desempate e persiste os itens.
  const [pcpScraping, setPcpScraping] = useState(false);
  const handlePcpScrapeDetail = async () => {
    if (!detailItem?.id) return;
    setPcpScraping(true);
    try {
      const r = await procurementService.pcpScrape(detailItem.id);
      showToast(
        `✓ PCP sincronizado: ${r.items?.length || 0} item(ns), ${r.declarations?.length || 0} declaração(ões)${r.lotes?.length ? `, ${r.lotes.length} lote(s)` : ""}`,
        'success',
      );
      await resyncDetail(detailItem.id);
    } catch (e: any) {
      showToast(`Erro no scrape PCP: ${e?.response?.data?.message || e?.message || e}`, 'error');
    } finally {
      setPcpScraping(false);
    }
  };

  // BNC: scrape público (bnccompras.com — mesma plataforma do BLL, HTTP/cheerio).
  const [bncScraping, setBncScraping] = useState(false);
  const handleBncScrapeDetail = async () => {
    if (!detailItem?.id) return;
    setBncScraping(true);
    try {
      const id = detailItem.id;
      const r = await comLinkDoPortal({
        portal: 'bnc',
        executar: () => procurementService.bncScrapePublic(id),
        comLink: (link) => procurementService.bncScrapePublic(id, link),
        pedir: pedirLinkDoPortal,
      });
      if (r === CANCELADO) return;
      showToast(
        `✓ BNC sincronizado: edital ${r.info?.numeroEdital || '—'}, ${r.lotes} lote(s), ${r.itens} item(ns)`,
        'success',
      );
      await resyncDetail(detailItem.id);
    } catch (e: any) {
      showToast(`Erro no scrape BNC: ${e?.response?.data?.message || e?.message || e}`, 'error');
    } finally {
      setBncScraping(false);
    }
  };

  /**
   * ComprasNet no Encontrar: sincroniza status, mensagens e datas do item.
   *
   * Era a lacuna do menu — item com portalOrigem='comprasnet' não tinha AÇÃO
   * NENHUMA de sincronizar. E não por descuido do menu: o `reenrichSingleItem`
   * do backend recusa ComprasNet de propósito ("a fonte é o scrape, não o
   * PNCP") e manda usar "Sincronizar do Portal" — que só existia dentro do
   * workspace da proposta. Ou seja, a mensagem do backend apontava para um
   * botão que, ali, não existia.
   *
   * `scrape-now` é o caminho certo porque é POR ITEM e não exige proposta: é o
   * mesmo scrape autenticado do "Buscar Mensagens" do Monitorar, com
   * side-effects no item (mensagens, status, reabertura SUSPENSA→PARTICIPANDO,
   * dataEncerramento com audit trail).
   *
   * O que ele NÃO traz são os ITENS da proposta — esses dependem de uma
   * proposta criada e vêm pelo "Sincronizar do portal" da Montagem. O rótulo
   * diz isso para o operador não achar que sincronizou algo que não
   * sincronizou.
   */
  const [comprasnetScraping, setComprasnetScraping] = useState(false);
  const handleComprasnetScrapeDetail = async () => {
    // Guarda de reentrada: este scrape passa pela fila do Firefox e pode levar
    // minutos (o axios espera 450s). Sem isso, um segundo clique enfileira
    // outro job pro mesmo item e os dois disputam a mesma aba.
    if (!detailItem?.id || comprasnetScraping) return;
    setComprasnetScraping(true);
    try {
      const r = await procurementService.scrapeItemNow(detailItem.id);
      if (!r?.ok) {
        showToast(r?.error || 'Erro ao sincronizar com o ComprasNet', 'error');
        return;
      }
      const partes: string[] = [];
      if (r.saved > 0) partes.push(`${r.saved} mensagem(ns) nova(s)`);
      if (r.statusChange) partes.push(`status: ${r.statusChange.from} → ${r.statusChange.to}`);
      showToast(partes.length ? `✓ ComprasNet: ${partes.join(', ')}` : '✓ ComprasNet: sem novidades', 'success');
      await resyncDetail(detailItem.id);
    } catch (e: any) {
      showToast(`Erro no scrape ComprasNet: ${e?.response?.data?.message || e?.message || e}`, 'error');
    } finally {
      setComprasnetScraping(false);
    }
  };

  const handleReenrichItem = async () => {
    if (!detailItem?.id) return;
    setReenrichingItem(true);
    setReenrichItemResult(null);
    try {
      const itemId = detailItem.id;
      const r = await procurementService.reenrichItem(itemId);
      setReenrichItemResult(r); // faixa inline no card (persistente)

      // Item foi pra lixeira (contratação direta) → remove da lista + fecha modal.
      if (r.status === 'descartado') {
        setItems((prev) => prev.filter((it) => it.id !== itemId));
        loadDashboard();
        showToast('Licitação movida pra lixeira (contratação direta)');
        setDetailItem(null);
        return;
      }

      // Refetch COMPLETO sempre (28/05/2026) — não só quando changed=true.
      // O modal exibe muitos campos (valorEstimado, modalidade, objeto, datas)
      // que o patch parcial não cobria. Buscar o item inteiro garante que a tela
      // reflita o estado real sem precisar reload. Atualiza modal + lista.
      try {
        const fresh = await procurementService.getItem(itemId);
        if (fresh) {
          setDetailItem((prev: any) => prev && prev.id === fresh.id ? { ...prev, ...fresh } : prev);
          setItems((prev) => prev.map((it) => it.id === fresh.id ? { ...it, ...fresh } : it));
        }
      } catch {
        // fallback: patch parcial com o que o re-enrich retornou
        const patch: any = { portalOrigem: r.portal, numeroCompra: r.numeroCompra, dataAbertura: r.dataAbertura, dataEncerramento: r.dataEncerramento, situacao: r.situacao };
        setDetailItem((prev: any) => prev ? { ...prev, ...patch } : prev);
        setItems((prev) => prev.map((it) => it.id === itemId ? { ...it, ...patch } : it));
      }
    } catch (e: any) {
      // Sem e.response = timeout/rede: o PNCP não respondeu a tempo (VPN sobrecarregada).
      const isNetwork = !e?.response;
      setReenrichItemResult({
        status: 'erro',
        msg: isNetwork
          ? 'PNCP não respondeu a tempo (VPN sobrecarregada agora) — tente de novo em alguns minutos'
          : (e?.response?.data?.message || e?.message),
      });
    } finally {
      setReenrichingItem(false);
    }
  };

  const openContratacaoDireta = async () => {
    setCdBusy(true);
    try {
      const data = await procurementService.contratacaoDiretaPreview();
      setCdModal(data);
    } catch (e: any) {
      showToast('Erro ao buscar contratação direta: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setCdBusy(false);
    }
  };
  const applyContratacaoDireta = async () => {
    setCdBusy(true);
    try {
      const r = await procurementService.contratacaoDiretaDiscard();
      showToast(`✓ ${r.discarded} licitação(ões) de contratação direta movida(s) pra lixeira`, 'success');
      setCdModal(null);
      loadItems(); loadDashboard();
    } catch (e: any) {
      showToast('Erro ao descartar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setCdBusy(false);
    }
  };

  // ── Verificar Vencidos — padrão idêntico ao Re-enriquecer (open/start/poll/stop/close)
  // 24/05/2026 (A3): polling continua mesmo após running=false enquanto houver
  // items enfileirados aguardando callback assíncrono (enqueued > terminalSinceEnqueue).
  const pollCheckExpired = async () => {
    try {
      const d = await procurementService.checkExpiredProgress();
      setCheckExpiredData(d);
      const enqueuedPending = (d.enqueued || 0) - (d.terminalSinceEnqueue || 0);
      if (d.running) {
        checkExpiredPollRef.current = setTimeout(pollCheckExpired, 2500);
      } else if (enqueuedPending > 0) {
        // Loop ativo terminou mas ainda esperando callbacks do scraper.
        // Poll a cada 10s — recarrega lista pra renderizar status real chegando.
        loadItems();
        loadDashboard();
        checkExpiredPollRef.current = setTimeout(pollCheckExpired, 10000);
      } else {
        // Tudo resolvido — recarrega final e para.
        loadItems();
        loadDashboard();
      }
    } catch {
      checkExpiredPollRef.current = setTimeout(pollCheckExpired, 4000);
    }
  };

  const openCheckExpired = async () => {
    setCheckExpiredModal(true);
    if (checkExpiredData?.running) return;
    setCheckExpiredData(null);
    setCheckExpiredPending(null);
    try {
      const c = await procurementService.checkExpiredCount();
      if (c.running) { pollCheckExpired(); return; }
      setCheckExpiredPending(c.pending);
    } catch {
      setCheckExpiredPending(-1);
    }
  };

  const startCheckExpiredNow = async () => {
    setCheckExpiredPending(null);
    try {
      await procurementService.startCheckExpired();
    } catch (e: any) {
      showToast('Erro ao iniciar: ' + (e?.response?.data?.message || e?.message), 'error');
    }
    if (checkExpiredPollRef.current) clearTimeout(checkExpiredPollRef.current);
    pollCheckExpired();
  };

  const handleStopCheckExpired = async () => {
    try {
      await procurementService.stopCheckExpired();
      showToast('Parando a verificação…');
    } catch (e: any) {
      showToast('Erro ao parar: ' + (e?.response?.data?.message || e?.message), 'error');
    }
  };

  const closeCheckExpired = () => setCheckExpiredModal(false);

  const applyExcl = async () => {
    if (!exclModal) return;
    setExclBusy(true);
    try {
      const r = await procurementService.applyExclusions(Array.from(exclDescartar), Array.from(exclWhitelist));
      showToast(`✓ ${r.descartados} descartada(s)${r.whitelisted ? `, ${r.whitelisted} mantida(s) na whitelist` : ''}`, 'success');
      setExclModal(null);
      await loadItems();
    } catch (e: any) {
      showToast(`Erro: ${e?.response?.data?.message || e?.message}`, 'error');
    } finally {
      setExclBusy(false);
    }
  };

  const handleCreateProposalFromList = async (item: any) => {
    if (preparingProposal) return;
    setPreparingProposal(true);
    // Fetch itens from API for pre-fill
    try {
      const details = await procurementService.getItemDetails(item.id);
      await prepareProposalFromItem(item, details?.itens || []);
    } catch (e: any) {
      // Fallback: abre sem itens, mas avisando — silenciar aqui era o que fazia
      // a proposta nascer vazia sem o user entender o porquê.
      showToast(
        'Não consegui buscar os itens no PNCP agora (portal lento ou fora). A proposta abre sem itens — use "Sincronizar com o portal" ou adicione manualmente.',
        'error',
      );
      await prepareProposalFromItem(item);
    } finally {
      setPreparingProposal(false);
    }
  };

  /**
   * Dispara desistência de proposta detectando portal automaticamente:
   * - BLL  → withdrawProposalBll (HTTP-puro assíncrono) + abre modal de progresso
   * - Resto (ComprasNet) → withdrawCompraAuto (extension VNC) síncrono
   */
  // Abre o modal de Análise IA JÁ carregando a análise salva e a lista de
  // arquivos do PNCP. Extraída do botão ⚡ da lista porque o menu da página de
  // trabalho só abria o modal — ele ficava preso em "Carregando arquivos…"
  // pra sempre, sem nunca disparar a busca.
  const openAiModal = async (item: any) => {
    if (!item?.id) return;
    setAiModalItem(item);
    setAiModalData(null);
    setAiModalAnalyzedAt(null);
    setAiModalError(null);
    setAiModalFiles(null);
    setAiModalFilesErro(null);
    setAiModalSelectedFile(null);
    setAiModalLoading(true);
    try {
      const [stored, files] = await Promise.all([
        procurementService.getStoredEditalAnalysis(item.id),
        procurementService.listEditalFiles(item.id).then(
          (f: any) => ({ f, erro: null as string | null }),
          (e: any) => ({ f: null, erro: mensagemDoErroDeCarga(e) }),
        ),
      ]);
      setAiModalData(stored.analysis);
      setAiModalAnalyzedAt(stored.analyzedAt);
      setAiModalFilesErro(files.erro);
      setAiModalFiles(files.erro ? null : Array.isArray(files.f) ? files.f : []);
    } catch (err: any) {
      setAiModalError(err?.response?.data?.message || err.message);
    } finally {
      setAiModalLoading(false);
    }
  };

  const handleWithdrawProposal = async (p: any) => {
    // Rota por portalOrigem (rotaDaDesistencia.ts). PCP ainda não tem fluxo de
    // desistência no backend; BLL e BNC vão pelo gkz, cada um no seu endpoint.
    const rota = rotaDaDesistencia(p.item?.portalOrigem);
    if (rota.tipo === 'indisponivel') {
      showToast(rota.motivo, 'error');
      return;
    }
    // Só ADMIN desiste no Licitanet (o backend da #518 recusa os outros perfis com 403): dito antes de perguntar.
    if (rota.tipo === 'licitanet' && !isAdmin) {
      showToast('Desistir no Licitanet é só para admin: a desistência apaga a proposta no portal.', 'error');
      return;
    }
    const portalLabel = rota.rotulo;
    if (!(await confirm({
      title: 'Desistir desta proposta no portal?',
      danger: true,
      confirmLabel: 'Desistir no portal',
      message: <>Portal detectado: <strong>{portalLabel}</strong>.</>,
      consequence: rota.tipo === 'licitanet'
        ? CONSEQUENCIA_DA_DESISTENCIA_LICITANET
        : 'A proposta é cancelada NO PORTAL e marcada como desistida no Hub. Enquanto o prazo estiver aberto, você pode corrigir e enviar de novo.',
    }))) return;
    if (rota.tipo === 'licitanet') {
      // Síncrono (DELETE por registro, direto no portal): o resultado vem na hora, com a frase do PORTAL. Vai num aviso
      // que fica até alguém ler, e não num toast de 4s: uma desistência pela metade deixa a proposta mutilada no órgão.
      let aviso: { titulo: string; frase: string; tom: 'sucesso' | 'falha' | 'parcial' | 'desconhecido' };
      try {
        const r = await runWithProgress(p.item.id, 'withdraw', 'Desistindo da proposta no portal', () => procurementService.withdrawProposalLicitanet(p.id));
        aviso = resultadoDaDesistenciaLicitanet(r);
      } catch (err: any) {
        const frase = fraseDaRecusaDaDesistenciaLicitanet(err);
        aviso = frase === DESFECHO_DESCONHECIDO
          ? { tom: 'desconhecido', titulo: 'Resultado da desistência desconhecido', frase }
          : { tom: 'falha', titulo: 'A desistência não saiu', frase };
      }
      await loadProposals();
      await loadAutomationJobs();
      await confirm({
        title: aviso.titulo,
        message: <span data-resultado-da-desistencia={aviso.tom}>{aviso.frase}</span>,
        confirmLabel: 'Entendi',
        semCancelar: true,
        danger: aviso.tom === 'parcial' || aviso.tom === 'desconhecido',
        irreversivel: false,
      });
      return;
    }
    try {
      if (rota.tipo === 'gkz') {
        // BLL/BNC são assíncronos (jobId + execução em background), mas isso é
        // detalhe de transporte: a TELA é a mesma do ComprasNet — progresso no
        // card. Até 18/08/2026 aqui abria o modal antigo de job, e desistir num
        // portal parecia uma operação diferente de desistir no outro.
        const result = await runWithProgress(
          p.item.id, 'withdraw', 'Desistindo da compra no portal',
          async () => {
            const r = await pedirDesistenciaGkz(rota.portal, procurementService, p.id);
            const limite = Date.now() + 8 * 60 * 1000;
            while (Date.now() < limite) {
              await new Promise((res) => setTimeout(res, 3000));
              try {
                const j = await procurementService.getAutomationJob(r.jobId);
                if (j && ['completed', 'failed', 'cancelled', 'needs_manual'].includes(j.status)) {
                  return { ok: j.status === 'completed', error: j.error || j.status };
                }
              } catch { /* poll best-effort */ }
            }
            return { ok: false, error: 'tempo esgotado — veja o histórico de automações' };
          },
        );
        showToast(
          result.ok ? `✓ Desistência confirmada no ${rota.portal}` : `✗ Desistência falhou: ${result.error}`,
          result.ok ? 'success' : 'error',
        );
        await loadProposals();
        await loadAutomationJobs();
      } else {
        // ComprasNet: extension VNC, síncrono. Roda com PROGRESSO ao vivo —
        // antes era um toast que sumia e o usuário ficava sem saber se algo
        // estava acontecendo (a operação leva até 2min).
        const result = await runWithProgress(
          p.item.id, 'withdraw', 'Desistindo da compra no portal',
          () => procurementService.withdrawCompraAuto(p.item.id),
        );
        if (result.ok) {
          showToast(`✓ Desistência confirmada em ${Math.round(result.durationMs / 1000)}s`, 'success');
        } else {
          showToast(`✗ Desistência falhou: ${result.error}`, 'error');
        }
        await loadProposals();
        await loadAutomationJobs();
      }
    } catch (err: any) {
      showToast(`Erro ao desistir: ${err?.response?.data?.message || err?.message || err}`, 'error');
    }
  };

  const handleDeleteProposal = (id: number) => {
    // Abre modal de confirmação (em vez de deletar direto). User escolhe
    // destino + se mantém monitoramento/dispute.
    const p = proposals.find((x: any) => x.id === id);
    setDeleteDestination('lixeira');
    setDeleteKeepMonitored(false);
    setDeleteKeepDispute(false);
    setDeleteProposalModal({
      type: 'single',
      ids: [id],
      titles: [p?.title || p?.item?.orgaoNome || `#${id}`],
    });
  };

  const handleBulkDeleteProposals = () => {
    if (selectedProposalIds.size === 0) return;
    const ids = Array.from(selectedProposalIds);
    const titles = ids.map((id) => {
      const p = proposals.find((x: any) => x.id === id);
      return p?.title || p?.item?.orgaoNome || `#${id}`;
    });
    setDeleteDestination('lixeira');
    setDeleteKeepMonitored(false);
    setDeleteKeepDispute(false);
    setDeleteProposalModal({ type: 'bulk', ids, titles });
  };

  const confirmDeleteProposals = async () => {
    if (!deleteProposalModal || deletingProposals) return;
    setDeletingProposals(true);
    try {
      const opts = {
        destination: deleteDestination,
        keepMonitored: deleteKeepMonitored,
        keepDispute: deleteKeepDispute,
      };
      if (deleteProposalModal.type === 'bulk') {
        const res = await procurementService.bulkDeleteProposals(deleteProposalModal.ids, opts);
        if (res.failed > 0) {
          showToast(`${res.deleted} deletadas · ${res.failed} falharam`, 'error');
        } else {
          showToast(`${res.deleted} proposta(s) deletada(s)`);
        }
      } else {
        await procurementService.deleteProposal(deleteProposalModal.ids[0], opts);
        showToast('Proposta excluída');
      }
      setSelectedProposalIds(new Set());
      setDeleteProposalModal(null);
      loadProposals();
    } catch (e: any) {
      showToast('Erro ao excluir: ' + (e?.response?.data?.message || e?.message), 'error');
    }
    setDeletingProposals(false);
  };

  const toggleProposalSelection = (id: number) => {
    setSelectedProposalIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllProposalsSelected = () => {
    setSelectedProposalIds((prev) => {
      if (prev.size === filteredProposals.length) return new Set();
      return new Set(filteredProposals.map((p: any) => p.id));
    });
  };

  const handleDownloadProposal = async (id: number) => {
    try {
      const { url } = await procurementService.getProposalUrl(id);
      window.open(url, '_blank');
    } catch (e: any) { showToast('Erro ao baixar arquivo', 'error'); }
  };

  // ==================== Automation Actions ====================

  // A carga dos jobs vira ESTADO (07/10/2026): com ela falhando, a lista ficava vazia e a checagem antes de enviar
  // lia "nenhuma tentativa de envio" → verde, mesmo na proposta 379, com quatro envios recusados. A lista só é
  // gravada quando a chamada dá certo; o estado vai para a checagem, que não fica verde sem histórico conhecido.
  const { rodar: rodarCargaDosJobs, ...cargaDosJobs } = useEstadoDaCarga();
  const loadAutomationJobs = useCallback(async () => {
    await rodarCargaDosJobs(async () => {
      const data = await procurementService.getAutomationJobs();
      setAutomationJobs(Array.isArray(data) ? data : []);
    });
  }, [rodarCargaDosJobs]);

  // R1.18: auto-refresh — recarrega propostas + jobs sozinho. 5s quando há scrape
  // ativo conhecido; baseline 20s pra detectar scrape de QUALQUER item (inclusive
  // iniciado em background/outra aba). Pausa quando a aba não está visível.
  useEffect(() => {
    if (subView !== 'proposals') return;
    const hasPendingScrape = automationJobs.some((j: any) =>
      j.action === 'scrape_proposal' && ['queued', 'running'].includes(j.status)
    );
    const intervalMs = hasPendingScrape ? 5000 : 20000;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadAutomationJobs();
      loadProposals();
    }, intervalMs);
    return () => clearInterval(interval);
  }, [subView, automationJobs, loadAutomationJobs, loadProposals]);

  // A sincronização da proposta pelo JOB (16/10/2026, backend #465): o POST devolve o jobId na hora e a tela consulta, em
  // vez de segurar a conexão até o fim e morrer nos 60 s do nginx ("Erro: Network Error" no TCE-PR, 15/10). Devolve o
  // mesmo formato do síncrono. Ver utils/scrapeAssincrono.
  const sincronizarProposta = (proposalId: number, itemId?: number) => sincronizarPeloJob({
    iniciar: () => procurementService.iniciarScrapeDaProposta(proposalId),
    consultar: (jobId) => procurementService.consultarJobDeScrape(jobId),
    aoReaproveitar: () => showToast(FRASE_REAPROVEITADO),
    // O `progresso` do job é o mesmo objeto do automation-progress: alimenta o quadro do runWithProgress.
    aoConsultar: (job) => {
      const pg = job.progresso;
      if (!itemId || !pg || !Array.isArray(pg.steps)) return;
      setActiveOps((prev) => prev[itemId]
        ? { ...prev, [itemId]: { ...prev[itemId], currentStep: pg.currentStep || prev[itemId].currentStep, steps: pg.steps, elapsedMs: pg.elapsedMs || prev[itemId].elapsedMs } }
        : prev);
    },
  });

  /**
   * Sincronizar a proposta pelo portal: UM caminho, para a página de trabalho do Cadastrar e para o "tentar de novo" do
   * job (16/10/2026). Até aqui o "tentar de novo" tinha um caminho próprio: BLL ia para `/procurement/automation/scrape`,
   * cauterizado no backend desde 27/05/2026 (sempre 400 "descontinuado"), e BNC pulava o pedido do link.
   *
   * PCP pelo scrape próprio; BLL e BNC pelo fluxo do link (sem link resolvível, a tela pede o link do processo, que vai
   * pelo scrape do portal e depois a sincronização roda de novo); o resto pela sincronização do job. Ela mesma avisa.
   * Devolve 'ok' / 'falhou' (o portal respondeu sem sucesso) / 'erro' (exceção) / 'cancelado' (desistiu do link).
   */
  const sincronizarDoPortal = async (p: { id: number; item: { id: number; portalOrigem?: string | null } }): Promise<'ok' | 'falhou' | 'erro' | 'cancelado'> => {
    const po = String(p.item?.portalOrigem || '').toLowerCase();
    try {
      if (po === 'pcp') {
        const r = await runWithProgress(p.item.id, 'scrape', 'Sincronizando do portal', () => procurementService.pcpScrape(p.item.id));
        showToast(r ? '✓ Sincronizado com o PCP' : 'Falha ao sincronizar', r ? 'success' : 'error');
        return r ? 'ok' : 'falhou';
      }
      const sincronizar = () => runWithProgress(p.item.id, 'scrape', 'Sincronizando do portal', () => sincronizarProposta(p.id, p.item.id), { progressoPeloJob: true });
      const result = po === 'bll' || po === 'bnc'
        ? await comLinkDoPortal({
            portal: po,
            executar: sincronizar,
            comLink: async (link) => {
              await runWithProgress(p.item.id, 'scrape', 'Conferindo o link do processo', () =>
                po === 'bll' ? procurementService.bllScrapePublic(p.item.id, link) : procurementService.bncScrapePublic(p.item.id, link));
              return sincronizar();
            },
            pedir: pedirLinkDoPortal,
          })
        : await sincronizar();
      if (result === CANCELADO) return 'cancelado';
      if (result?.ok) { showToast(`✓ Carregado: ${result.scrapedItems?.length || 0} item(ns)`, 'success'); return 'ok'; }
      showToast(`✗ Falhou: ${result?.error || 'erro'}`, 'error');
      return 'falhou';
    } catch (err: any) {
      showToast(`Erro: ${err?.response?.data?.message || err?.message || err}`, 'error');
      return 'erro';
    }
  };

  React.useEffect(() => {
    if (proposalModal) {
      // Drawer aberto = ÚNICA camada: fecha o painel expansível da linha
      // (antes ficavam lista + painel + modal empilhados, 3 níveis visíveis).
      setExpandedProposalId(null);
    }
  }, [proposalModal]);

  // PCP: carrega as informações complementares (desempate) do proposal pro editor.
  React.useEffect(() => {
    if (editingProposal?.item?.portalOrigem === 'pcp') {
      const info = (editingProposal.acceptedTerms as any)?.pcpInfoComplementar;
      // Vazio (proposta PCP ainda não sincronizada / desempate não revelado) →
      // mostra o default padrão Art. 60 pro user ver/ajustar.
      const loaded = Array.isArray(info) && info.length
        ? info.map((x: any) => ({ ...x }))
        : DEFAULT_PCP_DESEMPATE_FE.map((x) => ({ ...x }));
      setPcpInfoComp(loaded);
    } else {
      setPcpInfoComp([]);
    }
  }, [editingProposal]);

  const handleStartAutomation = async () => {
    if (!automationModal || !automationPortalId) return;
    setAutomationStarting(true);
    try {
      let job: any;
      // Detect BLL portal — uses a different sync endpoint with its own flow
      const selectedPortal = portals.find((p: any) => String(p.id) === automationPortalId);
      const isPcp = automationModal.item?.portalOrigem === 'pcp';
      const isBll = selectedPortal?.portalName?.toLowerCase().includes('bll');
      const isBnc = selectedPortal?.portalName?.toLowerCase().includes('bnc');

      if (isPcp) {
        // PCP — submit ASSÍNCRONO HTTP-puro (scrape → declarações → desempate
        // → RegistroItem). Backend retorna jobId imediato; frontend pollar a
        // timeline. Mesmo padrão de UX do BLL/ComprasNet.
        if (automationDryRun) {
          showToast('Modo teste não suportado pelo PCP — execute sem dryRun', 'error');
          setAutomationStarting(false);
          return;
        }
        const result = await procurementService.submitProposalPcp(automationModal.id);
        job = { id: result.jobId, status: 'queued' };
        showToast('Envio PCP iniciado — acompanhe o progresso no modal');
      } else if (isBll || isBnc) {
        // BLL e BNC = mesma plataforma (base gkz): submit ASSÍNCRONO HTTP-puro.
        // Backend retorna jobId imediato + executa em background. Frontend
        // polla pra timeline. Mesmo padrão de UX do ComprasNet startAutomation.
        const portalTag = isBnc ? 'BNC' : 'BLL';
        if (automationDryRun) {
          showToast(`Modo teste não suportado pelo ${portalTag} HTTP — execute sem dryRun`, 'error');
          setAutomationStarting(false);
          return;
        }
        // GATE pré-envio (P0 auditoria): checa os documentos obrigatórios ANTES
        // de enfileirar — antes o job falhava em background e o erro sumia se o
        // modal fosse fechado (Diamantino DE 007/2026, job 5161). Docs gerados+
        // assinados contam (a ponte materializa no envio).
        try {
          const docsStatus = await procurementService.getBllDocsStatus(automationModal.id);
          if (docsStatus?.missing?.length > 0) {
            showToast(
              `⛔ ${docsStatus.missing.length} documento(s) obrigatório(s) sem arquivo: ` +
                docsStatus.missing.slice(0, 3).map((m: string) => `"${m}"`).join(', ') +
                (docsStatus.missing.length > 3 ? '…' : '') +
                ' — anexe na tabela de documentos exigidos (ou gere+assine a Proposta Financeira).',
              'error',
            );
            setAutomationStarting(false);
            return;
          }
        } catch { /* status indisponível → deixa o backend validar */ }
        const result = isBnc
          ? await procurementService.submitProposalBnc(automationModal.id)
          : await procurementService.submitProposalBll(automationModal.id);
        // result = { ok, jobId } — job ainda 'queued/running', polling popula.
        job = { id: result.jobId, status: 'queued' };
        showToast(`Envio ${portalTag} iniciado — acompanhe o progresso no modal`);
      } else {
        // ComprasNet: mesmo caminho do workspace (submit-proposal do item);
        // o /automation/start foi descontinuado.
        const itemId = automationModal.item?.id || automationModal.itemId;
        const r = await procurementService.submitProposalAuto(itemId, { proposalId: automationModal.id, dryRun: automationDryRun });
        showToast(r?.ok ? `✓ Enviado ao ComprasNet${automationDryRun ? ' (modo teste)' : ''}` : `✗ ${r?.error || 'falhou'}`, r?.ok ? 'success' : 'error');
        job = null;
      }
      setAutomationModal(null);
      setAutomationPortalId('');
      setAutomationDryRun(false);
      loadAutomationJobs();
      // Open job detail modal
      if (job?.id) {
        const fullJob = await procurementService.getAutomationJob(job.id);
        mostrarJob(fullJob);
        startJobPolling(job.id);
      }
    } catch (e: any) {
      showToast('Erro ao iniciar automacao: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setAutomationStarting(false);
    }
  };

  const stopJobPolling = () => {
    if (automationJobPollingRef.current) {
      clearInterval(automationJobPollingRef.current);
      automationJobPollingRef.current = null;
      setAutomationJobPolling(null);
    }
  };

  const startJobPolling = (jobId: number) => {
    stopJobPolling();
    automationModalOpenRef.current = true;
    const interval = setInterval(async () => {
      try {
        const job = await procurementService.getAutomationJob(jobId);
        // Only update modal if user hasn't closed it (ref avoids stale closure)
        if (automationModalOpenRef.current) {
          mostrarJob(job);
        }
        if (['completed', 'failed', 'cancelled', 'needs_manual'].includes(job.status)) {
          stopJobPolling();
          loadAutomationJobs();
          // Delay to allow backend callback to finish updating proposal status
          setTimeout(() => loadProposals(), 2000);
          // TOAST GLOBAL de desfecho (P0 auditoria): o polling continua mesmo com
          // o modal fechado — antes o erro do job em background morria invisível
          // (user fechava o modal e nunca ficava sabendo da falha).
          if (!automationModalOpenRef.current) {
            if (job.status === 'failed') {
              showToast(`⛔ Job #${jobId} FALHOU: ${job.errorMessage || 'erro no envio'} — abra "Ver último job" no card`, 'error');
            } else if (job.status === 'needs_manual') {
              showToast(`⚠ Job #${jobId} precisa de ação manual (captcha/2FA) — abra "Ver último job"`, 'error');
            } else if (job.status === 'completed') {
              showToast(`✓ Job #${jobId} concluído com sucesso`);
            }
          }
        }
      } catch { /* ignore polling errors */ }
    }, 3000);
    automationJobPollingRef.current = interval;
    setAutomationJobPolling(interval);
  };

  const handleViewJob = async (job: any) => {
    try {
      const fullJob = await procurementService.getAutomationJob(job.id);
      mostrarJob(fullJob);
      if (['queued', 'running'].includes(fullJob.status)) {
        startJobPolling(fullJob.id);
      } else {
        // Job already terminal — refresh lists to sync table badges
        loadAutomationJobs();
        loadProposals();
      }
    } catch (e: any) { showToast('Erro ao carregar detalhes do job', 'error'); }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (automationJobPolling) clearInterval(automationJobPolling);
    };
  }, [automationJobPolling]);

  // Load tab data on tab change
  useEffect(() => {
    if (subView === 'documents' && documents.length === 0) loadDocuments();
    if (subView === 'proposals' && declarationTemplates.length === 0) loadDeclarationTemplates();
    if (subView === 'items' && portals.length === 0) loadPortals();
    if (subView === 'portals' && portals.length === 0) loadPortals();
    if (subView === 'proposals' && portals.length === 0) loadPortals();
    if (subView === 'portalHealth') loadPortalHealth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subView]);

  // ==================== Render ====================

  const renderDashboard = () => {
    // Encontrar = funil de entrada. Cards focados em itens SEM decisão final:
    //   - Abertas = novo + analisando + interessado (triagem em curso).
    //   - Suspensa/Revogada: terminais PNCP (separados pra não poluir).
    //   - Total: tudo exceto descartado.
    // Removidos: Analisando/Interessado (agregados em Abertas), Participando
    // (já tem aba Cadastrar dedicada).
    // "Abertas" = triagem com prazo EM PÉ. O backend manda abertasVigentes já
    // descontando os vencidos; o fallback cobre backend antigo.
    const vencidas = dashboard.vencidas || 0;
    const triagem = (dashboard.novo || 0) + (dashboard.analisando || 0) + (dashboard.interessado || 0);
    const abertas = dashboard.abertasVigentes ?? Math.max(0, triagem - vencidas);
    const suspensa = dashboard.suspensa || 0;
    const revogada = dashboard.revogada || 0;
    // Total do Encontrar = só os status do funil aqui (Abertas + Suspensa + Revogada).
    // Não inclui participando (Cadastrar), descartado (Lixeira), nem terminais pós-envio.
    const totalEncontrar = abertas + vencidas + suspensa + revogada;
    // Fase 2 do padrão visual (guia §2, modelo fiscal-docs): superfície calma
    // única, cor no NÚMERO, seleção por ring — antes cada card tinha fundo
    // pintado da própria cor (o principal ponto fora do padrão apontado 08/08).
    // Sem nenhuma carga boa do painel, os números não são zero: são "não sei" (ver loadDashboard).
    const semNumerosDoPainel = !cargaDoPainel.atualizadoEm;
    const cards = [
      { label: 'Total', count: totalEncontrar, filter: '', sub: 'Tudo que aparece aqui (Abertas + Vencidas + Suspensa + Revogada)' },
      { label: 'Abertas', count: abertas, filter: 'abertas', sub: 'Em triagem e ainda dentro do prazo' },
      { label: 'Vencidas', count: vencidas, filter: 'vencidas', sub: 'Prazo passou sem decisão (inclui suspensas vencidas) — só para conferir o que escapou' },
      { label: 'Suspensa', count: suspensa, filter: 'suspensa', sub: 'Sessão pausada pelo portal ou pelo PNCP, com prazo ainda em pé' },
      { label: 'Revogada', count: revogada, filter: 'revogada', sub: 'Órgão anulou a licitação' },
    ];
    return (
      <EstadoDaLista carga={cargaDoPainel} temDados oQue="os números do painel" onTentarDeNovo={loadDashboard} vazio={null}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {cards.map((c) => {
          const ativo = c.filter !== '' ? statusFilter === c.filter : !statusFilter;
          return (
            // Padrão da Central (L2, 23/10/2026): número branco, o escolhido pela barra do primário. A cor de Suspensa e
            // Revogada continua no selo de cada linha, que é onde ela diz algo sobre a licitação.
            <CartaoDoTopo
              key={c.label}
              rotulo={c.label}
              valor={semNumerosDoPainel ? '—' : c.count}
              ativo={ativo}
              onClick={() => { setStatusFilter(statusFilter === c.filter ? '' : c.filter); setItemsPage(1); }}
              title={c.sub}
            />
          );
        })}
      </div>
      </EstadoDaLista>
    );
  };

  const toggleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder(column === 'valorEstimado' ? 'DESC' : 'ASC');
    }
    setItemsPage(1);
  };

  const SortHeader = ({ column, label, className = '' }: { column: string; label: string; className?: string }) => (
    <th
      onClick={() => toggleSort(column)}
      className={`px-3 py-2.5 text-xs font-medium uppercase cursor-pointer hover:text-white transition-colors select-none ${sortBy === column ? 'text-white' : 'text-slate-400'} ${className}`}
      aria-sort={sortBy === column ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : undefined}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === column ? (
          <span className="text-white">{sortOrder === 'ASC' ? '▲' : '▼'}</span>
        ) : (
          <span className="text-slate-600">⇅</span>
        )}
      </div>
    </th>
  );

  // Tabela do Encontrar — extraída para `licitacoes/EncontrarTable` (Onda 3).
  // O JSX foi movido verbatim e as props têm os mesmos nomes das variáveis
  // que ele já usava, então o corpo do componente não mudou um caractere.
  const renderItemsTable = () => (
    <EncontrarTable
      SortHeader={SortHeader}
      dashboard={dashboard}
      dupMap={dupMap}
      dupSuggestions={dupSuggestions}
      editingDateId={editingDateId}
      editingDateValue={editingDateValue}
      handleBulkDiscard={handleBulkDiscard}
      handleCreateProposalFromList={handleCreateProposalFromList}
      handleDiscardItem={handleDiscardItem}
      handleRefreshSituacao={handleRefreshSituacao}
      handleSaveDate={handleSaveDate}
      handleStatusChange={handleStatusChange}
      isSituacaoBlockingEvent={isSituacaoBlockingEvent}
      isSituacaoOverdue={isSituacaoOverdue}
      items={items}
      itemsMeta={itemsMeta}
      itemsPage={itemsPage}
      loading={loading}
      openDetail={openDetail}
      preparingProposal={preparingProposal}
      readItemIds={readItemIds}
      refreshingSituacaoId={refreshingSituacaoId}
      scraperPendingIds={scraperPendingIds}
      searchIdFilter={searchIdFilter}
      searchInput={searchInput}
      portalsWithAccess={portalsWithAccess}
      searches={searches}
      selectedIds={selectedIds}
      setEditingDateId={setEditingDateId}
      setEditingDateValue={setEditingDateValue}
      setItemsPage={setItemsPage}
      setSearchIdFilter={setSearchIdFilter}
      setSearchInput={setSearchInput}
      setShowDupReview={setShowDupReview}
      setStatusFilter={setStatusFilter}
      setTrashMode={setTrashMode}
      statusFilter={statusFilter}
      toggleSelectAll={toggleSelectAll}
      toggleSelectItem={toggleSelectItem}
      trashMode={trashMode}
    />
  );

  const renderSearchesList = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white">Perfis de Busca</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const data = await procurementService.exclusionPreview();
                if (!data.items.length) {
                  showToast('Nenhuma licitação cadastrada bate os termos de exclusão', undefined);
                  return;
                }
                setExclModal(data);
                // Default: descartar todas que NÃO têm proposta; whitelist vazio.
                setExclDescartar(new Set(data.items.filter((i) => !i.temProposta).map((i) => i.id)));
                setExclWhitelist(new Set());
              } catch (e: any) {
                showToast(`Erro: ${e?.response?.data?.message || e?.message}`, 'error');
              }
            }}
            className="flex items-center gap-2 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/40 text-red-300 rounded-lg text-sm transition-colors"
            title="Revisar e descartar itens já cadastrados que batem os termos de exclusão"
          >
            <Icon name="trash" className="w-4 h-4" /> Revisar exclusões
          </button>
          <button
            onClick={openCreateSearch}
            className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors"
          >
            <Icon name="plus" className="w-4 h-4" /> Novo Perfil
          </button>
        </div>
      </div>

      <EstadoDaLista
        carga={cargaDosPerfis}
        temDados={searches.length > 0}
        oQue="os perfis de busca"
        onTentarDeNovo={loadSearches}
        vazio={
          <div className="text-center py-12 text-slate-400">
            <p className="mb-2">Nenhum perfil de busca configurado</p>
            <p className="text-sm">Crie um perfil para monitorar licitacoes automaticamente</p>
          </div>
        }
      >
      {(
        <div className="grid gap-3">
          {searches.map((s) => (
            <div key={s.id} className={`border rounded-lg p-4 transition-all ${s.isActive ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-800/20 border-slate-700/50 opacity-60'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h4 className="font-medium text-white">{s.name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                    {s.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePreviewSearch(s)}
                    disabled={previewingSearchId === s.id}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400 disabled:opacity-40 transition-colors"
                    title="Dry-run: mostra o que casaria hoje com as palavras-chave, sem criar itens"
                  >
                    {previewingSearchId === s.id ? (
                      <Icon name="refresh" className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <span>🧪</span>
                    )}
                    Testar perfil
                  </button>
                  <button
                    onClick={() => handleExecuteSearch(s.id)}
                    disabled={executingSearchId === s.id || !s.isActive}
                    className="p-1.5 text-slate-400 hover:text-green-400 disabled:opacity-30 transition-colors"
                    title="Executar busca"
                  >
                    {executingSearchId === s.id ? (
                      <Icon name="refresh" className="w-4 h-4 animate-spin" />
                    ) : (
                      <Icon name="play" className="w-4 h-4" />
                    )}
                  </button>
                  <button onClick={() => openEditSearch(s)} className="p-1.5 text-slate-400 hover:text-white transition-colors" title="Editar">
                    <Icon name="edit" className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteConfirm(s)} className="p-1.5 text-slate-400 hover:text-red-400 transition-colors" title="Excluir">
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                {s.keywords?.length > 0 && (
                  <div className="text-slate-400">
                    <span className="text-slate-500">Palavras-chave:</span>{' '}
                    {s.keywords.map((kw: string, i: number) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 bg-orange-500/10 text-orange-400 rounded mr-1">{kw}</span>
                    ))}
                  </div>
                )}
                {s.excludeKeywords?.length > 0 && (
                  <div className="text-slate-400">
                    <span className="text-slate-500">Excluir:</span>{' '}
                    {s.excludeKeywords.map((kw: string, i: number) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded mr-1">{kw}</span>
                    ))}
                  </div>
                )}
                {s.uf?.length > 0 && (
                  <div className="text-slate-400">
                    <span className="text-slate-500">UF:</span>{' '}
                    {s.uf.map((u: string, i: number) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded mr-1">{u}</span>
                    ))}
                  </div>
                )}
                {s.modalidades?.length > 0 && (
                  <div className="text-slate-400">
                    <span className="text-slate-500">Modalidades:</span>{' '}
                    {s.modalidades.map((m: number, i: number) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded mr-1">{alModalidades.find((am) => am.id === m)?.nome || MODALIDADES[m] || m}</span>
                    ))}
                  </div>
                )}
              </div>
              {s.lastSearchAt && (
                <div className="text-xs text-slate-500 mt-2">
                  Ultima busca: {fmtDateTime(s.lastSearchAt)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </EstadoDaLista>
    </div>
  );

  const renderSearchModal = () => {
    if (!searchModal) return null;
    return (
      // Formulário: antes fechava no clique fora (mousedown no fundo); agora só pelo × ou Cancelar.
      <CascaDeModal
        largura="base"
        titulo={searchModal === 'create' ? 'Novo Perfil de Busca' : 'Editar Perfil'}
        onClose={() => setSearchModal(null)}
        corpoClassName="p-4 space-y-4"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={() => setSearchModal(null)} className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors">Cancelar</button>
            <button
              onClick={handleSaveSearch}
              disabled={!searchForm.name.trim()}
              className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
            >
              {searchModal === 'create' ? 'Criar' : 'Salvar'}
            </button>
          </div>
        }
      >
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Nome *</label>
              <input
                type="text"
                value={searchForm.name}
                onChange={(e) => setSearchForm({ ...searchForm, name: e.target.value })}
                placeholder="Ex: Telecom PR"
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-400 ${campo}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Palavras-chave</label>
              <KeywordChipsInput
                value={searchForm.keywords}
                onChange={(v) => setSearchForm({ ...searchForm, keywords: v })}
                placeholder="Digite e dê Enter — ex: telefonia, voip, pabx"
                chipClass="bg-orange-500/15 text-orange-300 border border-orange-600/30"
              />
              <p className="text-xs text-slate-500 mt-1">Enter ou vírgula adiciona; × remove. Dirige a busca do AlertaLicitação. Use <strong>tokens únicos</strong> — o motor do AL trata espaço como "OU" entre palavras (ex: "comunicação unificada" vira lixo). Para termos compostos use underscore: <code className="text-slate-400">pabx_virtual</code>.</p>
              {(() => {
                const multiWord = searchForm.keywords
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => /\s/.test(k));
                if (multiWord.length === 0) return null;
                return (
                  <p className="text-xs text-amber-400 mt-1">
                    ⚠ O AlertaLicitação vai <strong>ignorar</strong> keyword(s) com espaço: {multiWord.map((k) => `"${k}"`).join(', ')}. Troque o espaço por underscore ou separe em tokens.
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Excluir quando contém</label>
              <KeywordChipsInput
                value={searchForm.excludeKeywords}
                onChange={(v) => setSearchForm({ ...searchForm, excludeKeywords: v })}
                placeholder="Digite e dê Enter — ex: estrada vicinal, pavimentação"
                chipClass="bg-red-500/15 text-red-300 border border-red-600/30"
              />
              <p className="text-xs text-slate-500 mt-1">Se o objeto da licitação contém <strong>qualquer</strong> um destes termos, ela é <strong>descartada</strong> mesmo que uma palavra-chave tenha batido. Útil para palavras ambíguas (ex: "ramal" telefônico × "ramal" de estrada vicinal em obra). Aceita termos compostos com espaço; sem distinção de acento/maiúscula.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-300">Estados (UF)</label>
                <button
                  type="button"
                  onClick={() => setSearchForm({ ...searchForm, uf: searchForm.uf.length === BR_STATES.length ? [] : [...BR_STATES] })}
                  className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                >
                  {searchForm.uf.length === BR_STATES.length ? 'Desmarcar todos' : 'Selecionar todos'}
                </button>
              </div>
              <div className="grid grid-cols-9 gap-1">
                {BR_STATES.map((uf) => (
                  <label
                    key={uf}
                    className={`flex items-center justify-center text-xs py-1.5 rounded cursor-pointer transition-colors ${
                      searchForm.uf.includes(uf)
                        ? 'bg-blue-500/30 text-blue-300 border border-blue-500/40'
                        : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:bg-slate-700 hover:text-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={searchForm.uf.includes(uf)}
                      onChange={(e) => {
                        setSearchForm({
                          ...searchForm,
                          uf: e.target.checked
                            ? [...searchForm.uf, uf]
                            : searchForm.uf.filter((u) => u !== uf),
                        });
                      }}
                      className="sr-only"
                    />
                    {uf}
                  </label>
                ))}
              </div>
              {searchForm.uf.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">{searchForm.uf.length} estado(s) selecionado(s)</p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-300">Modalidades</label>
                <button
                  type="button"
                  onClick={() => {
                    const allIds = alModalidades.map((m) => m.id as number);
                    setSearchForm({ ...searchForm, modalidades: searchForm.modalidades.length === allIds.length ? [] : allIds });
                  }}
                  className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                >
                  {alModalidades.length > 0 && searchForm.modalidades.length === alModalidades.length ? 'Desmarcar todas' : 'Selecionar todas'}
                </button>
              </div>
              {erroDasModalidades && alModalidades.length === 0 ? (
                <p role="alert" className="text-xs text-red-400">
                  Não consegui carregar as modalidades do AlertaLicitação ({erroDasModalidades}): o filtro por modalidade fica indisponível.{' '}
                  <button type="button" onClick={loadAlModalidades} className="underline">Tentar de novo</button>
                </p>
              ) : alModalidades.length === 0 ? (
                <p className="text-xs text-slate-500">Nenhuma modalidade capturada ainda do AlertaLicitação.</p>
              ) : (
                <div className="grid grid-cols-2 gap-1.5">
                  {alModalidades.map((m) => (
                    <label key={m.id as number} className="flex items-center gap-2 text-xs text-slate-300 hover:bg-slate-700/50 px-2 py-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchForm.modalidades.includes(m.id as number)}
                        onChange={(e) => {
                          const numId = m.id as number;
                          setSearchForm({
                            ...searchForm,
                            modalidades: e.target.checked
                              ? [...searchForm.modalidades, numId]
                              : searchForm.modalidades.filter((x) => x !== numId),
                          });
                        }}
                        className="rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="flex-1">{m.nome}</span>
                      <span className="text-slate-500">{m.count}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">Espelho do vocabulário real do AlertaLicitação. UF e modalidade são informativos — o perfil IPCOM cobre todo o Brasil; o filtro real é a palavra-chave.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Email para notificacoes</label>
              <input
                type="email"
                value={searchForm.notifyEmail}
                onChange={(e) => setSearchForm({ ...searchForm, notifyEmail: e.target.value })}
                placeholder="adm@ipcom.com.br"
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-400 ${campo}`}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={searchForm.isActive}
                onChange={(e) => setSearchForm({ ...searchForm, isActive: e.target.checked })}
                className="rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500"
              />
              Ativo (executa automaticamente)
            </label>
      </CascaDeModal>
    );
  };

  const highlightKeywords = (text: string, keywords: string[]) => {
    if (!keywords || keywords.length === 0) return text;
    // Keyword de FRASE é cadastrada com underscore (`pabx_virtual`) mas o objeto
    // da compra traz a frase com espaço — sem as duas formas, as 4 keywords de
    // frase nunca marcavam nada. Mais longas primeiro pra "pabx virtual" vencer
    // "pabx" no split (senão o resto da frase fica sem marcação).
    const needles = Array.from(
      new Set(keywords.flatMap((k) => [k, k.replace(/_/g, ' ')]).map((k) => k.trim()).filter(Boolean)),
    ).sort((a, b) => b.length - a.length);
    const escaped = needles.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const splitter = new RegExp(`(${escaped.join('|')})`, 'gi');
    // `test()` numa regex com flag /g avança o lastIndex a cada chamada, então
    // dentro do map o resultado alternava entre true e false e a marcação sumia
    // de forma intermitente. O matcher é ancorado e SEM /g (lastIndex sempre 0);
    // o split com captura já devolve os trechos casados inteiros.
    const matcher = new RegExp(`^(?:${escaped.join('|')})$`, 'i');
    const parts = text.split(splitter);
    return parts.map((part, i) =>
      matcher.test(part)
        ? <mark key={i} className="bg-white/10 text-white font-semibold px-0.5 rounded">{part}</mark>
        : part
    );
  };

  const BENEFICIO_MEEPP: Record<number, { label: string; color: string }> = {
    1: { label: 'Exclusivo ME/EPP', color: 'bg-slate-600/30 text-slate-300' },
    2: { label: 'Subcontratacao ME/EPP', color: 'bg-slate-600/30 text-slate-300' },
    3: { label: 'Cota Reservada ME/EPP', color: 'bg-slate-600/30 text-slate-300' },
  };

  const renderDetailModal = () => {
    if (!detailItem) return null;
    const statusInfo = getStatusInfo(detailItem.status);
    const hasMeEpp = detailItens.some((it: any) => it.tipoBeneficio && it.tipoBeneficio <= 3);
    // Contas do "dá tempo?": viviam numa fileira de cartões no corpo e subiram
    // para a faixa da casca, junto do valor estimado (prévia da gaveta, 05/10/2026).
    const enc = detailItem.dataEncerramento ? new Date(detailItem.dataEncerramento) : null;
    const ab = detailItem.dataAbertura ? new Date(detailItem.dataAbertura) : null;
    const now = new Date();
    const hoursToEnc = enc ? (enc.getTime() - now.getTime()) / 3600000 : null;
    const hoursToAb = ab ? (ab.getTime() - now.getTime()) / 3600000 : null;
    const fmtRel = (h: number | null, target: Date | null) => {
      if (h == null) return '—';
      if (h < 0) return 'passou';
      if (h < 2) return `${Math.max(1, Math.round(h * 60))}min`;
      if (h < 48) return `${Math.round(h)}h`;
      if (!target) return `${Math.floor(h / 24)}d`;
      const startT = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
      const startN = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      return `${Math.round((startT - startN) / 86400000)}d`;
    };
    const colorEnc = hoursToEnc == null ? 'text-slate-300'
      : hoursToEnc < 0 ? 'text-slate-500'
      : hoursToEnc < 24 ? 'text-red-300'
      : 'text-white';
    const colorAb = hoursToAb == null ? 'text-slate-300'
      : hoursToAb < 0 ? 'text-slate-500'
      : hoursToAb < 24 ? 'text-amber-300'
      : 'text-slate-200';
    const primaria = ['novo', 'analisando', 'interessado', 'participando'].includes(detailItem.status);
    const numeroDaCompra = detailItem.numeroCompra
      ? `nº ${formatNumeroCompraComAno(detailItem.numeroCompra, detailItem.anoCompra)}`
      : null;
    return (
      // MESMO drawer do Cadastrar (P1 UX 04/07: "substitui o modal centrado,
      // mesma linguagem do CRM"). Desde 05/10/2026 a moldura é a CascaDeGaveta:
      // gaveta de LEITURA, então clique fora e Esc fecham. A ação primária e o ⋯
      // desceram para o rodapé, como em toda casca; Descartar continua no ⋯
      // porque ação destrutiva não fica solta.
      <CascaDeGaveta
        titulo={getOrgaoDisplay(detailItem) || 'Detalhes da Licitacao'}
        tituloDica={getOrgaoDisplay(detailItem)}
        subtitulo={[numeroDaCompra, detailItem.orgaoCnpj].filter(Boolean).join(' · ') || undefined}
        rotulo="Detalhe da licitação"
        onClose={() => setDetailItem(null)}
        fecharAoClicarFora
        corpoClassName="p-4"
        selos={<>
          {/* Status é leitura: quem decide é o portal (suspensa/revogada) ou
              as duas ações do rodapé. Eram TRÊS selects de status na mesma
              tela (linha, header e aba Dados), sendo que dois gravavam sem
              confirmação — o mesmo risco que o confirm da tabela corrigiu. */}
          <span className={`text-xs px-2.5 py-1 rounded-full shrink-0 ${statusInfo.color}`}>{statusInfo.label}</span>
          {hasMeEpp && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600/30 text-slate-300 shrink-0">ME/EPP</span>}
          {/* Troca de portal MUDOU pra cá (09/08, receita Monitorar): o select
              saiu da linha da tabela (vazava com table-fixed + risco de troca
              acidental por scroll). Mesmo confirm + handlePortalChange. */}
          <PortalSelect
            value={detailItem.portalOrigem || 'pncp'}
            colorClass={(PORTAL_MAP as any)[detailItem.portalOrigem]?.color}
            portalsWithAccess={portalsWithAccess}
            extraPortals={extraPortals}
            unmapped={
              detailItem.portalOrigem
                && !PORTAL_MAP[detailItem.portalOrigem]
                && !extraPortals.find(ep => ep.key === detailItem.portalOrigem)
                ? {
                    value: detailItem.portalOrigem,
                    label: isMuralSource(detailItem)
                      ? '📰 Mural/Diário'
                      : `⚠ ${detailItem.portalOrigem} (não mapeado)`,
                  }
                : null
            }
            onChange={async (nv) => {
              // O confirm agora diz a VERDADE sobre o caso escolhido: em 4
              // dos 39 portais a troca muda o robô; nos outros é só o
              // rótulo de origem, e prometer automação onde não há foi
              // parte do que tornava esta tela confusa.
              const temAutomacao = PORTAIS_COM_AUTOMACAO.has(nv);
              const label = PORTAL_MAP[nv]?.label || extraPortals.find(e => e.key === nv)?.label || nv;
              if (!(await confirm({
                title: 'Alterar o portal desta licitação?',
                message: <>Portal novo: <strong>{label}</strong>.</>,
                confirmLabel: 'Alterar portal',
                consequence: temAutomacao
                  ? 'Muda qual automação o HUB usa daqui pra frente: scrape, envio e disputa.'
                  : 'Este portal não tem automação no HUB — a troca registra a origem, mas scrape, envio e disputa continuam manuais.',
              }))) return;
              handlePortalChange(detailItem.id, nv);
              setDetailItem({ ...detailItem, portalOrigem: nv });
            }}
          />
          {/* Fonte raw do PNCP (usuarioNome) quando portal genérico/null */}
          {(!detailItem.portalOrigem || detailItem.portalOrigem === 'pncp') && detailItem.fontePncp && (
            <span
              className="text-xs px-2 py-0.5 rounded-full shrink-0 bg-slate-700/60 text-slate-300 max-w-[200px] truncate"
              title={`Fonte PNCP: ${detailItem.fontePncp}`}
            >
              Fonte: {detailItem.fontePncp}
            </span>
          )}
          {/* Badge de tipo de dispensa (informativo, não auto-discarta) */}
          {(() => {
            const b = getDispensaBadge(detailItem);
            if (!b) return null;
            return (
              <span
                className={`text-xs px-2 py-0.5 rounded-full shrink-0 border ${b.color}`}
                title={b.title}
              >
                {b.label}
              </span>
            );
          })()}
        </>}
        faixa={[
          { k: 'Valor estimado', v: fmt(detailItem.valorEstimado) },
          { k: 'Encerramento', v: fmtRel(hoursToEnc, enc), cls: colorEnc, sub: detailItem.dataEncerramento ? fmtDateTime(detailItem.dataEncerramento) : '—' },
          { k: 'Abertura', v: fmtRel(hoursToAb, ab), cls: colorAb, sub: detailItem.dataAbertura ? fmtDateTime(detailItem.dataAbertura) : '—' },
          {
            k: 'Modalidade',
            v: <span className="block max-w-[220px] truncate text-sm font-medium">{detailItem.modalidadeNome || '—'}</span>,
            dica: detailItem.modalidadeNome || undefined,
            sub: detailItem.modoDisputa || '—',
          },
        ]}
        aviso={<>
          {/* Resultado do re-enrich per-item (faixa inline) */}
          {(reenrichingItem || reenrichItemResult) && (
            <div className={`mx-4 mt-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${
              reenrichingItem ? 'bg-slate-700/50 text-slate-300'
              : reenrichItemResult?.status === 'enriquecido' || reenrichItemResult?.status === 'sincronizado' ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-600/30'
              : reenrichItemResult?.status === 'erro' ? 'bg-red-500/15 text-red-200 border border-red-600/30'
              : 'bg-slate-700/50 text-slate-300'
            }`}>
              {reenrichingItem ? (
                <><Icon name="refresh" className="w-3.5 h-3.5 animate-spin" /> Consultando o PNCP…</>
              ) : (
                <>
                  <span className="font-medium">
                    {reenrichItemResult?.status === 'enriquecido' ? '✓ Atualizado'
                      : reenrichItemResult?.status === 'sincronizado' ? '✓ Sincronizado'
                      : reenrichItemResult?.status === 'inalterado' ? 'Já estava atualizado'
                      : reenrichItemResult?.status === 'sem-dados' ? 'Sem dados no PNCP'
                      : reenrichItemResult?.status === 'pulado' ? 'Pulado'
                      : 'Erro'}
                  </span>
                  {reenrichItemResult?.portal && <span className="text-slate-400">· portal: <span className="text-current">{reenrichItemResult.portal}</span></span>}
                  {reenrichItemResult?.numeroCompra && <span className="text-slate-400">· nº {reenrichItemResult.numeroCompra}</span>}
                  {reenrichItemResult?.msg && <span className="text-slate-400">· {reenrichItemResult.msg}</span>}
                  <button onClick={() => setReenrichItemResult(null)} className="ml-auto text-slate-500 hover:text-white"><Icon name="x" className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
          )}

          {/* Sem abas no caminho da decisão (D-3): objeto, prazo, itens e
              ações ficam visíveis de uma vez. Antes, os dois insumos que
              respondem "participo?" (itens e edital) estavam atrás de duas abas
              com lazy-load, e a dobra visível era gasta com uma grade de 12
              células. */}
          {detailLoading && <div className="px-4 pt-3 text-xs text-slate-500 animate-pulse">Carregando detalhes...</div>}

          {/* Painel de possível duplicata cross-portal (Classe B.2) */}
          {detailItem && dupMap.has(detailItem.id) && (() => {
            const { twin, similarity } = dupMap.get(detailItem.id)!;
            return (
              <div className="mx-4 mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-amber-300 text-sm font-medium">⚠ Possível duplicata</span>
                  <span className="text-[10px] text-amber-400/70">{Math.round(similarity * 100)}% de similaridade no objeto</span>
                </div>
                <p className="text-xs text-slate-300 mb-2">
                  Esta licitação parece ser a mesma de outro portal
                  {' '}(<span className={(PORTAL_MAP as any)[twin.portalOrigem]?.color || 'text-slate-400'}>{(PORTAL_MAP as any)[twin.portalOrigem]?.label || twin.portalOrigem}</span>),
                  {' '}mesma sessão e município:
                </p>
                <div className="text-xs text-slate-400 bg-central-elevado/50 rounded px-2 py-1.5 mb-2">
                  <div className="text-slate-300">{twin.orgaoNome || twin.unidadeNome || '—'}{twin.numeroCompra ? ` — nº ${twin.numeroCompra}` : ''}</div>
                  <div className="line-clamp-2">{twin.objetoCompra || ''}</div>
                  <div className="text-slate-500 mt-0.5">Status: {getStatusInfo(twin.status).label}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={mergingDup}
                    onClick={async () => {
                      if (await confirm({
                        title: 'Unificar as duas licitações numa só?',
                        danger: true,
                        confirmLabel: 'Unificar',
                        consequence: 'A de mais atividade (proposta, disputa, status) é mantida e a outra é removida. Não pode ser desfeito.',
                      })) {
                        handleMergeDuplicate(detailItem.id, twin.id);
                      }
                    }}
                    className="px-3 py-1 rounded bg-amber-500/80 hover:bg-amber-500 text-slate-900 text-xs font-medium disabled:opacity-50"
                  >
                    {mergingDup ? 'Unificando...' : 'Juntar (mesma licitação)'}
                  </button>
                  <button
                    disabled={mergingDup}
                    onClick={() => handleDismissDuplicate(detailItem.id, twin.id)}
                    className="px-3 py-1 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-xs disabled:opacity-50"
                  >
                    Não é duplicata
                  </button>
                </div>
              </div>
            );
          })()}

        </>}
        rodape={<>
          <MaisAcoes abrePara="cima" itens={[
            ...(detailItem.portalOrigem === 'bll'
              ? [{ label: '🔄 Sincronizar do portal (BLL)', onClick: handleBllScrapePublic }]
              : detailItem.portalOrigem === 'pcp'
              ? [{ label: '🔄 Sincronizar do portal (PCP)', onClick: handlePcpScrapeDetail }]
              : detailItem.portalOrigem === 'bnc'
              ? [{ label: '🔄 Sincronizar do portal (BNC)', onClick: handleBncScrapeDetail }]
              : detailItem.portalOrigem === 'comprasnet'
              ? // Traz status/mensagens/datas. Os ITENS da proposta não vêm
                // por aqui (dependem de proposta criada) — o rótulo avisa.
                [{
                  label: comprasnetScraping
                    ? 'Sincronizando…'
                    : '🔄 Sincronizar do portal (ComprasNet)',
                  onClick: handleComprasnetScrapeDetail,
                }]
              : [{ label: '🔎 Buscar dados no PNCP', onClick: handleReenrichItem }]),
            { label: '🗑️ Descartar licitação', danger: true, onClick: async () => {
              const hasProposta = (proposals || []).some((pp: any) => pp.itemId === detailItem.id && !pp.archived);
              const okConfirm = await confirm({
                title: 'Descartar esta licitação?',
                danger: true,
                confirmLabel: 'Descartar',
                consequence: (
                  <>
                    Vai para a <strong>Lixeira</strong> e é recuperável por 90 dias.
                    {hasProposta && <> A proposta é <strong>arquivada</strong> e jobs/sessões de disputa são cancelados.</>}
                  </>
                ),
              });
              if (!okConfirm) return;
              try {
                await procurementService.deleteItem(detailItem.id);
                showToast(hasProposta ? '✓ Licitação descartada + proposta arquivada' : '✓ Licitação na Lixeira');
                setDetailItem(null);
                loadItems();
                loadDashboard();
                setPipelineRefreshKey(k => k + 1);
              } catch (e: any) {
                showToast('Erro ao descartar: ' + (e.response?.data?.message || e.message), 'error');
              }
            } },
          ]} />
          <div className="ml-auto flex items-center gap-2">
              {/* Edital ao lado da primária: é o insumo nº1 da decisão e vivia
                  enterrado na aba Anexos (subiu ao cabeçalho, e com a casca de
                  gaveta desceu ao rodapé junto da ação). Pega o 1º arquivo cujo
                  nome/tipo cheire a edital; o resto segue em "Mais detalhes". */}
              {(() => {
                const edital = (detailArquivos || []).find((a: any) =>
                  /edital|aviso|termo de refer/i.test(`${a.titulo || ''} ${a.tipoDocumentoNome || ''}`),
                ) || (detailArquivos || [])[0];
                if (!edital?.url && !edital?.uri) return null;
                return (
                  <a
                    href={edital.url || edital.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-xs transition-colors"
                    title={edital.titulo || 'Abrir edital'}
                  >
                    <Icon name="document" className="w-3.5 h-3.5" /> Edital
                  </a>
                );
              })()}
              {(() => {
                const isExcluded = detailPncpStatus === 'gone' || detailPncpStatus === 'not_found' || !!detailItem.excludedFromPncpAt;
                const repub = detailRepublication || detailItem.republicationCandidate || null;
                const useRepub = isExcluded && !!repub?.pncpUrl;
                const pncpUrl = pncpPublicUrl(detailItem.pncpId);
                const opUrl = operationalPortalUrl(detailItem);
                const href = useRepub
                  ? repub.pncpUrl
                  : (opUrl || pncpUrl || (detailItem.linkOrigem && !/^https?:\/\//.test(detailItem.linkOrigem) ? 'https://' + detailItem.linkOrigem : detailItem.linkOrigem));
                if (!href) return null;
                const canResolve = !useRepub && !opUrl && !!detailItem.pncpId
                  && String(detailItem.portalOrigem || '').toLowerCase() !== 'comprasnet';
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={canResolve ? async (e) => {
                      // linkOrigem ainda é PNCP: tenta resolver o link do portal
                      // (linkSistemaOrigem) na hora; fallback = abrir o PNCP mesmo.
                      e.preventDefault();
                      try {
                        const r = await procurementService.resolvePortalLink(detailItem.id);
                        if (r?.link) {
                          setDetailItem((d: any) => (d && d.id === detailItem.id ? { ...d, linkOrigem: r.link } : d));
                          window.open(r.link, '_blank', 'noopener');
                          return;
                        }
                      } catch { /* fallback PNCP */ }
                      window.open(href, '_blank', 'noopener');
                    } : undefined}
                    className={`shrink-0 p-1 transition-colors ${useRepub ? 'text-amber-400 hover:text-amber-300' : 'text-slate-500 hover:text-white'}`}
                    title={useRepub ? 'Abrir republicação no PNCP (link original foi excluído)' : opUrl ? 'Abrir no portal' : canResolve ? 'Abrir no portal (resolve o link do portal; se não achar, abre o PNCP)' : 'Abrir no PNCP'}
                  >
                    <Icon name="externalLink" className="w-4 h-4" />
                  </a>
                );
              })()}
                      {primaria && (
              <button
                onClick={handleCreateProposalFromDetail}
                disabled={preparingProposal}
                title={preparingProposal ? 'Buscando itens...' : 'Cadastrar Proposta'}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Icon name={preparingProposal ? 'refresh' : 'plus'} className={`w-4 h-4 ${preparingProposal ? 'animate-spin' : ''}`} />
                {preparingProposal ? 'Buscando itens...' : 'Cadastrar Proposta'}
              </button>
            )}
          </div>
        </>}
      >
            {/* ========== TAB DADOS ========== */}
            {(() => {
              // Banner ativa em DOIS cenários:
              // 1) Verificação live retornou 'gone'/'not_found' (PNCP confirmou exclusão agora)
              // 2) Flag persistida no DB (excludedFromPncpAt != null) — cron já detectou anteriormente,
              //    cobre o caso do PNCP estar momentaneamente indisponível (403/timeout).
              const isLiveGone = detailPncpStatus === 'gone' || detailPncpStatus === 'not_found';
              const isPersistedGone = !!detailItem?.excludedFromPncpAt;
              const showExclusionBanner = isLiveGone || isPersistedGone;
              const repub = detailRepublication || detailItem?.republicationCandidate || null;
              return (
              <div className="space-y-4">
                {showExclusionBanner && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-red-400 text-base leading-none mt-0.5">⚠</span>
                      <div className="flex-1 text-sm">
                        <div className="text-red-300 font-medium">
                          {detailPncpStatus === 'not_found'
                            ? 'Esta licitação não foi encontrada no PNCP'
                            : 'Esta licitação foi excluída no PNCP'}
                        </div>
                        <div className="text-slate-300 mt-1">
                          O órgão pode ter republicado com novo número. O link do PNCP que tínhamos vinculado não funciona mais.
                        </div>
                        {repub ? (
                          <div className="mt-2 rounded bg-central-elevado/60 p-2 border border-central-borda">
                            <div className="text-xs text-slate-500 uppercase">Possível republicação encontrada</div>
                            <div className="text-slate-200 text-sm mt-1 line-clamp-3">{repub.objeto}</div>
                            <div className="text-slate-400 text-xs mt-1">
                              Publicada em {repub.dataPublicacao} · similaridade {Math.round((repub.similarity || 0) * 100)}%
                            </div>
                            <a
                              href={repub.pncpUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-2 text-xs px-2 py-1 rounded bg-primary hover:bg-primary-strong text-white"
                            >
                              Abrir no PNCP
                            </a>
                          </div>
                        ) : (
                          <div className="text-slate-400 text-xs mt-2">Nenhuma republicação automática encontrada — verifique manualmente no portal do órgão.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Banner: item via AlertaLicitação com origem não-PNCP — dados parciais */}
                {detailItem.sourceAggregator === 'alertalicitacao' &&
                  typeof detailItem.pncpId === 'string' &&
                  detailItem.pncpId.startsWith('AL-') &&
                  // BNC/BLL têm scraper público — depois de "Sincronizar do Portal"
                  // (valor preenchido) os dados estão completos, some o aviso.
                  !(Number(detailItem.valorEstimado) > 0) && (
                  <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-400 text-base leading-none mt-0.5">⚠</span>
                      <div className="flex-1 text-sm">
                        <div className="text-yellow-200 font-medium">
                          Dados parciais — origem {(PORTAL_MAP as any)[detailItem.portalOrigem]?.label || detailItem.portalOrigem || 'externa'}
                        </div>
                        <div className="text-yellow-100/80 text-xs mt-1">
                          Encontrado via AlertaLicitação, mas este portal não possui API pública. Encerramento,
                          UASG, SRP, modo de disputa e valor estimado podem estar vazios — apenas o portal
                          original tem essas informações completas.
                        </div>
                        {detailItem.linkOrigem && (
                          <a
                            href={detailItem.linkOrigem}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-100 text-xs font-medium transition-colors"
                          >
                            <Icon name="externalLink" className="w-3.5 h-3.5" />
                            Abrir no {(PORTAL_MAP as any)[detailItem.portalOrigem]?.label || 'portal original'}
                          </a>
                        )}
                        {detailItem.linkAgregador && (
                          <a
                            href={detailItem.linkAgregador}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 ml-2 px-3 py-1.5 rounded border border-yellow-500/30 hover:bg-yellow-500/10 text-yellow-100 text-xs font-medium transition-colors"
                          >
                            <Icon name="externalLink" className="w-3.5 h-3.5" />
                            Ver no AlertaLicitação
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Última mudança de status (se houver) */}
                {detailLogs.length > 0 && (
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                    <Icon name="clock" className="w-3 h-3 opacity-70" />
                    Último evento: <span className="text-slate-300">{detailLogs[0]?.notes || `Status alterado para ${detailLogs[0]?.newStatus || detailLogs[0]?.status || '—'}`}</span>
                    <span className="text-slate-600">·</span>
                    <span>{detailLogs[0]?.createdAt ? fmtDateTime(detailLogs[0].createdAt) : '—'}</span>
                  </div>
                )}

                {/* Orgao */}
                <div className="bg-central-elevado rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase mb-1">Orgao / Entidade</div>
                  <div className="text-white font-medium">{detailItem.unidadeNome || detailItem.orgaoNome || '—'}</div>
                  {detailItem.unidadeNome && detailItem.orgaoNome && detailItem.unidadeNome !== detailItem.orgaoNome && (
                    <div className="text-slate-500 text-xs mt-0.5">Vinculado a: {detailItem.orgaoNome}</div>
                  )}
                  <div className="text-slate-400 text-sm">
                    CNPJ: {detailItem.orgaoCnpj || '—'} | {detailItem.municipio || ''}{detailItem.municipio && detailItem.uf ? '/' : ''}{detailItem.uf || ''}
                    {detailExtra?.unidadeOrgao?.codigoUnidade && <> | UASG: {detailExtra.unidadeOrgao.codigoUnidade}</>}
                  </div>
                </div>

                {/* Objeto */}
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-1">Objeto da Compra</div>
                  <div className="text-slate-200 text-sm leading-relaxed">
                    {detailItem.matchedKeywords?.length > 0
                      ? highlightKeywords(detailItem.objetoCompra || '—', detailItem.matchedKeywords)
                      : (detailItem.objetoCompra || '—')}
                  </div>
                  {detailItem.matchedKeywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {detailItem.matchedKeywords.map((kw: string, i: number) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-600/30 text-slate-300 rounded text-xs">{kw}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* "Dados da licitação": mesmo rótulo do bloco colapsável da página
                    de trabalho (§8 do guia). Tudo que NÃO decide participação sai da dobra
                    (grade completa, amparo legal, troca de portal, observações). */}
                <button
                  onClick={() => setDetailMore(!detailMore)}
                  className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
                >
                  <Icon name={detailMore ? 'chevronDown' : 'chevronRight'} className="w-4 h-4" />
                  Dados da licitação
                </button>

                {detailMore && (<>
                {/* Info grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Valor Estimado</div>
                    <div className="text-white font-bold">{fmt(detailItem.valorEstimado)}</div>
                  </div>
                  {detailExtra?.valorTotalHomologado != null && detailExtra.valorTotalHomologado > 0 && (
                    <div className="bg-central-elevado rounded-lg p-3">
                      <div className="text-xs text-slate-500">Valor Homologado</div>
                      <div className="text-white font-bold">{fmt(detailExtra.valorTotalHomologado)}</div>
                    </div>
                  )}
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Modalidade</div>
                    <div className="text-slate-200 text-sm">{detailItem.modalidadeNome || '—'}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Modo de Disputa</div>
                    <div className="text-slate-200 text-sm">{detailItem.modoDisputa || '—'}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Publicacao</div>
                    <div className="text-slate-200 text-sm">{fmtDate(detailItem.dataPublicacao)}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Abertura Proposta</div>
                    <div className="text-white text-sm font-medium">{fmtDateTime(detailItem.dataAbertura)}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Fim Recebimento Propostas</div>
                    <div className="text-slate-200 text-sm">{fmtDateTime(detailItem.dataEncerramento)}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Abertura Sessao / Lances</div>
                    <div className="text-white text-sm font-medium">
                      {detailItem.dataSessaoInicio ? fmtDateTime(detailItem.dataSessaoInicio) : '—'}
                      {detailItem.dataSessaoFim && (
                        <span className="text-slate-400 ml-1">ate {fmtDateTime(detailItem.dataSessaoFim).split(' ')[1] || ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">Situacao</div>
                    <div className={`text-sm ${detailItem.situacao && /revogad|cancelad|desert|anulad|fracassad/i.test(detailItem.situacao) ? 'text-red-400 font-medium' : 'text-slate-200'}`}>{detailItem.situacao || '—'}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">SRP</div>
                    <div className="text-slate-200 text-sm">{detailItem.srp ? 'Sim' : 'Nao'}</div>
                  </div>
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500">N. Compra</div>
                    <div className="text-slate-200 text-sm">{detailItem.numeroCompra || '—'}</div>
                  </div>
                  {detailExtra?.processo && (
                    <div className="bg-central-elevado rounded-lg p-3">
                      <div className="text-xs text-slate-500">Processo</div>
                      <div className="text-slate-200 text-sm">{detailExtra.processo}</div>
                    </div>
                  )}
                  {detailExtra?.numeroControlePNCP && (
                    <div className="bg-central-elevado rounded-lg p-3">
                      <div className="text-xs text-slate-500">Controle PNCP</div>
                      <div className="text-slate-200 text-sm font-mono text-xs">{detailExtra.numeroControlePNCP}</div>
                    </div>
                  )}
                </div>

                {/* Amparo Legal */}
                {detailExtra?.amparoLegal && (
                  <div className="bg-central-elevado rounded-lg p-3">
                    <div className="text-xs text-slate-500 uppercase mb-1">Amparo Legal</div>
                    <div className="text-slate-200 text-sm font-medium">{detailExtra.amparoLegal.nome}</div>
                    {detailExtra.amparoLegal.descricao && (
                      <div className="text-slate-400 text-xs mt-1 leading-relaxed">{detailExtra.amparoLegal.descricao}</div>
                    )}
                  </div>
                )}

                {/* Observações. O select de status que ficava aqui saiu: era o
                    terceiro da tela e gravava SEM confirmação. */}
                <div className="border-t border-central-borda pt-4">
                  <div>
                    <label className="text-sm text-slate-300 block mb-1">Observacoes</label>
                    <textarea
                      defaultValue={detailItem.notes || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (detailItem.notes || '')) {
                          handleStatusChange(detailItem.id, detailItem.status, e.target.value);
                        }
                      }}
                      rows={3}
                      className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-400 ${campo}`}
                      placeholder="Adicione observacoes sobre esta licitacao..."
                    />
                  </div>
                </div>
                </>)}
              </div>
              );
            })()}

            {/* ========== TAB ITENS ========== */}
            {(
              <div>
                {detailDataFromRepub && (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                    <span className="font-medium">Dados da republicação</span> — a licitação original foi excluída no PNCP. Estes itens vieram do edital republicado.
                  </div>
                )}
                {/* Falha COM lista na mão vira ressalva, não bloqueio: o backend
                    devolve os itens do carregamento anterior quando a chamada de
                    agora falha, e esconder essa lista atrás do aviso apagava da
                    tela item que já estava conferido. */}
                {!detailLoading && detailItensErro && detailItens.length > 0 && (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                    {detailItensErro} — estes itens são do último carregamento e não deu para conferir com o portal agora.
                    <button
                      onClick={() => detailItem && loadDetailExtra(detailItem.id, true)}
                      className="ml-2 underline hover:text-amber-100"
                    >
                      Tentar de novo
                    </button>
                  </div>
                )}
                {detailLoading ? (
                  <div className="text-center py-12 text-slate-400 animate-pulse">Carregando itens da licitacao...</div>
                ) : detailItensErro && detailItens.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <div className="text-amber-300 text-sm">Não foi possível carregar os itens agora</div>
                    <div className="text-xs text-slate-500 mt-1">{detailItensErro}. Os itens podem existir — isto não quer dizer que a licitação não tem itens.</div>
                    <button
                      onClick={() => detailItem && loadDetailExtra(detailItem.id, true)}
                      className="mt-3 px-3 py-1.5 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-xs transition-colors"
                    >
                      Tentar de novo
                    </button>
                  </div>
                ) : detailItens.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">Nenhum item encontrado para esta licitacao</div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-central-borda">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-central-elevado/80">
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase w-12">#</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Descricao</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase w-28">ME/EPP</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-400 uppercase w-24">Qtd</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase w-20">Unid</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-400 uppercase w-28">Valor Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-400 uppercase w-28">Valor Total</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase w-28">Situacao</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-central-borda">
                        {detailItens.map((it: any) => {
                          const beneficio = BENEFICIO_MEEPP[it.tipoBeneficio];
                          return (
                            <tr key={it.numeroItem} className="hover:bg-central-elevado">
                              <td className="px-3 py-2 text-slate-400 text-xs">{it.numeroItem}</td>
                              <td className="px-3 py-2 text-slate-200 text-xs leading-relaxed">
                                {detailItem.matchedKeywords?.length > 0
                                  ? highlightKeywords(it.descricao || '', detailItem.matchedKeywords)
                                  : it.descricao}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {beneficio ? (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${beneficio.color}`}>{beneficio.label}</span>
                                ) : (
                                  <span className="text-xs text-slate-600">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right text-xs text-slate-300">{it.quantidade != null ? Number(it.quantidade).toLocaleString('pt-BR') : '—'}</td>
                              <td className="px-3 py-2 text-center text-xs text-slate-400">{it.unidadeMedida || '—'}</td>
                              <td className="px-3 py-2 text-right text-xs text-slate-300">{fmt(it.valorUnitarioEstimado)}</td>
                              <td className="px-3 py-2 text-right text-xs text-white font-medium">{fmt(it.valorTotal)}</td>
                              <td className="px-3 py-2 text-center text-xs text-slate-400">{it.situacaoCompraItemNome || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-central-elevado/60">
                          <td colSpan={6} className="px-3 py-2 text-right text-xs font-medium text-slate-400 uppercase">Total</td>
                          <td className="px-3 py-2 text-right text-sm text-white font-bold">{fmt(detailItens.reduce((sum: number, it: any) => sum + (Number(it.valorTotal) || 0), 0))}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ========== TAB ANEXOS ========== */}
            {detailMore && (
              <div>
                {Array.isArray(detailItem.bllRequiredDocuments) && detailItem.bllRequiredDocuments.length > 0 && (
                  <div className="mb-4 rounded-lg border border-central-borda bg-central-elevado/40 overflow-hidden">
                    <div className="px-3 py-2 bg-central-elevado text-xs font-semibold text-slate-300 flex items-center justify-between">
                      <span>📋 Documentos exigidos no processo (BLL)</span>
                      <span className="text-[10px] text-slate-400">
                        {detailItem.bllRequiredDocuments.filter((d: any) => d.obrigatorio).length} obrigatório(s) de {detailItem.bllRequiredDocuments.length}
                      </span>
                    </div>
                    <div className="divide-y divide-central-borda">
                      {detailItem.bllRequiredDocuments.map((d: any, i: number) => (
                        <div key={i} className="px-3 py-1.5 flex items-center justify-between text-xs">
                          <span className="text-slate-300">{d.documento}</span>
                          <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${d.obrigatorio ? 'bg-slate-600/40 text-white font-semibold' : 'bg-slate-600/40 text-slate-400'}`}>
                            {d.obrigatorio ? 'OBRIGATÓRIO' : 'opcional'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detailDataFromRepub && (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                    <span className="font-medium">Dados da republicação</span> — a licitação original foi excluída no PNCP. Estes anexos vieram do edital republicado.
                  </div>
                )}
                {!detailLoading && detailArquivosErro && detailArquivos.length > 0 && (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                    {detailArquivosErro} — estes anexos são do último carregamento; pode haver documento novo no portal.
                  </div>
                )}
                {detailLoading ? (
                  <div className="text-center py-12 text-slate-400 animate-pulse">Carregando anexos...</div>
                ) : detailArquivosErro && detailArquivos.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm text-amber-300">
                    Não foi possível carregar os anexos agora ({detailArquivosErro}).
                  </div>
                ) : detailArquivos.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">Nenhum anexo encontrado para esta licitacao</div>
                ) : (() => {
                  // Edital principal: tipoDocumentoId == 1 (Edital) OU primeiro arquivo cujo
                  // título contenha "edital". Cai pro primeiro anexo se nada bater.
                  const isEdital = (a: any) =>
                    a.tipoDocumentoId === 1 ||
                    /edital/i.test(a.tipoDocumentoNome || '') ||
                    /^edital\b|^edital\s|\bedital\b/i.test(a.titulo || '');
                  const editalIdx = detailArquivos.findIndex(isEdital);
                  const edital = editalIdx >= 0 ? detailArquivos[editalIdx] : null;
                  const outros = edital ? detailArquivos.filter((_, i) => i !== editalIdx) : detailArquivos;
                  return (
                    <div className="space-y-3">
                      {edital && (
                        <a
                          href={edital.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-4 bg-central-cartao hover:bg-central-elevado border border-central-borda rounded-lg p-4 transition-all group"
                        >
                          <div className="w-12 h-12 bg-slate-600/30 rounded-lg flex items-center justify-center shrink-0">
                            <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Edital principal</div>
                            <div className="text-base text-white transition-colors truncate mt-0.5">{edital.titulo || 'Edital'}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{edital.tipoDocumentoNome || 'Edital'} · Publicado: {fmtDate(edital.dataPublicacaoPncp)}</div>
                          </div>
                          <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-central-borda text-central-texto text-sm font-medium">
                            <Icon name="externalLink" className="w-4 h-4" /> Baixar
                          </span>
                        </a>
                      )}
                      {outros.length > 0 && (
                        <div className="space-y-2">
                          {edital && <div className="text-xs uppercase tracking-wide text-slate-500 pt-1">Outros anexos</div>}
                          {outros.map((arq: any) => (
                            <a
                              key={arq.sequencialDocumento}
                              href={arq.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 bg-central-elevado/60 hover:bg-central-elevado rounded-lg p-3 transition-colors group"
                            >
                              <div className="w-10 h-10 bg-slate-600/30 rounded-lg flex items-center justify-center shrink-0">
                                <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-white transition-colors truncate">{arq.titulo || 'Documento'}</div>
                                <div className="text-xs text-slate-500">{arq.tipoDocumentoNome || 'Arquivo'} | Publicado: {fmtDate(arq.dataPublicacaoPncp)}</div>
                              </div>
                              <Icon name="externalLink" className="w-4 h-4 text-slate-500 group-hover:text-white shrink-0" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ========== TAB HISTORICO ========== */}
            {detailMore && (
              <div>
                {/* Add observation */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newLogMessage}
                    onChange={(e) => setNewLogMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddLog()}
                    placeholder="Adicionar observacao..."
                    className={`flex-1 rounded-lg px-3 py-2 text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                  />
                  <button
                    onClick={handleAddLog}
                    disabled={!newLogMessage.trim()}
                    className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                  >
                    Adicionar
                  </button>
                </div>

                {logsLoading ? (
                  <div className="text-center py-12 text-slate-400 animate-pulse">Carregando historico...</div>
                ) : (
                  <EstadoDaLista
                    carga={cargaDoHistorico}
                    temDados={detailLogs.length > 0}
                    oQue="o histórico do item"
                    onTentarDeNovo={() => detailItem && loadItemLogs(detailItem.id)}
                    vazio={<div className="text-center py-12 text-slate-400">Nenhum registro no historico</div>}
                  >
                  <div className="space-y-3">
                    {detailLogs.map((log: any) => (
                      <div key={log.id} className="relative pl-6 pb-3 border-l-2 border-central-borda last:border-l-0">
                        <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-slate-500" />
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                log.source === 'pncp_sync' ? 'bg-slate-600/30 text-slate-300' :
                                log.source === 'manual' ? 'bg-amber-500/20 text-amber-300' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {log.source === 'pncp_sync' ? 'PNCP' : log.source === 'manual' ? 'Manual' : 'Sistema'}
                              </span>
                              {log.changedBy && <span className="text-xs text-slate-500">{log.changedBy}</span>}
                            </div>
                            <p className="text-sm text-slate-200">{log.message}</p>
                            {log.metadata?.resultados && (
                              <div className="mt-2 bg-central-elevado rounded p-2 text-xs text-slate-300">
                                {log.metadata.resultados.map((r: any, i: number) => (
                                  <div key={i} className="mb-1">
                                    <span className="text-slate-400">Item {r.numeroItem}:</span> {r.descricao?.substring(0, 80)}
                                    {r.resultados?.[0] && (
                                      <span className="ml-2 text-white">
                                        Vencedor: {r.resultados[0].nomeRazaoSocialFornecedor || '—'} — {fmt(r.resultados[0].valorTotalHomologado)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(log.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  </EstadoDaLista>
                )}
              </div>
            )}
      </CascaDeGaveta>
    );
  };

  const DOC_TYPES: Record<string, string> = {
    certidao_negativa_federal: 'CND Federal',
    certidao_negativa_estadual: 'CND Estadual',
    certidao_negativa_municipal: 'CND Municipal',
    cndt: 'CNDT (Trabalhista)',
    fgts: 'CRF/FGTS',
    balanco_patrimonial: 'Balanco Patrimonial',
    contrato_social: 'Contrato Social',
    atestado_capacidade: 'Atestado de Capacidade',
    alvara: 'Alvara',
    procuracao: 'Procuracao',
    certidao_falencia: 'Certidao de Falencia',
    licenca_anatel: 'Licenca ANATEL',
    registro_crea: 'Registro CREA',
    contrato_stfc: 'Contrato STFC/CNG',
    certificacao_iso: 'Certificacao ISO',
    sicaf: 'SICAF',
    declaracao: 'Declaracao',
    certidao_junta_comercial: 'Certidao Junta Comercial',
    cartao_cnpj: 'Cartao CNPJ',
    programa_integridade: 'Programa de Integridade',
    cnh: 'CNH',
    comprovante_endereco: 'Comprovante Endereco',
    datasheet_equipamento: 'Datasheet Equipamento',
    certificado_anatel: 'Certificado ANATEL',
    cicad_municipal: 'CICAD Municipal',
    cicad_estadual: 'CICAD Estadual',
    cnd_tcu: 'CND TCU',
    cnd_cgu: 'CND CGU',
    outro: 'Outro',
  };

  const DOC_CATEGORIES: Record<string, string> = {
    'habilitacao_fiscal': 'Habilitacao Fiscal',
    'habilitacao_juridica': 'Habilitacao Juridica',
    'qualificacao_economica': 'Qualificacao Economica Financeira',
    'qualificacao_tecnica': 'Qualificacao Tecnica',
    'declaracoes': 'Declaracoes e Propostas',
    'sicaf': 'SICAF',
    'equipamentos': 'Equipamentos',
  };

  const CATEGORY_ORDER = [
    'habilitacao_fiscal', 'habilitacao_juridica', 'qualificacao_economica',
    'qualificacao_tecnica', 'declaracoes', 'sicaf', 'equipamentos', '_sem_categoria',
  ];

  const TYPE_TO_CATEGORY: Record<string, string> = {
    certidao_negativa_federal: 'habilitacao_fiscal',
    certidao_negativa_estadual: 'habilitacao_fiscal',
    certidao_negativa_municipal: 'habilitacao_fiscal',
    cndt: 'habilitacao_fiscal',
    fgts: 'habilitacao_fiscal',
    certidao_falencia: 'habilitacao_fiscal',
    contrato_social: 'habilitacao_juridica',
    cartao_cnpj: 'habilitacao_juridica',
    certidao_junta_comercial: 'habilitacao_juridica',
    alvara: 'habilitacao_juridica',
    procuracao: 'habilitacao_juridica',
    cnh: 'habilitacao_juridica',
    comprovante_endereco: 'habilitacao_juridica',
    balanco_patrimonial: 'qualificacao_economica',
    atestado_capacidade: 'qualificacao_tecnica',
    registro_crea: 'qualificacao_tecnica',
    acervo_tecnico: 'qualificacao_tecnica',
    certificacao_iso: 'qualificacao_tecnica',
    pim_cpd: 'qualificacao_tecnica',
    acreditacao_regulatoria: 'qualificacao_tecnica',
    declaracao: 'declaracoes',
    programa_integridade: 'declaracoes',
    sicaf: 'sicaf',
    certificado_mei: 'sicaf',
    certificado_mei_camara: 'sicaf',
    licenca_anatel: 'equipamentos',
    contrato_stfc: 'equipamentos',
    certificado_inmetro: 'equipamentos',
    certificado_anatel: 'equipamentos',
    datasheet_equipamento: 'equipamentos',
    cicad_municipal: 'habilitacao_juridica',
    cicad_estadual: 'habilitacao_juridica',
    cnd_tcu: 'habilitacao_fiscal',
    cnd_cgu: 'habilitacao_fiscal',
  };

  const TYPE_TO_NAME: Record<string, string> = {
    certidao_negativa_federal: 'CND Federal',
    certidao_negativa_estadual: 'CND Estadual',
    certidao_negativa_municipal: 'CND Municipal',
    cndt: 'CNDT - Certidao Negativa de Debitos Trabalhistas',
    fgts: 'CRF FGTS',
    certidao_falencia: 'Certidao Negativa de Falencia',
    contrato_social: 'Contrato Social',
    cartao_cnpj: 'Cartao CNPJ',
    certidao_junta_comercial: 'Certidao da Junta Comercial',
    balanco_patrimonial: 'Balanco Patrimonial',
    atestado_capacidade: 'Atestado de Capacidade Tecnica',
    registro_crea: 'Registro CREA',
    certificacao_iso: 'Certificacao ISO',
    sicaf: 'Registro SICAF',
    licenca_anatel: 'Licenca ANATEL',
    contrato_stfc: 'Contrato STFC/CNG',
    certificado_anatel: 'Certificado ANATEL',
    datasheet_equipamento: 'Datasheet Equipamento',
    declaracao: 'Declaracao',
    alvara: 'Alvara de Funcionamento',
    procuracao: 'Procuracao',
    cicad_municipal: 'CICAD Municipal',
    cicad_estadual: 'CICAD Estadual',
    cnd_tcu: 'CND TCU - Certidao Negativa de Contas Julgadas Irregulares',
    cnd_cgu: 'CND CGU - Certidao Correcional Entes Privados',
  };

  const getExpiryStatus = (expiryDate: string, noExpiry?: boolean) => {
    // 23/10/2026 (padrão da Central, L1 de Licitações): a cor pelo mapa de tons; o prazo folgado é o normal, neutro.
    if (noExpiry) return { label: 'Permanente', color: TONE_BADGE.neutral, days: Infinity };
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate.slice(0, 10) + 'T12:00:00');
    const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: 'Vencido', color: TONE_BADGE.danger, days: daysLeft };
    if (daysLeft <= 7) return { label: `Vence em ${daysLeft}d`, color: TONE_BADGE.danger, days: daysLeft };
    if (daysLeft <= 14) return { label: `Vence em ${daysLeft}d`, color: TONE_BADGE.warning, days: daysLeft };
    return { label: `${daysLeft}d restantes`, color: TONE_BADGE.neutral, days: daysLeft };
  };

  const docStats = useMemo(() => {
    let expired = 0, expiring = 0, valid = 0, noExpiry = 0, permanent = 0, archived = 0;
    const urgentDocs: any[] = [];
    const now = new Date();
    documents.forEach(doc => {
      if (doc.archived) { archived++; return; }
      if (doc.noExpiry) { permanent++; return; }
      if (!doc.expiryDate) { noExpiry++; return; }
      const expiry = new Date(doc.expiryDate.slice(0, 10) + 'T12:00:00');
      const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) {
        expired++;
        urgentDocs.push({ ...doc, _daysLeft: daysLeft });
      } else if (daysLeft <= 14) {
        expiring++;
        urgentDocs.push({ ...doc, _daysLeft: daysLeft });
      } else {
        valid++;
      }
    });
    urgentDocs.sort((a, b) => a._daysLeft - b._daysLeft);
    return { total: documents.length - archived, expired, expiring, valid, noExpiry, permanent, archived, urgentDocs };
  }, [documents]);

  const subcategorySuggestions = useMemo(() => {
    const byCategory: Record<string, string[]> = {};
    documents.forEach(doc => {
      if (doc.subcategory && doc.category) {
        if (!byCategory[doc.category]) byCategory[doc.category] = [];
        if (!byCategory[doc.category].includes(doc.subcategory)) {
          byCategory[doc.category].push(doc.subcategory);
        }
      }
    });
    Object.keys(byCategory).forEach(k => byCategory[k].sort());
    return byCategory;
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    // Documentos arquivados ficam ocultos por padrão (histórico) — toggle revela
    let filtered = showArchivedDocs ? documents : documents.filter(d => !d.archived);
    if (docSearch) {
      const q = docSearch.toLowerCase();
      filtered = filtered.filter(d =>
        d.name?.toLowerCase().includes(q) ||
        (DOC_TYPES[d.type] || d.type || '').toLowerCase().includes(q) ||
        (d.subcategory || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q)
      );
    }
    if (docCategoryFilter) {
      filtered = filtered.filter(d => d.category === docCategoryFilter);
    }
    if (docTypeFilter) {
      filtered = filtered.filter(d => d.type === docTypeFilter);
    }
    if (docStatusFilter) {
      const now = new Date();
      filtered = filtered.filter(d => {
        if (d.noExpiry) return docStatusFilter === 'permanent';
        if (!d.expiryDate) return docStatusFilter === 'no_expiry';
        const expiry = new Date(d.expiryDate.slice(0, 10) + 'T12:00:00');
        const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (docStatusFilter === 'expired') return daysLeft < 0;
        if (docStatusFilter === 'expiring') return daysLeft >= 0 && daysLeft <= 14;
        if (docStatusFilter === 'valid') return daysLeft > 14;
        return false;
      });
    }
    return filtered;
  }, [documents, docSearch, docCategoryFilter, docTypeFilter, docStatusFilter, showArchivedDocs]);

  const documentsByCategory = useMemo(() => {
    const groups: Record<string, any[]> = {};
    CATEGORY_ORDER.forEach(cat => { groups[cat] = []; });
    filteredDocuments.forEach(doc => {
      const cat = doc.category || '_sem_categoria';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(doc);
    });
    return groups;
  }, [filteredDocuments]);

  const PORTALS = [
    'ComprasNet', 'Licitacoes-e', 'Compras Publicas', 'BEC / Pregao SP',
    'Siga Rio de Janeiro', 'Siga Espirito Santo', 'Compras Minas Gerais',
    'Compras Santa Catarina', 'Procergs', 'ComprasRS', 'Banrisul',
    'ComprasNet Goias', 'BLL - Bolsa de Licitacoes', 'Publicaxo', 'Licitanet',
    'Compras Amazonas', 'Compras Mato Grosso', 'Compras Pernambuco',
    'Compras BR', 'Licitar Digital', 'BNC - Bolsa Nacional de Compras',
  ];

  const SYSTEMS: Record<string, string> = {
    todos: 'Todos', monitorar: 'Monitorar', cadastrar: 'Cadastrar',
    encontrar: 'Encontrar', disputar: 'Disputar',
  };

  // ==================== Metrics Dashboard ====================
  // Mesmo mapa do módulo (statusColors) — era duplicado com cores inline.
  const PROPOSAL_STATUSES = PROPOSAL_STATUSES_MAP;

  const renderMetricsDashboard = () => {
    if (!metrics && cargaDasMetricas.estado === 'erro') {
      // Antes: "Carregando metricas..." para sempre. A carga terminou, e falhou.
      return <EstadoDaLista carga={cargaDasMetricas} temDados={false} oQue="as métricas" onTentarDeNovo={loadMetrics} vazio={null}>{null}</EstadoDaLista>;
    }
    if (metricsLoading || !metrics) {
      return <div className="text-center py-16 text-slate-400 animate-pulse">Carregando metricas...</div>;
    }

    const funnel = metrics.conversionFunnel || {};
    const funnelSteps = [
      { key: 'novo', label: 'Abertas', color: '#22c55e', icon: 'plus' },
      { key: 'analisando', label: 'Analisando', color: '#eab308', icon: 'search' },
      { key: 'interessado', label: 'Interessado', color: '#3b82f6', icon: 'star' },
      { key: 'participando', label: 'Participando', color: '#f97316', icon: 'play' },
      { key: 'ganho', label: 'Ganho', color: '#10b981', icon: 'check' },
    ];
    const maxFunnel = Math.max(...funnelSteps.map((s) => funnel[s.key] || 0), 1);

    const byUf = metrics.byUf || [];
    const maxUf = Math.max(...byUf.map((u: any) => u.count || 0), 1);

    const byModalidade = metrics.byModalidade || [];
    const maxMod = Math.max(...byModalidade.map((m: any) => m.count || 0), 1);

    const byMonth = metrics.byMonth || [];
    const maxMonth = Math.max(...byMonth.map((m: any) => m.count || 0), 1);

    const nextOpenings = metrics.nextOpenings || [];

    return (
      <div className="space-y-6">
        {/* recarga que falhou: os números abaixo são da última carga boa, e a tela diz isso */}
        <EstadoDaLista carga={cargaDasMetricas} temDados oQue="as métricas" onTentarDeNovo={loadMetrics} vazio={null}>{null}</EstadoDaLista>
        {/* Funnel cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {funnelSteps.map((step) => (
            <div key={step.key} className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-white">{funnel[step.key] || 0}</div>
              <div className="text-xs text-slate-400 mt-1">{step.label}</div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-700">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(((funnel[step.key] || 0) / maxFunnel) * 100, 2)}%`, backgroundColor: step.color }} />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By UF */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white mb-3">Por UF (Top 10)</h3>
            {byUf.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Sem dados</div>
            ) : (
              <div className="space-y-2">
                {byUf.map((u: any) => (
                  <div key={u.uf} className="flex items-center gap-2">
                    <span className="text-xs text-slate-300 w-6 text-right font-mono">{u.uf}</span>
                    <div className="flex-1 h-5 bg-slate-700 rounded overflow-hidden">
                      <div className="h-full bg-blue-500/60 rounded flex items-center px-2" style={{ width: `${Math.max((u.count / maxUf) * 100, 8)}%` }}>
                        <span className="text-[10px] text-white font-medium">{u.count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* By Modalidade */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white mb-3">Por Modalidade</h3>
            {byModalidade.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Sem dados</div>
            ) : (
              <div className="space-y-2">
                {byModalidade.map((m: any) => (
                  <div key={m.modalidade} className="flex items-center gap-2">
                    <span className="text-xs text-slate-300 w-32 truncate" title={m.modalidade}>{m.modalidade}</span>
                    <div className="flex-1 h-5 bg-slate-700 rounded overflow-hidden">
                      <div className="h-full bg-orange-500/60 rounded flex items-center px-2" style={{ width: `${Math.max((m.count / maxMod) * 100, 8)}%` }}>
                        <span className="text-[10px] text-white font-medium">{m.count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* By Month */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white mb-3">Por Mes (Ultimos 6 meses)</h3>
            {byMonth.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Sem dados</div>
            ) : (
              <div className="flex items-end gap-2 h-40">
                {byMonth.map((m: any) => {
                  const pct = Math.max((m.count / maxMonth) * 100, 5);
                  const [year, month] = (m.month || '').split('-');
                  const label = month ? `${month}/${year?.slice(2)}` : m.month;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-white font-medium">{m.count}</span>
                      <div className="w-full bg-emerald-500/60 rounded-t" style={{ height: `${pct}%` }} />
                      <span className="text-[10px] text-slate-400">{label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Next Openings */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white mb-3">Proximas Aberturas</h3>
            {nextOpenings.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Nenhuma abertura proxima</div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {nextOpenings.map((item: any) => (
                  <div key={item.id} className="flex items-start gap-2 text-xs">
                    <span className="text-orange-400 font-medium whitespace-nowrap">{fmtDateTime(item.dataAbertura)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-300 truncate">{getOrgaoDisplay(item)}</div>
                      <div className="text-slate-500 truncate">{(item.objetoCompra || '').substring(0, 80)}</div>
                    </div>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] ${getStatusInfo(item.status).color}`}>{getStatusInfo(item.status).label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
          <span className="text-slate-400 text-sm">Total: </span>
          <span className="text-white font-bold text-lg">{funnel.total || 0}</span>
          <span className="text-slate-400 text-sm"> licitacoes monitoradas</span>
        </div>
      </div>
    );
  };

  // ==================== Proposals Tab ====================
  const AUTOMATION_STATUSES: Record<string, { label: string; color: string }> = {
    queued: { label: 'Na fila', color: 'bg-slate-500/20 text-slate-400' },
    running: { label: 'Executando', color: 'bg-blue-500/20 text-blue-400' },
    completed: { label: 'Concluido', color: 'bg-emerald-500/20 text-emerald-400' },
    failed: { label: 'Falhou', color: 'bg-red-500/20 text-red-400' },
    cancelled: { label: 'Cancelado', color: 'bg-slate-500/20 text-slate-300' },
    needs_manual: { label: 'Acao Manual', color: 'bg-yellow-500/20 text-yellow-400' },
  };

  const getLatestJob = (proposalId: number) => {
    return automationJobs.find((j: any) => j.proposalId === proposalId);
  };

  /**
   * UNIVERSO DA ABA (Ativas / Encerradas / Arquivadas / Todas).
   *
   * Existe porque os stat cards contavam `!archived` — "não arquivadas" — e a
   * lista abaixo deles filtrava `!archived && !isEncerrada && !isItemTerminal`.
   * Dois universos com o mesmo nome na mesma tela: o card dizia "Precisam
   * reenviar 3", o clique filtrava e apareciam 2 (a terceira era a proposta 274,
   * SÃO PAULO SSP, item homologado e prazo vencido em 07/08). Pelo mesmo motivo
   * "Propostas ativas 107" convivia com a aba "Ativas (5)".
   *
   * Agora o card conta o que a lista mostra, porque contam da mesma base.
   * Chips de prazo e busca seguem DEPOIS: eles refinam a lista, não mudam o
   * universo que os cards descrevem.
   */
  // Baldes das abas: UMA classificação para as abas, os stat cards e a lista.
  // A regra é pura e testada em proposalStage.classifyProposals — antes vivia
  // em três cópias aqui dentro, duas jurando no comentário estar sincronizadas.
  const proposalBuckets = useMemo(() => classifyProposals(proposals), [proposals]);

  const proposalUniverse = useMemo(
    () => proposalBuckets[proposalViewMode] ?? proposals,
    [proposalBuckets, proposalViewMode, proposals],
  );

  const proposalMetrics = useMemo(() => {
    const active = proposalUniverse;
    // "Precisa de ação", não "precisa reenviar": a regra inclui RASCUNHO, que
    // nunca foi ao portal — não há o que reenviar. O rótulo antigo e o tooltip
    // ("Portal reabriu/alterou após o envio") descreviam outra coisa.
    const needsAction = active.filter((p: any) => {
      const job = automationJobs.find((j: any) => j.proposalId === p.id);
      return p.status === 'rascunho' || (job && ['failed', 'needs_manual'].includes(job.status));
    });
    const completed = active.filter((p: any) => {
      const job = automationJobs.find((j: any) => j.proposalId === p.id);
      return job && job.status === 'completed';
    });
    const waiting = active.filter((p: any) => {
      const job = automationJobs.find((j: any) => j.proposalId === p.id);
      return p.status === 'enviada' && (!job || !['completed', 'failed', 'needs_manual'].includes(job.status));
    });
    return { total: active.length, needsAction: needsAction.length, completed: completed.length, waiting: waiting.length };
  }, [proposalUniverse, automationJobs]);

  const archivedCount = useMemo(() => proposals.filter((p: any) => p.archived).length, [proposals]);

  const filteredProposals = useMemo(() => {
    // O filtro por aba vive em `proposalUniverse` — mesma base que os stat
    // cards contam. Era aqui que havia a segunda cópia da regra.
    let list = proposalUniverse;

    // Chips de prazo
    if (prazoFilter) {
      const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(); endOfDay.setHours(23, 59, 59, 999);
      const in3Days = new Date(endOfDay.getTime() + 2 * 24 * 60 * 60 * 1000);
      const in7Days = new Date(endOfDay.getTime() + 6 * 24 * 60 * 60 * 1000);
      list = list.filter((p: any) => {
        const enc = p.item?.dataEncerramento;
        if (!enc) return false;
        const encT = new Date(enc).getTime();
        if (prazoFilter === 'hoje') return encT >= startOfDay.getTime() && encT <= endOfDay.getTime();
        if (prazoFilter === '3d') return encT >= startOfDay.getTime() && encT <= in3Days.getTime();
        if (prazoFilter === 'semana') return encT >= startOfDay.getTime() && encT <= in7Days.getTime();
        if (prazoFilter === 'custom') {
          const s = prazoCustomStart ? new Date(prazoCustomStart + 'T00:00:00').getTime() : 0;
          const e = prazoCustomEnd ? new Date(prazoCustomEnd + 'T23:59:59').getTime() : Number.MAX_SAFE_INTEGER;
          return encT >= s && encT <= e;
        }
        return true;
      });
    }
    if (proposalStatusFilter) list = list.filter((p: any) => p.status === proposalStatusFilter);
    if (agendaFilter) list = list.filter((p: any) => estadoDaAgenda(p.item) === agendaFilter);
    // R1.18: filtro pelos badges de contagem (precisam reenviar / concluídas / aguardando)
    if (metaFilter) {
      list = list.filter((p: any) => {
        const job = automationJobs.find((j: any) => j.proposalId === p.id);
        if (metaFilter === 'needsAction') return p.status === 'rascunho' || (job && ['failed', 'needs_manual'].includes(job.status));
        if (metaFilter === 'completed') return job && job.status === 'completed';
        if (metaFilter === 'waiting') return p.status === 'enviada' && (!job || !['completed', 'failed', 'needs_manual'].includes(job.status));
        return true;
      });
    }
    if (proposalSearch.trim()) {
      const q = proposalSearch.toLowerCase();
      list = list.filter((p: any) =>
        (p.item?.orgaoNome || '').toLowerCase().includes(q) ||
        (p.item?.unidadeNome || '').toLowerCase().includes(q) ||
        (p.item?.objetoCompra || '').toLowerCase().includes(q) ||
        (p.item?.numeroCompra || '').includes(q) ||
        (p.title || '').toLowerCase().includes(q)
      );
    }
    if (proposalSortBy) {
      list = [...list].sort((a: any, b: any) => {
        let va: any, vb: any;
        if (proposalSortBy === 'orgaoNome') { va = getOrgaoDisplay(a.item); vb = getOrgaoDisplay(b.item); }
        else if (proposalSortBy === 'totalValue') { va = a.totalValue || 0; vb = b.totalValue || 0; }
        else if (proposalSortBy === 'status') { va = a.status || ''; vb = b.status || ''; }
        else if (proposalSortBy === 'dataEncerramento') { va = proposalNextEventTs(a.item)?.ts ?? Number.MAX_SAFE_INTEGER; vb = proposalNextEventTs(b.item)?.ts ?? Number.MAX_SAFE_INTEGER; }
        else { va = ''; vb = ''; }
        if (va < vb) return proposalSortOrder === 'ASC' ? -1 : 1;
        if (va > vb) return proposalSortOrder === 'ASC' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [proposalUniverse, prazoFilter, prazoCustomStart, prazoCustomEnd, proposalStatusFilter, agendaFilter, metaFilter, automationJobs, proposalSearch, proposalSortBy, proposalSortOrder]);

  // "Quais estão na agenda": contagem por estado na aba atual. Conta ITEM, não
  // proposta: duas propostas do mesmo item são um evento só.
  const agendaCounts = useMemo(() => {
    const porEstado: Record<string, Set<number>> = { na_agenda: new Set(), marcado_sem_evento: new Set(), fora: new Set() };
    for (const p of proposalUniverse as any[]) {
      const e = estadoDaAgenda(p.item);
      if (e && p.item?.id != null) porEstado[e].add(p.item.id);
    }
    return { na_agenda: porEstado.na_agenda.size, marcado_sem_evento: porEstado.marcado_sem_evento.size, fora: porEstado.fora.size };
  }, [proposalUniverse]);
  // Para o painel: itens com evento em TODA a lista, não só na aba atual.
  const itensComEventoNaLista = useMemo(
    () => new Set((proposals as any[]).filter((p) => p.item?.googleEventId).map((p) => p.item.id)).size,
    [proposals],
  );

  // ⛔ Nada otimista. O DELETE cancela no Google e SÓ ENTÃO limpa o carimbo: se
  // o Google recusar, a rota falha e nada mudou no servidor. Em sucesso, relê a
  // lista em vez de adivinhar o estado novo.
  const handleMarcarAgenda = async (p: any) => {
    const itemId = p.item?.id;
    if (!itemId) return;
    setAgendaBusyItemId(itemId);
    try {
      const r = await procurementService.marcarNaAgenda(itemId);
      setAgendaMotivos((prev) => {
        const n = { ...prev };
        if (!r.publicado && r.motivo) n[itemId] = r.motivo; else delete n[itemId];
        return n;
      });
      // publicado: false NÃO é falha (modo seco, ou regra que impede o evento):
      // a marca ficou. Toast de sucesso com o motivo.
      showToast(mensagemDoMarcar(r));
      await loadProposals();
    } catch (e: any) {
      showToast('Não foi possível pôr na agenda: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setAgendaBusyItemId(null);
    }
  };

  const handleRemoverAgenda = async (p: any) => {
    const itemId = p.item?.id;
    if (!itemId) return;
    const tinhaEvento = !!p.item?.googleEventId;
    if (!(await confirm({
      title: 'Tirar esta licitação da agenda?',
      confirmLabel: 'Tirar da agenda',
      danger: tinhaEvento,
      consequence: tinhaEvento
        ? 'O evento é cancelado no Google. O item fica fora da agenda mesmo com proposta enviada, e a varredura não o recria.'
        : 'O item fica fora da agenda mesmo com proposta enviada, e a varredura não o recria.',
    }))) return;
    setAgendaBusyItemId(itemId);
    try {
      const r = await procurementService.removerDaAgenda(itemId);
      setAgendaMotivos((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      // removido: false é o caso idempotente (não havia evento), não falha.
      showToast(mensagemDoRemover(r));
      await loadProposals();
    } catch (e: any) {
      showToast('Não foi possível tirar da agenda (nada mudou): ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setAgendaBusyItemId(null);
    }
  };

  // Counts pra cada tab (sempre mostra todos pra usuário ver volumetria)
  // R1.13: "Encerrada" só passa pra histórico DEPOIS de virar o dia
  // F2 (24/05/2026): items em status TERMINAL vão pra Arquivadas (não contam
  // em Ativas/Encerradas) — alinhado com o filtro acima.
  // Contadores das abas: comprimento dos MESMOS baldes que alimentam a lista e
  // os stat cards. Era a terceira cópia da regra — tinha o próprio `TERMINAL` e
  // um comentário dizendo "mesma regra do filtro acima", que é justamente o
  // tipo de garantia que nada verifica.
  const viewModeCounts = useMemo(() => ({
    ativas: proposalBuckets.ativas.length,
    cotacoes: proposalBuckets.cotacoes.length,
    encerradas: proposalBuckets.encerradas.length,
    arquivadas: proposalBuckets.arquivadas.length,
    todas: proposalBuckets.todas.length,
  }), [proposalBuckets]);

  const encerradasCount = viewModeCounts.encerradas;

  const toggleProposalSort = (column: string) => {
    if (proposalSortBy === column) {
      setProposalSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setProposalSortBy(column);
      setProposalSortOrder(column === 'totalValue' ? 'DESC' : 'ASC');
    }
  };

  const ProposalSortHeader = ({ column, label, className = '' }: { column: string; label: string; className?: string }) => (
    <th
      onClick={() => toggleProposalSort(column)}
      className={`px-3 py-2 text-xs font-medium text-slate-400 uppercase cursor-pointer hover:text-slate-200 transition-colors select-none ${className}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {proposalSortBy === column ? (
          <span className="text-orange-400">{proposalSortOrder === 'ASC' ? '▲' : '▼'}</span>
        ) : (
          <span className="text-slate-600">⇅</span>
        )}
      </div>
    </th>
  );


  // ==================== W-F2: página de trabalho da proposta ====================
  // A proposta viva vem da LISTA (que já faz polling) — sem 2ª fonte de verdade.
  const workspaceLiveProposal = useMemo(
    () => (workspaceProposalId ? proposals.find((p: any) => p.id === workspaceProposalId) || null : null),
    [workspaceProposalId, proposals],
  );
  const workspaceLatestJob = useMemo(
    () => (workspaceProposalId ? automationJobs.find((j: any) => j.proposalId === workspaceProposalId) || null : null),
    [workspaceProposalId, automationJobs],
  );
  const ws = useProposalWorkspace(workspaceProposalId, {
    liveProposal: workspaceLiveProposal,
    latestJob: workspaceLatestJob,
    showToast,
    onRequestReload: loadProposals,
  });

  const openWorkspace = (p: any) => {
    setExpandedProposalId(null);
    setWorkspaceProposalId(p.id);
  };

  // Ações do workspace: os MODAIS continuam aqui (submit/automação/job/IA/
  // adequação) — a página só dispara. Assim o fluxo crítico de envio não muda
  // de implementação junto com o redesign.
  /**
   * D-III — "ganhamos?" e "onde o processo está?" são perguntas diferentes e
   * agora moram em colunas diferentes.
   *
   * O desfecho vai para `desfecho` (fonte manual). O status recebe apenas
   * `encerrada`, que é o terminal neutro: encerra o acompanhamento sem afirmar
   * homologação, que é fato do portal e não nosso. ⛔ Antes daqui saía
   * `status = 'ganho' | 'perdido'` — valores depreciados que viravam
   * `homologado` e levavam o placar junto.
   */
  // D1 (12/10/2026): os três envios ao ComprasNet declaravam ME/EPP = sim fixo no corpo, e o backend dava precedência a ele:
  // o "ME/EPP: Não" da proposta nunca chegava. Agora o front não manda, e o backend usa `acceptedTerms.meepp`.
  const marcarDesfechoEEncerrar = async (itemId: number, desfecho: 'ganho' | 'perdido') => {
    try {
      await procurementService.registrarDesfecho(itemId, desfecho);
    } catch (e: any) {
      // D5 (12/10/2026): só o "já capturado do PNCP" deixa encerrar (o resultado existe, é documento). Qualquer outra
      // falha NÃO encerra: antes encerrava, e o item sumia sem desfecho do Cadastrar e do Monitorar.
      if (!desfechoJaCapturado(e)) {
        showToast(fraseDoDesfechoQueNaoGravou(e), 'error');
        return;
      }
      showToast('O desfecho já tinha sido capturado do PNCP; encerrei o acompanhamento.', 'success');
    }
    handleStatusChange(itemId, 'encerrada');
  };

  /**
   * Manda a proposta PRA DISPUTA — a decisão passou a ser do operador.
   *
   * Até 01/09/2026 a sessão nascia sozinha: o callback de envio criava uma, e
   * um cron de hora em hora criava a que faltasse nos 30 dias seguintes. A tela
   * Disputar enchia de esqueleto de licitação que ninguém ia disputar com robô,
   * e o auto-create ainda chumbava o portal em "ComprasNet" — item de BLL/BNC/
   * PCP nascia com o portal errado. Aqui o portal sai do `portalOrigem`/
   * `linkOrigem` do item, como no resto da tela.
   */
  const handleEnviarParaDisputa = useCallback(async (p: any, opts?: { navigate?: boolean }) => {
    const it = p?.item;
    if (!it?.id) return;
    if (p.disputeSessionId) { onNavigate?.('lic-disputar'); return; }
    // D4 (Heitor, 12/10/2026): pergunta nomeando a proposta e o portal, e manda o proposalId (sem ele o backend pegava a
    // proposta MAIS RECENTE do item, ou criava uma). A segunda sessão do mesmo item é recusada no backend (409).
    const portalDaDisputa = PORTAL_MAP[it.portalOrigem]?.label || it.portalOrigem || 'portal não identificado';
    if (!(await confirm(confirmacaoDeMandarParaDisputa(p, portalDaDisputa)))) return;
    try {
      const portalId = findPortalIdBySource(it.portalOrigem || null, it.linkOrigem || null);
      await disputeService.createSession({
        itemId: it.id,
        proposalId: p.id,
        portalId: portalId ? parseInt(portalId, 10) : undefined,
        disputeStartTime: it.dataSessaoInicio || it.dataAbertura,
        disputeEndTime: it.dataSessaoFim,
      } as any);
      showToast('Sessão de disputa criada — configure o robô na tela Disputar', 'success');
      await loadProposals();
      if (opts?.navigate !== false) onNavigate?.('lic-disputar');
    } catch (e: any) {
      showToast('Erro ao criar disputa: ' + (e?.response?.data?.message || e?.message), 'error');
    }
  }, [onNavigate, findPortalIdBySource, loadProposals]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * A proposta que ainda NÃO foi mandada pra disputa e tem sessão marcada.
   *
   * É o sinal que substituiu o cron reconciliador: enquanto ele criava a sessão
   * calado, isto aparece na linha do Cadastrar, que é onde a decisão é tomada.
   * Só vale pra Pregão/Dispensa — ⛔ pelo NOME da modalidade, nunca pelo
   * `modalidadeId` (regra indelével: o id significa coisas diferentes conforme a
   * origem e vem trocado).
   */
  const faltaMandarParaDisputa = useCallback((p: any): boolean => {
    if (!p?.item?.id || p.disputeSessionId) return false;
    if (p.status !== 'enviada') return false;
    if (!/preg[ãa]o|dispensa/i.test(String(p.item.modalidadeNome || ''))) return false;
    const quando = p.item.dataSessaoInicio || p.item.dataEncerramento;
    return !!quando && new Date(quando).getTime() > Date.now();
  }, []);

  const workspaceActions = useMemo(() => ({
    onBack: () => setWorkspaceProposalId(null),
    onLoadItems: async () => {
      const p = workspaceLiveProposal;
      if (!p?.item?.id) return;
      const po = String(p.item?.portalOrigem || '').toLowerCase();
      // Proposta ainda vazia: o caminho bom é o PNCP + composição da Análise IA
      // (traz grupo, CATSER, tipo e teto). Scrape do portal é pra atualizar
      // proposta que já tem itens.
      if (ws.items.length === 0) {
        // Sob runWithProgress: esta chamada leva segundos (getItemDetails busca
        // no PNCP) e era a ÚNICA das duas pernas sem feedback nenhum — a tela
        // ficava idêntica até os itens aparecerem, e o operador clicava de novo
        // achando que o botão não pegou (MPRJ PE 233/2026, 31/08/2026).
        const r = await runWithProgress(p.item.id, 'load-items', 'Carregando itens do portal', () =>
          handleLoadProposalItems(String(p.item.id)),
        );
        if (r?.items?.length) {
          ws.setItems(r.items as any);
          if (r.meses) ws.setForm({ contractMonths: String(r.meses) });
          return;
        }
      }
      const r = await sincronizarDoPortal(p);
      if (r === 'erro' || r === 'cancelado') return;
      try {
        await loadProposals();
        await ws.reload();
      } catch (err: any) {
        showToast(`Erro: ${err?.response?.data?.message || err?.message || err}`, 'error');
      }
    },
    onOpenSubmit: () => {
      const p = workspaceLiveProposal;
      if (!p) return;
      // TODO portal novo: entre AQUI, não crie um segundo caminho.
      //
      // Até 18/08/2026 BLL e PCP desviavam para o modal de Automação, e o
      // ComprasNet usava este. Mesma ação, duas telas diferentes: o operador
      // via o progresso no card num portal e numa lista de jobs no outro.
      // O envio agora é UM fluxo para todos — quem varia é o endpoint lá
      // dentro, não a tela.
      setSubmitModal({ proposal: p, dryRun: false });
    },
    onWithdraw: () => { if (workspaceLiveProposal) handleWithdrawProposal(workspaceLiveProposal); },
    onViewJob: (job: any) => handleViewJob(job),
    onOpenAi: () => { if (workspaceLiveProposal?.item) openAiModal(workspaceLiveProposal.item); },
    onOpenPortal: () => {
      const it = workspaceLiveProposal?.item;
      if (!it) return;
      const link = portalLinkUrl(it);
      if (link) window.open(link.url, '_blank');
      else showToast('Sem link do portal para esta licitação', 'error');
    },
    onSyncPncp: async () => {
      const it = workspaceLiveProposal?.item;
      if (!it?.id) return;
      try {
        await procurementService.syncItemWithPncp(it.id);
        showToast('Dados atualizados pelo PNCP', 'success');
        await loadProposals();
      } catch (e: any) {
        showToast('Erro ao buscar no PNCP: ' + (e?.response?.data?.message || e?.message), 'error');
      }
    },
    onToggleMonitored: async (v: boolean) => {
      const it = workspaceLiveProposal?.item;
      if (!it?.id) return;
      try {
        await procurementService.toggleItemMonitored(it.id, v);
        showToast(v ? 'Monitoramento ativado' : 'Monitoramento desativado', 'success');
        await loadProposals();
      } catch (e: any) {
        showToast('Erro: ' + (e?.response?.data?.message || e?.message), 'error');
      }
    },

    onMarkResult: async (status: string) => {
      const it = workspaceLiveProposal?.item;
      if (!it?.id) return;
      const labels: Record<string, string> = { ganho: 'ganha', perdido: 'perdida', encerrada: 'encerrada', desistida: 'desistida' };
      if (!(await confirm({
        title: `Marcar esta licitação como ${labels[status] || status}?`,
        confirmLabel: 'Marcar',
        consequence: 'O item sai do acompanhamento e vai para o histórico.',
      }))) return;
      handleStatusChange(it.id, status);
    },
    onMarkSent: async () => {
      const p = workspaceLiveProposal;
      if (!p) return;
      if (!(await confirm({
        title: 'Marcar como enviada manualmente?',
        confirmLabel: 'Marcar como enviada',
        consequence: 'Isto NÃO envia nada ao portal: apenas registra no Hub que o cadastro já foi feito à mão. Fica marcada como envio manual na auditoria.',
      }))) return;
      try {
        await procurementService.marcarPropostaEnviada(p.id);
        showToast('Proposta marcada como enviada', 'success');
        await loadProposals();
      } catch (e: any) {
        showToast('Erro ao marcar: ' + (e?.response?.data?.message || e?.message), 'error');
      }
    },
    onArchive: async () => {
      const p = workspaceLiveProposal;
      if (!p) return;
      if (!p.archived && !(await confirm({
        title: 'Arquivar esta proposta?',
        confirmLabel: 'Arquivar',
        consequence: 'Sai da lista principal, mas continua acessível em "Arquivadas".',
      }))) return;
      try {
        await procurementService.archiveProposal(p.id, !p.archived);
        showToast(p.archived ? 'Proposta desarquivada' : 'Proposta arquivada', 'success');
        await loadProposals();
      } catch (e: any) {
        showToast('Erro ao arquivar: ' + (e?.response?.data?.message || e?.message), 'error');
      }
    },
    onDelete: () => { if (workspaceLiveProposal) { setWorkspaceProposalId(null); handleDeleteProposal(workspaceLiveProposal.id); } },
    onCreateDispute: () => { if (workspaceLiveProposal) handleEnviarParaDisputa(workspaceLiveProposal); },
    onOpenDispute: () => onNavigate?.('lic-disputar'),
    onAdequar: () => { if (workspaceLiveProposal) { setEditingProposal(workspaceLiveProposal); openAdequarModal(workspaceLiveProposal); } },
    onOpenGeneratePanel: () => { /* a seção Documentos do workspace tem o painel */ },
    showToast,
  }), [workspaceLiveProposal, ws, showToast]); // eslint-disable-line react-hooks/exhaustive-deps


  // Sem lista carregada (carregando ou falhou antes da primeira carga), os contadores não são zero: são "não sei".
  const semNumerosDePropostas = cargaDasPropostas.estado !== 'pronto' && proposals.length === 0;
  const renderProposalsTab = () => (
    <div>
      {/* Actions bar — "Nova Proposta" (selecionar do dropdown) removido: pra
          licitação do Encontrar cria-se a proposta do próprio Encontrar; pra nova,
          usa-se "Importar licitação" (por link/UASG ou sem edital). */}
      {/* Toolbar no padrão do guia §3b (ListToolbar): busca à esquerda,
          filtros, e UMA ação primária laranja à direita. Era artesanal, com o
          botão laranja à esquerda da busca — invertido em relação ao resto do HUB. */}
      <div className="mb-4">
        <ListToolbar
          search={(
            <ToolbarSearch
              value={proposalSearch}
              onChange={setProposalSearch}
              placeholder="Buscar por orgao, objeto..."
              widthClass="w-80"
            />
          )}
          filters={(
            <select
              value={proposalStatusFilter}
              onChange={(e) => setProposalStatusFilter(e.target.value)}
              className={`${campo} rounded-lg px-3 py-2 text-sm focus:outline-none`}
            >
              <option value="">Todos os status</option>
              {Object.entries(PROPOSAL_STATUSES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          )}
          actions={(
            <>
              <button
                onClick={() => setShowTemplatesModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors"
                title="Gerenciar templates de declaração"
              >
                <Icon name="document" className="w-4 h-4" /> Templates
              </button>
              <button
                onClick={() => { setImportModal(true); setImportInput(''); setImportPreview(null); setImportUasg(''); setImportNumero(''); setImportModalidade('pe'); setImportPortal('comprasnet'); setImportSemEdital(false); setImportOrgao(''); setImportObjeto(''); setImportValor(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors"
                title="Cadastrar uma licitação: por link do portal, UASG + número, ou sem edital publicado (oportunidade pré-edital)"
              >
                <Icon name="download" className="w-4 h-4" /> Importar licitação
              </button>
            </>
          )}
        />
      </div>

      {/* R1.13: Tabs de visualização — default mostra só Ativas. Histórico expandido sob demanda. */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1 bg-central-cartao border border-central-borda rounded-lg p-1">
          <button
            onClick={() => { setProposalViewMode('ativas'); setShowHistorico(false); }}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              proposalViewMode === 'ativas' && !showHistorico
                ? 'bg-central-elevado text-white font-semibold'
                : 'text-slate-400 hover:text-white hover:bg-central-elevado'
            }`}
          >
            Ativas <span className="opacity-70">({semNumerosDePropostas ? '—' : viewModeCounts.ativas})</span>
          </button>
          {/* Cotações (pré-edital): o órgão mandou o TR e pediu orçamento, sem
              edital publicado. Não há data para acompanhar, então não é "Ativa";
              mas também não é histórico, e por isso fica aqui na frente. A aba
              some quando não há nenhuma. A proposta volta sozinha para Ativas
              assim que o portal trouxer a data ou o nº do pregão for preenchido. */}
          {(viewModeCounts.cotacoes > 0 || proposalViewMode === 'cotacoes') && (
            <button
              onClick={() => { setProposalViewMode('cotacoes'); setShowHistorico(false); }}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                proposalViewMode === 'cotacoes'
                  ? 'bg-central-elevado text-white font-semibold'
                  : 'text-slate-400 hover:text-white hover:bg-central-elevado'
              }`}
              title="Orçamentos pedidos pelo órgão com base em TR, sem edital publicado. Voltam para Ativas assim que houver data ou número do pregão."
            >
              Cotações <span className="opacity-70">({viewModeCounts.cotacoes})</span>
            </button>
          )}
          {showHistorico && ([
            { id: 'encerradas', label: 'Encerradas', count: viewModeCounts.encerradas },
            { id: 'arquivadas', label: 'Arquivadas', count: viewModeCounts.arquivadas },
            { id: 'todas', label: 'Todas', count: viewModeCounts.todas },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setProposalViewMode(tab.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                proposalViewMode === tab.id
                  ? 'bg-central-elevado text-white font-semibold'
                  : 'text-slate-400 hover:text-white hover:bg-central-elevado'
              }`}
            >
              {tab.label} <span className="opacity-70">({tab.count})</span>
            </button>
          ))}
        </div>
        <button
          onClick={() => { setShowHistorico((v) => !v); if (showHistorico) setProposalViewMode('ativas'); }}
          className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-central-elevado transition-colors"
          title={showHistorico ? 'Esconder histórico' : `Ver histórico (${viewModeCounts.encerradas + viewModeCounts.arquivadas} propostas)`}
        >
          {showHistorico ? '← Esconder histórico' : `Histórico (${viewModeCounts.encerradas + viewModeCounts.arquivadas}) ▾`}
        </button>
        {/* Bulk archive na tab Encerradas: triagem em 1 clique pra esvaziar
            o histórico de items vencidos há mais de 30 dias. Items arquivados
            saem do funil principal mas continuam acessíveis em "Arquivadas". */}
        {showHistorico && proposalViewMode === 'encerradas' && viewModeCounts.encerradas > 0 && (
          <button
            onClick={async () => {
              const days = 30;
              const ok = await confirm({
                title: `Arquivar todas as propostas encerradas há mais de ${days} dias?`,
                confirmLabel: 'Arquivar',
                consequence: 'Elas saem do funil principal e continuam acessíveis em "Arquivadas".',
              });
              if (!ok) return;
              try {
                const res = await procurementService.bulkArchiveOldProposals(days);
                showToast(`${res.archived} propostas arquivadas`);
                loadProposals();
              } catch (e: any) {
                showToast('Erro ao arquivar em lote: ' + (e?.response?.data?.message || e?.message), 'error');
              }
            }}
            className="ml-auto text-xs px-2.5 py-1 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto transition-colors flex items-center gap-1.5"
            title="Arquivar todas as propostas com data de encerramento > 30 dias"
          >
            📦 Arquivar &gt;30d
          </button>
        )}
      </div>

      {/* Chips de prazo — escondidos em Cotações: lá nenhuma proposta tem data,
          e qualquer chip clicado só esvaziaria a lista. */}
      {proposalViewMode !== 'cotacoes' && (
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <span className="text-[10px] text-slate-500 uppercase font-medium mr-1">Prazo:</span>
        {([
          { id: '', label: 'Qualquer' },
          { id: 'hoje', label: 'Encerra hoje' },
          { id: '3d', label: 'Em 3 dias' },
          { id: 'semana', label: 'Esta semana' },
          { id: 'custom', label: 'Personalizado' },
        ] as const).map((chip) => (
          <button
            key={chip.id || 'all'}
            onClick={() => setPrazoFilter(chip.id as any)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
              prazoFilter === chip.id
                ? 'bg-central-elevado border-central-borda text-white font-semibold'
                : 'bg-central-cartao border-central-borda text-slate-400 hover:text-slate-200'
            }`}
          >
            {chip.label}
          </button>
        ))}
        {prazoFilter === 'custom' && (
          <div className="flex items-center gap-1 ml-1">
            <input
              type="date"
              value={prazoCustomStart}
              onChange={(e) => setPrazoCustomStart(e.target.value)}
              className={`rounded px-2 py-0.5 text-[11px] ${campo}`}
            />
            <span className="text-slate-500 text-[11px]">até</span>
            <input
              type="date"
              value={prazoCustomEnd}
              onChange={(e) => setPrazoCustomEnd(e.target.value)}
              className={`rounded px-2 py-0.5 text-[11px] ${campo}`}
            />
          </div>
        )}
      </div>
      )}

      {/* Agenda do Google: "quais estão na agenda" de um olhar. Um chip por
          estado; "Na agenda" aparece sempre, os outros só quando existem. */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <span className="text-[10px] text-slate-500 uppercase font-medium mr-1">Agenda:</span>
        {(['na_agenda', 'marcado_sem_evento', 'fora'] as const)
          .filter((k) => k === 'na_agenda' || agendaCounts[k] > 0 || agendaFilter === k)
          .map((k) => {
            const s = SELO_DA_AGENDA[k];
            const ativo = agendaFilter === k;
            return (
              <button
                key={k}
                onClick={() => setAgendaFilter(ativo ? '' : k)}
                title={s.dica}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                  ativo ? `${s.cor} ring-1 ring-white/40 font-semibold` : 'bg-central-cartao border-central-borda text-slate-400 hover:text-slate-200'
                }`}
              >
                {s.icone} {s.rotulo} <span className="opacity-70">({agendaCounts[k]})</span>
              </button>
            );
          })}
      </div>

      {/* Summary — Fase 2 padrão visual (guia §2): stat cards no modelo fiscal
          (cor no número, alerta por borda, filtro ativo por ring). Eram pills
          coloridas cheias na horizontal. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        {[
          { key: '', label: proposalMetrics.total === 1 ? 'Proposta ativa' : 'Propostas ativas', value: proposalMetrics.total, alert: false, tip: 'Todas as propostas ativas' },
          // "Precisam reenviar" era falso: a regra conta RASCUNHO, que nunca foi
          // ao portal. Reenviar pressupõe um envio que não houve.
          { key: 'needsAction', label: proposalMetrics.needsAction === 1 ? 'Precisa de ação' : 'Precisam de ação', value: proposalMetrics.needsAction, alert: proposalMetrics.needsAction > 0, tip: 'Rascunho ainda não enviado, ou automação que falhou' },
          { key: 'completed', label: proposalMetrics.completed === 1 ? 'Concluída' : 'Concluídas', value: proposalMetrics.completed, alert: false, tip: 'Enviadas e com o certame já encerrado' },
          { key: 'waiting', label: 'Aguardando', value: proposalMetrics.waiting, alert: false, tip: 'Enviadas, aguardando sessão/julgamento' },
        ].map((c) => {
          const ativo = c.key === '' ? !metaFilter : metaFilter === c.key;
          return (
            // L4 (24/10/2026): o CartaoDoTopo; número branco; a barra vermelha só em "Precisam de ação" quando há.
            <CartaoDoTopo
              key={c.label}
              rotulo={c.label}
              valor={semNumerosDePropostas ? '—' : c.value}
              ativo={ativo}
              barra={c.alert ? 'danger' : null}
              onClick={() => setMetaFilter(c.key === '' ? '' : (metaFilter === c.key ? '' : (c.key as '' | 'needsAction' | 'completed' | 'waiting')))}
              title={c.tip}
            />
          );
        })}
      </div>

      {/* R1.18: seção destacada "Encerra hoje/amanhã" — prioriza o urgente.
          Fora de Cotações: a faixa conta prazo, e lá não existe prazo nenhum —
          os chips só levariam a uma lista vazia. */}
      {proposalViewMode !== 'cotacoes' && (() => {
        const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
        const endToday = new Date(); endToday.setHours(23, 59, 59, 999);
        const endTomorrow = new Date(endToday.getTime() + 24 * 60 * 60 * 1000);
        const active = proposals.filter((p: any) => !p.archived);
        const hoje = active.filter((p: any) => {
          const enc = p.item?.dataEncerramento; if (!enc) return false;
          const t = new Date(enc).getTime();
          return t >= nowTick && t <= endToday.getTime();
        });
        const amanha = active.filter((p: any) => {
          const enc = p.item?.dataEncerramento; if (!enc) return false;
          const t = new Date(enc).getTime();
          return t > endToday.getTime() && t <= endTomorrow.getTime();
        });
        if (hoje.length === 0 && amanha.length === 0) return null;
        return (
          <div className="mb-4 flex items-center gap-3 flex-wrap bg-red-500/10 border border-red-500/30 rounded-central px-4 py-2.5">
            <Icon name="clock" className="w-4 h-4 text-red-300" />
            <span className="text-xs font-semibold text-red-200">Prazos urgentes:</span>
            {hoje.length > 0 && (
              <button
                onClick={() => setPrazoFilter(prazoFilter === 'hoje' ? '' : 'hoje')}
                className={`text-xs px-2.5 py-1 rounded-full bg-red-500/25 text-red-200 border transition-all hover:bg-red-500/40 ${prazoFilter === 'hoje' ? 'border-red-400 ring-1 ring-red-400/50' : 'border-red-500/40'}`}
              >
                {hoje.length} {hoje.length === 1 ? 'encerra hoje' : 'encerram hoje'}
              </button>
            )}
            {amanha.length > 0 && (
              <button
                onClick={() => setPrazoFilter(prazoFilter === '3d' ? '' : '3d')}
                className="text-xs px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/40 transition-all hover:bg-amber-500/30"
              >
                {amanha.length} {amanha.length === 1 ? 'encerra amanhã' : 'encerram amanhã'}
              </button>
            )}
          </div>
        );
      })()}

      {/* Bulk action bar — aparece quando >=1 proposta selecionada via checkbox */}
      {selectedProposalIds.size > 0 && (
        <div className="flex items-center justify-between mb-3 px-4 py-2 bg-central-elevado border border-central-borda rounded-lg">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-white font-medium">{selectedProposalIds.size} proposta(s) selecionada(s)</span>
            <button
              onClick={() => setSelectedProposalIds(new Set())}
              className="text-slate-400 hover:text-white text-xs"
            >
              limpar seleção
            </button>
          </div>
          <button
            onClick={handleBulkDeleteProposals}
            className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 rounded text-sm font-medium transition-colors flex items-center gap-1.5"
          >
            <Icon name="trash" className="w-3.5 h-3.5" />
            Deletar {selectedProposalIds.size} proposta{selectedProposalIds.size > 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Explica o balde: sem isto "Cotações" seria só mais uma aba sem critério visível. */}
      {proposalViewMode === 'cotacoes' && (
        <div className="mb-3 px-4 py-2.5 bg-central-cartao border border-central-borda rounded-lg text-xs text-slate-300">
          <strong>Pré-edital.</strong> Orçamentos pedidos pelo órgão com base em TR: sem número de compra e sem
          data de sessão, não há prazo para acompanhar. Cada uma volta sozinha para <strong>Ativas</strong> quando
          o portal publicar a data ou quando o nº do pregão for preenchido nos dados da licitação.
        </div>
      )}

      {/* Proposals table */}
      {/* Carregando, erro ou vazio de verdade (design/EstadoDaLista). "Nenhuma proposta cadastrada" só com a carga
          pronta; com a API fora a tela diz que não conseguiu carregar. "Nenhuma encontrada" é o filtro, sobre dado real. */}
      <EstadoDaLista
        carga={cargaDasPropostas}
        temDados={proposals.length > 0}
        oQue="as propostas"
        onTentarDeNovo={loadProposals}
        vazio={<div className="text-center py-16 text-slate-400">Nenhuma proposta cadastrada</div>}
      >
      {filteredProposals.length === 0 ? (
        <div className="text-center py-16 text-slate-400">Nenhuma proposta encontrada</div>
      ) : (
        <>
        {/* R1.15 Fase B: sort bar + cards (mantém <table> internamente pra preservar 470 linhas de ações condicionais sem risco — visual de cards via CSS table:block) */}
        <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filteredProposals.length > 0 && selectedProposalIds.size === filteredProposals.length}
              ref={(el) => { if (el) el.indeterminate = selectedProposalIds.size > 0 && selectedProposalIds.size < filteredProposals.length; }}
              onChange={toggleAllProposalsSelected}
              className="rounded border-slate-500 bg-slate-700"
            />
            <span className="uppercase tracking-wide">Selecionar tudo</span>
          </label>
          <span className="opacity-30">·</span>
          <span className="uppercase tracking-wide">Ordenar:</span>
          <button onClick={() => toggleProposalSort('orgaoNome')} className={`hover:text-slate-200 ${proposalSortBy === 'orgaoNome' ? 'text-white font-semibold' : ''}`}>
            Órgão {proposalSortBy === 'orgaoNome' && (proposalSortOrder === 'ASC' ? '↑' : '↓')}
          </button>
          <button onClick={() => toggleProposalSort('totalValue')} className={`hover:text-slate-200 ${proposalSortBy === 'totalValue' ? 'text-white font-semibold' : ''}`}>
            Valor {proposalSortBy === 'totalValue' && (proposalSortOrder === 'ASC' ? '↑' : '↓')}
          </button>
          <button onClick={() => toggleProposalSort('status')} className={`hover:text-slate-200 ${proposalSortBy === 'status' ? 'text-white font-semibold' : ''}`}>
            Status {proposalSortBy === 'status' && (proposalSortOrder === 'ASC' ? '↑' : '↓')}
          </button>
          <button onClick={() => toggleProposalSort('dataEncerramento')} className={`hover:text-slate-200 ${proposalSortBy === 'dataEncerramento' ? 'text-white font-semibold' : ''}`}>
            Próximo evento {proposalSortBy === 'dataEncerramento' && (proposalSortOrder === 'ASC' ? '↑' : '↓')}
          </button>
        </div>
        <table className="w-full text-sm proposal-cards-table">
          <tbody>
              {filteredProposals.map((p: any) => {
                const st = PROPOSAL_STATUSES[p.status] || PROPOSAL_STATUSES.rascunho;
                const latestJob = getLatestJob(p.id);
                const jobSt = latestJob ? (AUTOMATION_STATUSES[latestJob.status] || AUTOMATION_STATUSES.queued) : null;
                const modBadge = p.item?.modalidadeNome?.includes('Pregão') ? 'PE'
                  : p.item?.modalidadeNome?.includes('Dispensa') ? 'DE'
                  : p.item?.modalidadeNome?.includes('Concorrência') ? 'CC'
                  : p.item?.modalidadeNome ? p.item.modalidadeNome.substring(0, 4) : '';
                // L4 (24/10/2026): a linha não é mais pintada pelo status da proposta (azul enviada, verde aceita,
                // vermelho rejeitada…), como o Monitorar deixou de pintar pela fase: o selo de status já diz.
                const rowStyle: React.CSSProperties = {};
                const isExpanded = expandedProposalId === p.id;
                // A "borda por modalidade" (R1.15) saiu em 24/10/2026: borda de <tr> não é desenhada numa tabela
                // border-separate (.proposal-cards-table), então ela nunca aparecia. A modalidade está no selo (M2).
                return (
                <React.Fragment key={p.id}>
                  <tr
                    className={`group transition-colors ${p.archived ? 'opacity-50' : ''} cursor-pointer ${isExpanded ? 'proposta-aberta' : ''}`}
                    style={rowStyle}
                    onClick={(e) => {
                      // não abrir se clicou em botão/link/input
                      const target = e.target as HTMLElement;
                      if (target.closest('button, a, input, [role="button"]')) return;
                      // W-F2: a linha abre a PÁGINA DE TRABALHO (antes expandia o
                      // drawer de detalhe, que segue disponível no menu ⋮).
                      openWorkspace(p);
                    }}
                  >
                    <td className="px-3 py-2 max-w-[340px]" title="Clique para abrir a proposta">
                      {/* R1.15: tag bar inline com modalidade — consistência visual com Disputar */}
                      <div className="flex items-center gap-1 mb-1">
                        <input
                          type="checkbox"
                          checked={selectedProposalIds.has(p.id)}
                          onChange={(e) => { e.stopPropagation(); toggleProposalSelection(p.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="mr-1 rounded border-slate-500 bg-slate-700 cursor-pointer"
                          title="Selecionar para ação em lote"
                        />
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium border ${
                          modBadge === 'PE' ? 'bg-blue-500/15 text-blue-300 border-blue-700/40'
                          : modBadge === 'DE' ? 'bg-purple-500/15 text-purple-300 border-purple-700/40'
                          : 'bg-slate-500/15 text-slate-300 border-slate-600/40'
                        }`}>
                          {modBadge || '?'} {modBadge === 'PE' ? 'Pregão' : modBadge === 'DE' ? 'Dispensa' : (p.item?.modalidadeNome?.split(' ')[0] || '')}
                        </span>
                        {p.item?.srp && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300 border border-slate-600/40 font-medium" title="Sistema de Registro de Preços">SRP</span>
                        )}
                        {(() => {
                          const sb = getStructureBadge(p);
                          if (!sb) return null;
                          return <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${sb.color}`} title={sb.full}>{sb.short}</span>;
                        })()}
                        {(() => {
                          const md = getModoDisputaBadge(p.item?.modoDisputa);
                          if (!md) return null;
                          return <span className={`text-[9px] px-1.5 py-0.5 rounded border border-transparent font-medium ${md.color}`} title={`Modo de disputa: ${md.full}`}>{md.short}</span>;
                        })()}
                        {p.item?.pcpInversaoFase && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-600/40 font-medium whitespace-nowrap shrink-0" title="Inversão de fase: o portal exige Documentos de Habilitação antes de liberar a Proposta de Preços. O envio automático salva Declarações + Info Complementares; o resto (habilitação) é manual.">↻ Inversão</span>
                        )}
                        {p.item?.pcpGarantiaContratual && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-600/40 font-medium whitespace-nowrap shrink-0" title="Exige Comprovante de Seguro Garantia (apólice). Precisa anexar manualmente no portal.">🛡 Seguro</span>
                        )}
                        {p.archived && (
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded bg-slate-600/50 text-slate-300 border border-slate-500 whitespace-nowrap shrink-0"
                            title="Proposta arquivada — abra o menu (⋮) e clique em Desarquivar para restaurar"
                          >
                            📁 Arquivada
                          </span>
                        )}
                        {/* Status terminal — proposta cadastrada mas a licitação mudou de estado. "Revogada": o
                            órgão anulou (decidir arquivar). A SUSPENSÃO não tem selo aqui desde 08/10/2026: ela mora
                            só na coluna de status, que diz quem suspendeu (utils/suspensaoDaLicitacao). Aqui havia um
                            segundo "Suspensa" na mesma linha, dizendo "PNCP suspendeu" até em item de ComprasBR. */}
                        {p.item?.status === 'revogada' && (
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-600/40 whitespace-nowrap shrink-0"
                            title="Órgão revogou/anulou a licitação. Decida se arquiva a proposta (menu ⋮)."
                          >
                            ❌ Revogada
                          </span>
                        )}
                        {/* Adiada por AVISO DE TEXTO, sem prazo novo — o portal não
                            registrou o evento, então nem status nem data mudaram e sem
                            este selo a linha ia para "Encerradas" no dia seguinte. */}
                        {isProrrogadoSemData(p.item) && (
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-600/40 whitespace-nowrap shrink-0"
                            title={`Sessão adiada sem data nova. Aviso do portal: "${String(p.item?.situacaoCompra?.ultimoAviso?.mensagem || '').slice(0, 220)}" — fica em Ativas até o órgão publicar o novo prazo, que o robô detecta sozinho.`}
                          >
                            ⏸️ Adiada sem data
                          </span>
                        )}
                        {(() => {
                          const e = estadoDaAgenda(p.item);
                          if (!e) return null;
                          const s = SELO_DA_AGENDA[e];
                          const motivo = e === 'marcado_sem_evento' ? agendaMotivos[p.item.id] : null;
                          return (
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap shrink-0 ${s.cor}`}
                              title={motivo ? `${s.dica} Motivo: ${motivo}` : s.dica}
                            >
                              {s.icone} {s.rotulo}
                            </span>
                          );
                        })()}
                        {p.item?.status === 'aguardando_julgamento' && (
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300 border border-slate-600/40 whitespace-nowrap shrink-0"
                            title="Proposta enviada, aguardando julgamento do órgão."
                          >
                            ⏳ Aguardando
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="text-slate-200 text-xs font-medium truncate">{getOrgaoDisplay(p.item) || '—'}</div>
                        {p.item?.id && (unreadCounts[p.item.id] || 0) > 0 && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 bg-slate-600/30 text-white rounded-full text-[10px] font-bold">{unreadCounts[p.item.id]}</span>
                        )}
                      </div>
                      <div className="text-slate-500 text-[10px] truncate">{p.item?.objetoCompra || ''}</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.item?.portalOrigem && PORTAL_MAP[p.item.portalOrigem] ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${PORTAL_MAP[p.item.portalOrigem].color}`}>{PORTAL_MAP[p.item.portalOrigem].label}</span>
                      ) : p.item?.portalOrigem ? <span className="text-[10px] text-slate-500">{p.item.portalOrigem}</span> : p.item ? (
                        <button
                          type="button"
                          data-portal-nao-registrado
                          onClick={(e) => { e.stopPropagation(); openDetail(p.item); }}
                          className="text-[10px] text-slate-400 underline decoration-dotted hover:text-white"
                          title="O portal desta licitação não está registrado no HUB. Clique para abrir a licitação e escolher o portal."
                        >
                          não registrado
                        </button>
                      ) : <span className="text-[10px] text-slate-500">—</span>}
                    </td>
                    {/* R1.18: numeroCompra pode ou não já conter "/ano" (varia por origem).
                        Nº e UASG são os dois dados que se digitam à mão pra achar a compra
                        no portal — clicar copia (e não abre a proposta). */}
                    <td className="px-3 py-2 text-center text-xs text-slate-300">
                      {(() => {
                        const num = formatNumeroCompraComAno(p.item?.numeroCompra, p.item?.anoCompra);
                        if (num === '—') return <span className="text-slate-600">—</span>;
                        return (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); copiarDaLinha(num, 'Nº da compra'); }}
                            className="px-1 py-0.5 rounded hover:bg-central-elevado hover:text-white transition-colors"
                            title="Clique para copiar o nº da compra"
                          >
                            {num}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-400">
                      {p.item?.codigoUnidade ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); copiarDaLinha(String(p.item.codigoUnidade), 'UASG'); }}
                          className="px-1 py-0.5 rounded hover:bg-central-elevado hover:text-white transition-colors"
                          title="Clique para copiar a UASG"
                        >
                          {p.item.codigoUnidade}
                        </button>
                      ) : '—'}
                    </td>
                    {/* R1.15: Mod. agora aparece como tag bar na primeira coluna */}
                    <td className="hidden" aria-hidden></td>
                    <td className="px-3 py-2 text-right text-xs text-white font-medium">{fmt(p.totalValue)}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {/* SELO DE ENVIO (26/10/2026, S1/V1/P1 do Heitor): um selo só, em duas linhas, no lugar da pílula
                            Rascunho/Enviada e da pílula de 9 px da via (C-4), que não se percebia. Enviada é verde (feito é
                            estado); não enviada é âmbar com menos de 24 h; o robô que falhou é vermelho, com o motivo. O clique
                            abre a prova. Status sem envio a dizer (aceita, rejeitada…) segue com a pílula de antes. Regras em
                            utils/seloDeEnvio.ts. */}
                        {(() => {
                          const selo = seloDoEnvio({ proposal: p, jobs: automationJobs });
                          if (selo.estado === 'outro') {
                            return <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.color}`}>{st.label}</span>;
                          }
                          return (
                            <button
                              type="button"
                              data-selo-de-envio={selo.estado}
                              onClick={(e) => { e.stopPropagation(); setProvaDoEnvio({ proposal: p, selo }); }}
                              className={`flex flex-col items-start text-left px-2 py-1 rounded-lg border min-w-[150px] max-w-[230px] hover:brightness-125 transition ${TONE_BADGE[selo.tom]} ${TONE_BORDER[selo.tom]}`}
                              title={`${selo.titulo} — ${selo.linha2}. Clique para ver a prova do envio.`}
                            >
                              <span className="text-[11px] font-semibold leading-tight">{selo.titulo}</span>
                              <span className="text-[10px] text-slate-300/80 leading-tight truncate max-w-full">{selo.linha2}</span>
                            </button>
                          );
                        })()}
                        {p.item?.aiAnalysis?.criticidadeGeral && (
                          <span
                            title={`Análise IA: criticidade ${p.item.aiAnalysis.criticidadeGeral}${p.item.aiAnalysis.pontosDeAtencao?.length ? ` — ${p.item.aiAnalysis.pontosDeAtencao.length} pontos de atenção` : ''}`}
                            className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                              p.item.aiAnalysis.criticidadeGeral === 'alta' ? 'bg-red-500/20 text-red-300' :
                              p.item.aiAnalysis.criticidadeGeral === 'media' ? 'bg-yellow-500/20 text-yellow-300' :
                              'bg-slate-600/30 text-slate-300'
                            }`}
                          >
                            IA {p.item.aiAnalysis.criticidadeGeral === 'alta' ? '⚠' : p.item.aiAnalysis.criticidadeGeral === 'media' ? '◐' : '✓'}
                          </span>
                        )}
                        {/* O aviso que precisa pegar ANTES de abrir qualquer coisa: este
                            objeto se executa com GENTE, e a régua de preço é outra. */}
                        {p.item?.aiAnalysis?.execucao?.forneceMaoDeObra === true && (
                          <span
                            title={`Execução com mão de obra própria${p.item.aiAnalysis.execucao.janelaAtendimento ? ` — ${p.item.aiAnalysis.execucao.janelaAtendimento}` : ''}. O custo tem folha; não precifique como software.`}
                            className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-amber-500/20 text-amber-300"
                          >
                            👥 mão de obra
                          </span>
                        )}
                        {(p as any)._docsCount > 0 && (
                          <span title={`${(p as any)._docsCount} doc(s) gerado(s)`} className="text-[9px] px-1 py-0.5 rounded bg-slate-600/30 text-slate-300">
                            {(p as any)._docsCount} doc{(p as any)._docsCount > 1 ? 's' : ''}
                          </span>
                        )}
                        {(p as any).item?.monitored && (
                          <span title="Monitorando mensagens">
                            <svg className="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                      {(() => {
                        const sc = p.item?.situacaoCompra;
                        // Só bloqueia quando a mensagem é Suspensão/Revogação/Anulação.
                        // Eventos de Alteração/Esclarecimento também setam
                        // permitirInclusao=false no portal, mas não impedem o cadastro.
                        if (sc && isSituacaoBlockingEvent(sc)) {
                          const reabertura = sc.ultimoAviso?.previsaoReabertura;
                          const overdue = isSituacaoOverdue(sc);
                          const shortLabel = overdue
                            ? (reabertura ? `⛔ Venc. ${reabertura.slice(0, 5)}` : '⛔ Vencida')
                            : (reabertura ? `⚠ Susp. ${reabertura.slice(0, 5)}` : '⚠ Suspensa');
                          // Quem suspendeu e desde quando (08/10/2026): o órgão, no portal, na data do aviso.
                          const susp = suspensaoDaLicitacao(p.item);
                          const desde = susp?.fonte === 'portal' && susp.desde ? fmtDateTime(susp.desde) : null;
                          const fullTooltip = overdue
                            ? `Vencida — previsão ${reabertura} já passou. Portal ainda bloqueado. Clique no refresh para re-verificar.`
                            : `Suspensa pelo órgão no portal${desde ? ` desde ${desde}` : ''}${reabertura ? ` — reabertura prevista ${reabertura}` : ''}. ${sc.ultimoAviso?.mensagem || 'Cadastro bloqueado pelo portal'}`;
                          return (
                            <>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full cursor-help inline-flex items-center gap-1 whitespace-nowrap ${
                                overdue ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                              }`}
                              title={fullTooltip}
                              data-suspensa={susp?.fonte}
                            >
                              {!overdue && !reabertura && susp?.fonte === 'portal' ? '⚠ Suspensa pelo órgão' : shortLabel}
                              {p.item?.id && (
                                <button
                                  onClick={(e) => handleRefreshSituacao(p.item.id, e)}
                                  disabled={refreshingSituacaoId === p.item.id}
                                  className="text-current hover:opacity-70 disabled:opacity-40 ml-0.5"
                                  title="Verificar status no portal agora"
                                >
                                  <svg className={`w-3 h-3 ${refreshingSituacaoId === p.item.id ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                              )}
                            </span>
                            {desde && !overdue && (
                              <span className="text-[9px] text-slate-400 whitespace-nowrap" title={fullTooltip} data-suspensa-desde>
                                desde {desde}
                              </span>
                            )}
                            </>
                          );
                        }
                        const sit = p.item?.situacao || p.item?.status || '';
                        const itemStatus = p.item?.status || '';
                        if (itemStatus === 'suspensa' || /suspens/i.test(sit)) {
                          // Só o status do item diz suspensa: veio da sincronização do PNCP, sem aviso do portal no
                          // HUB. O title diz isso, em vez de afirmar quem suspendeu.
                          return (
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-amber-500/20 text-amber-400 cursor-help"
                              title="O PNCP informa esta licitação como suspensa; o portal não mandou aviso ao HUB, então não há motivo nem data aqui. Proposta ativa, aguardando reabertura: o sistema detecta sozinho."
                              data-suspensa="pncp"
                            >
                              Suspensa
                            </span>
                          );
                        }
                        if (itemStatus === 'revogada' || /revogad|anulad|desert|fracassad/i.test(sit)) {
                          return <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-red-500/20 text-red-400">{sit.split(' ')[0] || 'Revogada'}</span>;
                        }
                        if (/evento\s*pendente/i.test(sit)) {
                          return <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-amber-500/20 text-amber-400">Pendente</span>;
                        }
                        if (/encerrad/i.test(sit)) {
                          return <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-slate-500/20 text-slate-400">Encerrada</span>;
                        }
                        if (/homologad/i.test(sit)) {
                          return <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-slate-600/30 text-slate-300">Homologada</span>;
                        }
                        return <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-slate-600/30 text-slate-300">Aberta</span>;
                      })()}
                      {/* Avisos NÃO APURADOS (07/10/2026): o robô nunca coletou o quadro de avisos deste item, e
                          "nenhuma marca" aqui parecia "o órgão não publicou nada". Cinza, informação, não alarme:
                          são ~60 itens com proposta, de todos os portais menos o ComprasNet. Ver utils/avisosDoPortal. */}
                      {avisosNaoApurados(p.item) && (
                        <span className="text-[10px] text-slate-500 italic whitespace-nowrap cursor-help" title={EXPLICACAO_NAO_APURADO} data-avisos="nao-apurados">
                          {TEXTO_NAO_APURADO}
                        </span>
                      )}
                      </div>
                    </td>
                    {/* R1.15: SRP agora aparece como badge inline na primeira coluna */}
                    <td className="hidden" aria-hidden></td>
                    <td className="px-3 py-2 text-left text-[10px] whitespace-nowrap">
                      {/* 2 janelas distintas: Proposta (entrega = dataEncerramento) e
                          Sessão/Lances (dataSessaoInicio→Fim). Em Pregão sem dataSessaoInicio,
                          o início da disputa = dataEncerramento (mesmo momento) — derivado. */}
                      {(() => {
                        const enc = p.item?.dataEncerramento ? new Date(p.item.dataEncerramento) : null;
                        const ss = p.item?.dataSessaoInicio ? new Date(p.item.dataSessaoInicio) : null;
                        const sf = p.item?.dataSessaoFim ? new Date(p.item.dataSessaoFim) : null;
                        const isPregao = /preg/i.test(p.item?.modalidadeNome || '');
                        const sessIni = ss || (isPregao && enc ? enc : null);
                        const sessDerived = !ss && !!sessIni;
                        const fmtHM = (x: Date) => x.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
                        // UX 08/08: dd/mm sem ano (padrão do Encontrar) — ano no tooltip
                        const fmtDM = (x: Date) => x.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
                        const prorrog = getProrrogacao(p.item);
                        const showProrrog = !!prorrog && !isLicitacaoOver(p.item);
                        if (!enc && !sessIni) return <span className="text-slate-600">—</span>;
                        return (
                          <div className="flex flex-col items-start gap-0.5">
                            {enc && <span className="text-slate-300" title={`Prazo para envio da proposta: ${fmtDateTime(enc)}`}>📄 Prop. até {fmtDM(enc)} {fmtHM(enc)}</span>}
                            {sessIni && (
                              <span className={sessDerived ? 'text-slate-400 italic' : 'text-slate-200'} title={sessDerived ? 'Pregão: início da disputa = fim das propostas (derivado)' : `Sessão pública / envio de lances: ${fmtDateTime(sessIni)}`}>
                                ⚖️ Sessão {fmtDM(sessIni)} {fmtHM(sessIni)}{sf ? `–${fmtHM(sf)}` : (sessDerived ? ' ≈' : '')}
                              </span>
                            )}
                            {showProrrog && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40" title={`Prorrogada: ${fmtDateTime(prorrog!.from)} → ${fmtDateTime(prorrog!.to)}`}>⏱ Prorrogada</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] text-slate-300 whitespace-nowrap">
                      {(() => {
                        // Countdown pro PRÓXIMO EVENTO (proposta OU sessão, o que vier
                        // primeiro) — não só o fim da proposta. 📄=proposta, ⚖️=sessão.
                        const ne = proposalNextEventTs(p.item);
                        if (!ne) return null;
                        const now = nowTick; // R1.18: ligado ao tick de 1s → countdown vivo
                        const encT = ne.ts;
                        const diffMs = encT - now;
                        const diffH = Math.floor(diffMs / (1000 * 60 * 60));
                        // `days` por DIA DE CALENDÁRIO (não floor(horas/24)).
                        const encDate = new Date(encT);
                        const nowDate = new Date(now);
                        const startEnc = new Date(encDate.getFullYear(), encDate.getMonth(), encDate.getDate()).getTime();
                        const startNow = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()).getTime();
                        const days = Math.round((startEnc - startNow) / (1000 * 60 * 60 * 24));
                        const ic = ne.kind === 'sessao' ? '⚖️' : '📄';
                        let label = '', cls = '';
                        // Licitação suspensa: o prazo também está. Contar "6d" para ele era afirmar uma data que o
                        // órgão tirou do calendário (14713, 08/10/2026). A contagem volta quando reabrir.
                        const suspensa = !!suspensaoDaLicitacao(p.item);
                        // "Encerrada" em vermelho é a leitura ERRADA de um adiamento: o
                        // prazo que venceu é o velho, e a licitação segue viva.
                        if (suspensa) { label = '⏸ prazo suspenso'; cls = TONE_BADGE.warning; }
                        else if (isProrrogadoSemData(p.item, now)) { label = '⏸ sem data'; cls = 'bg-amber-500/20 text-amber-300'; }
                        else if (!ne.future || diffMs < 0) { label = 'Encerrada'; cls = 'bg-red-500/20 text-red-400'; }
                        else if (diffH < 12) {
                          const totalSec = Math.floor(diffMs / 1000);
                          const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0');
                          const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
                          const ss = String(totalSec % 60).padStart(2, '0');
                          label = `${ic} ${hh}:${mm}:${ss}`; cls = 'bg-red-500/30 text-red-200 tabular-nums';
                        }
                        else if (diffH < 24) { label = `${ic} ${diffH}h`; cls = 'bg-red-500/20 text-red-300'; }
                        else if (days === 1) { label = `${ic} amanhã`; cls = 'bg-slate-600/30 text-slate-300'; }
                        else if (days <= 3) { label = `${ic} ${days}d`; cls = 'bg-slate-600/30 text-slate-300'; }
                        else if (days <= 7) { label = `${ic} ${days}d`; cls = 'bg-slate-600/30 text-slate-300'; }
                        else if (days <= 30) { label = `${ic} ${days}d`; cls = 'bg-slate-600/30 text-slate-300'; }
                        else { label = `${ic} ${Math.floor(days / 7)}sem`; cls = 'bg-slate-600/30 text-slate-400'; }
                        return (
                          <span
                            className={`mt-0.5 inline-block text-[9px] px-1.5 py-0.5 rounded font-medium ${cls}`}
                            title={suspensa
                              ? 'Licitação suspensa: o prazo também está. A contagem volta quando o órgão reabrir.'
                              : ne.kind === 'sessao' ? 'Próximo evento: sessão/lances' : 'Próximo evento: encerramento da proposta'}
                            data-contagem={suspensa ? 'suspensa' : 'correndo'}
                          >
                            {label}
                          </span>
                        );
                      })()}
                      {(() => {
                        // Portal suspenso — a data atual está obsoleta e a nova só é
                        // conhecida após a reabertura. Sinaliza a previsão publicada no
                        // aviso do Quadro Informativo (ultimoAviso.previsaoReabertura).
                        const prev = p.item?.situacaoCompra?.ultimoAviso?.previsaoReabertura;
                        const blocked = isSituacaoBlockingEvent(p.item?.situacaoCompra);
                        if (!blocked || !prev) return null;
                        return (
                          <div
                            className="text-[9px] text-amber-400 mt-0.5 cursor-help"
                            title="Data atual está obsoleta — nova data será publicada pelo ComprasNet na reabertura"
                          >
                            Nova data ~{prev.slice(0, 5)}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(() => {
                        const noActiveJob = !latestJob || !['queued', 'running'].includes(latestJob.status);
                        const scrapeJob = automationJobs.find((j: any) => j.proposalId === p.id && j.action === 'scrape_proposal');
                        // Allow re-scrape whenever the proposal is still open, the portal can be
                        // identified, and nothing else is running. We don't require a historical
                        // scrape_proposal job because the Nova Proposta flow scrapes before the
                        // proposal exists (proposalId=null), so the history link is missing.
                        const hasKnownPortal = !!(p.item?.portalOrigem || p.item?.linkOrigem);
                        const canRescrape = hasKnownPortal && noActiveJob && !['enviada', 'aceita'].includes(p.status);
                        const hasItems = p.proposalItems && p.proposalItems.length > 0;
                        const isEncerrada = p.item?.dataEncerramento && new Date(p.item.dataEncerramento) < new Date();
                        const isMenuOpen = openProposalMenu === p.id;

                        // W-F5: a linha mostra em que ESTÁGIO a proposta está
                        // (mesma máquina de estados da página de trabalho), em vez
                        // de um botão que mudava de nome e função conforme 7 guards.
                        const stageState = deriveProposalStage({
                          proposal: p,
                          item: p.item,
                          latestJob,
                          generatedDocs: null,
                        });
                        const jobBadge = latestJob && ['queued', 'running'].includes(latestJob.status)
                          ? { label: latestJob.action === 'scrape_proposal' ? 'Carregando…' : 'Executando…', cls: TONE_BADGE.live }
                          : latestJob && ['failed', 'needs_manual'].includes(latestJob.status)
                            ? { label: latestJob.status === 'needs_manual' ? 'Ação manual' : 'Falhou', cls: TONE_BADGE.danger }
                            : null;
                        const primaryBtn = (
                          <button
                            onClick={(e) => { e.stopPropagation(); openWorkspace(p); }}
                            className={`px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-opacity hover:opacity-80 ${jobBadge ? jobBadge.cls : TONE_BADGE[STAGE_TONE[stageState.current]]}`}
                            title={jobBadge ? 'Automação em andamento — clique para abrir' : `${STAGE_LABELS[stageState.current]}${stageState.summary[stageState.current] ? ` · ${stageState.summary[stageState.current]}` : ''}${stageState.blocked ? ` · ${stageState.blocked.reason}` : ''}`}
                          >
                            {jobBadge ? jobBadge.label : STAGE_LABELS[stageState.current]}
                            {!jobBadge && stageState.blocked && <span className="ml-1">⚠</span>}
                          </button>
                        );


                        return (
                          <div className="flex items-center justify-center gap-1">
                            {(() => {
                              const link = portalLinkUrl(p.item);
                              if (!link) return null;
                              return (
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-400 hover:text-white transition-colors" title={link.title}>
                                  <Icon name="externalLink" className="w-3.5 h-3.5" />
                                </a>
                              );
                            })()}
                            {/* Mandar pra disputa / abrir a disputa já criada.
                                Fica FIXO na linha (não no hover) porque é o
                                sinal que substituiu o cron que criava a sessão
                                sozinha: proposta enviada com sessão marcada e
                                sem robô precisa gritar na lista. */}
                            {p.disputeSessionId ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); onNavigate?.('lic-disputar'); }}
                                className="p-1 text-slate-300 hover:text-white hover:bg-central-elevado rounded transition-colors"
                                title="Disputa criada no Hub — abrir a tela Disputar"
                              >
                                <Icon name="target" className="w-3.5 h-3.5" />
                              </button>
                            ) : faltaMandarParaDisputa(p) ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEnviarParaDisputa(p); }}
                                className="p-1 text-amber-400 hover:text-amber-200 hover:bg-amber-500/20 rounded transition-colors"
                                title="Proposta enviada e sessão marcada, mas SEM disputa no Hub — clique para mandar para a tela Disputar"
                              >
                                <Icon name="target" className="w-3.5 h-3.5" />
                              </button>
                            ) : null}
                            {/* Quick actions: aparecem só ao hover na linha */}
                            <div className="hidden md:flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={async (e) => { e.stopPropagation(); await openAiModal(p.item); }}
                                className="p-1 text-slate-400 hover:text-white hover:bg-central-elevado rounded transition-colors"
                                title={p.item?.aiAnalysisAt ? 'Ver Análise IA salva' : 'Analisar Edital com IA'}
                              >
                                <Icon name="zap" className="w-3.5 h-3.5" />
                              </button>
                              {/* "Abrir" saiu daqui (23/08): a linha inteira já abre a
                                  proposta no clique, e o badge de estágio ao lado também
                                  — três alvos pra mesma ação. */}
                            </div>
                            {primaryBtn}

                            {/* Dropdown menu */}
                            <div className="relative proposal-menu-container">
                              <button
                                onClick={() => setOpenProposalMenu(isMenuOpen ? null : p.id)}
                                className="p-1 text-slate-400 hover:text-slate-200 transition-colors rounded hover:bg-central-elevado"
                                title="Mais acoes"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <circle cx="10" cy="4" r="1.5" />
                                  <circle cx="10" cy="10" r="1.5" />
                                  <circle cx="10" cy="16" r="1.5" />
                                </svg>
                              </button>
                              {isMenuOpen && (
                                <div className="absolute right-0 top-full mt-1 z-50 bg-central-cartao border border-central-borda rounded-lg shadow-xl py-1 min-w-[180px]">

                                  {/* 🚀 Enviar/Reenviar proposta ao portal — substitui o botão da coluna Ações */}
                                  {p.item?.id && hasItems && noActiveJob && !isEncerrada && ['rascunho', 'enviada', 'desistida'].includes(p.status) && (() => {
                                    const portalBlocked = isSituacaoBlockingEvent(p.item?.situacaoCompra);
                                    const label = p.status === 'rascunho' ? 'Enviar pro portal' : 'Reenviar pro portal';
                                    return (
                                      <button
                                        onClick={() => {
                                          setOpenProposalMenu(null);
                                          if (portalBlocked) {
                                            showToast(`Cadastro bloqueado pelo ComprasNet — ${p.item?.situacaoCompra?.ultimoAviso?.mensagem || 'evento pendente de publicação'}`, 'error');
                                            return;
                                          }
                                          setAutomationModal(p);
                                          setAutomationPortalId(findPortalIdBySource(p.item?.portalOrigem || null, p.item?.linkOrigem || null));
                                          setAutomationDryRun(false);
                                        }}
                                        disabled={portalBlocked}
                                        className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 ${
                                          portalBlocked
                                            ? 'text-slate-500 cursor-not-allowed'
                                            : 'text-slate-300 hover:bg-central-elevado'
                                        }`}
                                        title={portalBlocked ? 'Bloqueado: portal com evento pendente de publicação' : label}
                                      >
                                        <Icon name="upload" className="w-3.5 h-3.5 text-slate-400" />
                                        🚀 {label}
                                      </button>
                                    );
                                  })()}

                                  {/* 🎯 Mandar para disputa — a criação da sessão é
                                      MANUAL desde 01/09/2026 (ver handleEnviarParaDisputa). */}
                                  {p.item?.id && /preg[ãa]o|dispensa/i.test(String(p.item?.modalidadeNome || '')) && (
                                    <button
                                      onClick={() => {
                                        setOpenProposalMenu(null);
                                        handleEnviarParaDisputa(p);
                                      }}
                                      className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                      title={p.disputeSessionId ? 'Já existe sessão — abre a tela Disputar' : 'Cria a sessão de disputa e abre a tela Disputar'}
                                    >
                                      <Icon name="target" className={`w-3.5 h-3.5 ${p.disputeSessionId ? 'text-slate-400' : 'text-amber-400'}`} />
                                      🎯 {p.disputeSessionId ? 'Abrir na tela Disputar' : 'Mandar para disputa'}
                                    </button>
                                  )}

                                  {/* Desistir via extension (substitui legacy withdrawProposal Playwright/CDP) */}
                                  {p.item?.id && p.status === 'enviada' && noActiveJob && !isEncerrada && (
                                    <button
                                      onClick={async () => {
                                        setOpenProposalMenu(null);
                                        await handleWithdrawProposal(p);
                                      }}
                                      className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                    >
                                      <Icon name="x-circle" className="w-3.5 h-3.5 text-slate-400" />
                                      🗑️ Desistir Compra
                                    </button>
                                  )}

                                  {/* Monitor messages toggle — controla item.monitored (o que
                                      o cron de coleta realmente checa), não mais o monitorMessages
                                      da proposta (que ficava dessincronizado). */}
                                  {p.status === 'enviada' && (p as any).item?.id && (
                                    <button
                                      onClick={async () => {
                                        setOpenProposalMenu(null);
                                        const isMon = !!(p as any).item?.monitored;
                                        try {
                                          await procurementService.toggleItemMonitored((p as any).item.id, !isMon);
                                          showToast(isMon ? 'Monitoramento desativado' : 'Monitoramento de mensagens ativado');
                                          loadProposals();
                                        } catch (e: any) {
                                          showToast('Erro ao alterar monitoramento: ' + (e.response?.data?.message || e.message), 'error');
                                        }
                                      }}
                                      className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                    >
                                      <Icon name="bell" className={`w-3.5 h-3.5 ${(p as any).item?.monitored ? 'text-white' : 'text-slate-400'}`} />
                                      {(p as any).item?.monitored ? 'Desativar monitoramento' : 'Monitorar mensagens'}
                                    </button>
                                  )}

                                  {/* Agenda do Google (ADMIN). Pôr: mesmo sem proposta
                                      enviada. Tirar: cancela o evento e mantém fora. */}
                                  {isAdmin && p.item?.id && (() => {
                                    const e = estadoDaAgenda(p.item);
                                    const ocupado = agendaBusyItemId === p.item.id;
                                    return (
                                      <>
                                        {e !== 'na_agenda' && (
                                          <button
                                            disabled={ocupado}
                                            onClick={async () => { setOpenProposalMenu(null); await handleMarcarAgenda(p); }}
                                            className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2 disabled:opacity-50"
                                            title={e === 'marcado_sem_evento'
                                              ? 'Já marcado. Tenta de novo criar o evento (por exemplo, depois de corrigir a data da sessão)'
                                              : 'Põe na agenda do Google mesmo sem proposta enviada'}
                                          >
                                            <Icon name="calendar" className="w-3.5 h-3.5 text-slate-400" />
                                            {ocupado ? 'Pondo na agenda…' : e === 'marcado_sem_evento' ? 'Tentar criar o evento' : 'Pôr na agenda'}
                                          </button>
                                        )}
                                        {e !== 'fora' && (
                                          <button
                                            disabled={ocupado}
                                            onClick={async () => { setOpenProposalMenu(null); await handleRemoverAgenda(p); }}
                                            className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2 disabled:opacity-50"
                                            title="Cancela o evento no Google e mantém o item fora da agenda"
                                          >
                                            <Icon name="calendar" className="w-3.5 h-3.5 text-slate-400" />
                                            {ocupado ? 'Tirando da agenda…' : 'Tirar da agenda'}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}

                                  {/* Arquivar/Desarquivar */}
                                  <button
                                    onClick={async () => {
                                      setOpenProposalMenu(null);
                                      // Confirmar só ao arquivar — desarquivar é reversível e de baixo risco.
                                      if (!p.archived) {
                                        const orgao = getOrgaoDisplay(p.item) || p.title || 'esta proposta';
                                        if (!(await confirm({
                                          title: `Arquivar "${orgao.substring(0, 60)}"?`,
                                          confirmLabel: 'Arquivar',
                                          consequence: 'Sai da lista principal, mas continua acessível em "Arquivadas".',
                                        }))) return;
                                      }
                                      try {
                                        await procurementService.archiveProposal(p.id, !p.archived);
                                        loadProposals();
                                        showToast(p.archived ? 'Proposta desarquivada' : 'Proposta arquivada', 'success');
                                      } catch {}
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                  >
                                    <Icon name="archive" className="w-3.5 h-3.5" />
                                    {p.archived ? 'Desarquivar' : 'Arquivar'}
                                  </button>

                                  {/* Divider before delete */}
                                  <div className="border-t border-central-borda my-1" />

                                  {/* Marcar resultado — quick action pra triagem manual rápida.
                                      Atalho pros 4 status finais sem precisar abrir o modal de detalhes.
                                      handleStatusChange dispara cleanupOnFinalStatus no backend
                                      (revoga propostas, fecha disputes, monitored=false). */}
                                  <div className="border-t border-central-borda my-1" />
                                  <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">Marcar resultado</div>
                                  <button
                                    onClick={async () => {
                                      setOpenProposalMenu(null);
                                      if (await confirm({
                                        title: 'Registrar que GANHAMOS esta licitação?',
                                        confirmLabel: 'Registrar',
                                        consequence: 'Grava o desfecho e encerra o acompanhamento — o item sai do Cadastrar e do Monitorar.',
                                      })) {
                                        // D-III: duas gravações porque são duas
                                        // perguntas. O desfecho vai para a coluna
                                        // própria; o status só encerra o
                                        // acompanhamento. Antes isto escrevia
                                        // `status = 'ganho'`, valor depreciado que
                                        // virava `homologado` e apagava o placar.
                                        await marcarDesfechoEEncerrar(p.itemId, 'ganho');
                                      }
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                  >
                                    ✅ Ganhei
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setOpenProposalMenu(null);
                                      if (await confirm({
                                        title: 'Registrar que PERDEMOS esta licitação?',
                                        confirmLabel: 'Marcar',
                                        consequence: 'Grava o desfecho e encerra o acompanhamento — o item sai do Cadastrar e do Monitorar.',
                                      })) {
                                        await marcarDesfechoEEncerrar(p.itemId, 'perdido');
                                      }
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                  >
                                    ❌ Perdi
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setOpenProposalMenu(null);
                                      if (await confirm({
                                        title: 'Marcar como encerrada, sem ganhar nem perder?',
                                        confirmLabel: 'Marcar',
                                        consequence: 'Útil quando a licitação foi homologada mas a IPCOM não venceu.',
                                      })) {
                                        handleStatusChange(p.itemId, 'encerrada');
                                      }
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                  >
                                    📋 Encerrada (sem ganhar)
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setOpenProposalMenu(null);
                                      if (await confirm({
                                        title: 'Marcar como desistida?',
                                        confirmLabel: 'Marcar',
                                        consequence: 'Usado quando desistimos antes da sessão, por exemplo edital com cláusula impossível.',
                                      })) {
                                        handleStatusChange(p.itemId, 'desistida');
                                      }
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-central-elevado flex items-center gap-2"
                                  >
                                    🚪 Desisti
                                  </button>
                                  <div className="border-t border-central-borda my-1" />
                                  {/* Excluir — abre modal de confirmação com opções (lixeira/encontrar + monitorar) */}
                                  <button
                                    onClick={() => {
                                      setOpenProposalMenu(null);
                                      handleDeleteProposal(p.id);
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-xs text-red-400 hover:bg-central-elevado flex items-center gap-2"
                                  >
                                    <Icon name="trash" className="w-3.5 h-3.5" />
                                    Excluir
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                </React.Fragment>
                );
              })}
          </tbody>
        </table>
        </>
      )}
      </EstadoDaLista>

    </div>
  );

  // ==================== Proposal Modal ====================
  const renderProposalModal = () => {
    if (!proposalModal) return null;
    // CRIAR proposta é só a porta de entrada: escolher portal e licitação. Todo
    // o trabalho (precificar itens, termos, documentos, PDF) vive na página de
    // trabalho, que é o padrão do módulo desde 11/08. Este drawer sobreviveu ao
    // cutover do workspace só para a criação, e mantê-lo com as três abas
    // deixava dois formulários concorrentes para a mesma coisa.
    const comprasnetPortals = portals.filter((p: any) => p.system === 'comprasnet' || p.system === 'todos');
    return (
      <>
      {/* DRAWER lateral (P1 UX 04/07): substitui o modal centrado — mesma
          linguagem do CRM. Painel expansível de trás fecha ao abrir (effect).
          Desde a casca de gaveta (05/10/2026): é FORMULÁRIO, então clique fora e
          Esc NÃO fecham. Antes o clique no fundo fechava e jogava fora o que foi
          digitado (closeProposalModal limpa todo o estado). Sai pelo × ou pelo
          Cancelar; a primária desceu para o rodapé, como em toda casca. */}
      <CascaDeGaveta
        titulo="Nova Proposta"
        subtitulo={selectedProposalItemInfo ? (
          <>
            {getOrgaoDisplay(selectedProposalItemInfo) || ''}
            {selectedProposalItemInfo.numeroCompra ? ` — ${formatNumeroCompraComAno(selectedProposalItemInfo.numeroCompra, selectedProposalItemInfo.anoCompra)}` : ''}
            {selectedProposalItemInfo.dataEncerramento ? ` · Enc: ${fmtDateTime(selectedProposalItemInfo.dataEncerramento)}` : ''}
          </>
        ) : undefined}
        onClose={closeProposalModal}
        corpoClassName="p-4 space-y-4"
        rodape={<>
          <button onClick={closeProposalModal} className="px-4 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleCreateProposal}
            disabled={!proposalForm.title || !proposalForm.itemId}
            title="Criar e abrir"
            className="ml-auto flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            Criar e abrir
          </button>
        </>}
      >
            {/* Top row: Portal + Licitação */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-300 block mb-1">Portal</label>
                <PortalAccountSelect
                  value={proposalPortalId}
                  onChange={setProposalPortalId}
                  accounts={comprasnetPortals}
                />
              </div>
              <div>
                <label className="text-sm text-slate-300 block mb-1">Licitacao *</label>
                <select
                  value={proposalForm.itemId}
                  onChange={(e) => {
                    setProposalForm({ ...proposalForm, itemId: e.target.value });
                    const selected = proposalItems.find((i: any) => String(i.id) === e.target.value);
                    if (selected) {
                      // Auto-generate title
                      if (!proposalForm.title) {
                        setProposalForm(f => ({ ...f, itemId: e.target.value, title: `Proposta — ${(getOrgaoDisplay(selected) || '').substring(0, 80)}` }));
                      }
                      // Auto-select portal
                      const autoPortalId = findPortalIdBySource(selected.portalOrigem, selected.linkOrigem);
                      if (autoPortalId) setProposalPortalId(autoPortalId);
                      // Popula o card de info da licitação — sem isso os campos
                      // número/ano/UASG/CNPJ não aparecem no fluxo Nova Proposta
                      // e o onBlur (updateItemDetails) nunca dispara (sem .id).
                      setSelectedProposalItemInfo({
                        id: selected.id,
                        orgaoNome: selected.orgaoNome,
                        unidadeNome: selected.unidadeNome,
                        objetoCompra: selected.objetoCompra,
                        modalidade: selected.modalidadeNome || (selected.modalidadeId ? MODALIDADES[selected.modalidadeId] : ''),
                        valorEstimado: selected.valorEstimado,
                        processoSei: selected.processoSei || '',
                        criterioJulgamento: selected.criterioJulgamento || '',
                        modoDisputa: selected.modoDisputa || '',
                        orgaoCnpj: selected.orgaoCnpj || '',
                        codigoUnidade: selected.codigoUnidade || '',
                        numeroCompra: selected.numeroCompra || '',
                        anoCompra: selected.anoCompra || '',
                        dataAbertura: selected.dataAbertura || null,
                        dataEncerramento: selected.dataEncerramento || null,
                        dataSessaoInicio: selected.dataSessaoInicio || null,
                      });
                    } else {
                      setSelectedProposalItemInfo(null);
                    }
                  }}
                  className={`w-full ${campo} rounded-lg px-3 py-2 text-sm`}
                >
                  <option value="">Selecione a licitacao...</option>
                  {proposalItems.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      [{getStatusInfo(item.status).label}] {(getOrgaoDisplay(item) || '').substring(0, 40)} — {(item.objetoCompra || '').substring(0, 50)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Info card (when item selected) */}
            {selectedProposalItemInfo && (
              <div className="bg-central-elevado rounded-lg p-3 border border-central-borda">
                <div className="text-xs text-slate-500 uppercase mb-1">Informacoes da Licitacao</div>
                <div className="text-sm text-white font-medium">{getOrgaoDisplay(selectedProposalItemInfo) || '—'}</div>
                <div className="flex items-center gap-4 mt-1 flex-wrap">
                  {selectedProposalItemInfo.modalidade && (
                    <div className="text-xs text-slate-500">Modalidade: {selectedProposalItemInfo.modalidade}</div>
                  )}
                  {selectedProposalItemInfo.valorEstimado != null && (
                    <div className="text-xs text-slate-500">Valor Estimado: <span className="text-white font-medium">{fmt(selectedProposalItemInfo.valorEstimado)}</span></div>
                  )}
                </div>
                {/* Campos editáveis para declarações + sync PNCP */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Processo SEI / Administrativo</label>
                    <input
                      type="text"
                      value={selectedProposalItemInfo.processoSei || ''}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, processoSei: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { processoSei: e.target.value }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                      placeholder="200.083808/2025"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Critério de Julgamento</label>
                    <input
                      type="text"
                      value={selectedProposalItemInfo.criterioJulgamento || ''}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, criterioJulgamento: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { criterioJulgamento: e.target.value }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                      placeholder="MENOR PREÇO"
                    />
                  </div>
                  {/*
                    Julgamento por lote x global (soma). O portal NÃO expõe isso
                    e o critério do PNCP ("Menor preço") não distingue: no
                    PE 021/2026 a BLL mostrava 3 lotes com um 1º colocado cada e
                    o edital julgava pela SOMA — ganhamos o lote 1 e ficamos em
                    2º na licitação. Marcado aqui à mão, é o que autoriza o HUB
                    a somar os lotes pra apurar a classificação real.
                  */}
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Julgamento dos lotes</label>
                    <select
                      value={selectedProposalItemInfo.julgamentoAgrupamento || 'por-lote'}
                      onChange={(e) => {
                        const v = e.target.value === 'global' ? 'global' : null;
                        setSelectedProposalItemInfo((prev: any) => ({ ...prev, julgamentoAgrupamento: v }));
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { julgamentoAgrupamento: v }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                      title="Global = o edital soma os lotes pra classificar (menor preço global). Confira no edital ou no chat do pregoeiro."
                    >
                      <option value="por-lote">Por lote (cada lote decide o seu)</option>
                      <option value="global">Global — soma dos lotes</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Nº da Compra <span className="text-slate-600 text-[9px]">(número / ano)</span></label>
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={selectedProposalItemInfo.numeroCompra || ''}
                        onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, numeroCompra: e.target.value }))}
                        onBlur={(e) => {
                          if (selectedProposalItemInfo.id) {
                            // Manda "numero/ano" — backend (splitNumeroAno) separa e grava certo
                            const num = e.target.value;
                            const ano = selectedProposalItemInfo.anoCompra;
                            procurementService.updateItemDetails(selectedProposalItemInfo.id, { numeroCompra: ano ? `${num}/${ano}` : num }).catch(() => {});
                          }
                        }}
                        className={`flex-1 rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                        placeholder="90052"
                      />
                      <input
                        type="text"
                        value={selectedProposalItemInfo.anoCompra || ''}
                        onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, anoCompra: e.target.value }))}
                        onBlur={(e) => {
                          if (selectedProposalItemInfo.id) {
                            const ano = e.target.value;
                            const num = selectedProposalItemInfo.numeroCompra;
                            procurementService.updateItemDetails(selectedProposalItemInfo.id, { numeroCompra: ano ? `${num}/${ano}` : num }).catch(() => {});
                          }
                        }}
                        className={`w-16 rounded px-2 py-1 text-xs outline-none transition-colors text-center ${campo}`}
                        placeholder="ano"
                        title="Ano da licitação"
                        maxLength={4}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">CNPJ do Órgão <span className="text-slate-300">*</span> <span className="text-slate-600 text-[9px]">(p/ busca PNCP)</span></label>
                    <input
                      type="text"
                      value={selectedProposalItemInfo.orgaoCnpj || ''}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, orgaoCnpj: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          // Normaliza: remove pontuação se houver
                          const clean = e.target.value.replace(/[^\d]/g, '');
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { orgaoCnpj: clean }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campoComEstado(!selectedProposalItemInfo.orgaoCnpj && 'border-amber-500/40')}`}
                      placeholder="06.279.103/0001-19"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">UASG / Código da Unidade</label>
                    <input
                      type="text"
                      value={selectedProposalItemInfo.codigoUnidade || ''}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, codigoUnidade: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { codigoUnidade: e.target.value }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                      placeholder="925129"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Abertura</label>
                    <input
                      type="datetime-local"
                      value={(() => {
                        // R1.13: datetime-local não tem TZ — formata em BRT local pra evitar bug UTC
                        if (!selectedProposalItemInfo.dataAbertura) return '';
                        const d = new Date(selectedProposalItemInfo.dataAbertura);
                        const pad = (n: number) => String(n).padStart(2, '0');
                        const fmtInBRT = d.toLocaleString('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
                        // en-CA dá "YYYY-MM-DD, HH:MM" — converte pra "YYYY-MM-DDTHH:MM"
                        return fmtInBRT.replace(', ', 'T');
                      })()}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, dataAbertura: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { dataAbertura: e.target.value || undefined }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Encerramento</label>
                    <input
                      type="datetime-local"
                      value={(() => {
                        if (!selectedProposalItemInfo.dataEncerramento) return '';
                        const d = new Date(selectedProposalItemInfo.dataEncerramento);
                        const fmtInBRT = d.toLocaleString('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
                        return fmtInBRT.replace(', ', 'T');
                      })()}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, dataEncerramento: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { dataEncerramento: e.target.value || undefined }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Abertura da sessão pública</label>
                    <input
                      type="datetime-local"
                      value={(() => {
                        if (!selectedProposalItemInfo.dataSessaoInicio) return '';
                        const d = new Date(selectedProposalItemInfo.dataSessaoInicio);
                        const fmtInBRT = d.toLocaleString('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
                        return fmtInBRT.replace(', ', 'T');
                      })()}
                      onChange={(e) => setSelectedProposalItemInfo((prev: any) => ({ ...prev, dataSessaoInicio: e.target.value }))}
                      onBlur={(e) => {
                        if (selectedProposalItemInfo.id) {
                          procurementService.updateItemDetails(selectedProposalItemInfo.id, { dataSessaoInicio: e.target.value || undefined }).catch(() => {});
                        }
                      }}
                      className={`w-full rounded px-2 py-1 text-xs outline-none transition-colors ${campo}`}
                    />
                    <div className="text-[9px] text-slate-500 mt-0.5">Início da disputa/lances. No ComprasNet, "Sincronizar do Portal" sobrescreve com o valor oficial.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Na criação, os itens aparecem em LEITURA: o operador precisa ver
                o que está levando pra proposta (e perceber se veio vazio), sem
                que isso vire um segundo formulário de precificação — precificar
                é o estágio Montagem da página de trabalho. */}
            {(
              <div className="bg-central-elevado/40 rounded-lg border border-central-borda p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-slate-500 uppercase">
                    Itens que vão para a proposta{proposalItemsForm.length > 0 ? ` (${proposalItemsForm.length})` : ''}
                  </div>
                  {proposalItemsForm.length > 0 && (
                    <div className="text-xs text-slate-400">
                      Referência do edital: <span className="text-white font-medium">{fmt(
                        proposalItemsForm.reduce((a: number, it: any) => a + (Number(it.estimatedPrice) || 0) * (Number(it.quantity) || 0), 0),
                      )}</span>
                    </div>
                  )}
                </div>
                {proposalItemsForm.length === 0 ? (
                  <div className="text-sm text-amber-300">
                    Nenhum item carregado. A proposta será criada mesmo assim e você pode puxar os itens
                    com “Sincronizar do portal” na página de trabalho.
                  </div>
                ) : (
                  <div className="divide-y divide-central-borda">
                    {proposalItemsForm.map((it: any, idx: number) => (
                      <div key={idx} className="flex items-baseline gap-3 py-1.5 text-sm">
                        <span className="text-slate-500 text-xs shrink-0 w-10">{it.itemNumber ?? idx + 1}</span>
                        <span className="text-slate-200 flex-1 min-w-0 truncate" title={it.description}>{it.description || '—'}</span>
                        <span className="text-slate-400 text-xs shrink-0">{it.quantity} {it.unit}</span>
                        <span className="text-slate-300 text-xs shrink-0 w-28 text-right">{fmt(it.estimatedPrice)}</span>
                      </div>
                    ))}
                    <div className="pt-2 text-xs text-slate-500">
                      Os preços são definidos na Montagem, depois de criar.
                    </div>
                  </div>
                )}
              </div>
            )}

      </CascaDeGaveta>
      </>
    );
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleDownloadCategoryZip = async (category: string) => {
    setDownloadingZip(category);
    try {
      await procurementService.downloadDocumentsZip(category);
      showToast('ZIP baixado com sucesso');
    } catch (e: any) {
      showToast('Erro ao baixar ZIP: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setDownloadingZip(null);
    }
  };

  // ==================== A linha do documento (27/10/2026, decisões do Heitor A1, C1, M1, D1) ====================
  // Antes: cinco ícones de 14 px sem texto (renovar, ver, arquivar, editar, excluir) e a versão nova escondida no Editar.
  // Agora: a ação de rotina à vista e com texto (Renovar na certidão, Nova versão no resto), o clique na linha abre o
  // documento, e o resto no ⋯ com o Excluir em vermelho. Soltar um arquivo em cima da linha também sobe a nova versão.
  const abrirEditarDoc = (doc: any) => {
    setDocForm({ name: doc.name, type: doc.type, category: doc.category || '', subcategory: doc.subcategory || '', description: doc.description || '', issueDate: doc.issueDate?.split('T')[0] || '', expiryDate: doc.expiryDate?.split('T')[0] || '', isActive: doc.isActive, noExpiry: !!doc.noExpiry });
    setEditingDoc(doc); setDocFile(null); setDocModal('edit');
  };

  const renovarPeloRobo = (doc: any) => {
    const url = RENEWAL_URLS[doc.type]?.url || doc.renewalUrl || '';
    if (doc.type === 'cndt') return void handleRenewCndt();
    if (doc.type === 'cicad_estadual') return void handleAutoRenew('cicad_estadual', procurementService.renewCicadEstadual, url);
    if (doc.type === 'cnd_tcu') return void handleAutoRenew('cnd_tcu', procurementService.renewCndTcu, url);
    if (doc.type === 'fgts') return void handleAutoRenew('fgts', procurementService.renewCrfFgts, url);
    setNovaVersao({ doc });
  };

  const acaoPrincipalDaLinha = (doc: any) => {
    const a = acaoPrincipalDoDocumento(doc);
    if (a.modo === 'robo') return renovarPeloRobo(doc);
    if (a.modo === 'site') {
      // Sem robô: o site do órgão numa aba e, aqui, a nova versão esperando o PDF emitido.
      const url = doc.renewalUrl || RENEWAL_URLS[doc.type]?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
    setNovaVersao({ doc });
  };

  const itensDoMenuDoDocumento = (doc: any): ItemDeMenu[] => {
    const site = doc.renewalUrl || RENEWAL_URLS[doc.type]?.url;
    const a = acaoPrincipalDoDocumento(doc);
    return [
      { label: 'Editar dados', onClick: () => abrirEditarDoc(doc) },
      ...(a.modo !== 'arquivo' ? [{ label: 'Subir a versão nova (arquivo)', onClick: () => setNovaVersao({ doc }) }] : []),
      ...(site ? [{ label: `Abrir o site (${doc.renewalLabel || RENEWAL_URLS[doc.type]?.label || 'emissão'})`, onClick: () => { window.open(site, '_blank', 'noopener,noreferrer'); } }] : []),
      { label: 'Histórico de versões', onClick: () => setHistoricoDoDoc(doc) },
      ...(doc.s3Key ? [{ label: 'Ver e link público', onClick: () => handleViewDocument(doc) }] : []),
      { label: doc.archived ? 'Desarquivar' : 'Arquivar', title: doc.archived ? undefined : 'Mantém no sistema, sem alertar', onClick: () => handleArchiveDocs([doc.id], !doc.archived) },
      { label: 'Excluir', danger: true, separadoAntes: true, onClick: () => setDeleteConfirm({ ...doc, _deleteType: 'document' }) },
    ];
  };

  /** C1 + drop: a linha abre o documento no clique e recebe o arquivo da nova versão (regra do HUB: toda zona de upload aceita drop). */
  const propsDaLinhaDoDocumento = (doc: any) => ({
    'data-linha-do-documento': doc.id,
    title: doc.s3Key ? 'Clique para ver o documento; solte um arquivo para subir a nova versão' : 'Solte um arquivo para subir a nova versão',
    onClick: () => { if (doc.s3Key) handleViewDocument(doc); },
    onDragOver: (e: React.DragEvent) => {
      if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
      e.preventDefault();
      if (linhaComArquivo !== doc.id) setLinhaComArquivo(doc.id);
    },
    onDragLeave: () => setLinhaComArquivo((v) => (v === doc.id ? null : v)),
    onDrop: (e: React.DragEvent) => {
      const f = e.dataTransfer?.files?.[0];
      if (!f) return;
      e.preventDefault();
      setLinhaComArquivo(null);
      setNovaVersao({ doc, arquivo: f });
    },
    className: `cursor-pointer hover:bg-central-elevado ${linhaComArquivo === doc.id ? 'ring-2 ring-inset ring-primary bg-central-elevado' : ''}`,
  });

  const acoesDaLinhaDoDocumento = (doc: any) => {
    const a = acaoPrincipalDoDocumento(doc);
    const ocupado = (a.modo === 'robo' && (renewingAuto === doc.type || (doc.type === 'cndt' && renewingCndt)));
    return (
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            data-acao-principal-do-documento={a.modo}
            onClick={() => acaoPrincipalDaLinha(doc)}
            disabled={ocupado}
            className="inline-flex items-center gap-1.5 min-h-[32px] px-3 py-1.5 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-xs font-medium whitespace-nowrap disabled:opacity-50"
            title={a.modo === 'robo' ? 'O robô emite e grava a certidão nova; a anterior fica guardada' : a.modo === 'site' ? 'Abre o site do órgão e, aqui, espera o PDF emitido' : 'Escolher o arquivo da versão nova'}
          >
            <Icon name={a.modo === 'arquivo' ? 'upload' : 'refresh'} className="w-3.5 h-3.5" />
            {ocupado ? 'Renovando…' : a.rotulo}
          </button>
          <MaisAcoes itens={itensDoMenuDoDocumento(doc)} title={`Mais ações de ${doc.name}`} />
        </div>
      </td>
    );
  };

  const renderDocRow = (doc: any) => {
    const expiry = getExpiryStatus(doc.expiryDate, doc.noExpiry);
    return (
      <tr key={doc.id} {...propsDaLinhaDoDocumento(doc)}>
        <td className="px-3 py-2">
          <div className="text-central-texto text-xs font-medium">{doc.name}</div>
          {doc.fileName && <div className="text-slate-500 text-xs">{linhaComArquivo === doc.id ? 'solte para subir a nova versão' : doc.fileName}</div>}
        </td>
        <td className="px-3 py-2 text-xs text-slate-300">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">{DOC_TYPES[doc.type] || doc.type}</span>
        </td>
        <td className="px-3 py-2 text-center text-xs text-slate-300">{fmtDate(doc.issueDate)}</td>
        <td className="px-3 py-2 text-center text-xs text-slate-300">{doc.noExpiry ? <span className="text-slate-500 italic">Nao se aplica</span> : fmtDate(doc.expiryDate)}</td>
        <td className="px-3 py-2 text-center">
          {doc.archived ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-600/40 text-slate-400">Arquivado</span>
          ) : expiry ? (
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${expiry.color}`}>{expiry.label}</span>
          ) : (
            <span className="text-xs text-slate-600">—</span>
          )}
        </td>
        {acoesDaLinhaDoDocumento(doc)}
      </tr>
    );
  };

  const renderDocTable = (docs: any[]) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-central-elevado">
            <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Nome</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Tipo</th>
            <th className="px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase">Emissao</th>
            <th className="px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase">Validade</th>
            <th className="px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase">Status</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-slate-400 uppercase w-48">Ações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-central-borda">
          {docs.map(renderDocRow)}
        </tbody>
      </table>
    </div>
  );

  const renderDocumentsTab = () => {
    // PILOTO do guia visual (docs/ui-guidelines.md §2, 08/08/2026 — modelo
    // fiscal-docs): superfície translúcida calma, cor no NÚMERO (não no fundo),
    // atenção via BORDA colorida (Vencidos>0), seleção via ring.
    // Sem nenhuma carga boa, "Vencidos 0" seria a afirmação mais perigosa da tela: certidão vencida passa por em dia.
    const semNumerosDeDocumentos = !cargaDosDocumentos.atualizadoEm && documents.length === 0;
    // 23/10/2026 (padrão das listas): o CartaoDoTopo; número branco; a barra no tom só no que pede ação (vencidos, vencendo).
    const cards: { key: string; label: string; value: number; barra: 'danger' | 'warning' | null }[] = [
      { key: '', label: 'Total', value: docStats.total, barra: null },
      { key: 'expired', label: 'Vencidos', value: docStats.expired, barra: docStats.expired > 0 ? 'danger' : null },
      { key: 'expiring', label: 'Vencendo (14d)', value: docStats.expiring, barra: docStats.expiring > 0 ? 'warning' : null },
      { key: 'valid', label: 'Em Dia', value: docStats.valid, barra: null },
      { key: 'no_expiry', label: 'Sem Validade', value: docStats.noExpiry, barra: null },
      { key: 'permanent', label: 'Nao se aplica', value: docStats.permanent, barra: null },
    ];

    const getCatStatus = (docs: any[]) => {
      let hasExpired = false, hasExpiring = false;
      const now = new Date();
      for (const d of docs) {
        if (d.noExpiry || !d.expiryDate) continue;
        const days = Math.ceil((new Date(d.expiryDate.slice(0, 10) + 'T12:00:00').getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (days < 0) hasExpired = true;
        else if (days <= 14) hasExpiring = true;
      }
      return { hasExpired, hasExpiring };
    };

    return (
      <div className="space-y-6">
        {/* Dashboard Cards — padrão fiscal-docs (guia §2) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {cards.map(c => (
            <CartaoDoTopo
              key={c.key}
              rotulo={c.label}
              valor={semNumerosDeDocumentos ? '—' : c.value}
              barra={semNumerosDeDocumentos ? null : c.barra}
              ativo={docStatusFilter === c.key}
              onClick={() => { setDocStatusFilter(docStatusFilter === c.key ? '' : c.key); setDocCategoryFilter(''); setDocTypeFilter(''); }}
            />
          ))}
        </div>

        {/* Urgent Docs Mini-list */}
        {docStats.urgentDocs.length > 0 && !docStatusFilter && (
          <div className="bg-central-cartao border border-red-600/40 rounded-central p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="alertTriangle" className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-red-400">Próximos a Vencer</span>
            </div>
            <div className="space-y-1">
              {docStats.urgentDocs.map((doc: any) => {
                const expiry = getExpiryStatus(doc.expiryDate, doc.noExpiry);
                return (
                  <div key={doc.id} className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-red-500/5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-white truncate">{doc.name}</span>
                      <span className="text-slate-500">{DOC_TYPES[doc.type] || doc.type}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {expiry && <span className={`px-1.5 py-0.5 rounded-full ${expiry.color}`}>{expiry.label}</span>}
                      {(RENEWAL_URLS[doc.type] || doc.renewalUrl) && (
                        <a href={doc.renewalUrl || RENEWAL_URLS[doc.type]?.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white" title="Renovar">
                          <Icon name="refresh" className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="search" className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={docSearch}
              onChange={e => setDocSearch(e.target.value)}
              placeholder="Buscar por nome, tipo ou descricao..."
              className={`w-full rounded-lg pl-9 pr-3 py-2 text-sm placeholder-slate-500 ${campo}`}
            />
          </div>
          <select
            value={docCategoryFilter}
            onChange={e => setDocCategoryFilter(e.target.value)}
            className={`rounded-lg px-3 py-2 text-sm ${campo}`}
          >
            <option value="">Todas categorias</option>
            {Object.entries(DOC_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            <option value="_sem_categoria">Sem Categoria</option>
          </select>
          <select
            value={docTypeFilter}
            onChange={e => setDocTypeFilter(e.target.value)}
            className={`rounded-lg px-3 py-2 text-sm ${campo}`}
          >
            <option value="">Todos tipos</option>
            {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="flex items-center bg-central-cartao border border-central-borda rounded-lg overflow-hidden">
            <button
              onClick={() => setDocViewMode('folders')}
              className={`p-2 transition-colors ${docViewMode === 'folders' ? 'bg-central-elevado text-white font-semibold' : 'text-slate-400 hover:text-white'}`}
              title="Visao Pastas"
            >
              <Icon name="folder" className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDocViewMode('list')}
              className={`p-2 transition-colors ${docViewMode === 'list' ? 'bg-central-elevado text-white font-semibold' : 'text-slate-400 hover:text-white'}`}
              title="Visao Lista"
            >
              <Icon name="list" className="w-4 h-4" />
            </button>
          </div>
          {docStats.archived > 0 && (
            <button
              onClick={() => setShowArchivedDocs(!showArchivedDocs)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors border ${showArchivedDocs ? 'bg-central-elevado border-central-borda text-white font-semibold' : 'bg-central-cartao border-central-borda text-slate-400 hover:text-white'}`}
              title="Mostrar/ocultar documentos arquivados"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
              {showArchivedDocs ? 'Ocultar' : 'Arquivados'} ({docStats.archived})
            </button>
          )}
          <div className="relative">
            <input type="file" ref={renewFileRef} onChange={handleRenewFileSelected} accept=".pdf" className="hidden" />
            <button
              onClick={() => setShowRenewalMenu(!showRenewalMenu)}
              disabled={renewingCndt || !!renewingAuto || !!renewingUpload}
              className="flex items-center gap-1.5 px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50 rounded-lg text-sm transition-colors"
              title="Renovar certidoes"
            >
              {(renewingCndt || !!renewingAuto || !!renewingUpload) ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="refresh" className="w-3.5 h-3.5" />}
              Renovar Certidao
              <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showRenewalMenu && (
              <div className="absolute right-0 top-full mt-1 bg-central-cartao border border-central-borda rounded-lg shadow-xl z-50 min-w-[260px] py-1">
                <div className="px-4 py-1 text-[10px] text-slate-400 uppercase tracking-wider">Robo emite sozinho (cron diario)</div>
                <button
                  onClick={() => { setShowRenewalMenu(false); handleRenewCndt(); }}
                  disabled={renewingCndt}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingCndt ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="refresh" className="w-3 h-3 text-emerald-400" />}
                  CNDT (Trabalhista)
                  <span className="ml-auto text-[10px] text-slate-400">auto</span>
                </button>
                <button
                  onClick={() => handleAutoRenew('cicad_estadual', procurementService.renewCicadEstadual, 'https://receita.pr.gov.br/certificado')}
                  disabled={renewingAuto === 'cicad_estadual'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingAuto === 'cicad_estadual' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="refresh" className="w-3 h-3 text-emerald-400" />}
                  CICAD Estadual (certificado)
                  <span className="ml-auto text-[10px] text-slate-400">auto</span>
                </button>
                <button
                  onClick={() => handleAutoRenew('cnd_tcu', procurementService.renewCndTcu, 'https://certidoes.apps.tcu.gov.br/emitir-certidao-contas-julgadas-irregulares')}
                  disabled={renewingAuto === 'cnd_tcu'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingAuto === 'cnd_tcu' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="refresh" className="w-3 h-3 text-emerald-400" />}
                  CND TCU (Contas Irregulares)
                  <span className="ml-auto text-[10px] text-slate-400">auto</span>
                </button>
                <button
                  onClick={() => handleAutoRenew('fgts', procurementService.renewCrfFgts, 'https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf')}
                  disabled={renewingAuto === 'fgts'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingAuto === 'fgts' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="refresh" className="w-3 h-3 text-emerald-400" />}
                  CRF/FGTS (Caixa)
                  <span className="ml-auto text-[10px] text-slate-400">auto</span>
                </button>
                <div className="border-t border-central-borda my-1" />
                <div className="px-4 py-1 text-[10px] text-slate-400 uppercase tracking-wider">Gerar boleto + enviar email</div>
                <button
                  onClick={() => handleGenerateGuia('certidao_falencia', procurementService.generateGuiaFalencia, 'https://www.tjpr.jus.br/custas-judiciais-e-taxa-judiciaria')}
                  disabled={renewingAuto === 'certidao_falencia'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingAuto === 'certidao_falencia' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                  Falencia e Concordata (TJPR)
                  <span className="ml-auto text-[10px] text-slate-400">boleto</span>
                </button>
                <div className="border-t border-central-borda my-1" />
                <div className="px-4 py-1 text-[10px] text-slate-400 uppercase tracking-wider">Abrir site + enviar PDF</div>
                <button
                  onClick={() => handleOpenAndUpload('certidao_negativa_estadual', 'https://cdwfazenda.paas.pr.gov.br/cdwportal/certidao/automatica')}
                  disabled={renewingUpload === 'certidao_negativa_estadual'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingUpload === 'certidao_negativa_estadual' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
                  CND Estadual (PR)
                </button>
                <button
                  onClick={() => handleOpenAndUpload('certidao_negativa_municipal', 'https://cascavel.atende.net/autoatendimento/servicos/certidao-negativa-de-debitos/detalhar/1')}
                  disabled={renewingUpload === 'certidao_negativa_municipal'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingUpload === 'certidao_negativa_municipal' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
                  CND Municipal (Cascavel)
                </button>
                <button
                  onClick={() => handleOpenAndUpload('cicad_municipal', 'https://cascavel.atende.net/autoatendimento/servicos/comprovante-de-inscricao-municipal/detalhar/1')}
                  disabled={renewingUpload === 'cicad_municipal'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingUpload === 'cicad_municipal' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
                  CICAD Municipal (Cascavel)
                </button>
                <button
                  onClick={() => handleOpenAndUpload('certidao_negativa_federal', 'https://servicos.receitafederal.gov.br/servico/certidoes/#/home/cnpj')}
                  disabled={renewingUpload === 'certidao_negativa_federal'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingUpload === 'certidao_negativa_federal' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
                  CND Federal (Receita)
                </button>
                {/* CND CGU saiu do grupo "auto" em 08/09/2026: o portal passou a abrir
                    hCaptcha de imagens depois do Consultar, inclusive por IP residencial.
                    O robo (POST /cnd-cgu) ainda existe e passa o WAF, mas para no captcha. */}
                <button
                  onClick={() => handleOpenAndUpload('cnd_cgu', 'https://certidoes.cgu.gov.br/')}
                  disabled={renewingUpload === 'cnd_cgu'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingUpload === 'cnd_cgu' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
                  CND CGU (Correcional)
                </button>
                <button
                  onClick={() => handleOpenAndUpload('certidao_junta_comercial', 'https://www.empresafacil.pr.gov.br/acoes/certidao')}
                  disabled={renewingUpload === 'certidao_junta_comercial'}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-central-elevado transition-colors flex items-center gap-2"
                >
                  {renewingUpload === 'certidao_junta_comercial' ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
                  Certidao Junta Comercial (Empresa Facil)
                </button>
                {/* CRF/FGTS (Caixa) movido pro grupo "auto" acima — 27/05/2026.
                    Backend renewCrfFgts → /crf-fgts HTTP puro via Mac mini SOCKS + pdfkit. */}
              </div>
            )}
          </div>
          <input type="file" ref={bulkInputRef} onChange={handleBulkImport} multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" />
          <button
            onClick={() => bulkInputRef.current?.click()}
            disabled={bulkImporting}
            className="flex items-center gap-1.5 px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50 rounded-lg text-sm transition-colors"
            // realce por ring, não por bg: um bg somado por classList briga em especificidade com o
            // fundo do próprio botão (regra do CLAUDE.md para zonas de drop)
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-primary'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-primary'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('ring-2', 'ring-primary');
              if (!bulkImporting && e.dataTransfer?.files?.length) importBulkFiles(e.dataTransfer.files);
            }}
          >
            {bulkImporting ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="upload" className="w-3.5 h-3.5" />}
            Importar
          </button>
          <button
            onClick={() => { setDocForm({ name: '', type: 'outro', category: '', subcategory: '', description: '', issueDate: '', expiryDate: '', isActive: true, noExpiry: false }); setDocFile(null); setEditingDoc(null); setDocModal('create'); }}
            className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors"
          >
            <Icon name="plus" className="w-4 h-4" /> Novo
          </button>
        </div>

        {/* Active Filters */}
        {(docSearch || docCategoryFilter || docTypeFilter || docStatusFilter) && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">{filteredDocuments.length} de {documents.length} documentos</span>
            <button onClick={() => { setDocSearch(''); setDocCategoryFilter(''); setDocTypeFilter(''); setDocStatusFilter(''); }} className="text-slate-300 hover:text-white underline">Limpar filtros</button>
          </div>
        )}

        {/* Content */}
        <EstadoDaLista
          carga={cargaDosDocumentos}
          temDados={documents.length > 0}
          oQue="os documentos"
          onTentarDeNovo={loadDocuments}
          vazio={<div className="text-center py-12 text-slate-400">Nenhum documento cadastrado</div>}
        >
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-8 text-slate-400">Nenhum documento encontrado com os filtros selecionados</div>
        ) : docViewMode === 'folders' ? (
          /* Folder View */
          <div className="space-y-2">
            {CATEGORY_ORDER.map(catKey => {
              const docs = documentsByCategory[catKey];
              if (!docs || docs.length === 0) return null;
              const isExpanded = expandedCategories.has(catKey);
              const catLabel = catKey === '_sem_categoria' ? 'Sem Categoria' : (DOC_CATEGORIES[catKey] || catKey);
              const { hasExpired, hasExpiring } = getCatStatus(docs);

              // Group docs by subcategory
              const docsWithoutSub: any[] = [];
              const subGroups: Record<string, any[]> = {};
              docs.forEach((doc: any) => {
                if (doc.subcategory) {
                  if (!subGroups[doc.subcategory]) subGroups[doc.subcategory] = [];
                  subGroups[doc.subcategory].push(doc);
                } else {
                  docsWithoutSub.push(doc);
                }
              });
              const sortedSubKeys = Object.keys(subGroups).sort();

              return (
                <div key={catKey} className="rounded-central border border-central-borda overflow-hidden">
                  <button
                    onClick={() => toggleCategory(catKey)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-central-cartao hover:bg-central-elevado transition-colors text-left"
                  >
                    <Icon name={isExpanded ? 'folderOpen' : 'folder'} className={`w-4 h-4 ${hasExpired ? 'text-red-400' : hasExpiring ? 'text-yellow-400' : 'text-slate-400'}`} />
                    <span className="text-sm font-medium text-white flex-1">{catLabel}</span>
                    <div className="flex items-center gap-2">
                      {hasExpired && <span className="w-2 h-2 rounded-full bg-red-400" title="Documentos vencidos" />}
                      {hasExpiring && !hasExpired && <span className="w-2 h-2 rounded-full bg-yellow-400" title="Documentos vencendo" />}
                      <span className="text-xs text-slate-400 bg-slate-700 px-2 py-0.5 rounded-full">{docs.length}</span>
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); handleDownloadCategoryZip(catKey); }}
                        className="p-1 rounded hover:bg-central-elevado text-slate-400 hover:text-white transition-colors"
                        title={`Baixar ${catLabel} como ZIP`}
                      >
                        {downloadingZip === catKey
                          ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                          : <Icon name="download" className="w-3.5 h-3.5" />}
                      </span>
                      <Icon name="chevronDown" className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                    </div>
                  </button>
                  {isExpanded && (() => {
                    // Detect grouped subcategories (prefix before "/" shared by 2+ subcats)
                    const prefixMap: Record<string, string[]> = {};
                    sortedSubKeys.forEach(sk => {
                      const si = sk.indexOf('/');
                      if (si > 0) {
                        const pfx = sk.substring(0, si);
                        if (!prefixMap[pfx]) prefixMap[pfx] = [];
                        prefixMap[pfx].push(sk);
                      }
                    });
                    const groupedPrefixes = new Set(Object.keys(prefixMap).filter(p => prefixMap[p].length >= 2));

                    // Build ordered render list
                    const rendered = new Set<string>();
                    const items: { type: 'flat', key: string }[] | { type: 'group', prefix: string, children: string[] }[] = [];
                    sortedSubKeys.forEach(sk => {
                      if (rendered.has(sk)) return;
                      const si = sk.indexOf('/');
                      if (si > 0 && groupedPrefixes.has(sk.substring(0, si))) {
                        const pfx = sk.substring(0, si);
                        if (!rendered.has('__g__' + pfx)) {
                          rendered.add('__g__' + pfx);
                          (items as any[]).push({ type: 'group', prefix: pfx, children: prefixMap[pfx].sort() });
                          prefixMap[pfx].forEach(s => rendered.add(s));
                        }
                      } else {
                        (items as any[]).push({ type: 'flat', key: sk });
                        rendered.add(sk);
                      }
                    });

                    return (
                      <div>
                        {(items as any[]).map((item: any) => {
                          if (item.type === 'group') {
                            const groupDocs = item.children.flatMap((sk: string) => subGroups[sk]);
                            const gKey = `${catKey}:${item.prefix}`;
                            const isGExp = expandedSubcategories.has(gKey);
                            const gStatus = getCatStatus(groupDocs);
                            return (
                              <div key={item.prefix}>
                                <button
                                  onClick={() => toggleSubcategory(gKey)}
                                  className="w-full flex items-center gap-2 pl-8 pr-4 py-2 bg-central-cartao hover:bg-central-elevado transition-colors text-left border-t border-central-borda"
                                >
                                  <Icon name={isGExp ? 'folderOpen' : 'folder'} className={`w-3.5 h-3.5 ${gStatus.hasExpired ? 'text-red-400' : gStatus.hasExpiring ? 'text-yellow-400' : 'text-slate-400'}`} />
                                  <span className="text-xs font-medium text-slate-200 flex-1">{item.prefix}</span>
                                  <div className="flex items-center gap-2">
                                    {gStatus.hasExpired && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                                    {gStatus.hasExpiring && !gStatus.hasExpired && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />}
                                    <span className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded-full">{groupDocs.length}</span>
                                    <Icon name="chevronDown" className={`w-3 h-3 text-slate-500 transition-transform ${isGExp ? '' : '-rotate-90'}`} />
                                  </div>
                                </button>
                                {isGExp && item.children.map((sk: string) => {
                                  const childDocs = subGroups[sk];
                                  const childName = sk.substring(item.prefix.length + 1);
                                  const cKey = `${catKey}:${sk}`;
                                  const isCExp = expandedSubcategories.has(cKey);
                                  const cStatus = getCatStatus(childDocs);
                                  return (
                                    <div key={sk}>
                                      <button
                                        onClick={() => toggleSubcategory(cKey)}
                                        className="w-full flex items-center gap-2 pl-14 pr-4 py-1.5 bg-central-fundo hover:bg-central-elevado transition-colors text-left border-t border-central-borda"
                                      >
                                        <Icon name={isCExp ? 'folderOpen' : 'folder'} className={`w-3 h-3 ${cStatus.hasExpired ? 'text-red-400' : cStatus.hasExpiring ? 'text-yellow-400' : 'text-slate-500'}`} />
                                        <span className="text-xs text-slate-300 flex-1">{childName}</span>
                                        <div className="flex items-center gap-2">
                                          {cStatus.hasExpired && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                                          {cStatus.hasExpiring && !cStatus.hasExpired && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />}
                                          <span className="text-[10px] text-slate-500 bg-slate-700/30 px-1.5 py-0.5 rounded-full">{childDocs.length}</span>
                                          <Icon name="chevronDown" className={`w-3 h-3 text-slate-500 transition-transform ${isCExp ? '' : '-rotate-90'}`} />
                                        </div>
                                      </button>
                                      {isCExp && <div className="pl-8">{renderDocTable(childDocs)}</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          // Flat subcategory (no grouping)
                          const subDocs = subGroups[item.key];
                          const subExpandKey = `${catKey}:${item.key}`;
                          const isSubExpanded = expandedSubcategories.has(subExpandKey);
                          const subStatus = getCatStatus(subDocs);
                          return (
                            <div key={item.key}>
                              <button
                                onClick={() => toggleSubcategory(subExpandKey)}
                                className="w-full flex items-center gap-2 pl-8 pr-4 py-2 bg-central-cartao hover:bg-central-elevado transition-colors text-left border-t border-central-borda"
                              >
                                <Icon name={isSubExpanded ? 'folderOpen' : 'folder'} className={`w-3.5 h-3.5 ${subStatus.hasExpired ? 'text-red-400' : subStatus.hasExpiring ? 'text-yellow-400' : 'text-slate-400'}`} />
                                <span className="text-xs font-medium text-slate-200 flex-1">{item.key}</span>
                                <div className="flex items-center gap-2">
                                  {subStatus.hasExpired && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                                  {subStatus.hasExpiring && !subStatus.hasExpired && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />}
                                  <span className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded-full">{subDocs.length}</span>
                                  {(() => { const allArch = subDocs.every((d: any) => d.archived); return (
                                    <span role="button" onClick={(e) => { e.stopPropagation(); handleArchiveDocs(subDocs.map((d: any) => d.id), !allArch); }}
                                      className="p-1 rounded hover:bg-central-elevado text-slate-400 hover:text-white transition-colors"
                                      title={allArch ? 'Desarquivar pasta' : 'Arquivar pasta (mantém no sistema, fora do download e dos alertas)'}>
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                                    </span>
                                  ); })()}
                                  <Icon name="chevronDown" className={`w-3 h-3 text-slate-500 transition-transform ${isSubExpanded ? '' : '-rotate-90'}`} />
                                </div>
                              </button>
                              {isSubExpanded && <div className="pl-4">{renderDocTable(subDocs)}</div>}
                            </div>
                          );
                        })}
                        {/* Docs without subcategory */}
                        {docsWithoutSub.length > 0 && renderDocTable(docsWithoutSub)}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="bg-central-cartao rounded-central border border-central-borda overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-central-fundo/95 border-b border-central-borda">
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Nome</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Categoria</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Subcategoria</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Tipo</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-slate-400 uppercase">Emissao</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-slate-400 uppercase">Validade</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-slate-400 uppercase w-48">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-central-borda">
                {filteredDocuments.map((doc: any) => {
                  const expiry = getExpiryStatus(doc.expiryDate, doc.noExpiry);
                  return (
                    <tr key={doc.id} {...propsDaLinhaDoDocumento(doc)}>
                      <td className="px-3 py-2">
                        <div className="text-white text-xs font-medium">{doc.name}</div>
                        {doc.fileName && <div className="text-slate-500 text-xs">{doc.fileName}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-300">{DOC_CATEGORIES[doc.category] || doc.category || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-300">{doc.subcategory || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">{DOC_TYPES[doc.type] || doc.type}</span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-slate-300">{fmtDate(doc.issueDate)}</td>
                      <td className="px-3 py-2 text-center text-xs text-slate-300">{doc.noExpiry ? <span className="text-slate-500 italic">Nao se aplica</span> : fmtDate(doc.expiryDate)}</td>
                      <td className="px-3 py-2 text-center">
                        {doc.archived ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-600/40 text-slate-400">Arquivado</span>
                        ) : expiry ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${expiry.color}`}>{expiry.label}</span>
                        ) : (
                          <span className="text-xs text-slate-600">—</span>
                        )}
                      </td>
                      {acoesDaLinhaDoDocumento(doc)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </EstadoDaLista>
      </div>
    );
  };

  // ==================== Declaration Templates Tab ====================

  const DECL_VARIABLES = [
    { key: '{{orgaoNome}}', desc: 'Nome do órgão' },
    { key: '{{numeroCompra}}', desc: 'Nº da compra' },
    { key: '{{anoCompra}}', desc: 'Ano da compra' },
    { key: '{{modalidadeNome}}', desc: 'Modalidade' },
    { key: '{{objeto}}', desc: 'Objeto da contratação' },
    { key: '{{empresaNome}}', desc: 'Nome da empresa' },
    { key: '{{empresaCnpj}}', desc: 'CNPJ da empresa' },
    { key: '{{empresaEndereco}}', desc: 'Endereço da empresa' },
    { key: '{{representante}}', desc: 'Nome do representante' },
    { key: '{{representanteCpf}}', desc: 'CPF do representante' },
    { key: '{{representanteRg}}', desc: 'RG do representante' },
    { key: '{{representanteCargo}}', desc: 'Cargo do representante' },
    { key: '{{codigoUnidade}}', desc: 'UASG' },
  ];

  const handleSaveDeclTemplate = async () => {
    try {
      const data = {
        name: declTemplateForm.name,
        title: declTemplateForm.title,
        body: declTemplateForm.body,
        isActive: declTemplateForm.isActive,
        category: declTemplateForm.category || 'declaracao',
        // Termo é pontual — nunca entra no preset "Padrão"
        isDefault: declTemplateForm.category === 'termo' ? false : !!declTemplateForm.isDefault,
        sortOrder: parseInt(String(declTemplateForm.sortOrder)) || 0,
      };
      if (declTemplateModal === 'edit' && editingDeclTemplate) {
        await procurementService.updateDeclarationTemplate(editingDeclTemplate.id, data);
        showToast('Template atualizado');
      } else {
        await procurementService.createDeclarationTemplate(data);
        showToast('Template criado');
      }
      setDeclTemplateModal(null);
      setEditingDeclTemplate(null);
      loadDeclarationTemplates();
    } catch (e: any) { showToast('Erro: ' + (e.response?.data?.message || e.message), 'error'); }
  };


  const renderPortalHealthTab = () => {
    return (
      <div className="space-y-6">
        {/* Health Status (existing) */}
        <div>
          <h3 className="text-lg font-medium text-white mb-3">Disponibilidade</h3>
          {portalHealthLoading ? (
            <div className="text-center py-8 text-slate-400 animate-pulse">Verificando portais...</div>
          ) : portalHealthData.length === 0 ? (
            <div className="text-center py-8 text-slate-400">Clique em "Verificar Agora" para checar a disponibilidade</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {portalHealthData.map((portal: any, i: number) => {
                const statusConfig: Record<string, { bg: string; text: string; label: string; dot: string }> = {
                  // Operante é o normal: neutro (23/10/2026). Instável e parado seguem no tom.
                  operante: { bg: 'bg-central-cartao border-central-borda', text: 'text-central-texto', label: 'Operante', dot: 'bg-slate-400' },
                  instavel: { bg: 'bg-yellow-500/10 border-yellow-500/30', text: 'text-yellow-400', label: 'Instavel', dot: 'bg-yellow-400' },
                  parado: { bg: 'bg-red-500/10 border-red-500/30', text: 'text-red-400', label: 'Parado', dot: 'bg-red-400' },
                };
                const cfg = statusConfig[portal.status] || statusConfig.parado;
                return (
                  <div key={i} className={`rounded-central border p-4 ${cfg.bg}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-white">{portal.name}</h3>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${cfg.dot} animate-pulse`} />
                        <span className={`text-xs font-medium ${cfg.text}`}>{cfg.label}</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Tempo de resposta</span>
                        <span className={cfg.text}>{portal.responseTimeMs}ms</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Acessos (redesenho de 23/10/2026, A1/S1): a tela mora em licitacoes/AcessosAosPortais.
  const renderPortalsTab = () => (
    <div>
      <AcessosAosPortais
        portais={portals}
        carga={cargaDosPortais}
        onTentarDeNovo={loadPortals}
        onNovo={cargaDosPortais.estado === 'pronto' ? () => { setPortalForm({ portalName: '', titular: '', system: 'todos', portalUrl: '', companyCnpj: '', companyName: '', login: '', password: '', notes: '' }); setEditingPortal(null); setPortalModal('create'); } : undefined}
        onEditar={(p: any) => {
          setPortalForm({ portalName: p.portalName, titular: p.titular || '', system: p.system || 'todos', portalUrl: p.portalUrl || '', companyCnpj: p.companyCnpj || '', companyName: p.companyName || '', login: p.login || '', password: '', notes: p.notes || '' });
          setEditingPortal(p); setPortalModal('edit');
        }}
        onExcluir={(p: any) => setDeleteConfirm({ ...p, _deleteType: 'portal' })}
        avisar={showToast}
        Icon={Icon}
        onTitularesAplicados={loadPortals}
      />
    </div>
  );

  const handleDocTypeChange = (type: string) => {
    const updates: any = { type };
    if (!docForm.name.trim() || docForm.name === (TYPE_TO_NAME[docForm.type] || '')) {
      updates.name = TYPE_TO_NAME[type] || '';
    }
    if (!docForm.category || docForm.category === (TYPE_TO_CATEGORY[docForm.type] || '')) {
      updates.category = TYPE_TO_CATEGORY[type] || '';
    }
    setDocForm(prev => ({ ...prev, ...updates }));
  };

  const renderDocModal = () => {
    if (!docModal) return null;
    return (
      // Formulário: antes fechava no clique fora (mousedown no fundo); agora só pelo × ou Cancelar.
      // A caixa inteira aceita o arquivo (regra de upload do CLAUDE.md), pelo mesmo
      // handleDocFileChange do input.
      <CascaDeModal
        largura="sm"
        titulo={docModal === 'create' ? 'Novo Documento' : 'Editar Documento'}
        onClose={() => setDocModal(null)}
        aoSoltarArquivos={(e) => { e.preventDefault(); handleDocFileChange(e.dataTransfer.files?.[0] || null); }}
        corpoClassName="p-4 space-y-3"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={() => setDocModal(null)} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancelar</button>
            <button onClick={handleSaveDocument} disabled={!docForm.name.trim()} className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm">{docModal === 'create' ? 'Criar' : 'Salvar'}</button>
          </div>
        }
      >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Tipo</label>
                <select value={docForm.type} onChange={(e) => handleDocTypeChange(e.target.value)} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}>
                  {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Categoria</label>
                <select value={docForm.category} onChange={(e) => setDocForm({ ...docForm, category: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}>
                  <option value="">Sem categoria</option>
                  {Object.entries(DOC_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Subcategoria</label>
              <input
                type="text"
                list="subcategory-suggestions"
                value={docForm.subcategory}
                onChange={(e) => setDocForm({ ...docForm, subcategory: e.target.value })}
                className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                placeholder="Ex: 2024 Balanco, ANATEL, DP752/DP722..."
                maxLength={200}
              />
              <datalist id="subcategory-suggestions">
                {(subcategorySuggestions[docForm.category] || []).map((s: string) => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Nome *</label>
              <input type="text" value={docForm.name} onChange={(e) => setDocForm({ ...docForm, name: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="Ex: CND Federal" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Emissao</label>
                <input type="date" value={docForm.issueDate} onChange={(e) => setDocForm({ ...docForm, issueDate: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Validade</label>
                {/* Nao desabilitar quando permanente: campo travado sem causa visivel
                    parece bug. Preencher a data desmarca a flag automaticamente. */}
                <input type="date" value={docForm.expiryDate} onChange={(e) => setDocForm({ ...docForm, expiryDate: e.target.value, noExpiry: e.target.value ? false : docForm.noExpiry })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
                <label className="flex items-center gap-2 mt-1.5 text-xs text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={docForm.noExpiry}
                    onChange={(e) => setDocForm({ ...docForm, noExpiry: e.target.checked, ...(e.target.checked ? { expiryDate: '' } : {}) })}
                    className="rounded border-slate-600 bg-slate-700 text-orange-600 focus:ring-orange-500"
                  />
                  Nao se aplica (documento permanente)
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Descricao</label>
              <textarea value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} rows={2} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Arquivo (PDF/imagem) <span className="text-slate-500 font-normal">— ou arraste para cá</span></label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => handleDocFileChange(e.target.files?.[0] || null)} className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-orange-600 file:text-white hover:file:bg-orange-500" />
              {docExtracting && <p className="text-xs text-orange-400 mt-1">Extraindo datas do PDF...</p>}
              {editingDoc?.fileName && !docFile && <p className="text-xs text-slate-500 mt-1">Arquivo atual: {editingDoc.fileName}</p>}
            </div>
      </CascaDeModal>
    );
  };

  const renderProductModal = () => {
    if (!productModal) return null;
    return (
      // Formulário: antes fechava no clique fora (mousedown no fundo); agora só pelo × ou Cancelar.
      <CascaDeModal
        largura="sm"
        titulo={productModal === 'create' ? 'Novo Produto' : 'Editar Produto'}
        onClose={() => setProductModal(null)}
        corpoClassName="p-4 space-y-3"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={() => setProductModal(null)} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancelar</button>
            <button onClick={handleSaveProduct} disabled={!productForm.name.trim()} className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm">{productModal === 'create' ? 'Criar' : 'Salvar'}</button>
          </div>
        }
      >
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Nome *</label>
              <input type="text" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="Ex: Link Internet 100Mbps" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Descricao</label>
              <textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} rows={2} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Categoria</label>
                <input type="text" value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="Ex: Telecom" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Unidade</label>
                <input type="text" value={productForm.unit} onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="UN, MES, HORA" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Preco Referencia (R$)</label>
                <input type="number" step="0.01" value={productForm.referencePrice} onChange={(e) => setProductForm({ ...productForm, referencePrice: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">NCM/NBS</label>
                <input type="text" value={productForm.ncmCode} onChange={(e) => setProductForm({ ...productForm, ncmCode: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="00000000" />
              </div>
            </div>
      </CascaDeModal>
    );
  };

  const renderPortalModal = () => {
    if (!portalModal) return null;
    return (
      // Formulário: antes fechava no clique fora (mousedown no fundo); agora só pelo × ou Cancelar.
      <CascaDeModal
        largura="sm"
        titulo={portalModal === 'create' ? 'Novo Acesso' : 'Editar Acesso'}
        onClose={() => setPortalModal(null)}
        corpoClassName="p-4 space-y-3"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={() => setPortalModal(null)} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancelar</button>
            <button onClick={handleSavePortal} disabled={!portalForm.portalName} className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm">{portalModal === 'create' ? 'Criar' : 'Salvar'}</button>
          </div>
        }
      >
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Portal *</label>
              <input
                type="text"
                list="portal-name-suggestions"
                value={portalForm.portalName}
                onChange={(e) => setPortalForm({ ...portalForm, portalName: e.target.value })}
                className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                placeholder="Nome do portal..."
              />
              <datalist id="portal-name-suggestions">
                {PORTALS.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Titular</label>
              <input type="text" value={portalForm.titular} onChange={(e) => setPortalForm({ ...portalForm, titular: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="De quem é a conta (pessoa ou empresa)" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Sistema</label>
              <select value={portalForm.system} onChange={(e) => setPortalForm({ ...portalForm, system: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}>
                {Object.entries(SYSTEMS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">URL do Portal</label>
              <input type="url" value={portalForm.portalUrl || ''} onChange={(e) => setPortalForm({ ...portalForm, portalUrl: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">CNPJ Empresa</label>
                <input type="text" value={portalForm.companyCnpj} onChange={(e) => setPortalForm({ ...portalForm, companyCnpj: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="06.163.889/0001-04" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Nome Empresa</label>
                <input type="text" value={portalForm.companyName} onChange={(e) => setPortalForm({ ...portalForm, companyName: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder="IPCOMP" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Login</label>
                <input type="text" value={portalForm.login} onChange={(e) => setPortalForm({ ...portalForm, login: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Senha</label>
                <input type="password" value={portalForm.password} onChange={(e) => setPortalForm({ ...portalForm, password: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} placeholder={editingPortal ? '(manter atual)' : ''} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Observacoes</label>
              <textarea value={portalForm.notes} onChange={(e) => setPortalForm({ ...portalForm, notes: e.target.value })} rows={2} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`} />
            </div>
            {/* As capacidades de automação saíram daqui (15/10/2026, aprovado pelo Heitor): escritas à mão, envelheciam
                (BLL "não implementado" rodando desde agosto). A fonte é o registro do backend, na aba Portais de Automação.
                O salvar não manda mais `capabilities`: o dado antigo fica no banco até o Heitor decidir. */}
      </CascaDeModal>
    );
  };

  const renderDocPreviewModal = () => {
    if (!previewDoc) return null;
    const docType = DOC_TYPES[previewDoc.type] || previewDoc.type;
    return (
      <DocumentViewerModal
        title={previewDoc.name}
        subtitle={docType + (previewDoc.fileName ? ' — ' + previewDoc.fileName : '')}
        fileUrl={previewUrl}
        fileName={previewDoc.fileName}
        loading={previewLoading}
        accent="blue"
        onClose={() => setPreviewDoc(null)}
        info={[
          { label: 'Tipo', value: docType },
          { label: 'Arquivo', value: previewDoc.fileName || '—' },
          ...(previewDoc.category ? [{ label: 'Categoria', value: DOC_CATEGORIES[previewDoc.category] || previewDoc.category }] : []),
          ...(previewDoc.issueDate ? [{ label: 'Emissão', value: fmtDate(previewDoc.issueDate) }] : []),
          // `expiryDate` e o nome no banco (procurement_documents). Documento de habilitacao
          // vencido derruba a proposta, entao a validade fica no painel, em ambar.
          ...(previewDoc.noExpiry
            ? [{ label: 'Validade', value: 'Nao se aplica', tone: 'muted' as const }]
            : previewDoc.expiryDate ? [{ label: 'Validade', value: fmtDate(previewDoc.expiryDate), tone: 'warning' as const }] : []),
          ...(previewDoc.createdAt ? [{ label: 'Enviado em', value: fmtDate(previewDoc.createdAt) }] : []),
        ]}
        headerActions={
          previewUrl ? (
            <button
              onClick={handleDownloadDocumentFile}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200"
              title="Baixar arquivo"
            >
              <Icon name="download" className="w-4 h-4" />
              Baixar
            </button>
          ) : null
        }
        // O link público mora no painel, não num rodapé. É a forma CERTA de mandar documento
        // de habilitação para fora (a URL do S3 expira em 1h e o pregoeiro recebe um link
        // morto), então precisa continuar à mão em vez de virar ação escondida.
        sidebarExtra={
          previewUrl ? (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Link público</h4>
              {publicLink ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 p-2 bg-slate-900/60 border border-slate-600 rounded-lg">
                    <input
                      readOnly
                      value={publicLink.url}
                      onFocus={(e) => e.currentTarget.select()}
                      className="flex-1 min-w-0 bg-transparent text-xs text-slate-300 outline-none"
                    />
                    <button onClick={handleCopyPublicLink} className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-500 shrink-0">
                      Copiar
                    </button>
                  </div>
                  {publicLink.expiresAt && (
                    <p className="text-[11px] text-slate-500">expira {fmtDate(publicLink.expiresAt)}</p>
                  )}
                </div>
              ) : (
                <div className="flex items-stretch">
                  <button
                    onClick={handleGeneratePublicLink}
                    disabled={generatingLink}
                    className="flex-1 px-3 py-1.5 bg-purple-600 text-white rounded-l-lg hover:bg-purple-500 text-xs font-medium disabled:opacity-60"
                  >
                    {generatingLink ? 'Gerando...' : 'Gerar link público'}
                  </button>
                  <select
                    value={linkDays}
                    onChange={(e) => setLinkDays(Number(e.target.value))}
                    disabled={generatingLink}
                    title="Validade do link público"
                    className="px-2 py-1.5 bg-purple-700 text-white text-xs rounded-r-lg border-l border-purple-800 hover:bg-purple-600 focus:outline-none disabled:opacity-60 cursor-pointer"
                  >
                    <option value={30}>30 dias</option>
                    <option value={90}>90 dias</option>
                    <option value={365}>1 ano</option>
                    <option value={0}>Sem expiração</option>
                  </select>
                </div>
              )}
            </div>
          ) : null
        }
      />
    );
  };

  const renderDeleteConfirm = () => {
    if (!deleteConfirm) return null;
    return (
      // Confirmação sem nada digitado: fecha ao clicar fora, como já era; ganha o ×.
      // Os quatro destinos (documento, produto, portal, perfil) seguem o mesmo roteamento.
      <CascaDeModal
        largura="xs"
        fecharAoClicarFora
        titulo="Confirmar exclusao"
        onClose={() => setDeleteConfirm(null)}
        corpoClassName="px-5 py-4"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors">Cancelar</button>
            <button
              onClick={() => {
                if (deleteConfirm._deleteType === 'document') handleDeleteDocument(deleteConfirm.id);
                else if (deleteConfirm._deleteType === 'product') handleDeleteProduct(deleteConfirm.id);
                else if (deleteConfirm._deleteType === 'portal') handleDeletePortal(deleteConfirm.id);
                else handleDeleteSearch(deleteConfirm.id);
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm transition-colors"
            >
              Excluir
            </button>
          </div>
        }
      >
          <p className="text-sm text-slate-400">
            Excluir "{deleteConfirm.name || deleteConfirm.portalName}"?
            {!deleteConfirm._deleteType && ' Os itens encontrados nao serao excluidos.'}
          </p>
      </CascaDeModal>
    );
  };

  // ==================== Automation Modals ====================

  const renderAutomationModal = () => {
    if (!automationModal) return null;
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target !== e.currentTarget) return; setAutomationModal(null); }}>
        <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Automatizar Envio</h3>
            <button onClick={() => { setAutomationModal(null); }} className="text-slate-400 hover:text-white"><Icon name="x" className="w-5 h-5" /></button>
          </div>

          <div className="space-y-4">
            {/* Proposal info */}
            <div className="rounded-lg p-3 bg-slate-700/50">
              <div className="text-sm text-white font-medium">{automationModal.title}</div>
              <div className="text-xs text-slate-400 mt-1">{getOrgaoDisplay(automationModal.item) || '—'}</div>
              <div className="text-xs text-slate-500 mt-1 truncate">{(automationModal.item?.objetoCompra || '').substring(0, 80)}</div>
              <div className="text-sm text-orange-400 mt-2 font-medium">{fmt(automationModal.totalValue)}</div>
            </div>

            {/* Manual edit warning */}
            {automationModal.proposalItems?.some((it: any) => !it.estimatedPrice || it.estimatedPrice === 0) && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="text-xs text-amber-400 font-medium">Itens editados manualmente</div>
                <div className="text-xs text-amber-400/80 mt-1">Esta proposta contem itens adicionados ou editados manualmente. A automacao mapeia itens por numero — se a quantidade ou ordem diferir do portal, o envio pode falhar.</div>
              </div>
            )}

            {/* Portal selection */}
            <div>
              <label className="text-sm text-slate-300 block mb-1">Portal de destino *</label>
              <select
                value={automationPortalId}
                onChange={(e) => setAutomationPortalId(e.target.value)}
                className={`w-full ${campo} rounded-lg px-3 py-2 text-sm`}
              >
                <option value="">Selecione o portal...</option>
                {portals.filter((p: any) => p.isActive).map((p: any) => (
                  <option key={p.id} value={p.id}>{p.portalName}{p.companyName ? ` (${p.companyName})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Dry run toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={automationDryRun}
                onChange={(e) => setAutomationDryRun(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500"
              />
              <div>
                <div className="text-sm text-white">Modo teste (dry run)</div>
                <div className="text-xs text-slate-400">Preenche os dados mas NAO envia a proposta</div>
              </div>
            </label>

            {!automationDryRun && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <div className="text-xs text-yellow-400 font-medium">Atencao: Envio Real</div>
                <div className="text-xs text-yellow-400/80 mt-1">A proposta sera enviada automaticamente ao portal. Verifique os dados antes de confirmar.</div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => { setAutomationModal(null); }} className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors">Cancelar</button>
            <button
              onClick={handleStartAutomation}
              disabled={!automationPortalId || automationStarting}
              className={`px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm transition-colors flex items-center gap-2`}
            >
              {automationStarting ? (
                <><Icon name="refresh" className="w-4 h-4 animate-spin" /> Iniciando...</>
              ) : (
                <><Icon name="robot" className="w-4 h-4" /> {automationDryRun ? 'Iniciar Teste' : 'Iniciar Envio'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAutomationJobModal = () => {
    if (!automationJobModal) return null;
    const job = automationJobModal;
    const jobSt = AUTOMATION_STATUSES[job.status] || AUTOMATION_STATUSES.queued;
    const steps = job.steps || [];

    const STEP_LABELS: Record<string, string> = {
      login: 'Login no portal',
      navigate: 'Navegar ate a licitacao',
      fill_items: 'Preencher itens da proposta',
      submit: 'Enviar proposta',
      withdraw: 'Desistir da compra',
      // BLL Fase 2 (PR7 — progresso em tempo real)
      iniciando: 'Iniciando…',
      login_bll: 'Autenticando no BLL (Bright Data captcha)',
      upload_anexos: 'Subindo anexos via Azure Blob',
      enviando_proposta: 'Enviando proposta',
      desistindo_proposta: 'Desistindo da proposta',
    };

    const STEP_STATUS_ICONS: Record<string, string> = {
      pending: 'text-slate-500',
      running: 'text-blue-400 animate-pulse',
      completed: 'text-emerald-400',
      failed: 'text-red-400',
      skipped: 'text-slate-600',
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target !== e.currentTarget) return; automationModalOpenRef.current = false; setAutomationJobModal(null) /* polling segue em background até o desfecho (toast global) */ }}>
        <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <PanelHeader
            title={`Automação — ${job.portal}`}
            badges={<span>Job #{job.id}{job.dryRun ? ' · modo teste' : ''}</span>}
            className="-mx-6 -mt-6 px-6 mb-4"
            secondary={<span className='inline-flex items-center gap-2'>
              <span className={`text-xs px-2 py-0.5 rounded-full ${jobSt.color}`}>{jobSt.label}</span>
              {jobLidoEm && <span className='text-[11px] text-slate-500 tabular-nums' data-job-lido-em>atualizado às {fmtTimeWithSeconds(jobLidoEm)}</span>}
            </span>}
            onClose={() => { automationModalOpenRef.current = false; setAutomationJobModal(null); }}
          />

          {/* Proposal info */}
          {job.proposal && (
            <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
              <div className="text-sm text-white">{job.proposal.title}</div>
              <div className="text-xs text-slate-400">{getOrgaoDisplay(job.proposal.item) || '—'}</div>
              <div className="text-xs text-orange-400 mt-1">{fmt(job.proposal.totalValue)}</div>
            </div>
          )}

          {/* Steps timeline */}
          <div className="space-y-3 mb-4">
            <h4 className="text-sm font-medium text-slate-300">Progresso</h4>
            {steps.length > 0 ? steps.map((step: any, i: number) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full border-2 ${
                    step.status === 'completed' ? 'bg-emerald-400 border-emerald-400' :
                    step.status === 'running' ? 'bg-blue-400 border-blue-400 animate-pulse' :
                    step.status === 'failed' ? 'bg-red-400 border-red-400' :
                    step.status === 'skipped' ? 'bg-slate-600 border-slate-600' :
                    'bg-slate-700 border-slate-500'
                  }`} />
                  {i < steps.length - 1 && <div className={`w-0.5 h-6 ${step.status === 'completed' ? 'bg-emerald-400/50' : 'bg-slate-600'}`} />}
                </div>
                <div className="flex-1 -mt-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${STEP_STATUS_ICONS[step.status] || 'text-slate-400'} flex-1`}>
                      {STEP_LABELS[step.name] || step.name}
                    </span>
                    {step.time && (
                      <span className="text-[10px] text-slate-500 font-mono shrink-0">{step.time}</span>
                    )}
                    {step.duration != null && (
                      <span className="text-[10px] text-slate-500">{(step.duration / 1000).toFixed(1)}s</span>
                    )}
                  </div>
                  {step.error && (
                    <div className="text-xs text-red-400 mt-0.5">{step.error}</div>
                  )}
                </div>
              </div>
            )) : job.status === 'queued' ? (
              <div className="text-xs text-slate-500">Aguardando início — extension Firefox vai pegar o job no próximo poll (até 5s)...</div>
            ) : job.status === 'running' ? (
              <div className="text-xs text-blue-400">Executando no portal (cnetmobile) — progresso aparece aqui em tempo real conforme o content.js reporta steps</div>
            ) : (
              <div className="text-xs text-slate-500">Sem steps registrados (job antigo, antes da v2.9.x)</div>
            )}
          </div>

          {/* Error */}
          {job.errorMessage && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
              <div className="text-xs text-red-400 font-medium">Erro</div>
              <div className="text-xs text-red-400/80 mt-1">{job.errorMessage}</div>
            </div>
          )}

          {/* Needs manual action */}
          {job.status === 'needs_manual' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
              <div className="text-xs text-yellow-400 font-medium">Acao Manual Necessaria</div>
              <div className="text-xs text-yellow-400/80 mt-1">
                Foi detectado CAPTCHA ou verificacao em 2 fatores. Acesse o portal manualmente para completar o envio.
              </div>
            </div>
          )}

          {/* Protocol number */}
          {job.protocolNumber && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
              <div className="text-xs text-emerald-400 font-medium">Protocolo</div>
              <div className="text-sm text-emerald-300 mt-1 font-mono">{job.protocolNumber}</div>
            </div>
          )}

          {/* Breakdown de itens — preenchido pelo Playwright após fillItemsViaApi.
              Visível mesmo quando o job falha em steps posteriores, dando ao
              usuário visibilidade do que já foi salvo no portal. */}
          {job.data?.fillItemsResult && (() => {
            const r = job.data.fillItemsResult;
            const total = r.total ?? 0;
            const savedN = (r.saved || []).length;
            const alreadyN = (r.alreadySaved || []).length;
            const failedN = (r.failed || []).length;
            const okN = savedN + alreadyN;
            return (
              <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-3 mb-4">
                <div className="text-xs text-slate-300 font-medium mb-2">Itens enviados ao portal</div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-emerald-400">✅ {okN}/{total} OK</span>
                  {savedN > 0 && <span className="text-slate-400">novos: {savedN}</span>}
                  {alreadyN > 0 && <span className="text-slate-400" title="Itens detectados como já cadastrados em job anterior — pulados pra evitar 500 do portal">já salvos: {alreadyN}</span>}
                  {failedN > 0 && <span className="text-red-400">falharam: {failedN}</span>}
                </div>
                {savedN > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1.5">Salvos: {(r.saved || []).join(', ')}</div>
                )}
                {alreadyN > 0 && (
                  <div className="text-[10px] text-slate-500 mt-0.5">Já salvos: {(r.alreadySaved || []).join(', ')}</div>
                )}
                {failedN > 0 && (
                  <div className="text-[10px] text-red-400 mt-0.5">Falharam: {(r.failed || []).map((f: any) => f.itemNumber).join(', ')}</div>
                )}
              </div>
            );
          })()}

          <div className="flex justify-end gap-2">
            {['queued', 'running'].includes(job.status) && (
              <button
                onClick={async () => {
                  try {
                    await procurementService.cancelAutomationJob(job.id);
                    showToast('Job cancelado');
                    if (automationJobPolling) { clearInterval(automationJobPolling); setAutomationJobPolling(null); }
                    const updated = await procurementService.getAutomationJob(job.id);
                    mostrarJob(updated);
                    loadAutomationJobs();
                    loadProposals();
                  } catch { showToast('Erro ao cancelar job', 'error'); }
                }}
                className="px-4 py-2 text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
              >
                Parar
              </button>
            )}
            {['failed', 'needs_manual'].includes(job.status) && job.proposal && (
              <button
                onClick={async () => {
                  const portalId = findPortalIdBySource(
                    job.proposal?.item?.portalOrigem || null,
                    job.proposal?.item?.linkOrigem || null,
                  );
                  if (!portalId) { showToast('Portal nao identificado para retry', 'error'); return; }
                  try {
                    let newJob: any;
                    if (job.action === 'scrape_proposal') {
                      // O MESMO caminho do Cadastrar (sincronizarDoPortal). Até 16/10/2026, BLL ia para o endpoint
                      // cauterizado em 27/05 (sempre 400) e BNC pulava o pedido do link.
                      await sincronizarDoPortal({
                        id: job.proposalId,
                        item: {
                          id: job.proposal.item?.id || job.proposal.itemId,
                          portalOrigem: job.proposal.item?.portalOrigem || job.portal || null,
                        },
                      });
                      loadAutomationJobs();
                      return;
                    } else if (job.action === 'withdraw_proposal') {
                      newJob = await procurementService.withdrawProposal(job.proposalId, parseInt(portalId, 10), job.dryRun);
                    } else {
                      const retryPortal = portals.find((p: any) => String(p.id) === String(portalId));
                      const pn = retryPortal?.portalName?.toLowerCase() || '';
                      const isBll = pn.includes('bll') || job.portal === 'bll';
                      const isBnc = pn.includes('bnc') || job.portal === 'bnc';
                      const isPcp = pn.includes('pcp') || pn.includes('compras p') || job.portal === 'pcp';
                      if (isPcp) {
                        // PCP também é assíncrono (submit-pcp → jobId). Caía no
                        // startAutomation, endpoint descontinuado (Gravatal, 07/09/2026).
                        if (job.dryRun) {
                          showToast('Modo teste não suportado pelo PCP', 'error');
                          return;
                        }
                        const result = await procurementService.submitProposalPcp(job.proposalId);
                        newJob = { id: result.jobId, status: 'queued' };
                      } else if (isBll || isBnc) {
                        // Retry do submit assíncrono (base gkz). BLL e BNC = mesma
                        // plataforma; retorna jobId, polling popula a timeline.
                        if (job.dryRun) {
                          showToast(`Modo teste não suportado pelo ${isBnc ? 'BNC' : 'BLL'} HTTP`, 'error');
                          return;
                        }
                        const result = isBnc
                          ? await procurementService.submitProposalBnc(job.proposalId)
                          : await procurementService.submitProposalBll(job.proposalId);
                        newJob = { id: result.jobId, status: 'queued' };
                      } else {
                        // ComprasNet: o startAutomation (/automation/start) foi
                        // descontinuado — o caminho vivo é o submit-proposal do item,
                        // o mesmo do botão "Enviar proposta" do workspace.
                        const itemId = job.proposal?.item?.id || job.proposal?.itemId;
                        if (!itemId) { showToast('Item da proposta não identificado para retry', 'error'); return; }
                        const r = await procurementService.submitProposalAuto(itemId, { proposalId: job.proposalId, dryRun: !!job.dryRun });
                        showToast(r?.ok ? '✓ Reenviado ao ComprasNet' : `✗ ${r?.error || 'falhou'}`, r?.ok ? 'success' : 'error');
                        loadAutomationJobs();
                        loadProposals();
                        return;
                      }
                    }
                    showToast('Novo job iniciado' + (job.dryRun ? ' (modo teste)' : ''));
                    loadAutomationJobs();
                    if (newJob?.id) {
                      const fullJob = await procurementService.getAutomationJob(newJob.id);
                      mostrarJob(fullJob);
                      startJobPolling(newJob.id);
                    }
                  } catch (e: any) {
                    showToast('Erro ao tentar novamente: ' + (e.response?.data?.message || e.message), 'error');
                  }
                }}
                className="px-4 py-2 text-sm bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
              >
                Tentar Novamente
              </button>
            )}
            <button
              onClick={() => { automationModalOpenRef.current = false; setAutomationJobModal(null) /* polling segue em background até o desfecho (toast global) */ }}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {provaDoEnvio && (() => {
        const { proposal: pp, selo } = provaDoEnvio;
        const job = selo.jobDaProva;
        const passos = passosDaProva(job);
        const num = formatNumeroCompraComAno(pp.item?.numeroCompra, pp.item?.anoCompra);
        return (
          <CascaDeModal
            titulo={`Prova do envio · ${getOrgaoDisplay(pp.item) || 'Licitação'}${num !== '—' ? ` ${num}` : ''}`}
            subtitulo={`${selo.titulo} — ${selo.linha2}`}
            onClose={() => setProvaDoEnvio(null)}
            largura="md"
          >
            <div className="space-y-3 text-sm" data-prova-do-envio={selo.estado}>
              {selo.estado === 'portal' && (job ? (
                <p className="text-slate-300">Trilha registrada pelo próprio robô{pp.submittedAt ? `, ${fmtDateTimeDeParede(pp.submittedAt)}` : ''}. O portal não devolve número de protocolo para este envio: a prova é esta trilha.</p>
              ) : (
                <p className="text-slate-300">Enviada pelo robô{pp.submittedAt ? ` em ${fmtDateTimeDeParede(pp.submittedAt)}` : ''}. A trilha deste envio é mais antiga que as 50 últimas automações que esta tela carrega, e não aparece aqui.</p>
              ))}
              {selo.estado === 'mao' && (
                <p className="text-slate-300">Marcada como enviada no HUB{pp.submittedAt ? ` em ${fmtDateTimeDeParede(pp.submittedAt)}` : ''}. O envio não passou pelo robô, e o HUB não guarda prova dele, nem quem marcou. Confira no portal.</p>
              )}
              {selo.estado === 'legado' && (
                <p className="text-slate-300">Enviada antes de o HUB registrar por onde. Não há prova guardada.</p>
              )}
              {selo.estado === 'monitor' && (
                <p className="text-slate-300">Proposta criada só para ler as mensagens da licitação. Nunca foi enviada ao portal.</p>
              )}
              {selo.estado === 'nao_enviada' && (
                <p className="text-slate-300">Ainda não enviada: {selo.linha2}.</p>
              )}
              {selo.estado === 'falhou' && job && (
                <div className="rounded-lg border border-central-borda bg-central-elevado p-3">
                  <p className="text-xs text-slate-400">Tentativa do robô{job.updatedAt || job.createdAt ? ` em ${fmtDateTime(job.updatedAt || job.createdAt)}` : ''}. O motivo, como o portal devolveu:</p>
                  <p className="text-slate-200 mt-1 whitespace-pre-wrap">{job.errorMessage || 'sem motivo registrado'}</p>
                </div>
              )}
              {passos.length > 0 && (
                <ol className="space-y-1" data-passos-da-prova>
                  {passos.map((x, i) => (
                    <li key={i} className="flex gap-3 text-xs">
                      <span className="text-slate-500 tabular-nums w-14 shrink-0">{x.hora || '—'}</span>
                      <span className={x.ok ? 'text-slate-200' : 'text-red-300'}>{x.texto}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </CascaDeModal>
        );
      })()}
      {novaVersao && (
        <NovaVersaoModal
          doc={novaVersao.doc}
          ativosDoMesmoTipo={documents.filter((d: any) => d.type === novaVersao.doc.type && d.isActive !== false).length}
          arquivoInicial={novaVersao.arquivo}
          onClose={() => setNovaVersao(null)}
          onPronto={() => { setNovaVersao(null); loadDocuments(); }}
          avisar={(m, t) => showToast(m, t || 'success')}
        />
      )}
      {historicoDoDoc && <HistoricoDeVersoesModal doc={historicoDoDoc} onClose={() => setHistoricoDoDoc(null)} />}
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all max-w-md ${
          toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header - contextual per subView */}
      {subView === 'items' && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Banco de Licitações</h2>
              <p className="text-sm text-slate-400">Encontre oportunidades no Portal Nacional de Contratações Públicas</p>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex items-center bg-central-cartao border border-central-borda rounded-lg p-0.5">
                <button
                  onClick={() => handleViewModeChange('table')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    viewMode === 'table' ? 'bg-central-elevado text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Icon name="list" className="w-3.5 h-3.5" />
                  Tabela
                </button>
                <button
                  onClick={() => handleViewModeChange('pipeline')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    viewMode === 'pipeline' ? 'bg-central-elevado text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Icon name="grid" className="w-3.5 h-3.5" />
                  Pipeline
                </button>
              </div>
              <button
                onClick={() => { setShowMetricsModal(true); loadMetrics(); }}
                className="flex items-center gap-2 px-4 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors"
              >
                <Icon name="chart" className="w-4 h-4" />
                Dashboard
              </button>
              <button
                onClick={() => setShowSearchProfilesModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors"
              >
                <Icon name="settings" className="w-4 h-4" />
                Perfis de Busca
              </button>
              {/* NOTA (05/07/2026): botão "Re-enriquecer PNCP" removido da toolbar —
                  auditoria mostrou que o cron (02h/14h + auto-recovery + stale watcher)
                  cobre 100% e o pool "pendente" é permanente (valor sigiloso/suspensas),
                  não backlog. O re-enrich por ITEM continua no card (🔄). Zumbis
                  não-PNCP agora são varridos pelo cron 03:50 (sweepNonPncpStale). */}
              {/* NOTA (24/05/2026): botão "Verificar vencidos" foi movido pra
                  headers das views Cadastrar (subView=proposals) e Monitorar
                  (subView=monitoring) — semanticamente faz mais sentido lá
                  (são items COM proposta enviada, não items de descoberta). */}
              <button
                onClick={openContratacaoDireta}
                className="flex items-center gap-2 px-4 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm transition-colors"
                title="Encontra e move pra lixeira as dispensas de contratação direta (fornecedor já nomeado no objeto) — não são oportunidades de participação"
              >
                <Icon name="trash" className="w-4 h-4" />
                Contratação direta
              </button>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
              >
                <Icon name="refresh" className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>
            </div>
          </div>
          {!trashMode && viewMode === 'table' && renderDashboard()}
          {trashMode && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
              <Icon name="trash" className="w-5 h-5 text-red-400" />
              <span className="text-red-400 text-sm">Mostrando itens descartados. Clique em "Restaurar" para mover de volta para a lista principal.</span>
            </div>
          )}
        </>
      )}
      {subView === 'searches' && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Perfis de Busca</h2>
            <p className="text-sm text-slate-400">Configure palavras-chave, estados e modalidades para monitoramento automatico</p>
          </div>
        </div>
      )}
      {subView === 'metrics' && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Dashboard de Licitacoes</h2>
            <p className="text-sm text-slate-400">Visao geral das licitacoes monitoradas por UF, modalidade e periodo</p>
          </div>
          <button
            onClick={loadMetrics}
            disabled={metricsLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
          >
            <Icon name="refresh" className={`w-4 h-4 ${metricsLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      )}
      {subView === 'documents' && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Documentos de Habilitacao</h2>
            <p className="text-sm text-slate-400">Gerencie certidões, atestados e documentos necessários para participar de licitações</p>
          </div>
        </div>
      )}
      {/* NOTA (23/05/2026): header 'products' (Catálogo de Produtos) removido
          junto com o menu Produtos. As render functions que sobraram sem uso
          foram podadas em 15/08/2026. */}
      {subView === 'proposals' && !embedded && !workspaceProposalId && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Propostas</h2>
            <p className="text-sm text-slate-400">Gerencie propostas vinculadas a licitações em andamento</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openCheckExpired}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto"
              title="Re-consulta PNCP+portal pras propostas enviadas com sessão vencida há >24h. Atualiza status (homologada/encerrada) e arquiva automaticamente quando confirmado."
            >
              <Icon name="refresh" className={`w-4 h-4 ${checkExpiredData?.running ? 'animate-spin' : ''}`} />
              {checkExpiredData?.running
                ? `Verificando ${checkExpiredData.processed}/${checkExpiredData.total}`
                : 'Verificar vencidos'}
            </button>
            {isAdmin && (
              <button
                onClick={() => setAgendaPanelOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto"
                title="Estado da integração com a agenda do Google: armada, modo, e o que a varredura faria agora"
              >
                <Icon name="calendar" className="w-4 h-4" /> Agenda
              </button>
            )}
            <ComprasNetHealthDot variant='inline' />
          </div>
        </div>
      )}
      {agendaPanelOpen && (
        <AgendaGooglePanel
          onClose={() => setAgendaPanelOpen(false)}
          onSincronizado={() => loadProposals()}
          itensComEvento={itensComEventoNaLista}
          showToast={showToast}
        />
      )}
      {subView === 'portals' && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Acessos aos Portais</h2>
            <p className="text-sm text-slate-400">Gerencie credenciais de acesso aos portais de licitação</p>
          </div>
        </div>
      )}
      {subView === 'portalHealth' && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Status dos Portais</h2>
            <p className="text-sm text-slate-400">Disponibilidade dos portais</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadPortalHealth}
              disabled={portalHealthLoading}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
            >
              <Icon name="refresh" className={`w-4 h-4 ${portalHealthLoading ? 'animate-spin' : ''}`} />
              {portalHealthLoading ? 'Verificando...' : 'Verificar Disponibilidade'}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {subView === 'items' && viewMode === 'table' && renderItemsTable()}
      {subView === 'items' && viewMode === 'pipeline' && (
        <PipelineView
          onOpenDetail={openDetail}
          onStatusChange={handlePipelineStatusChange}
          onRefresh={() => { loadItems(); loadDashboard(); }}
          refreshKey={pipelineRefreshKey}
          filters={{ q: searchFilter, searchId: searchIdFilter }}
        />
      )}
      {subView === 'searches' && renderSearchesList()}
      {subView === 'metrics' && renderMetricsDashboard()}
      {subView === 'documents' && renderDocumentsTab()}
      {/* Modal de Templates de Declaração: vive no RETURN PRINCIPAL. Dentro de
          renderProposalsTab ele não existia na página de trabalho, que substitui
          aquela função inteira — mesmo defeito que já escondeu o modal de
          assinatura. */}
      {/* R1.13: Templates de Declaração movido pra modal — sai do scroll principal */}
      {showTemplatesModal && (
      // Lista (cada ação abre o editor ou confirma por conta própria): fecha ao clicar
      // fora, como já era. z-40 de propósito: o editor de template (z-50) e o
      // confirm de exclusão abrem por cima dela. A rolagem passa da página à casca.
      <CascaDeModal
        largura="xl"
        zIndex="z-40"
        fecharAoClicarFora
        titulo="Templates de Declaração"
        onClose={() => setShowTemplatesModal(false)}
        corpoClassName="p-5"
      >
      {/* ==================== Declaration Templates Section ==================== */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-medium text-white">Templates de Declaração</h3>
            <p className="text-[10px] text-slate-500 mt-0.5">Modelos para gerar declarações por licitação. Use variáveis no corpo.</p>
          </div>
          {cargaDosTemplates.estado === 'pronto' && <button
            onClick={() => {
              setDeclTemplateForm({ name: '', title: '', body: '', isActive: true, sortOrder: 0, category: 'declaracao', isDefault: false });
              setEditingDeclTemplate(null);
              setDeclTemplateModal('create');
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs transition-colors"
          >
            <Icon name="plus" className="w-3.5 h-3.5" /> Novo Template
          </button>}
        </div>

        <EstadoDaLista carga={cargaDosTemplates} temDados={cargaDosTemplates.atualizadoEm !== null} oQue="os templates de declaração" onTentarDeNovo={loadDeclarationTemplates} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
        {declarationTemplates.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm bg-central-elevado/30 rounded-lg border border-central-borda/50">Nenhum template cadastrado</div>
        ) : (
          [
            { key: 'declaracao', label: '📋 Declarações (recorrentes)' },
            { key: 'termo', label: '📜 Termos (uso pontual)' },
          ].map((grp) => {
            const grpTpls = declarationTemplates.filter((t: any) => (t.category || 'declaracao') === grp.key);
            if (grpTpls.length === 0) return null;
            return (
          <div key={grp.key} className="mb-4">
          <div className="text-xs text-slate-400 font-medium mb-2">{grp.label} <span className="text-slate-600">({grpTpls.length})</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {grpTpls.map((tpl: any) => (
              <div key={tpl.id} className={`relative group/tpl bg-central-elevado rounded-lg border p-3 ${tpl.isActive ? 'border-central-borda' : 'border-central-borda/50 opacity-50'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name="document" className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-xs text-white font-medium truncate">{tpl.name}</span>
                    {tpl.isDefault && (tpl.category || 'declaracao') === 'declaracao' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400 shrink-0" title='Entra no atalho "Padrão" da geração'>Padrão</span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={() => {
                        setEditingDeclTemplate(tpl);
                        setDeclTemplateForm({ name: tpl.name || '', title: tpl.title || '', body: tpl.body || '', isActive: tpl.isActive !== false, sortOrder: tpl.sortOrder || 0, category: tpl.category || 'declaracao', isDefault: !!tpl.isDefault });
                        setDeclTemplateModal('edit');
                      }}
                      className="p-1 text-slate-400 hover:text-blue-400 transition-colors"
                      title="Editar"
                    ><Icon name="edit" className="w-3 h-3" /></button>
                    <button
                      onClick={async () => {
                        if (!(await confirm({ title: `Excluir "${tpl.name}"?`, danger: true, confirmLabel: 'Excluir' }))) return;
                        try { await procurementService.deleteDeclarationTemplate(tpl.id); showToast('Excluído'); loadDeclarationTemplates(); } catch (e: any) { showToast('Erro', 'error'); }
                      }}
                      className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                      title="Excluir"
                    ><Icon name="trash" className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 truncate">{tpl.title}</div>
                {/* Hover preview do conteúdo do template */}
                {tpl.body && (
                  <div className="absolute left-0 top-full mt-1 z-50 w-[520px] max-h-[400px] overflow-hidden bg-central-fundo/98 backdrop-blur border border-slate-600 rounded-lg p-3 shadow-2xl opacity-0 invisible group-hover/tpl:opacity-100 group-hover/tpl:visible transition-all duration-150 pointer-events-none">
                    <div className="text-[10px] text-orange-400 font-semibold mb-1">{tpl.title || tpl.name}</div>
                    <div className="text-[10px] text-slate-300 whitespace-pre-wrap leading-relaxed line-clamp-[18]">
                      {(tpl.body || '')
                        .replace(/\{\{empresaNome\}\}/g, COMPANY.name)
                        .replace(/\{\{empresaCnpj\}\}/g, COMPANY.cnpj)
                        .replace(/\{\{empresaEndereco\}\}/g, COMPANY.address)
                        .replace(/\{\{empresaTelefone\}\}/g, COMPANY.phone)
                        .replace(/\{\{representante\}\}/g, SIGNERS.fabio.name)
                        .replace(/\{\{representanteCpf\}\}/g, SIGNERS.fabio.cpf)
                        .replace(/\{\{representanteRg\}\}/g, SIGNERS.fabio.rg)
                        .replace(/\{\{representanteCargo\}\}/g, SIGNERS.fabio.cargo)
                        .replace(/\{\{[^}]+\}\}/g, '[…]')
                        .substring(0, 1200)}
                      {tpl.body.length > 1200 ? '…' : ''}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
            );
          })
        )}
        </EstadoDaLista>
      </div>
      </CascaDeModal>
      )}
      {signatureModal && (
        <SignatureBatchModal
          singleDoc={signatureSingleDoc}
          docs={ws.generatedDocs}
          proposal={editingProposal}
          replaceMode={signatureReplaceMode}
          onClose={() => { setSignatureModal(false); setSignatureSingleDoc(null); setSignatureReplaceMode(false); }}
          showToast={showToast}
          onSent={async () => {
            await loadProposals();
            if (workspaceProposalId) await ws.reloadGeneratedDocs();
          }}
        />
      )}
      {subView === 'proposals' && (workspaceProposalId ? (
        <ProposalWorkspace
          ws={ws}
          actions={workspaceActions}
          latestJob={workspaceLatestJob}
          activeOp={workspaceLiveProposal?.item?.id ? activeOps[workspaceLiveProposal.item.id] : null}
          fmt={fmt}
          catserOptions={CATSER_TELECOM}
          templates={declarationTemplates}
          jobs={automationJobs.filter((j: any) => j.proposalId === workspaceProposalId)}
          historicoDeEnvio={cargaDosJobs}
          onOpenSignature={(doc, opts) => {
            if (workspaceLiveProposal) setEditingProposal(workspaceLiveProposal);
            setSignatureSingleDoc(doc || null);
            setSignatureReplaceMode(Boolean(opts?.replace));
            setSignatureModal(true);
          }}
        />
      ) : renderProposalsTab())}
      {subView === 'portals' && renderPortalsTab()}
      {subView === 'monitoring' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">Monitorar</h2>
              <p className="text-sm text-slate-400">Acompanhe licitações em julgamento, avisos, suspensões e homologações.</p>
            </div>
            <button
              onClick={openCheckExpired}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto"
              title="Re-consulta PNCP+portal pras propostas enviadas com sessão vencida há >24h. Atualiza status (homologada/encerrada) e arquiva automaticamente quando confirmado."
            >
              <Icon name="refresh" className={`w-4 h-4 ${checkExpiredData?.running ? 'animate-spin' : ''}`} />
              {checkExpiredData?.running
                ? `Verificando ${checkExpiredData.processed}/${checkExpiredData.total}`
                : 'Verificar vencidos'}
            </button>
          </div>
          <MonitoringView showToast={showToast} />
        </>
      )}
      {subView === 'disputes' && <DisputeView showToast={showToast} onNavigate={onNavigate} />}
      {subView === 'portalHealth' && renderPortalHealthTab()}
      {/* NOTA (23/05/2026): subViews 'signatures' (Assinaturas) e 'products'
          (Produtos) removidas — menu correspondente foi removido e features
          não eram usadas. renderSignaturesTab / renderProductsTab / loadProducts
          / loadPendingSignatures + state relacionado também limpos. */}

      {/* Modals */}
      {renderSearchModal()}
      {renderDetailModal()}
      {renderProposalModal()}
      {/* Modal "Adequar ao lance final" — rateio dry-run com prova de soma */}
      {adequarModal && (
        // Casca de modal (05/10/2026). Clique fora e Esc fecham, como o fundo já
        // fechava: nada é gravado aqui, o Aplicar só escreve na página de trabalho
        // e quem salva é o operador. z-[70]: abre por cima da página de trabalho.
        <CascaDeModal
          titulo="Adequar ao lance final"
          largura="md"
          zIndex="z-[70]"
          fecharAoClicarFora
          corpoClassName="px-5 py-4 space-y-3"
          onClose={() => setAdequarModal(false)}
          rodape={<>
            <button onClick={() => setAdequarModal(false)} className="px-4 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-sm transition-colors">Cancelar</button>
            {adequarResult?.ok && Array.isArray(adequarResult.linhas) && (
              <button
                onClick={applyAdequacao}
                className="ml-auto px-4 py-1.5 bg-primary hover:bg-primary-strong text-white rounded text-sm font-medium transition-colors"
                title="Grava os unitários novos na página de trabalho — depois confira e SALVE a proposta"
              >✓ Aplicar na proposta</button>
            )}
          </>}
        >
            <p className="text-[11px] text-slate-400">
              Rateio proporcional aos <b>máximos do edital</b>, sem majorar nenhum unitário; o resíduo de arredondamento vai pra taxa única. Nada é salvo até você Aplicar e depois <b>Salvar</b> a proposta.
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                {/* Label LIGADO ao campo (08/10/2026): sem htmlFor/id o campo não tinha nome acessível, e quem
                    procurava por ele caía no placeholder "0,00", que o unitário de cada item da página também usa. */}
                <label htmlFor="adequar-valor-alvo" className="text-xs text-slate-400 block mb-1">Valor alvo (lance final) *</label>
                <input
                  id="adequar-valor-alvo"
                  type="number"
                  step="0.01"
                  min="0"
                  value={adequarAlvo}
                  onChange={(e) => setAdequarAlvo(e.target.value)}
                  className={`w-40 ${campo} rounded px-3 py-1.5 text-sm text-right outline-none focus:border-green-400`}
                  placeholder="0,00"
                  autoFocus
                />
                {adequarResult?.valorAlvoSugerido && (
                  <div className="text-[10px] text-slate-500 mt-1">
                    Sugerido: <button type="button" className="text-green-400 hover:underline" onClick={() => { setAdequarAlvo(String(adequarResult.valorAlvoSugerido.valor)); runAdequar(String(adequarResult.valorAlvoSugerido.valor)); }}>R$ {fmt(adequarResult.valorAlvoSugerido.valor)}</button>
                    <span className="text-slate-600"> ({adequarResult.valorAlvoSugerido.fonte})</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Meses</label>
                <input
                  type="number"
                  min="1"
                  value={adequarMeses}
                  onChange={(e) => setAdequarMeses(e.target.value)}
                  className={`w-20 ${campo} rounded px-3 py-1.5 text-sm text-right outline-none focus:border-green-400`}
                />
              </div>
              <button
                type="button"
                disabled={adequarLoading}
                onClick={() => runAdequar()}
                className="px-4 py-1.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 disabled:opacity-50 text-white rounded text-sm font-medium transition-colors"
              >{adequarLoading ? 'Calculando…' : 'Calcular'}</button>
            </div>

            {adequarResult && !adequarResult.ok && (
              <div className="px-3 py-2 rounded border border-red-600/50 bg-red-900/20 text-xs text-red-300">{adequarResult.erro}</div>
            )}
            {adequarResult?.aviso && (
              <div className="px-3 py-2 rounded border border-amber-600/50 bg-amber-900/20 text-xs text-amber-300">{adequarResult.aviso}</div>
            )}

            {adequarResult?.ok && Array.isArray(adequarResult.linhas) && (
              <>
                <div className="overflow-x-auto rounded-lg border border-central-borda">
                  <table className="w-full text-xs">
                    <thead className="bg-central-elevado">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-slate-400 font-medium">Item</th>
                        <th className="px-2 py-1.5 text-right text-slate-400 font-medium">Unit. máx</th>
                        <th className="px-2 py-1.5 text-right text-slate-400 font-medium">Unit. atual</th>
                        <th className="px-2 py-1.5 text-right text-green-400 font-medium">Unit. NOVO</th>
                        <th className="px-2 py-1.5 text-right text-slate-400 font-medium">Mensal</th>
                        <th className="px-2 py-1.5 text-right text-slate-400 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {adequarResult.linhas.map((l: any) => (
                        <tr key={l.itemNumber}>
                          <td className="px-2 py-1.5 text-slate-300">
                            <span className="text-slate-500 mr-1">{String(l.itemNumber).padStart(2, '0')}</span>
                            {l.description || ''}
                            {l.chargeType === 'taxa_unica' && <span className="text-[9px] text-slate-500 ml-1">(taxa única)</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-500">{l.unitMax}</td>
                          <td className="px-2 py-1.5 text-right text-slate-400">{l.unitAntes}</td>
                          <td className="px-2 py-1.5 text-right text-green-300 font-bold">{l.unitNovo}</td>
                          <td className="px-2 py-1.5 text-right text-slate-400">{l.mensalNovo != null ? fmt(l.mensalNovo) : '—'}</td>
                          <td className="px-2 py-1.5 text-right text-white font-medium">{fmt(l.totalNovo)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-central-elevado/60">
                      <tr>
                        <td colSpan={5} className="px-2 py-2 text-right text-slate-300">
                          Máximos: <span className="text-slate-400">R$ {fmt(adequarResult.somaMaxima)}</span>
                          <span className="mx-2 text-slate-600">→</span>
                          Alvo: <span className="text-slate-200">R$ {fmt(adequarResult.valorAlvo)}</span>
                          <span className="mx-2 text-slate-600">→</span>
                          <b className="text-green-400">Soma final:</b>
                        </td>
                        <td className="px-2 py-2 text-right text-green-400 font-bold text-sm">R$ {fmt(adequarResult.somaFinal)}</td>
                      </tr>
                      {adequarResult.diferenca !== 0 && (
                        <tr><td colSpan={6} className="px-2 pb-2 text-right text-[10px] text-amber-400">diferença p/ o alvo: R$ {fmt(adequarResult.diferenca)}</td></tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </>
            )}
        </CascaDeModal>
      )}
      {importModal && (
        // Formulário: antes fechava no clique fora (exceto importando); agora só pelo ×
        // ou Cancelar, travados enquanto importa.
        <CascaDeModal
          largura="sm"
          titulo="Importar licitação"
          onClose={() => { if (!importBusy) setImportModal(false); }}
          corpoClassName="p-4 space-y-4 text-sm"
          rodape={
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setImportModal(false)} disabled={importBusy} className="px-4 py-2 text-slate-300 hover:text-white text-sm disabled:opacity-40">Cancelar</button>
              <button onClick={handleImportSubmit} disabled={importBusy || (importSemEdital ? (!importOrgao.trim() && !importUasg.trim()) : (!importUasg || !importNumero))} className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-2">
                {importBusy ? <><Icon name="refresh" className="w-4 h-4 animate-spin" /> {importSemEdital ? 'Cadastrando…' : 'Importando…'}</> : <><Icon name="download" className="w-4 h-4" /> {importSemEdital ? 'Cadastrar oportunidade' : 'Importar e criar proposta'}</>}
              </button>
            </div>
          }
        >
              <p className="text-xs text-slate-400">Cadastre uma licitação específica (que não veio pelo Encontrar) pra participar. Cola o link do ComprasNet ou preenche UASG + número — o sistema puxa do PNCP (egress Mac mini) e cria a proposta no mesmo padrão.</p>

              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer bg-central-fundo/40 rounded-lg p-2.5 border border-central-borda">
                <input type="checkbox" checked={importSemEdital} onChange={(e) => setImportSemEdital(e.target.checked)} className="w-4 h-4" />
                <div>
                  <div className="font-medium">📝 Sem edital publicado (cadastro manual)</div>
                  <div className="text-[10px] text-slate-500">Oportunidade pré-edital: número opcional, você preenche órgão/objeto/valor. Cria como Rascunho.</div>
                </div>
              </label>

              {!importSemEdital && (
                <>
                  {/* Detecção por link */}
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Link do ComprasNet ou ID da compra</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={importInput}
                        onChange={(e) => setImportInput(e.target.value)}
                        placeholder="…/cadastro-propostas?compra=15842906000902026"
                        className={`flex-1 rounded-lg px-3 py-2 text-xs placeholder-slate-500 ${campo}`}
                      />
                      <button onClick={handleImportDetect} className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-xs whitespace-nowrap">Detectar</button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-600"><div className="flex-1 h-px bg-slate-700" />ou preencha manualmente<div className="flex-1 h-px bg-slate-700" /></div>
                </>
              )}

              {/* Campos manuais */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Portal</label>
                  <select value={importPortal} onChange={(e) => setImportPortal(e.target.value)} className={`w-full ${campo} rounded-lg px-2 py-2 text-xs`}>
                    <option value="comprasnet">ComprasNet</option>
                    <option value="bll">BLL</option>
                    <option value="bnc">BNC</option>
                    <option value="pcp">Portal Compras Públicas</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Modalidade</label>
                  <select value={importModalidade} onChange={(e) => setImportModalidade(e.target.value as 'pe' | 'de')} className={`w-full ${campo} rounded-lg px-2 py-2 text-xs`}>
                    <option value="pe">Pregão Eletrônico</option>
                    <option value="de">Dispensa Eletrônica</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">UASG / Cód. unidade</label>
                  <input type="text" value={importUasg} onChange={(e) => setImportUasg(e.target.value)} placeholder="158429" className={`w-full rounded-lg px-3 py-2 text-xs placeholder-slate-500 ${campo}`} />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Número / Ano{importSemEdital ? ' (opcional)' : ''}</label>
                  <input type="text" value={importNumero} onChange={(e) => setImportNumero(e.target.value)} placeholder={importSemEdital ? 'deixe vazio se não tem' : '90/2026'} className={`w-full rounded-lg px-3 py-2 text-xs placeholder-slate-500 ${campo}`} />
                </div>
              </div>

              {importSemEdital && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Órgão</label>
                    <input type="text" value={importOrgao} onChange={(e) => setImportOrgao(e.target.value)} placeholder="Ex.: Junta Comercial do Estado de São Paulo (JUCESP)" className={`w-full rounded-lg px-3 py-2 text-xs placeholder-slate-500 ${campo}`} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Objeto</label>
                    <textarea value={importObjeto} onChange={(e) => setImportObjeto(e.target.value)} rows={2} placeholder="Ex.: Serviços de Telefonia Fixa Comutada (STFC) — E1 + ramais DDR" className={`w-full rounded-lg px-3 py-2 text-xs placeholder-slate-500 resize-none ${campo}`} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Valor estimado (R$)</label>
                    <input type="number" step="0.01" value={importValor} onChange={(e) => setImportValor(e.target.value)} placeholder="89544.00" className={`w-full rounded-lg px-3 py-2 text-xs placeholder-slate-500 ${campo}`} />
                  </div>
                </div>
              )}

              {!importSemEdital && (<>
              <button onClick={handleImportPreview} disabled={importLoading || !importUasg || !importNumero} className="w-full px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-slate-200 rounded-lg text-xs flex items-center justify-center gap-2">
                <Icon name="search" className={`w-3.5 h-3.5 ${importLoading ? 'animate-spin' : ''}`} /> Pré-visualizar do PNCP (opcional)
              </button>

              {importPreview && !importPreview._empty && (
                <div className="bg-central-fundo/50 border border-central-borda rounded-lg p-3 text-xs space-y-1">
                  <div className="text-slate-300 font-medium">{importPreview.orgaoNome || '—'}</div>
                  {importPreview.unidadeNome && <div className="text-slate-500">{importPreview.unidadeNome} · {importPreview.uf || ''} {importPreview.municipio || ''}</div>}
                  {importPreview.objetoCompra && <div className="text-slate-400 line-clamp-2">{importPreview.objetoCompra}</div>}
                  {importPreview.valorEstimado != null && <div className="text-emerald-400">Estimado: {fmt(importPreview.valorEstimado)}</div>}
                  {(importPreview.dataAbertura || importPreview.dataEncerramento) && <div className="text-slate-500">Proposta: {importPreview.dataAbertura ? fmtDateTime(importPreview.dataAbertura) : '?'} → {importPreview.dataEncerramento ? fmtDateTime(importPreview.dataEncerramento) : '?'}</div>}
                </div>
              )}
              {importPreview?._empty && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-[11px] text-amber-300">Sem dados no PNCP agora — pode importar mesmo assim. Os dados completos (objeto, itens, datas de sessão) vêm via "Sincronizar do Portal" depois.</div>
              )}
              </>
              )}
        </CascaDeModal>
      )}
      {submitModal && (() => {
        const p = submitModal.proposal;
        // Mesma regra de pricedItems (proposalStage.ts): em item de MAIOR
        // DESCONTO quem diz se há oferta é o percentual, não o unitário
        // derivado — 0% é ofertar o estimado e 100% zera o unitário.
        const preenchidos = pricedItems(p);
        const totalValor = preenchidos.reduce((acc: number, pi: any) => acc + Number(pi.unitPrice) * (pi.quantity || 1), 0);
        // 3 estados de requiresBrandModel:
        //   true  → portal exige → BLOQUEIA se vazio (vermelho)
        //   false → portal não exige → libera
        //   null  → desconhecido (nunca scrapeado) → WARNING amarelo mas permite
        // `requiresModel` conta junto: o scrape marca as DUAS (tipoItemCatalogo
        // === 'M') e só uma delas sobrevivia ao caminho front→backend.
        // ⚠️ este guard já foi contornado uma vez — as flags do portal tinham
        // sido apagadas por um save da tela, o item caiu no estado "desconhecido"
        // e a Checagem liberou 4 itens de material sem modelo (422 do TRT-11,
        // 28/08/2026). Hoje o backend também recusa, e a preservação das flags
        // vive em helpers/portal-item-flags.util.ts. Este aqui é o aviso, não a
        // última linha de defesa.
        const exigeMarcaModelo = (pi: any) => pi.requiresBrandModel === true || pi.requiresModel === true;
        const sabeSeExige = (pi: any) =>
          typeof pi.requiresBrandModel === 'boolean' || typeof pi.requiresModel === 'boolean';
        const semBrandObrigatorio = preenchidos.filter((pi: any) => {
          if (!exigeMarcaModelo(pi)) return false;
          return !((pi.brand || '').trim() && (pi.model || '').trim());
        });
        const semBrandDesconhecido = preenchidos.filter((pi: any) => {
          if (sabeSeExige(pi)) return false;
          return !((pi.brand || '').trim() && (pi.model || '').trim());
        });
        const semMarcaModelo = semBrandObrigatorio; // alias usado no JSX
        const bloqueado = semBrandObrigatorio.length > 0 && !submitModal.dryRun;
        // Portal define o ENDPOINT e o que a tela promete — nunca o layout.
        const portalOrigem = String(p.item?.portalOrigem || '').toLowerCase();
        const ehGkz = portalOrigem === 'bll' || portalOrigem === 'bnc';
        const ehPcp = portalOrigem === 'pcp';
        const ehAssincrono = ehGkz || ehPcp;
        const nomePortal = ehPcp ? 'Portal de Compras Públicas'
          : portalOrigem === 'bnc' ? 'BNC' : portalOrigem === 'bll' ? 'BLL' : 'ComprasNet';
        // Só o ComprasNet tem dry-run: nos demais o envio é HTTP puro, não há
        // "preencher sem salvar". Mostrar a opção prometia o que não existe.
        const temDryRun = !ehAssincrono;
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.target === e.currentTarget && (setSubmitModal(null))}>
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
              <PanelHeader
                title="Enviar Proposta"
                badges={<span className="truncate">{getOrgaoDisplay(p.item) || '—'}</span>}
                onClose={() => setSubmitModal(null)}
              />
              <div className="p-4 space-y-3 text-sm">
                <div className="text-slate-300">
                  <div className="text-xs text-slate-400">Licitação</div>
                  <div className="truncate">{getOrgaoDisplay(p.item) || '—'}</div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-slate-900/50 rounded p-2">
                    <div className="text-slate-400">Items preenchidos</div>
                    <div className="text-emerald-400 text-base font-medium">{preenchidos.length} / {p.proposalItems?.length || 0}</div>
                  </div>
                  <div className="bg-slate-900/50 rounded p-2">
                    <div className="text-slate-400">Total estimado</div>
                    <div className="text-emerald-400 text-base font-medium">R$ {totalValor.toFixed(2)}</div>
                  </div>
                </div>
                {/* O que vai pro portal nesses itens é um PERCENTUAL, e o total
                    acima é o preço que ele produz. Dizer isso na hora de enviar
                    evita a leitura errada de "mandei R$ X" — que foi como o
                    envio do TRT-8 acabou em HTTP 422. */}
                {preenchidos.some((pi: any) => pi.priceMode === 'desconto') && (
                  <div className="text-[11px] text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded p-2 leading-snug">
                    {preenchidos.filter((pi: any) => pi.priceMode === 'desconto').length} item(ns) julgado(s) por{' '}
                    <strong>maior desconto</strong>: o portal recebe o percentual
                    ({preenchidos.filter((pi: any) => pi.priceMode === 'desconto')
                      .map((pi: any) => `item ${pi.itemNumber}: ${pi.discountPercent}%`).join(' · ')}
                    ) e calcula o preço unitário a partir do valor estimado.
                  </div>
                )}
                {temDryRun && (
                  <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer bg-slate-900/40 rounded p-2.5 border border-slate-700">
                    <input
                      type="checkbox"
                      checked={submitModal.dryRun}
                      onChange={(e) => setSubmitModal({ ...submitModal, dryRun: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Modo dry-run (teste)</div>
                      <div className="text-[10px] text-slate-500">Preenche os campos no portal mas NÃO clica "Salvar" final. Desmarque para cadastro real.</div>
                    </div>
                  </label>
                )}
                {!submitModal.dryRun && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded p-2 text-[11px] text-orange-300">
                    ⚠️ <strong>Modo REAL:</strong> ao confirmar, a proposta será CADASTRADA no {nomePortal}.
                  </div>
                )}
                {semBrandObrigatorio.length > 0 && !submitModal.dryRun && (
                  <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-xs text-red-300 space-y-1">
                    <div className="font-medium">⛔ Marca/Modelo obrigatório — portal vai rejeitar</div>
                    <div className="text-[11px] text-red-300/80">
                      {semBrandObrigatorio.length} {semBrandObrigatorio.length === 1 ? 'item' : 'itens'} confirmadamente exige marca/modelo:
                    </div>
                    <ul className="text-[11px] text-red-200 list-disc list-inside pl-1">
                      {semBrandObrigatorio.slice(0, 5).map((pi: any) => (
                        <li key={pi.itemNumber}>Item {pi.itemNumber}: {(pi.description || '').substring(0, 60)}{!(pi.brand || '').trim() ? ' [sem marca]' : ''}{!(pi.model || '').trim() ? ' [sem modelo]' : ''}</li>
                      ))}
                      {semBrandObrigatorio.length > 5 && <li>... e mais {semBrandObrigatorio.length - 5}</li>}
                    </ul>
                    <div className="text-[10px] text-red-300/80 pt-1">Use "Editar avançado" pra preencher antes de enviar.</div>
                  </div>
                )}
                {semBrandDesconhecido.length > 0 && !submitModal.dryRun && (
                  <div className="bg-amber-500/10 border border-amber-500/40 rounded p-3 text-xs text-amber-300 space-y-1">
                    <div className="font-medium">⚠ Marca/Modelo não detectado ainda</div>
                    <div className="text-[11px] text-amber-300/80">
                      {semBrandDesconhecido.length} {semBrandDesconhecido.length === 1 ? 'item sem' : 'itens sem'} marca/modelo cujo requisito não foi scrapeado:
                    </div>
                    <ul className="text-[11px] text-amber-200 list-disc list-inside pl-1">
                      {semBrandDesconhecido.slice(0, 5).map((pi: any) => (
                        <li key={pi.itemNumber}>Item {pi.itemNumber}: {(pi.description || '').substring(0, 60)}</li>
                      ))}
                    </ul>
                    <div className="text-[10px] text-amber-300/80 pt-1">Pode enviar — se portal rejeitar, edite e tente de novo (próximo scrape vai marcar como obrigatório).</div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-700">
                <button onClick={() => setSubmitModal(null)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Cancelar</button>
                <button
                  disabled={bloqueado || travaDoEnvio.ocupado}
                  onClick={async () => {
                    if (bloqueado) return;
                    const dryRun = temDryRun && submitModal.dryRun;

                    // Trava de documentos do gkz ANTES de enfileirar. Vinha do
                    // modal de Automação e não podia se perder na unificação:
                    // sem ela o job falha em segundo plano e o erro some junto
                    // com a tela (Diamantino DE 007/2026, job 5161).
                    // Fechar o modal durante a conferência CANCELA o envio
                    // (conferenciaDoEnvio.ts).
                    const seguir = await travaDoEnvio.executar(async () => {
                      const ok = await conferirAntesDeEnviar({
                        ehGkz,
                        proposalId: p.id,
                        statusDosDocumentos: (id) => procurementService.getBllDocsStatus(id),
                        aindaAberto: () => submitModalRef.current?.proposal?.id === p.id,
                        avisar: showToast,
                      });
                      if (ok) setSubmitModal(null);
                      return ok;
                    });
                    if (!seguir) return;

                    showToast(`Enviando proposta ao ${nomePortal}${dryRun ? ' (teste)' : ''} — acompanhe o progresso no card...`);
                    try {
                      const result = await runWithProgress(
                        p.item?.id,
                        'submit',
                        `Enviando proposta${dryRun ? ' (teste)' : ''}`,
                        async () => {
                          if (!ehAssincrono) {
                            return procurementService.submitProposalAuto(p.item.id, { proposalId: p.id, dryRun });
                          }
                          // BLL/BNC/PCP devolvem jobId na hora e seguem em
                          // background. Seguramos a operação aberta até o job
                          // terminar — soltar aqui apagaria o card no primeiro
                          // segundo, que é justamente o que mandava o operador
                          // procurar o progresso na lista de jobs.
                          const r = ehPcp
                            ? await procurementService.submitProposalPcp(p.id)
                            : portalOrigem === 'bnc'
                              ? await procurementService.submitProposalBnc(p.id)
                              : await procurementService.submitProposalBll(p.id);
                          const limite = Date.now() + 8 * 60 * 1000;
                          while (Date.now() < limite) {
                            await new Promise((res) => setTimeout(res, 3000));
                            try {
                              const j = await procurementService.getAutomationJob(r.jobId);
                              if (j && ['completed', 'failed', 'cancelled', 'needs_manual'].includes(j.status)) {
                                return { ok: j.status === 'completed', error: j.error || j.status, jobStatus: j.status, durationMs: 0 };
                              }
                            } catch { /* poll best-effort */ }
                          }
                          return { ok: false, error: 'tempo esgotado esperando o portal — veja o histórico de automações' };
                        },
                      );
                      // Jobs também: o selo "Última automação falhou" lê o job mais
                      // recente da lista, e sem recarregar ficava mostrando o job
                      // anterior por cima da proposta já enviada (Gravatal, 07/09/2026).
                      if (result.ok) {
                        showToast(
                          `✓ ${dryRun ? '(dryRun)' : 'Cadastrado'} no ${nomePortal}`
                            + (result.savedItems?.length ? `: ${result.savedItems.length} item(s)` : ''),
                          'success',
                        );
                        await Promise.all([loadProposals(), loadAutomationJobs()]);
                      } else {
                        showToast(`✗ Falhou: ${result.error}`, 'error');
                        await Promise.all([loadProposals(), loadAutomationJobs()]);
                      }
                    } catch (err: any) {
                      showToast(`Erro: ${err?.response?.data?.message || err?.message || err}`, 'error');
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${bloqueado ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : travaDoEnvio.ocupado ? 'bg-slate-700 text-slate-400 cursor-wait' : submitModal.dryRun ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                  title={bloqueado ? 'Preencha marca/modelo dos itens antes de cadastrar' : ''}
                >
                  {bloqueado ? '⚠ Marca/Modelo pendente' : travaDoEnvio.ocupado ? 'Conferindo documentos…' : submitModal.dryRun ? '🧪 Executar Dry-Run' : '🚀 Cadastrar No Portal'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {renderDocModal()}
      {renderProductModal()}
      {renderPortalModal()}
      {renderDocPreviewModal()}
      {renderDeleteConfirm()}
      {renderAutomationModal()}

      {/* Declaration Template Modal */}
      {declTemplateModal && (
        // Editor (corpo da declaração digitado): antes fechava no clique fora; agora só
        // pelo × ou Cancelar.
        <CascaDeModal
          largura="md"
          titulo={declTemplateModal === 'edit' ? 'Editar Template' : 'Novo Template'}
          onClose={() => setDeclTemplateModal(null)}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-2">
              <button onClick={() => setDeclTemplateModal(null)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Cancelar</button>
              <button onClick={handleSaveDeclTemplate} disabled={!declTemplateForm.name || !declTemplateForm.title || !declTemplateForm.body} className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm">Salvar</button>
            </div>
          }
        >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Nome interno *</label>
                  <input value={declTemplateForm.name} onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, name: e.target.value })} className={`w-full ${campo} rounded-lg px-3 py-2 text-sm`} placeholder="Ex: Declaração Unificada" />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Título no PDF *</label>
                  <input value={declTemplateForm.title} onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, title: e.target.value })} className={`w-full ${campo} rounded-lg px-3 py-2 text-sm`} placeholder="Ex: DECLARAÇÃO UNIFICADA PARA HABILITAÇÃO" />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 block mb-1">Corpo da declaração *</label>
                <textarea value={declTemplateForm.body} onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, body: e.target.value })} rows={12} className={`w-full ${campo} rounded-lg px-3 py-2 text-sm font-mono`} placeholder={'A empresa {{empresaNome}}, inscrita no CNPJ {{empresaCnpj}}...'} />
                <div className="text-[10px] text-slate-500 mt-1">Variáveis: {'{{orgaoNome}}'}, {'{{numeroCompra}}'}, {'{{anoCompra}}'}, {'{{modalidadeNome}}'}, {'{{objeto}}'}, {'{{empresaNome}}'}, {'{{empresaCnpj}}'}, {'{{empresaEndereco}}'}, {'{{representante}}'}, {'{{representanteCpf}}'}, {'{{representanteRg}}'}, {'{{representanteCargo}}'}, {'{{codigoUnidade}}'}</div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-400">Categoria:</label>
                  <select
                    value={declTemplateForm.category}
                    onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, category: e.target.value, isDefault: e.target.value === 'termo' ? false : declTemplateForm.isDefault })}
                    className={`${campo} rounded px-2 py-1 text-sm`}
                  >
                    <option value="declaracao">Declaração (recorrente)</option>
                    <option value="termo">Termo (uso pontual)</option>
                  </select>
                </div>
                {declTemplateForm.category !== 'termo' && (
                  <label className="flex items-center gap-2 cursor-pointer" title='Entra pré-selecionável pelo atalho "Padrão" no painel de geração'>
                    <input type="checkbox" checked={declTemplateForm.isDefault} onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, isDefault: e.target.checked })} className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500" />
                    <span className="text-sm text-slate-300">Incluir no padrão</span>
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={declTemplateForm.isActive} onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, isActive: e.target.checked })} className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500" />
                  <span className="text-sm text-slate-300">Ativo</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-400">Ordem:</label>
                  <input type="number" value={declTemplateForm.sortOrder} onChange={(e) => setDeclTemplateForm({ ...declTemplateForm, sortOrder: parseInt(e.target.value) || 0 })} className={`w-16 ${campo} rounded px-2 py-1 text-sm text-center`} />
                </div>
              </div>
        </CascaDeModal>
      )}
      {/* Modal "Testar perfil" — resultado do dry-run de keywords */}
      {searchPreview && (
        // Só leitura (dry-run, nada é criado): fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="md"
          zIndex="z-[60]"
          fecharAoClicarFora
          titulo={`🧪 Teste do perfil "${searchPreview.name}"`}
          onClose={() => setSearchPreview(null)}
          topo={<p className="px-5 pt-3 text-xs text-slate-400">{searchPreview.total} licitação(ões) ABERTAS casariam hoje na base crua do PNCP — nada foi criado</p>}
          corpoClassName="p-4"
        >
              {searchPreview.sample?.length === 0 ? (
                <div className="text-center text-slate-500 py-8">Nenhuma licitação aberta casa com as keywords atuais.</div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[11px] text-slate-500 mb-1">Amostra (10 mais próximas do encerramento):</div>
                  {searchPreview.sample.map((r: any, i: number) => (
                    <div key={i} className="bg-slate-700/30 border border-slate-600/50 rounded-lg p-2.5">
                      <div className="text-xs text-slate-200 leading-snug">{r.objeto}</div>
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{r.orgao}</span>
                        {r.municipio && <span>· {r.municipio}/{r.uf}</span>}
                        {r.modalidade && <span>· {r.modalidade}</span>}
                        {r.encerramento && <span>· Prop. até {fmtDate(r.encerramento)}</span>}
                        {r.jaNoHub && <span className="text-emerald-400">✓ já está no HUB</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </CascaDeModal>
      )}
      {renderAutomationJobModal()}

      {/* Metrics Dashboard Modal */}
      {showMetricsModal && (
        // Só leitura: antes não fechava ao clicar fora; agora fecha. Altura de tela:
        // o painel é largo e alto, e a rolagem passa da página à casca.
        <CascaDeModal
          largura="2xl"
          altura="tela"
          fecharAoClicarFora
          titulo="Dashboard de Licitacoes"
          onClose={() => setShowMetricsModal(false)}
          topo={<p className="px-6 pt-3 text-sm text-slate-400">Visao geral das licitacoes monitoradas por UF, modalidade e periodo</p>}
          corpoClassName="p-6"
        >
              {renderMetricsDashboard()}
        </CascaDeModal>
      )}

      {/* Search Profiles Modal */}
      {showSearchProfilesModal && !searchModal && (
        // Lista (o editor de perfil abre no lugar dela): antes não fechava ao clicar
        // fora; agora fecha, porque nada fica digitado aqui.
        <CascaDeModal
          largura="lg"
          fecharAoClicarFora
          titulo="Perfis de Busca"
          onClose={() => setShowSearchProfilesModal(false)}
          topo={<p className="px-6 pt-3 text-sm text-slate-400">Configure palavras-chave, estados e modalidades para monitoramento automatico</p>}
          corpoClassName="p-6"
        >
              {renderSearchesList()}
        </CascaDeModal>
      )}

      {/* Modal: Contratação direta (fornecedor já nomeado) → lixeira */}
      {cdModal && (
        // Prévia sem escolha: fecha ao clicar fora, como já era, exceto enquanto move.
        // "Mover pra lixeira" segue vermelho.
        <CascaDeModal
          largura="base"
          zIndex="z-[60]"
          fecharAoClicarFora
          titulo="Contratação direta detectada"
          onClose={() => { if (!cdBusy) setCdModal(null); }}
          topo={<p className="px-5 pt-3 text-sm text-slate-400">Dispensas com fornecedor já nomeado no objeto — não são oportunidades de participação.</p>}
          corpoClassName="p-5"
          rodape={
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setCdModal(null)} disabled={cdBusy} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Fechar</button>
              {cdModal.length > 0 && (
                <button onClick={applyContratacaoDireta} disabled={cdBusy} className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg">
                  {cdBusy ? 'Movendo…' : `Mover ${cdModal.length} pra lixeira`}
                </button>
              )}
            </div>
          }
        >
              {cdModal.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">Nenhuma contratação direta encontrada na lista atual. 👍</div>
              ) : (
                <>
                  <div className="text-sm text-slate-300 mb-3">{cdModal.length} licitação(ões) serão movidas pra lixeira (recuperável):</div>
                  <div className="rounded-lg border border-central-borda/50 divide-y divide-slate-700/40 max-h-[50vh] overflow-y-auto">
                    {cdModal.map((it) => (
                      <div key={it.id} className="px-3 py-2 text-xs">
                        <div className="text-slate-200 font-medium">{it.orgao} <span className="text-slate-500">· {it.municipio}</span></div>
                        <div className="text-slate-400 mt-0.5">{it.objeto}…</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
        </CascaDeModal>
      )}

      {/* Modal: Revisão de exclusões (preview → descartar / whitelist) */}
      {exclModal && (
        // Escolhas marcadas (descartar / manter): antes fechava ao clicar fora; agora
        // só pelo × ou Cancelar, travados enquanto aplica.
        <CascaDeModal
          largura="md"
          zIndex="z-[60]"
          titulo="Revisar exclusões"
          onClose={() => { if (!exclBusy) setExclModal(null); }}
          topo={
            <p className="px-5 pt-3 pb-2 text-sm text-slate-400 border-b border-central-borda">
              {exclModal.items.length} licitação(ões) batem os termos:{' '}
              {exclModal.termos.map((t) => (
                <span key={t} className="inline-block px-1.5 py-0.5 mr-1 rounded bg-red-500/15 text-red-300 text-xs">{t}</span>
              ))}
            </p>
          }
          corpoClassName="divide-y divide-slate-700/40"
          rodape={
            <>
              <div className="text-xs text-slate-400">{exclDescartar.size} a descartar · {exclWhitelist.size} na whitelist</div>
              <div className="ml-auto flex gap-2">
                <button onClick={() => setExclModal(null)} disabled={exclBusy} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancelar</button>
                <button onClick={applyExcl} disabled={exclBusy || (exclDescartar.size === 0 && exclWhitelist.size === 0)} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white rounded-lg">
                  {exclBusy ? 'Aplicando...' : 'Aplicar'}
                </button>
              </div>
            </>
          }
        >
              {exclModal.items.map((it: any) => {
                const desc = exclDescartar.has(it.id);
                const wl = exclWhitelist.has(it.id);
                return (
                  <div key={it.id} className="flex items-start gap-3 py-3 px-5 hover:bg-slate-700/20">
                    <input
                      type="checkbox"
                      disabled={it.temProposta || wl}
                      checked={desc}
                      onChange={(e) => setExclDescartar((prev) => { const n = new Set(prev); e.target.checked ? n.add(it.id) : n.delete(it.id); return n; })}
                      className="mt-1 disabled:opacity-30"
                      title={it.temProposta ? 'Tem proposta — não pode ser descartada' : 'Descartar'}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        {it.orgaoNome || '—'}
                        <span className="text-slate-500 font-normal"> · {it.portalOrigem || '?'} {it.numeroCompra}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{it.objetoCompra}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300">termo: {it.termoQueBateu}</span>
                        {it.temProposta && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">🔒 tem proposta — não será descartada</span>}
                        {wl && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">✓ manter (whitelist)</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setExclWhitelist((prev) => { const n = new Set(prev); n.has(it.id) ? n.delete(it.id) : n.add(it.id); return n; });
                        setExclDescartar((prev) => { const n = new Set(prev); n.delete(it.id); return n; }); // whitelist tira de descartar
                      }}
                      className={`shrink-0 text-xs px-2.5 py-1 rounded border transition-colors ${wl ? 'bg-emerald-600/30 border-emerald-600/50 text-emerald-200' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}
                    >
                      {wl ? '✓ Mantida' : 'Manter'}
                    </button>
                  </div>
                );
              })}
        </CascaDeModal>
      )}

      {/* Modal: Resumo da coleta (Sincronizar → AlertaLicitação) */}
      {syncModal && (
        // Só leitura (a coleta roda no servidor): fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="sm"
          zIndex="z-[60]"
          fecharAoClicarFora
          titulo={<span className="flex items-center gap-2">Resumo da coleta{syncData?.running && <Icon name="refresh" className="w-4 h-4 animate-spin text-slate-400" />}</span>}
          onClose={closeSync}
          topo={<p className="px-5 pt-3 text-sm text-slate-400">Busca no AlertaLicitação com as keywords dos perfis ativos.</p>}
          corpoClassName="p-5"
          rodape={<button onClick={closeSync} className="ml-auto px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg">Fechar</button>}
        >
              {syncData?.running ? (
                <div className="text-center py-8 text-slate-400 text-sm flex items-center justify-center gap-2">
                  <Icon name="refresh" className="w-4 h-4 animate-spin" /> Coletando do AlertaLicitação…
                </div>
              ) : syncData?.error ? (
                <div className="p-3 bg-red-500/15 border border-red-600/30 rounded-lg text-red-200 text-sm">⚠ {syncData.error}</div>
              ) : (
                <>
                  {/* Contadores */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2 text-center"><div className="text-xl font-bold text-emerald-300">{syncData?.inserted ?? 0}</div><div className="text-[10px] text-slate-400">novas</div></div>
                    <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-2 text-center"><div className="text-xl font-bold text-sky-300">{syncData?.updated ?? 0}</div><div className="text-[10px] text-slate-400">atualizadas</div></div>
                    <div className="bg-slate-700/40 rounded-lg p-2 text-center"><div className="text-xl font-bold text-slate-300">{syncData?.skipped ?? 0}</div><div className="text-[10px] text-slate-400">descartadas</div></div>
                    <div className="bg-slate-700/40 rounded-lg p-2 text-center"><div className="text-xl font-bold text-slate-300">{syncData?.received ?? 0}</div><div className="text-[10px] text-slate-400">recebidas</div></div>
                  </div>
                  {/* Custo vs franquia */}
                  <div className={`mb-4 px-3 py-2 rounded-lg text-xs ${(syncData?.received ?? 0) > 100 ? 'bg-amber-500/10 border border-amber-600/30 text-amber-200' : 'bg-emerald-500/10 border border-emerald-600/30 text-emerald-200'}`}>
                    {(syncData?.received ?? 0) > 100
                      ? `⚠ ${syncData.received} licitações recebidas hoje — acima das 100 grátis/dia (excedente pode gerar custo).`
                      : `✓ ${syncData?.received ?? 0} de 100 licitações grátis/dia — dentro da franquia (sem custo).`}
                  </div>
                  {/* Por keyword */}
                  {Array.isArray(syncData?.perKeyword) && syncData.perKeyword.length > 0 && (
                    <div>
                      <div className="text-xs text-slate-400 mb-1.5">Por palavra-chave</div>
                      <div className="rounded-lg border border-central-borda/50 divide-y divide-slate-700/40">
                        {syncData.perKeyword.map((p: any, i: number) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                            <span className="text-slate-200">{p.keyword}</span>
                            <span className="text-slate-500">{p.pagesFetched} página(s)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
        </CascaDeModal>
      )}

      {/* Modal: Progresso do re-enrich PNCP */}
      {/* Modal: Verificar Vencidos (idem padrão Re-enriquecer, mas pra items com proposta vencida >24h) */}
      {checkExpiredModal && (
        // Fecha ao clicar fora, como já era: o job roda no servidor e o próprio modal
        // avisa que dá para fechar. "Iniciar verificação" passa de verde a laranja.
        <CascaDeModal
          largura="base"
          zIndex="z-[60]"
          fecharAoClicarFora
          titulo={<span className="flex items-center gap-2">Verificar Vencidos{checkExpiredData?.running && <Icon name="refresh" className="w-4 h-4 animate-spin text-slate-400" />}</span>}
          onClose={closeCheckExpired}
          topo={<p className="px-5 pt-3 text-xs text-slate-400">Re-consulta PNCP + portal pras propostas enviadas com sessão vencida há mais de 24h. Atualiza status e arquiva quando confirma homologação/encerramento.</p>}
          corpoClassName="p-5 space-y-4"
          rodape={
            <div className="ml-auto flex items-center gap-2">
              {checkExpiredData?.running && (
                <button
                  onClick={handleStopCheckExpired}
                  className="px-3 py-2 text-sm bg-red-600/70 hover:bg-red-600 text-white rounded-lg flex items-center gap-2"
                >
                  <Icon name="x" className="w-4 h-4" />
                  Parar
                </button>
              )}
              <button onClick={closeCheckExpired} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg">Fechar</button>
            </div>
          }
        >
              {/* Estado ocioso — pre-start */}
              {!checkExpiredData && (
                <div className="text-center py-6">
                  {checkExpiredPending === null ? (
                    <div className="text-sm text-slate-400">Carregando…</div>
                  ) : checkExpiredPending < 0 ? (
                    <div className="text-sm text-red-400">Erro ao consultar.</div>
                  ) : checkExpiredPending === 0 ? (
                    <div>
                      <div className="text-2xl font-bold text-slate-500 mb-2">0</div>
                      <div className="text-sm text-slate-400">Nenhuma proposta enviada com sessão vencida há mais de 24h. ✓</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-3xl font-bold text-emerald-400 mb-2">{checkExpiredPending}</div>
                      <div className="text-sm text-slate-300 mb-1">proposta(s) enviada(s) com sessão vencida</div>
                      <div className="text-xs text-slate-500 mb-5">Pra cada uma, vamos consultar o PNCP + portal e finalizar caso confirmem homologação/fracasso/revogação.</div>
                      <button
                        onClick={startCheckExpiredNow}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm font-medium"
                      >
                        <Icon name="refresh" className="w-4 h-4" />
                        Iniciar verificação
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Job rodando ou concluído */}
              {checkExpiredData && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white font-medium">{checkExpiredData.processed} de {checkExpiredData.total} processados</span>
                    <span className={`text-xs ${checkExpiredData.running ? 'text-emerald-400' : checkExpiredData.aborted ? 'text-amber-400' : 'text-slate-400'}`}>
                      {checkExpiredData.running ? 'rodando…' : checkExpiredData.aborted ? `interrompido (${checkExpiredData.abortReason || 'aborted'})` : 'concluído'}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${checkExpiredData.total > 0 ? (checkExpiredData.processed / checkExpiredData.total * 100) : 0}%` }}
                    />
                  </div>
                  {/* 24/05/2026 — A3 2-fase: ComprasNet enfileira no scraper (fire-and-forget)
                      e status real vem via callback. enqueued = enviados em background,
                      terminalSinceEnqueue = callbacks que JÁ chegaram resolvendo pra terminal. */}
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="bg-emerald-500/15 text-emerald-300 rounded p-2 text-center">
                      <div className="text-lg font-bold">{checkExpiredData.transitioned}</div>
                      <div className="opacity-80">transicionados</div>
                    </div>
                    <div className="bg-sky-500/15 text-sky-300 rounded p-2 text-center" title="Enviados pro scraper, aguardando callback assíncrono (5-15min)">
                      <div className="text-lg font-bold">
                        {checkExpiredData.terminalSinceEnqueue || 0}/{checkExpiredData.enqueued || 0}
                      </div>
                      <div className="opacity-80">resolvidos async</div>
                    </div>
                    <div className="bg-slate-600/30 text-slate-400 rounded p-2 text-center">
                      <div className="text-lg font-bold">{checkExpiredData.unchanged}</div>
                      <div className="opacity-80">sem mudança</div>
                    </div>
                    <div className="bg-red-500/15 text-red-300 rounded p-2 text-center">
                      <div className="text-lg font-bold">{checkExpiredData.errors}</div>
                      <div className="opacity-80">erros</div>
                    </div>
                  </div>
                  {/* Aviso 2-fase quando há items aguardando callback */}
                  {!checkExpiredData.running && (checkExpiredData.enqueued || 0) > (checkExpiredData.terminalSinceEnqueue || 0) && (
                    <div className="bg-sky-500/10 border border-sky-500/30 rounded p-2.5 text-xs text-sky-200 flex items-start gap-2">
                      <Icon name="info" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>{(checkExpiredData.enqueued || 0) - (checkExpiredData.terminalSinceEnqueue || 0)} items ainda processando em background.</strong>
                        <br />Pode fechar esta janela — resultados chegam nos próximos 5-15min via scraper. A lista atualiza sozinha.
                      </div>
                    </div>
                  )}
                  {/* Log em tempo real, mais recentes no topo */}
                  <div className="bg-central-fundo/40 rounded border border-central-borda/40 max-h-[40vh] overflow-y-auto">
                    {(checkExpiredData.log || []).length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-500">Aguardando primeiros itens…</div>
                    ) : (
                      <ul className="divide-y divide-slate-700/30">
                        {(checkExpiredData.log || []).slice().reverse().map((l: any, i: number) => {
                          const badgeCls = l.status === 'transicionado' || l.status === 'resolvido_async'
                            ? 'bg-emerald-500/15 text-emerald-300'
                            : l.status === 'erro'
                              ? 'bg-red-500/15 text-red-300'
                              : l.status === 'enfileirado'
                                ? 'bg-sky-500/15 text-sky-300'
                                : 'bg-slate-600/30 text-slate-400';
                          return (
                            <li key={i} className="px-3 py-1.5 text-[11px] flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${badgeCls}`}>{l.status}</span>
                              <span className="text-slate-300 truncate flex-1" title={`${l.orgao} · ${l.numero}`}>
                                {l.orgao}
                                {l.numero && l.numero !== '—' && <span className="text-slate-500"> · {l.numero}</span>}
                              </span>
                              {l.before && l.after && (
                                <span className="text-[10px] text-emerald-400 whitespace-nowrap">{l.before} → {l.after}</span>
                              )}
                              {l.msg && !l.after && (
                                <span className="text-[10px] text-slate-500 truncate max-w-[180px]" title={l.msg}>{l.msg}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}
        </CascaDeModal>
      )}

      {/* Modal: Análise IA do Edital (acionado pelo menu de linha em lic-cadastrar) */}
      {aiModalItem && (
        // Fecha ao clicar fora, como já era (a análise fica salva no servidor). O
        // PanelHeader próprio vira o da casca; o rodapé (modelo, tokens, custo e
        // "Reanalisar", com confirm de custo) só aparece com análise carregada.
        <CascaDeModal
          largura="lg"
          fecharAoClicarFora
          titulo="Análise IA do Edital"
          subtitulo={`${aiModalItem.orgaoNome || aiModalItem.unidadeNome || 'Licitação'} · ${aiModalItem.modalidadeNome || ''} ${formatNumeroCompraComAno(aiModalItem.numeroCompra, aiModalItem.anoCompra)}`}
          onClose={() => setAiModalItem(null)}
          corpoClassName="p-5"
          rodape={aiModalData && !aiModalLoading ? (
            <>
                <div className="text-[10px] text-slate-500">
                  {aiModalData.modeloUsado || 'sonnet-4-5'}
                  {aiModalData.tokensUsados && <> · {aiModalData.tokensUsados.input?.toLocaleString('pt-BR')} in / {aiModalData.tokensUsados.output?.toLocaleString('pt-BR')} out</>}
                  {aiModalData.custoUsd != null && <> · US$ {Number(aiModalData.custoUsd).toFixed(4)}</>}
                </div>
                <button
                  onClick={async () => {
                    if (!(await confirm({
                      title: 'Reanalisar o edital?',
                      confirmLabel: 'Reanalisar',
                      consequence: 'Gera uma nova chamada à API Claude, com custo aproximado de R$ 0,60 a R$ 1,00.',
                    }))) return;
                    setAiModalError(null);
                    setAiModalRunning(true);
                    try {
                      await procurementService.clearEditalAnalysis(aiModalItem.id);
                      const result = await procurementService.analyzeEditalPncp(aiModalItem.id);
                      setAiModalData(result);
                      setAiModalAnalyzedAt(new Date().toISOString());
                    } catch (err: any) {
                      setAiModalError(err?.response?.data?.message || err.message);
                    } finally {
                      setAiModalRunning(false);
                    }
                  }}
                  className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded transition-colors inline-flex items-center gap-1"
                >
                  <Icon name="refresh" className="w-3 h-3" /> Reanalisar
                </button>
            </>
          ) : undefined}
        >
              {aiModalLoading && (
                <div className="text-center py-10 text-slate-400 animate-pulse">Carregando análise salva...</div>
              )}

              {!aiModalLoading && aiModalError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="text-red-300 font-medium">Erro: {aiModalError}</div>
                </div>
              )}

              {!aiModalLoading && !aiModalError && !aiModalData && !aiModalRunning && (
                <div className="space-y-4">
                  <div className="bg-slate-700/30 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-500/15 rounded-full flex items-center justify-center shrink-0">
                        <Icon name="zap" className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <div className="text-white text-sm font-medium">Selecione qual arquivo analisar</div>
                        <div className="text-slate-400 text-xs">Claude Sonnet 4.5 · 30-60s · ~R$ 0,60-1 por análise · fica salvo</div>
                      </div>
                    </div>

                    {aiModalFiles && aiModalFiles.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-[11px] text-slate-500 uppercase font-medium mb-1">Arquivos disponíveis no PNCP ({aiModalFiles.length})</div>
                        {aiModalFiles.map((f) => {
                          const isEdital = f.tipoDocumentoId === 1 || f.tipoDocumentoId === 2 || /edital/i.test(f.tipoDocumentoNome || '');
                          const isTR = /termo.*refer|^TR$|anexo.*i/i.test(f.tipoDocumentoNome || '') || /termo.*refer/i.test(f.titulo || '');
                          return (
                            <div key={f.sequencialDocumento} className="flex items-center justify-between gap-2 bg-slate-700/40 hover:bg-slate-700/60 rounded-lg p-2.5 transition-colors">
                              <div className="flex items-start gap-2 min-w-0 flex-1">
                                <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${isEdital ? 'bg-orange-500/15' : isTR ? 'bg-blue-500/15' : 'bg-slate-600/30'}`}>
                                  <svg className={`w-4 h-4 ${isEdital ? 'text-orange-400' : isTR ? 'text-blue-400' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-white truncate" title={f.titulo}>{f.titulo}</div>
                                  <div className="text-[11px] text-slate-500">
                                    {f.tipoDocumentoNome || 'Documento'}
                                    {f.dataPublicacaoPncp && <> · {fmtDate(f.dataPublicacaoPncp)}</>}
                                    {isEdital && <span className="ml-2 text-orange-400">[Edital]</span>}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  setAiModalError(null);
                                  setAiModalSelectedFile(f.sequencialDocumento);
                                  setAiModalRunning(true);
                                  try {
                                    const result = await procurementService.analyzeEditalPncp(aiModalItem.id, f.sequencialDocumento);
                                    setAiModalData(result);
                                    setAiModalAnalyzedAt(new Date().toISOString());
                                  } catch (err: any) {
                                    setAiModalError(err?.response?.data?.message || err.message);
                                  } finally {
                                    setAiModalRunning(false);
                                    setAiModalSelectedFile(null);
                                  }
                                }}
                                className="text-[11px] px-2.5 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 text-purple-200 rounded transition-colors inline-flex items-center gap-1 shrink-0"
                              >
                                <Icon name="zap" className="w-3 h-3" /> Analisar
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-sm">
                        {aiModalFilesErro
                          ? <span role="alert" className="text-amber-300" data-arquivos-do-pncp="nao-consultou">Não consegui consultar os arquivos no PNCP ({aiModalFilesErro}). Isto não quer dizer que não há arquivo: o upload manual abaixo segue disponível.</span>
                          : aiModalFiles === null ? 'Carregando arquivos...' : 'Nenhum arquivo encontrado no PNCP. Use upload manual abaixo.'}
                      </div>
                    )}

                    <div className="border-t border-central-borda mt-3 pt-3">
                      <label
                        className="flex items-center justify-center gap-2 px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-purple-600'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('bg-purple-600'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('bg-purple-600');
                          const file = e.dataTransfer?.files?.[0];
                          if (file && !aiModalRunning) analyzeAiModalEditalFile(file);
                        }}
                      >
                        <Icon name="upload" className="w-4 h-4" /> Enviar PDF manualmente
                        <input
                          type="file" accept="application/pdf,.pdf" className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            await analyzeAiModalEditalFile(file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {aiModalRunning && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6 text-center">
                  <div className="inline-block w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mb-3" />
                  <div className="text-white font-medium">Analisando edital com IA...</div>
                  <div className="text-purple-300/70 text-sm mt-1">Pode levar até 60 segundos. Aguarde.</div>
                </div>
              )}

              {aiModalData && !aiModalLoading && (
                <div className="space-y-3">
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-xs text-slate-500 uppercase mb-1">Resumo</div>
                        <div className="text-white text-sm">{aiModalData.resumo}</div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
                        aiModalData.criticidadeGeral === 'alta' ? 'bg-red-500/20 text-red-300' :
                        aiModalData.criticidadeGeral === 'media' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-green-500/20 text-green-300'
                      }`}>
                        {aiModalData.criticidadeGeral === 'alta' ? 'Criticidade Alta' :
                         aiModalData.criticidadeGeral === 'media' ? 'Criticidade Média' : 'Criticidade Baixa'}
                      </span>
                    </div>
                    {aiModalAnalyzedAt && (
                      <div className="text-[10px] text-slate-500 mt-2 pt-2 border-t border-central-borda">
                        Analisado em {fmtDateTime(aiModalAnalyzedAt)} · Fonte: {aiModalData.fonte || '—'}
                      </div>
                    )}
                  </div>

                  {/* COMO o objeto é executado. Fica ANTES dos pontos de atenção porque
                      muda a régua de preço inteira: operar um serviço com gente traz
                      folha, e o piso não desce com escala como o de software desce. */}
                  {aiModalData.execucao && (aiModalData.execucao.forneceMaoDeObra != null || aiModalData.execucao.janelaAtendimento) && (
                    <div className={`rounded-lg p-3 border ${
                      aiModalData.execucao.forneceMaoDeObra
                        ? 'bg-orange-500/10 border-orange-500/40'
                        : 'bg-slate-700/30 border-slate-600/40'
                    }`}>
                      <div className="text-sm font-semibold text-white mb-2 flex items-center gap-2 flex-wrap">
                        <span aria-hidden>👥</span>
                        Execução do objeto
                        {aiModalData.execucao.forneceMaoDeObra === true && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-200 font-medium uppercase">
                            você fornece a mão de obra
                          </span>
                        )}
                        {aiModalData.execucao.forneceMaoDeObra === false && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600/40 text-slate-300 font-medium uppercase">
                            sem mão de obra
                          </span>
                        )}
                        {aiModalData.execucao.dedicacaoExclusiva === true && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200 font-medium uppercase">
                            dedicação exclusiva
                          </span>
                        )}
                      </div>
                      {aiModalData.execucao.forneceMaoDeObra === true && (
                        <div className="text-xs text-orange-200/90 mb-2">
                          Contratação de <b>operação</b>, não só de sistema: o custo tem folha e escala de turno, e o piso não cai com escala.
                        </div>
                      )}
                      <div className="space-y-1.5 text-xs">
                        {aiModalData.execucao.janelaAtendimento && (
                          <div><span className="text-slate-400">Janela de atendimento: </span><span className="text-slate-200">{aiModalData.execucao.janelaAtendimento}</span></div>
                        )}
                        {aiModalData.execucao.dimensionamentoReferencial && (
                          <div><span className="text-slate-400">Dimensionamento: </span><span className="text-slate-200">{aiModalData.execucao.dimensionamentoReferencial}</span></div>
                        )}
                        {aiModalData.execucao.fundamento && (
                          <div className="text-slate-400 italic pt-1 border-t border-central-borda/60">{aiModalData.execucao.fundamento}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {aiModalData.pontosDeAtencao?.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <Icon name="alertTriangle" className="w-4 h-4 text-yellow-400" />
                        Pontos de Atenção ({aiModalData.pontosDeAtencao.length})
                      </div>
                      <div className="space-y-2">
                        {aiModalData.pontosDeAtencao.map((p: any, i: number) => (
                          <div key={i} className={`rounded-lg p-3 border ${
                            p.severidade === 'alta' ? 'bg-red-500/10 border-red-500/30' :
                            p.severidade === 'media' ? 'bg-yellow-500/10 border-yellow-500/30' :
                            'bg-blue-500/10 border-blue-500/30'
                          }`}>
                            <div className="flex items-start gap-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase shrink-0 ${
                                p.severidade === 'alta' ? 'bg-red-500/30 text-red-200' :
                                p.severidade === 'media' ? 'bg-yellow-500/30 text-yellow-200' :
                                'bg-blue-500/30 text-blue-200'
                              }`}>{p.severidade}</span>
                              <div className="flex-1">
                                <div className="text-white text-sm font-medium">{p.titulo}</div>
                                <div className="text-slate-300 text-xs mt-1">{p.descricao}</div>
                                {p.acaoSugerida && <div className="text-slate-400 text-xs mt-2 italic">→ {p.acaoSugerida}</div>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiModalData.prazosCriticos?.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <Icon name="clock" className="w-4 h-4 text-orange-400" />
                        Prazos Críticos de Documentos
                      </div>
                      <div className="space-y-2">
                        {aiModalData.prazosCriticos.map((p: any, i: number) => (
                          <div key={i} className="bg-slate-700/30 rounded-lg p-3">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="text-white text-sm font-medium">{p.documento}</div>
                              {p.diasEmissao && <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded">{p.diasEmissao} dias</span>}
                            </div>
                            <div className="text-slate-400 text-xs italic">"{p.regra}"</div>
                            {p.alerta && <div className="text-yellow-300 text-xs mt-2">⚠ {p.alerta}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiModalData.qualificacaoEconomicoFinanceira && (
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <div className="text-sm font-semibold text-white mb-2">Qualificação Econômico-Financeira</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        <div><span className="text-slate-500">Balanço patrimonial:</span> <span className="text-slate-200">{aiModalData.qualificacaoEconomicoFinanceira.balancoPatrimonial ? 'Exigido' : 'Não exigido'}</span></div>
                        {aiModalData.qualificacaoEconomicoFinanceira.indices && (
                          <div className="md:col-span-2"><span className="text-slate-500">Índices:</span> <span className="text-slate-200">{aiModalData.qualificacaoEconomicoFinanceira.indices}</span></div>
                        )}
                        {aiModalData.qualificacaoEconomicoFinanceira.garantiaPropostaPercent != null && (
                          <div><span className="text-slate-500">Garantia proposta:</span> <span className="text-slate-200">{aiModalData.qualificacaoEconomicoFinanceira.garantiaPropostaPercent}%</span></div>
                        )}
                        {aiModalData.qualificacaoEconomicoFinanceira.garantiaContratualPercent != null && (
                          <div><span className="text-slate-500">Garantia contratual:</span> <span className="text-slate-200">{aiModalData.qualificacaoEconomicoFinanceira.garantiaContratualPercent}%</span></div>
                        )}
                      </div>
                    </div>
                  )}

                  {aiModalData.atestadosCapacidadeTecnica?.exigido && (
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <div className="text-sm font-semibold text-white mb-2">Atestados de Capacidade Técnica</div>
                      <div className="text-xs text-slate-300 space-y-1">
                        {aiModalData.atestadosCapacidadeTecnica.quantidadeMinima && (
                          <div><span className="text-slate-500">Mínimo:</span> {aiModalData.atestadosCapacidadeTecnica.quantidadeMinima}</div>
                        )}
                        {aiModalData.atestadosCapacidadeTecnica.similaridadeObjeto && (
                          <div><span className="text-slate-500">Similaridade:</span> {aiModalData.atestadosCapacidadeTecnica.similaridadeObjeto}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {aiModalData.declaracoesEspecificas?.length > 0 && (
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <div className="text-sm font-semibold text-white mb-2">Declarações Específicas</div>
                      <ul className="text-xs text-slate-300 list-disc list-inside space-y-0.5">
                        {aiModalData.declaracoesEspecificas.map((d: string, i: number) => <li key={i}>{d}</li>)}
                      </ul>
                    </div>
                  )}

                  {aiModalData.habilitacaoTecnica?.length > 0 && (
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <div className="text-sm font-semibold text-white mb-2">Habilitação Técnica</div>
                      <ul className="text-xs text-slate-300 list-disc list-inside space-y-0.5">
                        {aiModalData.habilitacaoTecnica.map((d: string, i: number) => <li key={i}>{d}</li>)}
                      </ul>
                    </div>
                  )}

                  {aiModalData.vigenciaContratual && (
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <div className="text-sm font-semibold text-white mb-2">Vigência Contratual</div>
                      <div className="text-xs text-slate-300">{aiModalData.vigenciaContratual}</div>
                    </div>
                  )}
                </div>
              )}
        </CascaDeModal>
      )}

      {/* Painel lateral — revisão em batch de duplicatas fuzzy. Lista cada
          par lado-a-lado e botões Juntar/Não é duplicata. Só chega com
          sobrevivente marcado o par em que o servidor decidiu por atividade;
          o resto chega NEUTRO (ver licitacoes/revisaoDuplicatas.ts). Card some otimisticamente após ação,
          backend chamado por handleMergeDuplicate/handleDismissDuplicate
          (já existentes), e a lista de sugestões é recarregada no fim. */}
      {showDupReview && (
        // Gaveta de LEITURA (clique fora e Esc fecham). Fica em z-40 para o
        // "Ver detalhes" de um card abrir a gaveta do detalhe (z-50) POR CIMA
        // dela; com as duas em z-50 o detalhe abria por baixo, porque esta vem
        // depois no DOM.
        <CascaDeGaveta
          titulo="Revisão de duplicatas"
          subtitulo={`${dupSuggestions.length} ${dupSuggestions.length === 1 ? 'par detectado' : 'pares detectados'} — decida o que é a mesma licitação`}
          onClose={() => setShowDupReview(false)}
          fecharAoClicarFora
          largura="lista"
          zIndex="z-40"
          corpoClassName="p-3 space-y-3"
          rodape={<p className="text-[10px] text-slate-500">
            ⭐ = sobrevivente. <strong>Clique no card pra escolher qual fica.</strong> Par sem certeza chega sem escolha. Filhos do descartado são reapontados pro sobrevivente.
          </p>}
        >
              {dupSuggestions.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <Icon name="check" className="w-12 h-12 mx-auto mb-3 text-green-500/50" />
                  <p className="text-sm">Nenhuma duplicata pendente!</p>
                  <p className="text-xs text-slate-600 mt-1">Boa hora pra clicar Sincronizar</p>
                </div>
              ) : (
                dupSuggestions.map((sug: any, idx: number) => {
                  const pairKey = `${sug.a.id}-${sug.b.id}`;
                  // Sobrevivente: escolha do operador (survivorOverrides) ou, só
                  // quando o servidor decidiu por atividade, o sugerido por ele.
                  const leitura = lerPar(sug);
                  const survivor = ladoEscolhido(leitura, survivorOverrides[pairKey]);
                  const wasOverridden = !!leitura.ladoPadrao && !!survivorOverrides[pairKey] && survivorOverrides[pairKey] !== leitura.ladoPadrao;
                  const portalLabel = (k: string) => PORTAL_MAP[k]?.label || k || '—';
                  const sideCard = (s: any, side: 'a' | 'b', isSurvivor: boolean) => (
                    <div
                      onClick={() => setSurvivorOverrides((prev) => ({ ...prev, [pairKey]: side }))}
                      title={survivor == null ? 'Clique para escolher esta como a que fica' : isSurvivor ? 'Esta licitação vai sobreviver — clique no outro lado pra inverter' : 'Clique pra fazer esta sobreviver'}
                      className={`flex-1 p-2 rounded border cursor-pointer transition-all ${
                        survivor == null
                          ? 'border-slate-600 bg-slate-800/60 hover:border-orange-400/60 hover:bg-slate-800'
                          : isSurvivor
                            ? 'border-green-500/60 bg-green-500/10 ring-1 ring-green-500/30'
                            : 'border-slate-700 bg-slate-800/40 hover:border-slate-500 hover:bg-slate-800/70 opacity-70'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-1 mb-1">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500">
                          {survivor == null ? 'escolher esta' : isSurvivor ? '⭐ sobrevive' : 'descartado'}
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${PORTAL_MAP[s.portalOrigem]?.color || 'text-slate-400'} bg-slate-700/60`}>
                          {portalLabel(s.portalOrigem).slice(0, 18)}
                        </span>
                      </div>
                      <div className="text-xs text-white font-medium leading-tight line-clamp-2" title={s.orgaoNome}>
                        {s.orgaoNome || '—'}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1 space-y-0.5">
                        <div>{s.municipio ? `${s.municipio}/${s.uf}` : s.uf || '—'}</div>
                        <div>{s.numeroCompra ? `nº ${formatNumeroCompraComAno(s.numeroCompra, s.anoCompra)}` : '— sem número'}</div>
                        <div className="text-slate-500">
                          {s.orgaoCnpj ? `CNPJ ${s.orgaoCnpj.slice(0, 4)}…` : 'sem CNPJ'}
                        </div>
                        <div className="text-[10px] text-slate-600 truncate" title={s.pncpId}>{s.pncpId}</div>
                      </div>
                      {/* Objeto: principal sinal pra confirmar se é mesma licitação.
                          line-clamp-4 + hover/title pro texto completo. */}
                      {s.objetoCompra && (
                        <div className="mt-2 pt-2 border-t border-central-borda/60">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500 mb-0.5">
                            Objeto
                            {leitura.ladoCurto === side && (
                              <span className="ml-1 normal-case tracking-normal text-sky-300" title="Este portal grava só um resumo de uma linha do objeto">· resumo do portal</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-300 leading-snug line-clamp-4" title={s.objetoCompra}>
                            {s.objetoCompra}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); openDetail(items.find((it) => it.id === s.id) || s); }}
                        className="mt-1.5 text-[10px] text-orange-400 hover:text-orange-300"
                      >
                        Ver detalhes →
                      </button>
                    </div>
                  );
                  return (
                    <div key={pairKey} className="border border-central-borda rounded-lg overflow-hidden bg-central-fundo/60">
                      <div className="px-3 py-1.5 bg-central-elevado/60 flex items-center justify-between text-[11px]">
                        <span className="text-slate-400">Par {idx + 1} / {dupSuggestions.length}</span>
                        {/* O % informa, não decide: sem cor de alerta e, no
                            objeto curto, substituído pelo motivo. */}
                        <span
                          className={leitura.objetoCurto ? 'text-sky-300 font-medium' : 'text-slate-500'}
                          title={leitura.explicacao}
                        >
                          {leitura.rotulo}
                        </span>
                      </div>
                      <div className="p-2 flex gap-2">
                        {sideCard(sug.a, 'a', survivor === 'a')}
                        {sideCard(sug.b, 'b', survivor === 'b')}
                      </div>
                      {survivor == null && (
                        <div className="px-3 pb-1 text-[10px] text-slate-400">
                          {leitura.objetoCurto
                            ? 'Um lado tem só o resumo do portal: compare órgão, número e data, e clique na licitação que fica.'
                            : 'Par sem certeza: clique na licitação que fica.'}
                        </div>
                      )}
                      {wasOverridden && (
                        <div className="px-3 pb-1 text-[10px] text-amber-400 flex items-center gap-1">
                          ⚠ Você inverteu o sobrevivente sugerido pelo servidor (ele tinha mais atividade).
                          <button
                            onClick={() => setSurvivorOverrides((prev) => { const n = { ...prev }; delete n[pairKey]; return n; })}
                            className="underline hover:text-amber-300"
                          >
                            voltar ao sugerido
                          </button>
                        </div>
                      )}
                      <div className="px-2 pb-2 flex gap-2">
                        <button
                          onClick={() => {
                            if (survivor == null) return;
                            // survivorId (3º arg) FORÇA por cima do score do servidor:
                            // só vai quando o operador clicou num lado.
                            const p = pedidoDeJuntar(sug, survivor, !!survivorOverrides[pairKey]);
                            handleMergeDuplicate(p.itemId, p.otherId, p.survivorId);
                          }}
                          disabled={mergingDup || survivor == null}
                          title={survivor == null ? 'Escolha primeiro qual licitação fica' : undefined}
                          className={`flex-1 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded text-xs font-medium transition-colors disabled:opacity-50 ${mergingDup ? 'disabled:cursor-wait' : 'disabled:cursor-not-allowed'}`}
                        >
                          {survivor == null ? 'Escolha quem fica para juntar' : `✓ Juntar (sobrevive ${survivor === 'a' ? 'esquerda' : 'direita'})`}
                        </button>
                        <button
                          onClick={() => handleDismissDuplicate(sug.a.id, sug.b.id)}
                          className="flex-1 py-1.5 bg-slate-700/60 hover:bg-slate-700 text-slate-300 rounded text-xs transition-colors"
                        >
                          ✕ Não é duplicata
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
        </CascaDeGaveta>
      )}

      {/* Modal de confirmação ao deletar proposta(s) — single ou bulk.
          User escolhe destino (lixeira/encontrar) + se mantém monitoramento
          e dispute. Desacopla Cadastrar de Monitorar/Disputar. */}
      {deleteProposalModal && (
        // Destino e opções escolhidos: não fechava ao clicar fora e continua não
        // fechando. O × e o Cancelar ficam travados enquanto deleta, como o Cancelar
        // já ficava. O ícone de lixeira sai do título (§10.4).
        <CascaDeModal
          largura="sm"
          titulo={deleteProposalModal.type === 'bulk'
            ? `Deletar ${deleteProposalModal.ids.length} propostas`
            : 'Deletar proposta'}
          onClose={() => { if (!deletingProposals) setDeleteProposalModal(null); }}
          corpoClassName="p-5 space-y-5"
          rodape={
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => setDeleteProposalModal(null)}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white"
                disabled={deletingProposals}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteProposals}
                disabled={deletingProposals}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 rounded text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deletingProposals ? 'Deletando…' : `✓ Confirmar`}
              </button>
            </div>
          }
        >
              {deleteProposalModal.type === 'single' && (
                <div className="text-sm text-slate-300">
                  Deletar a proposta de <strong>{deleteProposalModal.titles[0]?.slice(0, 80)}</strong>?
                </div>
              )}
              {deleteProposalModal.type === 'bulk' && (
                <div className="text-sm text-slate-300">
                  Deletar <strong>{deleteProposalModal.ids.length}</strong> propostas selecionadas:
                  <div className="mt-2 max-h-32 overflow-y-auto bg-central-elevado/60 rounded p-2 text-xs text-slate-400 space-y-0.5">
                    {deleteProposalModal.titles.slice(0, 10).map((t, i) => (
                      <div key={i} className="truncate">• {t}</div>
                    ))}
                    {deleteProposalModal.titles.length > 10 && (
                      <div className="text-slate-500">… e mais {deleteProposalModal.titles.length - 10}</div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Pra onde mandar a licitação?</div>
                <label className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-slate-800/60">
                  <input
                    type="radio"
                    name="dest"
                    value="lixeira"
                    checked={deleteDestination === 'lixeira'}
                    onChange={() => setDeleteDestination('lixeira')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm text-white">🗑️ Lixeira <span className="text-xs text-slate-500">(padrão)</span></div>
                    <div className="text-[11px] text-slate-400">Marca como descartada. AL não recria no próximo sync. Some do funil.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-slate-800/60">
                  <input
                    type="radio"
                    name="dest"
                    value="encontrar"
                    checked={deleteDestination === 'encontrar'}
                    onChange={() => setDeleteDestination('encontrar')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm text-white">🔍 Voltar ao Encontrar</div>
                    <div className="text-[11px] text-slate-400">Volta como "novo" pra reanalisar depois.</div>
                  </div>
                </label>
              </div>

              <div className="border-t border-central-borda pt-4 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteKeepMonitored}
                    onChange={(e) => setDeleteKeepMonitored(e.target.checked)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm text-white">📡 Manter monitoramento de mensagens</div>
                    <div className="text-[11px] text-slate-400">VNC continua scrape do chat do portal. Útil pra ver 2ª convocação ou avisos pós-disputa.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteKeepDispute}
                    onChange={(e) => setDeleteKeepDispute(e.target.checked)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm text-white">⚔️ Manter sessões de disputa abertas</div>
                    <div className="text-[11px] text-slate-400">Preserva dispute_sessions ativas (default: encerra). Use se ainda há disputa em andamento.</div>
                  </div>
                </label>
              </div>
        </CascaDeModal>
      )}
    </div>
  );
}
